// assets/blade.js
// Refactored Frameless Glass Fencing Calculator Module

// Debug Configuration
const ENABLE_SIZE_DEBUG = false;

// Utility Classes
class DOMCache {
  constructor() {
    this.elements = {};
  }
  
  get(id) {
    // Re-query if not cached or previously returned null (element may not have
    // existed when first queried — e.g. inside a hidden tab on page load)
    if (!this.elements[id]) {
      this.elements[id] = document.getElementById(id);
    }
    return this.elements[id] || null;
  }

  getForce(id) {
    // Always re-query — use for elements that may appear/disappear (e.g. in tabs)
    this.elements[id] = document.getElementById(id);
    return this.elements[id] || null;
  }
  
  clear(id) {
    delete this.elements[id];
  }
  
  clearAll() {
    this.elements = {};
  }
}

class PriceHelper {
  static fromCents(cents) {
    return cents / 100;
  }
  
  static format(amount, shopifyData) {
    const currency = shopifyData.shop?.currency || 'USD';
    const moneyFormat = shopifyData.shop?.moneyFormat || '${{amount}}';
    return moneyFormat.replace('{{amount}}', amount.toFixed(2));
  }
}

class VariantHelper {
  static findVariant(product, criteria) {
    if (!product || !product.variants) return null;
    
    return product.variants.find(v => {
      if (!v.available) return false;
      
      return Object.entries(criteria).every(([key, value]) => {
        return !value || v[key] === value;
      });
    }) || product.variants.find(v => v.available);
  }
  
  static findVariantBySize(product, size) {
    return this.findVariant(product, { size: size.toString() });
  }
  
  static getAvailableSizes(product) {
    if (!product || !product.variants) return [];
    
    return product.variants
      .filter(v => v.available && v.size)
      .map(v => parseInt(v.size))
      .sort((a, b) => a - b);
  }
}

class PanelCalculator {
  // ─── Post count helper ────────────────────────────────────────────────────
  // posts = panels + 1 (base), +1 if gate present, +0 for rake (behaves like a panel).
  static estimatePosts(panels, hasGate, hasRake) {
    let posts = panels + 1;
    if (hasGate) posts += 1; // gate adds one extra post
    // hasRake adds no extra post — rake behaves like a panel
    return posts;
  }

  // ─── Deterministic layout solver ─────────────────────────────────────────
  // Order: rake (fixed, left) → panels (loop) → gate (fixed, after panels) → cut (right end).
  // postSize is included in every loop iteration so panel count is exact.
  //
  // Gate footprint = gateSize (leaf) only. No clearance gaps around gate posts.
  //
  // config: {
  //   total     – total section length (mm)
  //   panelSize – chosen panel size (mm)
  //   postSize  – post/channel width (mm)
  //   gateSize  – gate leaf width (mm), used when hasGate is true
  //   hasGate   – boolean
  //   hasRake   – boolean
  //   rakeSize  – raked-end panel width (mm), used when hasRake is true
  //   maxSheetLength – optional; if set, filler merges into under-max panel
  // }
  //
  // Returns: { layout, panelCount, postCount, cut, flexedPanelIdx, isFlexed }
  static solveFenceLayout(config) {
    const { total, panelSize, postSize, gateSize, hasGate, hasRake, rakeSize, maxSheetLength } = config;

    // Gate footprint is the gate leaf size only — no extra gap allowances.
    // Perfmesh gates sit directly against posts with no clearance gap.
    const gateFootprint = hasGate ? gateSize : 0;

    // 1. FIXED BASE: rake placed left (gate placed AFTER panel loop)
    let baseUsed = 0;
    if (hasRake) baseUsed += rakeSize;

    // 2. FILL PANELS LEFT → RIGHT (gate NOT included in loop — added after)
    let panelCount = 0;

    while (true) {
      const testPanels   = panelCount + 1;
      const usedPanels   = baseUsed + (testPanels * panelSize);
      const usedWithGate = usedPanels + gateFootprint;
      const postCount    = PanelCalculator.estimatePosts(testPanels, hasGate, hasRake);
      const totalUsed    = usedWithGate + (postCount * postSize);

      if (totalUsed > total) break;
      panelCount++;
    }

    // 3. FINAL CALCULATION
    let usedPanels   = baseUsed + (panelCount * panelSize);
    let usedWithGate = usedPanels + gateFootprint;
    let postCount    = PanelCalculator.estimatePosts(panelCount, hasGate, hasRake);
    let usedFinal    = usedWithGate + (postCount * postSize);
    let cut          = total - usedFinal;

    // 4. VALIDATION — minimum cut panel is 300 mm
    // SKIP when flex merge enabled: small cuts will be absorbed into the last panel
    if (cut < 300 && panelCount > 0 && !maxSheetLength) {
      panelCount--;
      usedPanels   = baseUsed + (panelCount * panelSize);
      usedWithGate = usedPanels + gateFootprint;
      postCount    = PanelCalculator.estimatePosts(panelCount, hasGate, hasRake);
      usedFinal    = usedWithGate + (postCount * postSize);
      cut          = total - usedFinal;
    }

    // 5. BUILD LAYOUT — cut panel always at right end
    const layout = [];
    if (hasRake)  layout.push({ type: 'rake',  size: rakeSize  });
    for (let i = 0; i < panelCount; i++) layout.push({ type: 'panel', size: panelSize });
    if (hasGate)  layout.push({ type: 'gate',  size: gateSize  });
    layout.push({ type: 'panel', size: cut });

    // 6. FLEXIBLE MERGE: if maxSheetLength set, merge filler into under-max panel
    let flexedPanelIdx = -1;
    let isFlexed = false;
    
    if (maxSheetLength) {
      const mergeResult = PanelCalculator.mergeFillerToFlexedPanel(layout, panelSize, cut, maxSheetLength);
      if (mergeResult.isFlexed) {
        layout.splice(...mergeResult.spliceArgs);
        flexedPanelIdx = mergeResult.flexedIdx;
        isFlexed = true;
      }
    }

    return { layout, panelCount, postCount, cut, flexedPanelIdx, isFlexed };
  }

  // ─── Flexible panel merge helper ──────────────────────────────────────────
  // Finds the last standard panel <= maxSheetLength and absorbs the filler into it.
  // Removes the separate cut panel from layout.
  //
  // Returns: { spliceArgs: [idx, delCount], flexedIdx, isFlexed }
  static mergeFillerToFlexedPanel(layout, panelSize, filler, maxSheetLength) {
    let flexedIdx = -1;
    
    // Scan backward from end, skip rake/gate, find last standard panel that can
    // absorb the filler and STILL stay within the sheet maximum
    for (let i = layout.length - 2; i >= 0; i--) {
      const panel = layout[i];
      if (panel.type === 'panel' && panel.size + filler <= maxSheetLength) {
        // Guard the size AFTER the merge, not before. Checking the pre-merge
        // size meant a full panel (size === maxSheetLength) always qualified and
        // then had the filler added on top, producing a panel LONGER than the
        // stock sheet - e.g. a 2400mm Blade panel drawn and billed as 2690mm,
        // which cannot be manufactured. Now a panel only absorbs the filler if
        // the result still fits.
        flexedIdx = i;
        break;
      }
    }
    
    // If found, merge filler into it and remove cut panel
    if (flexedIdx >= 0) {
      layout[flexedIdx].size += filler;
      layout[flexedIdx].isFlexed = true;
      
      return {
        spliceArgs: [layout.length - 1, 1],  // Remove last element (cut panel)
        flexedIdx,
        isFlexed: true
      };
    }
    
    // No panel below max; no merge
    return {
      spliceArgs: [0, 0],
      flexedIdx: -1,
      isFlexed: false
    };
  }
}

// Configuration Constants
const CONFIG = {
  DEFAULTS: {
    PANEL_LENGTH: 2400,
    GATE_LENGTH: 920,
    GAP_SPACING: 50,
    RETAINED_HEIGHT: 1500,
    RAKED_PANEL_WIDTH: 1252,   // James Glen blade rake panels are 1252 wide at EVERY height (SKUs 1252x1300 .. 1252x1800). Was 1200.
    MAX_PANEL_VARIANCE: 650,
    MAX_GAP_VARIANCE: 10,
    MAX_CALCULATED_PANEL_VARIANCE: 50
  },
  CONSTRAINTS: {
    MIN_SECTION_LENGTH: 100,
    MAX_SECTION_LENGTH: 100000,
    MIN_PANEL_WIDTH: 100,
    MAX_PANEL_WIDTH: 2100,
    MIN_GAP: 50,
    MAX_GAP: 75,
    GAP_INCREMENT: 5
  },
  PREVIEW: {
    PREFERRED_PANEL_WIDTH: 140,
    MIN_PANEL_WIDTH: 80,
    FALLBACK_CONTAINER_WIDTH: 800,
    GAP_WIDTH: 28
  }
};

// 3D Fence Renderer Class
class FenceRenderer3D {
  constructor(canvasId) {
    this.canvasId = canvasId;
    this.canvas = null;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.fenceGroup = null;
    this.poolGroup = null;
    this.groundGroup = null;
    this.sections = [];
    this.spigotColor = 'black';
    this.isInitialized = false;
    this.THREE = null; // Will be set after THREE.js loads
    
    // Shared materials to prevent shader conflicts
    this.materials = {};
    
    // Colors for different spigot finishes
    this.spigotColors = {
      'black': 0x2a2a2a,
      'white': 0xffffff,
      'mirror': 0xc0c0c0,
      'silver': 0xc0c0c0,
      'gray': 0x808080,
      'grey': 0x808080
    };
    
    this.init();
  }

  createSimpleQuadGeometry(width, height) {
    // Create non-indexed quad geometry to avoid element array buffer issues
    const geometry = new this.THREE.BufferGeometry();
    
    const vertices = new Float32Array([
      // Triangle 1
      -width/2, -height/2, 0,
      width/2, -height/2, 0,
      width/2, height/2, 0,
      
      // Triangle 2
      -width/2, -height/2, 0,
      width/2, height/2, 0,
      -width/2, height/2, 0
    ]);
    
    const normals = new Float32Array([
      0, 0, 1,  0, 0, 1,  0, 0, 1,
      0, 0, 1,  0, 0, 1,  0, 0, 1
    ]);
    
    geometry.setAttribute('position', new this.THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new this.THREE.BufferAttribute(normals, 3));
    
    
    return geometry;
  }

  createRakedPanelGeometry(width, height, panelData) {
    // Create pentagon-shaped raked panel geometry (rectangle with one corner cut at angle)
    // ✅ CRITICAL: Bottom vertices at Y=0 for hard baseline snapping
    const geometry = new this.THREE.BufferGeometry();
    
    const flipped = panelData.flipped || false;
    const retainedHeightMM = panelData.size || 600; // Retained height in mm (the LOWER side)
    
    // ✅ PETER'S REQUIREMENT: High side = standard panel height (1200mm = 1.2m)
    // Low side = retainedHeight (e.g., 600mm = 0.6m)
    const retainedHeight = retainedHeightMM / 1000; // Convert mm to meters
    
    // Calculate cut dimensions
    const cutHeight = height - retainedHeight; // Height difference between high and low sides
    const cutWidth = Math.max(width * 0.25, cutHeight * 1.5); // Proportional cut width
    
    // ✅ CRITICAL: Bottom vertices at Y=0 (floor level) NOT centered at origin
    // This allows positioning at Y=0 for direct ground contact
    let vertices;
    
    // Define the 5 pentagon points - BOTTOM-ALIGNED
    const bottomLeft = [-width/2, 0, 0];   // ✅ Y=0 (floor/ground)
    const bottomRight = [width/2, 0, 0];   // ✅ Y=0 (floor/ground)
    let topLeft, topRight, cutPoint;
    
    if (flipped) {
      // Flipped: LEFT side is HIGH (standard 1200mm), RIGHT side is LOW (retained height)
      // Use case: Raked panel on right side of section
      topLeft = [-width/2, height, 0];              // Left at full 1200mm height
      topRight = [width/2, retainedHeight, 0];      // Right at retained height (e.g., 600mm)
      cutPoint = [width/2 - cutWidth, height, 0];   // Cut point on top left edge
      
      // Triangulate the pentagon
      vertices = new Float32Array([
        ...bottomLeft, ...bottomRight, ...topRight,
        ...bottomLeft, ...topRight, ...topLeft,
        ...topRight, ...cutPoint, ...topLeft
      ]);
    } else {
      // Normal: RIGHT side is HIGH (standard 1200mm), LEFT side is LOW (retained height)
      // Use case: Raked panel on left side of section
      topRight = [width/2, height, 0];              // Right at full 1200mm height
      topLeft = [-width/2, retainedHeight, 0];      // Left at retained height (e.g., 600mm)
      cutPoint = [-width/2 + cutWidth, height, 0];  // Cut point on top right edge
      
      // Triangulate the pentagon
      vertices = new Float32Array([
        ...bottomLeft, ...bottomRight, ...topRight,
        ...bottomLeft, ...topRight, ...topLeft,
        ...topRight, ...cutPoint, ...topLeft
      ]);
    }
    
    const normals = new Float32Array([
      0, 0, 1,  0, 0, 1,  0, 0, 1,
      0, 0, 1,  0, 0, 1,  0, 0, 1,
      0, 0, 1,  0, 0, 1,  0, 0, 1
    ]);
    
    geometry.setAttribute('position', new this.THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new this.THREE.BufferAttribute(normals, 3));
    
    return geometry;
  }

  createGatePanel(width, height, panelData, sectionData) {
    // Create a group to hold the gate panel and hardware
    const gateGroup = new this.THREE.Group();
    
    // Create main gate panel geometry with 12mm thickness
    const panelGeometry = new this.THREE.BoxGeometry(width, height, 0.012);
    const gatePanel = new this.THREE.Mesh(panelGeometry, this.glassMaterial);
    gateGroup.add(gatePanel);
    
    // Get hardware color from section data or use spigot color as fallback
    let hardwareColor = 'black'; // default
    if (sectionData && sectionData.latchColor) {
      hardwareColor = sectionData.latchColor.toLowerCase();
    } else {
      // Fallback to spigot color
      const shapeSelect = document.getElementById('spigot-shape');
      if (shapeSelect && shapeSelect.value) {
        hardwareColor = shapeSelect.value.toLowerCase();
      }
    }
    
    const hardwareColorHex = this.spigotColors[hardwareColor] || this.spigotColors['black'];
    const hardwareMaterial = new this.THREE.MeshBasicMaterial({ 
      color: hardwareColorHex,
      side: this.THREE.DoubleSide
    });
    
    const isFlipped = panelData.flipped || false;
    
    // Create hinges on one side and latch on the other
    this.createGateHardware(gateGroup, width, height, hardwareMaterial, isFlipped);
    
    
    return gateGroup;
  }

  // Create hardware for gate panels (hinges + latch)
  createGateHardware(parentGroup, panelWidth, panelHeight, hardwareMaterial, isFlipped) {
    // Gate has hinges on one side and latch on the other
    const hingeX = isFlipped ? panelWidth/2 : -panelWidth/2;  // Hinge side
    const latchX = isFlipped ? -panelWidth/2 : panelWidth/2;  // Latch side (opposite)
    
    // Hardware box dimensions
    const boxWidth = panelWidth * 0.06;   
    const boxHeight = panelHeight * 0.15;  
    const boxDepth = 0.04;                 
    
    // Create hinge hardware (2 boxes on hinge side)
    const hingePositions = [
      { y: panelHeight * 0.3 },  // Upper hinge
      { y: -panelHeight * 0.3 }  // Lower hinge
    ];
    
    hingePositions.forEach((pos, index) => {
      const hingeGeometry = new this.THREE.BoxGeometry(boxWidth, boxHeight, boxDepth);
      const hinge = new this.THREE.Mesh(hingeGeometry, hardwareMaterial);
      
      hinge.position.set(hingeX, pos.y, boxDepth/2);
      parentGroup.add(hinge);
      
      
    });
    
    // Create latch hardware (1 box on latch side, centered)
    const latchGeometry = new this.THREE.BoxGeometry(boxWidth, boxHeight, boxDepth);
    const latch = new this.THREE.Mesh(latchGeometry, hardwareMaterial);
    
    latch.position.set(latchX, 0, boxDepth/2); // Centered vertically
    parentGroup.add(latch);
    
    
  }

  // Create edge-mounted hardware for hinge panels only
  createEdgeHardware(parentGroup, panelWidth, panelHeight, hardwareMaterial, isFlipped, panelType) {
    // Hardware box dimensions
    const boxWidth = panelWidth * 0.06;   // Width of the hardware box
    const boxHeight = panelHeight * 0.15;  // Height of the hardware box
    const boxDepth = 0.04;                 // Depth (thickness) of the hardware
    
    // Hinge panels: hinge side (left edge normal, right edge when flipped) 
    const edgeX = isFlipped ? panelWidth/2 : -panelWidth/2;
    
    // Create two hardware boxes - upper and lower
    const positions = [
      { y: panelHeight * 0.3 },  // Upper hardware
      { y: -panelHeight * 0.3 }  // Lower hardware
    ];
    
    positions.forEach((pos, index) => {
      // Create box geometry
      const hardwareGeometry = new this.THREE.BoxGeometry(boxWidth, boxHeight, boxDepth);
      const hardware = new this.THREE.Mesh(hardwareGeometry, hardwareMaterial);
      
      // Position on the edge of the panel
      hardware.position.set(
        edgeX, 
        pos.y, 
        boxDepth/2  // Slightly forward from panel
      );
      
      parentGroup.add(hardware);
      
      
    });
  }

  createHingePanel(width, height, panelData, sectionIndex) {
    // Create a group to hold the hinge panel and hardware
    const hingeGroup = new this.THREE.Group();
    
    // Create main hinge panel geometry with 12mm thickness
    const panelGeometry = new this.THREE.BoxGeometry(width, height, 0.012);
    const hingePanel = new this.THREE.Mesh(panelGeometry, this.glassMaterial);
    hingeGroup.add(hingePanel);
    
    // Get hardware color from spigot configuration
    let hardwareColor = 'black'; // default
    const shapeSelect = document.getElementById('spigot-shape');
    if (shapeSelect && shapeSelect.value) {
      hardwareColor = shapeSelect.value.toLowerCase();
    }
    
    const hardwareColorHex = this.spigotColors[hardwareColor] || this.spigotColors['black'];
    const hardwareMaterial = new this.THREE.MeshBasicMaterial({ 
      color: hardwareColorHex,
      side: this.THREE.DoubleSide
    });
    
    const isFlipped = panelData.flipped || false;
    
    // Create edge-mounted hardware boxes (like in the image)
    this.createEdgeHardware(hingeGroup, width, height, hardwareMaterial, isFlipped, 'hinge');
    
    
    return hingeGroup;
  }

  setupMinimalRenderer() {
    // Get canvas
    this.canvas = document.getElementById(this.canvasId);
    if (!this.canvas) {
      throw new Error(`Canvas with id "${this.canvasId}" not found`);
    }

    // ✅ PETER'S FIXED-SCALE RENDERING: 100px = 1000mm (10:1 ratio)
    // Panels maintain consistent visual size regardless of quantity
    // Canvas width expands based on total fence length (scrollable container)
    
    // Create the absolute minimum Three.js setup
    this.scene = new this.THREE.Scene();
    this.scene.background = new this.THREE.Color(0x87ceeb);
    
    // Camera - position to get good view of rectangular pool and fence layout
    const aspect = this.canvas.clientWidth / this.canvas.clientHeight || 1;
    this.camera = new this.THREE.PerspectiveCamera(60, aspect, 0.1, 1000);
    this.camera.position.set(40, 22, 40); // Moved further back and higher up
    this.camera.lookAt(0, 0, 0);
    
    
    
    // Minimal renderer - force non-indexed rendering
    try {
      this.renderer = new this.THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: false,
        alpha: false,
        depth: true,
        stencil: false,
        powerPreference: 'low-power'
      });
      
      // ✅ FIXED-SCALE: Set size based on container, will be updated when fence data loads
      const width = this.canvas.clientWidth || 800;  // Default 800px
      const height = this.canvas.clientHeight || 400; // Default 400px fixed height
      
      
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(1);
      this.renderer.setClearColor(0x87ceeb, 1.0);
      this.renderer.shadowMap.enabled = false;
      this.renderer.autoClear = true;
      
      
      
    } catch (error) {
      throw new Error('WebGL initialization failed');
    }
    
    // Setup OrbitControls
    try {
      const OrbitControlsClass = this.THREE.OrbitControls || window.OrbitControls;
      if (OrbitControlsClass) {
        this.controls = new OrbitControlsClass(this.camera, this.renderer.domElement);
        
        // Configure controls for best pool fence viewing
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.screenSpacePanning = false;
        
        // Limit vertical rotation
        this.controls.minPolarAngle = Math.PI / 6; // 30 degrees from top
        this.controls.maxPolarAngle = Math.PI / 2.2; // Almost horizontal
        
        // Set distance limits - allow much further zoom out
        this.controls.minDistance = 2;
        this.controls.maxDistance = 50;
        
        // Enable auto-rotate for a nice effect
        this.controls.autoRotate = false;
        this.controls.autoRotateSpeed = 0.5;
        
        
      } else {
        this.controls = null;
      }
    } catch (error) {
      this.controls = null;
    }
    
    // Create groups
    this.fenceGroup = new this.THREE.Group();
    this.poolGroup = new this.THREE.Group();
    this.groundGroup = new this.THREE.Group();
    
    this.scene.add(this.fenceGroup);
    this.scene.add(this.poolGroup);
    this.scene.add(this.groundGroup);
    
    // Create materials
    this.glassMaterial = new this.THREE.MeshBasicMaterial({ 
      color: 0x4080ff,
      transparent: true,
      opacity: 0.3,
      side: this.THREE.DoubleSide
    });
    
    this.groundMaterial = new this.THREE.MeshBasicMaterial({ 
      color: 0x4a7c59
    });
    
    this.poolMaterial = new this.THREE.MeshBasicMaterial({ 
      color: 0x4080ff,
      transparent: true,
      opacity: 0.8
    });
    
    // Create ground that's 3x the size of the pool (pool is 8x6, so ground is 24x18)
    try {
      const groundGeometry = this.createSimpleQuadGeometry(24, 18);
      const ground = new this.THREE.Mesh(groundGeometry, this.groundMaterial);
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = 0;
      this.groundGroup.add(ground);
      
    } catch (error) {
    }
    
    
    

  }

  addTestCube() {
    // Test cube no longer needed - 3D renderer is working
    
  }

  async init() {
    try {
      await this.waitForThreeJS();
      // Set THREE reference after it's loaded
      this.THREE = window.THREE || window.three;
      if (!this.THREE) {
        throw new Error('THREE.js not found after loading');
      }
      
      // ✅ PETER'S SCROLLABLE CONTAINER SETUP
      // Apply CSS for horizontal scrolling when canvas exceeds container width
      const container = this.canvas?.parentElement;
      if (container) {
        container.style.overflowX = 'auto';
        container.style.overflowY = 'hidden';
        container.style.whiteSpace = 'nowrap';
        container.style.maxWidth = '100%';
      }
      
      this.setupMinimalRenderer();
      this.isInitialized = true;
      
      // Hide loading indicator and show success message
      const loading = document.querySelector('.fence-3d-loading');
      if (loading) {
        loading.classList.add('hidden');
        
      }
      
      // Force a test render to ensure everything is working
      
      this.animate();
      
      // Add a simple test object to ensure rendering is working
      this.addTestCube();
    } catch (error) {
      this.showError('Failed to initialize 3D preview');
    }
  }

  async waitForThreeJS() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 100;
      
      const checkLibraries = () => {
        attempts++;
        
        // Check for THREE.js and OrbitControls
        const hasThree = window.THREE || window.three;
        const hasOrbitControls = (hasThree && hasThree.OrbitControls) || window.OrbitControls;
        
        
        if (hasThree) {
          
          
        }
        
        
        // Add delay to let OrbitControls attach
        if (hasThree && !hasOrbitControls && attempts < 10) {
          
        }
        
        if (hasThree && hasOrbitControls) {
          
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Required libraries failed to load'));
        } else {
          setTimeout(checkLibraries, 200);
        }
      };
      
      checkLibraries();
    });
  }


  clearGroup(group) {
    if (!group || !group.children) return;
    
    // Safely dispose of geometries and materials
    const childrenToRemove = [...group.children]; // Create a copy to avoid mutation during iteration
    
    childrenToRemove.forEach(child => {
      try {
        if (child.geometry && typeof child.geometry.dispose === 'function') {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(material => {
              if (material && typeof material.dispose === 'function') {
                material.dispose();
              }
            });
          } else if (typeof child.material.dispose === 'function') {
            // Don't dispose shared materials (glassMaterial, groundMaterial, poolMaterial)
            if (child.material !== this.glassMaterial && 
                child.material !== this.groundMaterial && 
                child.material !== this.poolMaterial) {
              child.material.dispose();
            }
          }
        }
      } catch (error) {
      }
      
      group.remove(child);
    });
  }


  calculateRectangleLayout(sections, scaleFactor) {
    const layout = [];
    const sectionLengths = sections.map(s => (s.length || 1000) * scaleFactor / 1000);
    
    // For rectangle, use first 4 sections or pad with default values
    const lengths = [
      sectionLengths[0] || 4, // top
      sectionLengths[1] || 3, // right  
      sectionLengths[2] || sectionLengths[0] || 4, // bottom (mirror top if not provided)
      sectionLengths[3] || sectionLengths[1] || 3  // left (mirror right if not provided)
    ];
    
    const halfWidth = lengths[1] / 2;  // right side length / 2
    const halfLength = lengths[0] / 2; // top side length / 2
    
    // Create rectangle starting from top, going clockwise
    layout.push({
      section: sections[0],
      start: { x: -halfLength, z: halfWidth },
      end: { x: halfLength, z: halfWidth },
      length: lengths[0],
      angle: 0
    });
    
    if (sections[1]) {
      layout.push({
        section: sections[1], 
        start: { x: halfLength, z: halfWidth },
        end: { x: halfLength, z: -halfWidth },
        length: lengths[1],
        angle: Math.PI / 2
      });
    }
    
    if (sections[2]) {
      layout.push({
        section: sections[2],
        start: { x: halfLength, z: -halfWidth },
        end: { x: -halfLength, z: -halfWidth },
        length: lengths[2],
        angle: Math.PI
      });
    }
    
    if (sections[3]) {
      layout.push({
        section: sections[3],
        start: { x: -halfLength, z: -halfWidth },
        end: { x: -halfLength, z: halfWidth },
        length: lengths[3],
        angle: -Math.PI / 2
      });
    }
    
    // Calculate pool dimensions (smaller than fence)
    const poolWidth = Math.max(1, halfWidth * 2 * 0.6);
    const poolLength = Math.max(1, halfLength * 2 * 0.6);
    
    return {
      sections: layout,
      poolShape: 'rectangle',
      poolDimensions: { width: poolWidth, length: poolLength }
    };
  }

  calculatePolygonLayout(sections, sideCount, scaleFactor) {
    const layout = [];
    const sectionLengths = sections.map(s => (s.length || 1000) * scaleFactor / 1000);
    const averageLength = sectionLengths.reduce((sum, len) => sum + len, 0) / sectionLengths.length || 4;
    
    // Calculate polygon vertices
    const angleStep = (2 * Math.PI) / sideCount;
    const radius = averageLength / (2 * Math.sin(Math.PI / sideCount));
    
    for (let i = 0; i < Math.min(sideCount, sections.length); i++) {
      const currentAngle = i * angleStep - Math.PI / 2; // Start from top
      const nextAngle = ((i + 1) % sideCount) * angleStep - Math.PI / 2;
      
      const start = {
        x: radius * Math.cos(currentAngle),
        z: radius * Math.sin(currentAngle)
      };
      
      const end = {
        x: radius * Math.cos(nextAngle),
        z: radius * Math.sin(nextAngle)
      };
      
      layout.push({
        section: sections[i],
        start,
        end,
        length: sectionLengths[i] || averageLength,
        angle: currentAngle + Math.PI / 2 // Perpendicular to radius
      });
    }
    
    const poolRadius = radius * 0.6;
    const poolShape = sideCount === 5 ? 'circle' : (sideCount === 6 ? 'oval' : 'circle');
    
    return {
      sections: layout,
      poolShape,
      poolDimensions: { radius: poolRadius, width: poolRadius * 1.2, length: poolRadius }
    };
  }


  createSimpleFenceSection(sectionLayout, sectionIndex) {
    const { start, end, section } = sectionLayout;
    
    // Calculate section direction and length
    const direction = {
      x: end.x - start.x,
      z: end.z - start.z
    };
    const sectionLength = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
    
    if (sectionLength === 0 || !isFinite(sectionLength)) return;
    
    // Normalize direction
    const normalizedDir = {
      x: direction.x / sectionLength,
      z: direction.z / sectionLength
    };
    
    try {
      // Get the actual panel configuration from the calculator state
      const sectionNumber = sectionIndex + 1;
      const sectionConfig = window.glassCalculator?.state?.sections?.[sectionNumber];
      const optimizedConfig = window.glassCalculator?.state?.optimizedConfigs?.[sectionNumber];
      
      if (!sectionConfig) {
        return;
      }

      
      // Use optimized config if available, otherwise calculate basic layout
      let numPanels = Math.max(1, Math.floor(parseFloat(sectionConfig.length) / 1800));
      let panelWidth = parseFloat(sectionConfig.panelLength) || 2400;
      let gapSpacing = parseFloat(sectionConfig.gapSpacing) || 25;
      
      // Scale down for 3D visualization
      // const scaleFactor = sectionLength / (parseFloat(sectionConfig.length) / 1000);
      // const scaledPanelWidth = panelWidth * scaleFactor / 1000;
      // const scaledGapSize = gapSpacing * scaleFactor / 1000;

      
      // Create individual panels along the section
      let currentPosition = 0;
      for (let i = 0; i < numPanels; i++) {
        const panelGeometry = new this.THREE.BoxGeometry(scaledPanelWidth, 1.2, 0.012);
        const panel = new this.THREE.Mesh(panelGeometry, this.glassMaterial);
        
        // Position panel along the section
        const panelCenter = currentPosition + scaledPanelWidth / 2;
        const panelX = start.x + normalizedDir.x * panelCenter;
        const panelZ = start.z + normalizedDir.z * panelCenter;
        
        if (isFinite(panelX) && isFinite(panelZ)) {
          panel.position.set(panelX, 0.6, panelZ);
          
          // Rotate panel to align with section
          const angle = Math.atan2(direction.z, direction.x);
          if (isFinite(angle)) {
            panel.rotation.y = angle + Math.PI / 2;
          }
          
          this.fenceGroup.add(panel);
          
        }
        
        currentPosition += scaledPanelWidth + scaledGapSize;
      }
      
    } catch (error) {
    }
  }


  createSectionPanels(sectionLayout, calculation) {
    const { start, end, section } = sectionLayout;
    
    // Calculate section direction and length
    const direction = {
      x: end.x - start.x,
      z: end.z - start.z
    };
    const sectionLength = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
    
    // Normalize direction
    direction.x /= sectionLength;
    direction.z /= sectionLength;
    
    // Calculate panel layout
    const totalPanels = (calculation.regularPanels || 0) + 
                       (calculation.hingePanels || 0) + 
                       (calculation.gatePanels || 0) + 
                       (calculation.rakedPanels || 0);
    
    if (totalPanels === 0) return;
    
    // Scale down panel dimensions for 3D display
    const scaleFactor = sectionLength / (parseInt(section.length) / 1000);
    const panelWidthsArr = calculation.panelWidths || Array(calculation.regularPanels || 0).fill(calculation.panelWidth || 2400);
    const gapSpacingsArr = calculation.gapSpacings || Array(calculation.totalGaps || calculation.numPanels).fill(calculation.gapSpacing || 25);

    // Calculate positions along the section
    let currentPos = 0;
    const panels = [];
    let panelIdx = 0;
    let gapIdx = 0;

    // Special case: wall‑to‑glass gate (no glass hinge panel).
    // In this mode the solver replaces the hinge panel with an extra
    // regular glass panel on the opposite side of the gate. The
    // calculation data already encodes this in panelWidthsArr, but the
    // original 3D layout logic only knew about hingePanels/regularPanels
    // counts, so it never showed the replacement panel. We reconstruct
    // the layout explicitly here using gate side + panel widths.
    // ── PATCH 3: W2G mid-run guard (3D renderer) ────────────────────────────────
    // These must be 'let' so the mid-run guard below can override them if needed.
    let gateHingeType = section.gateHingeType || calculation.gateHingeType || 'glass-to-glass';
    const gateSide = section.gateSide || calculation.gateSide || 'right';
    let isWallToGlass = gateHingeType === 'W2G-REMOVED-2026-08-30';

    // Mirror Patch 1: if the solver switched to G2G for a mid-run gate, the 3D
    // renderer must match. The calculation object will already have hingePanels=1
    // and a G2G gapSpacings array; using the stale W2G flag would produce the
    // wrong panel order and wrong gap values in the 3D scene.
    if (isWallToGlass) {
      const _pos    = section?.gatePosition !== undefined ? section.gatePosition : 0;
      const _maxPos = calculation?.regularPanels || 0;
      const _atEdge = _pos <= 0 || _pos >= _maxPos;
      if (!_atEdge) {
        // Gate is mid-run: override local variables only, do not mutate state
        gateHingeType = 'glass-to-glass';
        isWallToGlass = false;
      }
    }
    // ── END PATCH 3 ──────────────────────────────────────────────────────────────

    if (isWallToGlass && (calculation.gatePanels || 0) > 0 && panelWidthsArr.length > 0) {
      const totalGlass = panelWidthsArr.length;

      if (gateSide === 'left') {
        // LEFT wall‑to‑glass:
        // Wall on left → hinge bolt in wall → HINGE(10mm) on wall side, LATCH(7mm) on glass side.
        // Layout: [wall]-10mm(HINGE)-[gate]-7mm(LATCH)-[extraPanel]-var-[panels...]-endGap

        // Gate panel (directly against left wall, 10mm gap already consumed by solver)
        const gateWidth = ((section.gateLength ? parseInt(section.gateLength) : 920) || calculation.gateLength || 920) * scaleFactor / 1000;
        const gateGapL = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
        panels.push({ type: 'gate', width: gateWidth, position: currentPos, flipped: false });
        currentPos += gateWidth + gateGapL;
        gapIdx++;

        // Extra replacement panel (latch catches this — 7mm from gate on right)
        const extraWidth = (panelWidthsArr[0] || calculation.panelWidth || 2400) * scaleFactor / 1000;
        const extraGap = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
        panels.push({ type: 'regular', width: extraWidth, position: currentPos });
        currentPos += extraWidth + extraGap;
        gapIdx++;

        // Remaining regular glass panels
        for (let i = 1; i < totalGlass; i++) {
          const pw = (panelWidthsArr[i] || calculation.panelWidth || 2400) * scaleFactor / 1000;
          const gs = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
          panels.push({ type: 'regular', width: pw, position: currentPos });
          currentPos += pw + gs;
          gapIdx++;
        }
      } else {
        // RIGHT wall‑to‑glass:
        // Wall on right → hinge on wall → gate directly against wall (10mm gap on right of gate)
        // Latch side (left of gate) catches the extra glass panel (7mm gap on left of gate)
        // Layout: startGap-[panels...]-var-[extraPanel(last in glassWidths)]-7mm-[gate]-10mm-[wall]-endGap

        // Regular glass panels first (all except last in panelWidthsArr = the extra panel)
        for (let i = 0; i < totalGlass - 1; i++) {
          const pw = (panelWidthsArr[i] || calculation.panelWidth || 2400) * scaleFactor / 1000;
          const gs = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
          panels.push({ type: 'regular', width: pw, position: currentPos });
          currentPos += pw + gs;
          gapIdx++;
        }

        // Extra replacement panel (latch catches this — 7mm to the right toward gate)
        const extraWidth = (panelWidthsArr[totalGlass - 1] || calculation.panelWidth || 2400) * scaleFactor / 1000;
        const extraGap = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
        panels.push({ type: 'regular', width: extraWidth, position: currentPos });
        currentPos += extraWidth + extraGap;
        gapIdx++;

        // Gate panel (directly against right wall, 10mm gap already consumed by solver)
        const gateWidth = ((section.gateLength ? parseInt(section.gateLength) : 920) || calculation.gateLength || 920) * scaleFactor / 1000;
        const gateGapR = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
        panels.push({ type: 'gate', width: gateWidth, position: currentPos, flipped: true });
        currentPos += gateWidth + gateGapR;
        gapIdx++;
      }

      // Raked panel, if any, is always at the very end
      if (calculation.rakedPanels > 0) {
        const rakedWidth = (this.getRakedPanelWidth(section.fenceHeight) || 1200) * scaleFactor / 1000;
        panels.push({ type: 'raked', width: rakedWidth, position: currentPos });
      }

      // Create 3D panels and return early; we handled the whole layout.
      panels.forEach((panel, panelIndex) => {
        this.create3DPanel(panel, sectionLayout, panelIndex, direction, start);
      });
      return;
    }

    // Add regular panels
    for (let i = 0; i < (calculation.regularPanels || 0); i++) {
      const pw = (panelWidthsArr[panelIdx] || calculation.panelWidth || 2400) * scaleFactor / 1000;
      const gs = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
      panels.push({ type: 'regular', width: pw, position: currentPos });
      currentPos += pw + gs;
      panelIdx++;
      gapIdx++;
    }

    // Add hinge panels
    for (let i = 0; i < (calculation.hingePanels || 0); i++) {
      const pw = (panelWidthsArr[panelIdx] || calculation.panelWidth || 2400) * scaleFactor / 1000;
      const gs = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
      panels.push({ type: 'hinge', width: pw, position: currentPos });
      currentPos += pw + gs;
      panelIdx++;
      gapIdx++;
    }
    
    // Add gate panels
    if (calculation.gatePanels > 0 && section.gateLength) {
      const gateWidth = parseInt(section.gateLength) * scaleFactor / 1000;
      const gs = (gapSpacingsArr[gapIdx] || calculation.gapSpacing || 25) * scaleFactor / 1000;
      panels.push({ type: 'gate', width: gateWidth, position: currentPos });
      currentPos += gateWidth + gs;
      gapIdx++;
    }
    
    // Add raked panels
    if (calculation.rakedPanels > 0) {
      const rakedWidth = (this.getRakedPanelWidth(section.fenceHeight) || 1200) * scaleFactor / 1000;
      panels.push({ type: 'raked', width: rakedWidth, position: currentPos });
    }
    
    // Create 3D panels
    panels.forEach((panel, panelIndex) => {
      this.create3DPanel(panel, sectionLayout, panelIndex, direction, start);
    });
  }

  create3DPanel(panelData, sectionLayout, panelIndex, direction, sectionStart) {
    const panelHeight = 1.2; // 1.2m standard height
    
    // Create glass panel using shared materials
    const panelGeometry = new this.THREE.PlaneGeometry(panelData.width, panelHeight);
    let panelMaterial;
    
    switch (panelData.type) {
      case 'gate':
        panelMaterial = this.materials.gate;
        break;
      default:
        panelMaterial = this.materials.glass;
    }
    
    const panel = new this.THREE.Mesh(panelGeometry, panelMaterial);
    
    // Position panel along section
    const panelCenter = panelData.position + panelData.width / 2;
    const panelX = sectionStart.x + direction.x * panelCenter;
    const panelZ = sectionStart.z + direction.z * panelCenter;
    
    panel.position.set(panelX, panelHeight / 2, panelZ);
    
    // Rotate panel to align with section
    const angle = Math.atan2(direction.z, direction.x);
    panel.rotation.y = angle + Math.PI / 2;
    
    this.fenceGroup.add(panel);
    
    // Create spigots for this panel
    this.createSpigots(panelX, panelZ, panelData.width, panelData.height, angle + Math.PI / 2);
  }

  createSpigots(panelX, panelZ, panelWidth, panelHeight, angle) {
    
    
    // Spigot dimensions - making them huge for debugging visibility
    const spigotHeight = 1.0;   // Very tall to be unmistakable
    const spigotWidth = 1.0;    // Very wide
    const spigotDepth = 0.5;    // Deep
    
    // Get spigot color from calculator state
    let spigotFinish = 'black'; // default
    if (window.glassCalculator) {
      const shapeSelect = document.getElementById('spigot-shape');
      if (shapeSelect && shapeSelect.value) {
        spigotFinish = shapeSelect.value.toLowerCase();
      }
    }
    
    // Create spigot material using selected color
    const spigotColorHex = this.spigotColors[spigotFinish] || this.spigotColors['black'];
    const spigotMaterial = new this.THREE.MeshBasicMaterial({ 
      color: spigotColorHex
    });
    
    // Create base mounting spigot geometry (rectangular base)
    const spigotGeometry = new this.THREE.BoxGeometry(spigotWidth, spigotHeight, spigotDepth);
    
    // Calculate positions for spigots at the bottom corners of the panel
    const panelHalfWidth = panelWidth / 2;
    const spigotOffsetFromEdge = panelHalfWidth * 0.8; // Position spigots 80% toward the edges
    
    // Calculate local positions (before rotation)
    const localPositions = [
      { x: -spigotOffsetFromEdge, z: 0 }, // Left spigot
      { x: spigotOffsetFromEdge, z: 0 }   // Right spigot
    ];
    
    localPositions.forEach((localPos, index) => {
      const spigot = new this.THREE.Mesh(spigotGeometry, spigotMaterial);
      
      // Rotate the local position based on panel orientation
      const rotatedX = localPos.x * Math.cos(angle) - localPos.z * Math.sin(angle);
      const rotatedZ = localPos.x * Math.sin(angle) + localPos.z * Math.cos(angle);
      
      // Position spigot at ground level (bottom of panel)
      spigot.position.set(
        panelX + rotatedX,
        spigotHeight / 2, // Half height to sit on ground
        panelZ + rotatedZ
      );
      
      // Rotate spigot to match panel orientation
      spigot.rotation.y = angle;
      
      this.fenceGroup.add(spigot);
      
      
    });
    
    
  }

  clearFence() {
    // Remove all fence elements from the scene
    if (this.fenceGroup) {
      this.fenceGroup.children.slice().forEach(child => {
        this.fenceGroup.remove(child);
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }
    
    // Use our dedicated method to clear pool and grass
    // This ensures proper tracking of poolCreated and poolDimensionsChanged flags
    this.clearPoolAndGrass();
  }

  updateFence(fence3DData) {
    if (!this.isInitialized) return;
    
    
    
    // Store fence data persistently so it can be restored
    this.savedFence3DData = fence3DData ? JSON.parse(JSON.stringify(fence3DData)) : null;
    
    // Clear existing fence elements
    this.clearFence();
    
    // Reset rectangle corners so they get recalculated
    this.rectangleCorners = null;
    
    if (!fence3DData || fence3DData.length === 0) {
      
      return;
    }
    
    // ✅ PETER'S PURE FIXED-SCALE CANVAS (NO SHRINKING)
    // Calculate total fence length across all sections
    const totalFenceLength = fence3DData.reduce((sum, section) => sum + section.totalLength, 0);
    
    // ✅ FIXED SCALE: 150px = 1000mm (1.5:10 ratio)
    // Example: 1800mm panel = 270px (always)
    // Example: 1200mm panel = 180px (always)
    // NO VIEWPORT LIMITS - Canvas grows as wide as needed
    const PIXELS_PER_1000MM = 150;
    
    const canvasWidth = (totalFenceLength / 1000) * PIXELS_PER_1000;
    const canvasHeight = 400; // Fixed height
    
    // ✅ Canvas grows without limits - container handles scrolling
    // Minimum width for very small fences
    const actualCanvasWidth = Math.max(400, canvasWidth);
    
    // Update canvas size for scrolling
    if (this.renderer && this.canvas) {
      this.renderer.setSize(actualCanvasWidth, canvasHeight);
      this.canvas.style.width = `${actualCanvasWidth}px`;
      this.canvas.style.height = `${canvasHeight}px`;
      
      // Update camera aspect ratio
      if (this.camera) {
        this.camera.aspect = actualCanvasWidth / canvasHeight;
        this.camera.updateProjectionMatrix();
      }
    }
    
    // Store section lengths for pool sizing
    this.allSectionLengths = fence3DData.map(section => section.totalLength);
    
    // Store fence data for rectangle calculations
    this.currentFence3DData = fence3DData;
    
    // Calculate rectangle corners once before creating sections
    this.calculateRectangleCorners(fence3DData);
    
    // Create fence sections using structured data
    fence3DData.forEach((sectionData, sectionIndex) => {
      this.createFenceSectionFromStructuredData(sectionData, sectionIndex);
    });
    
    // Create basic pool layout
    this.createBasicPool();
    
    
  }

  // Method to restore saved fence data or rebuild from current state
  restoreFence() {
    if (this.savedFence3DData) {
      
      this.updateFence(this.savedFence3DData);
    } else {
      
      // Rebuild from current calculator state
      if (window.glassCalculator) {
        const freshFenceData = window.glassCalculator.build3DFenceData();
        if (freshFenceData.length > 0) {
          this.updateFence(freshFenceData);
        }
      }
    }
  }

  createFenceSectionFromStructuredData(sectionData, sectionIndex) {
    const { totalLength, panels } = sectionData;
    
    
    // ✅ PETER'S PURE FIXED SCALE: 0.15 means 150px = 1000mm (1.5:10 ratio)
    // Panels NEVER shrink - canvas grows instead
    const scaleFactor = 0.15; // Fixed scale for perfect panel sizing
    
    // Get the actual section length for this specific section
    const actualSectionLength = totalLength * scaleFactor;
    
    // Rectangle corners should already be calculated by updateFence
    
    // Get position info for this section
    
    const sectionInfo = this.rectangleCorners[sectionIndex];
    if (!sectionInfo) {
      return;
    }
    
    
    
    // Create panels for this section along the calculated path
    let currentPosition = 0;
    
    panels.forEach((panelData, panelIndex) => {
      const panelWidth = panelData.length * scaleFactor;
      const panelHeight = (panelData.height || 1200) * scaleFactor;
      const gapWidth = panelData.gap * scaleFactor;
      
      // Skip panel if it would exceed the section length
      if (currentPosition + panelWidth > actualSectionLength) {
        return;
      }
      
      
      
      let panel;
      
      // Create different panel types
      if (panelData.type === 'gate') {
        // Create gate panel with latch - get section data for latch color
        const sectionData = this.currentFence3DData?.[sectionIndex];
        panel = this.createGatePanel(panelWidth, panelHeight, panelData, sectionData);
      } else if (panelData.type === 'hinge') {
        // Create hinge panel with hardware
        panel = this.createHingePanel(panelWidth, panelHeight, panelData, sectionIndex);
      } else {
        // Create regular or raked panel
        let panelGeometry;
        if (panelData.type === 'raked') {
          panelGeometry = this.createRakedPanelGeometry(panelWidth, panelHeight, panelData);
        } else {
          panelGeometry = new this.THREE.BoxGeometry(panelWidth, panelHeight, 0.012);
        }
        
        // All panel types now use the same shared material
        panel = new this.THREE.Mesh(panelGeometry, this.glassMaterial);
      }
      
      // Position panel along the section using interpolation between start and end
      const panelCenter = currentPosition + panelWidth / 2;
      const t = panelCenter / actualSectionLength; // Interpolation factor (0 to 1)
      
      const panelX = sectionInfo.start.x + (sectionInfo.end.x - sectionInfo.start.x) * t;
      const panelZ = sectionInfo.start.z + (sectionInfo.end.z - sectionInfo.start.z) * t;
      
      // ✅ PETER'S HARD-CODED BASELINE: ALL panels with bottom edge at Y=0 (floor)
      // Raked panels: geometry already bottom-aligned (bottom at Y=0) → position.y = 0
      // BoxGeometry panels (regular/gate/hinge): centered at origin → position.y = height/2
      let panelY;
      if (panelData.type === 'raked') {
        panelY = 0; // Raked geometry has bottom vertices at Y=0
      } else {
        panelY = panelHeight / 2; // BoxGeometry centered, lift by half height to put bottom at Y=0
      }
      
      panel.position.set(panelX, panelY, panelZ);
      panel.rotation.y = sectionInfo.angle;
      
      this.fenceGroup.add(panel);
      
      
      // Create spigots for this panel
      this.createSpigots(panelX, panelZ, panelWidth, panelHeight, sectionInfo.angle);
      
      // Move to next panel position (including gap)
      currentPosition += panelWidth + gapWidth;
    });
  }

  calculateRectangleCorners(fence3DData) {
    // Use the fence data passed from updateFence method
    const scaleFactor = 0.01;
    
    if (!fence3DData || fence3DData.length === 0) {
      this.rectangleCorners = [];
      return;
    }
    
    // Use actual section lengths from the fence data
    const sectionLengths = fence3DData.map(section => section.totalLength * scaleFactor);
    
    
    // For a rectangular fence, we need at least 2 sections, ideally 4
    // If we have less than 4 sections, we'll create a partial rectangle
    const corners = [];
    
    // Define the direction vectors for each side (top, right, bottom, left)
    const directions = [
      { x: 1, z: 0, angle: 0 },           // Top: moving right (positive X)
      { x: 0, z: -1, angle: Math.PI/2 },   // Right: moving down (negative Z)
      { x: -1, z: 0, angle: Math.PI },     // Bottom: moving left (negative X)
      { x: 0, z: 1, angle: -Math.PI/2 }    // Left: moving up (positive Z)
    ];
    
    // Calculate the total rectangle dimensions first
    const topLength = sectionLengths[0] || 40; // Default 40 units if no section
    const rightLength = sectionLengths[1] || 30; // Default 30 units if no section
    
    // Position rectangle so it's centered at origin
    const halfWidth = topLength / 2;
    const halfHeight = rightLength / 2;
    
    // Define the actual corner positions for a centered rectangle
    const rectCorners = [
      { x: -halfWidth, z: halfHeight },   // Top-left
      { x: halfWidth, z: halfHeight },    // Top-right  
      { x: halfWidth, z: -halfHeight },   // Bottom-right
      { x: -halfWidth, z: -halfHeight }   // Bottom-left
    ];
    
    // Calculate section start/end points and angles
    for (let i = 0; i < Math.min(sectionLengths.length, 4); i++) {
      const startCorner = rectCorners[i];
      const endCorner = rectCorners[(i + 1) % 4];
      const direction = directions[i];
      
      corners.push({
        start: { x: startCorner.x, z: startCorner.z },
        end: { x: endCorner.x, z: endCorner.z },
        direction: { x: direction.x, z: direction.z },
        angle: direction.angle,
        length: sectionLengths[i]
      });
      
      
    }
    
    this.rectangleCorners = corners;
    
    // Store pool dimensions based on the rectangle (make pool smaller than fence)
    const newPoolDimensions = {
      length: topLength * 0.7,  // Pool is 70% of fence length
      width: rightLength * 0.7   // Pool is 70% of fence width
    };
    
    // Update pool dimensions and flag that they've changed
    const dimensionsChanged = !this.poolDimensions || 
      this.poolDimensions.length !== newPoolDimensions.length || 
      this.poolDimensions.width !== newPoolDimensions.width;
    
    this.poolDimensions = newPoolDimensions;
    this.poolDimensionsChanged = dimensionsChanged;
    
    
    
    if (dimensionsChanged) {
      
    }
    
  }


  createBasicPool() {
    // Only recreate if dimensions changed or if we don't have existing pool/grass
    if (!this.poolDimensionsChanged && this.poolCreated) {
      
      return;
    }
    
    // Clear existing grass and pool
    this.clearPoolAndGrass();
    
    // Use calculated pool dimensions or defaults
    const poolLength = this.poolDimensions?.length || 40; // 
    const poolWidth = this.poolDimensions?.width || 30;   //
    
    
    
    // Create grass ground (larger than fence area) with thickness
    const fenceSize = Math.max(poolLength, poolWidth) + 4; // Fence area + margin
    const grassLength = fenceSize * 2;
    const grassWidth = fenceSize * 2;
    const grassThickness = 0.2;
    const grassGeometry = new this.THREE.BoxGeometry(grassLength, grassThickness, grassWidth);
    const grassMaterial = new this.THREE.MeshBasicMaterial({ 
      color: 0x4a7c59 // Nice grass green
    });
    this.grassMesh = new this.THREE.Mesh(grassGeometry, grassMaterial);
    
    this.grassMesh.position.set(0, -grassThickness / 2, 0); // Position so top is at ground level
    
    this.scene.add(this.grassMesh);
    
    // Create blue pool with thickness (like a pool basin) - positioned in center
    const poolThickness = 0.4;
    const poolGeometry = new this.THREE.BoxGeometry(poolLength, poolThickness, poolWidth);
    const poolMaterial = new this.THREE.MeshBasicMaterial({ 
      color: 0x0080ff, // Bright blue pool water
      transparent: true,
      opacity: 0.9
    });
    this.poolMesh = new this.THREE.Mesh(poolGeometry, poolMaterial);
    
    // Position pool so its top surface is just slightly above the grass surface
    // This prevents z-fighting/glitchy rendering between pool and grass surfaces
    this.poolMesh.position.set(0, -poolThickness / 2 + 0.01, 0); // Pool top slightly above ground level
    
    this.scene.add(this.poolMesh);
    
    // Mark that pool has been created and dimensions have been applied
    this.poolCreated = true;
    this.poolDimensionsChanged = false;
    
    
    
  }

  // Clear existing pool and grass meshes
  clearPoolAndGrass() {
    if (this.grassMesh) {
      this.scene.remove(this.grassMesh);
      if (this.grassMesh.geometry) this.grassMesh.geometry.dispose();
      if (this.grassMesh.material) this.grassMesh.material.dispose();
      this.grassMesh = null;
    }
    
    if (this.poolMesh) {
      this.scene.remove(this.poolMesh);
      if (this.poolMesh.geometry) this.poolMesh.geometry.dispose();
      if (this.poolMesh.material) this.poolMesh.material.dispose();
      this.poolMesh = null;
    }
    
    this.poolCreated = false;
    
  }

  updateSpigotColor(color) {
    this.spigotColor = color || 'black';
    
    // Update existing spigots by replacing material
    if (this.fenceGroup && this.isInitialized) {
      const spigotColorHex = this.spigotColors[this.spigotColor.toLowerCase()] || this.spigotColors['black'];
      const newMaterial = new this.THREE.MeshBasicMaterial({ 
        color: spigotColorHex
      });
      
      this.fenceGroup.children.forEach(child => {
        // Only update spigots - check for specific spigot characteristics
        if (child.geometry && child.geometry.type === 'BoxGeometry' && 
            child.material !== this.glassMaterial) { // Don't change glass panels
          // Dispose old material to prevent memory leaks
          if (child.material && child.material.dispose) {
            child.material.dispose();
          }
          child.material = newMaterial;
        }
      });
    }
  }

  animate() {
    if (!this.isInitialized || !this.renderer || !this.scene || !this.camera) {
      return;
    }
    
    // Add counter to track frames
    if (!this.frameCount) this.frameCount = 0;
    this.frameCount++;
    
    try {
      // Update controls if available
      if (this.controls) {
        this.controls.update();
      }
      
      // Render the scene
      this.renderer.render(this.scene, this.camera);
      
      // Log first few frames to verify rendering
      if (this.frameCount <= 3) {
        
        if (this.controls) {
          
        }
      }
      
      requestAnimationFrame(() => this.animate());
    } catch (error) {
      // Don't stop animation, just skip this frame
      requestAnimationFrame(() => this.animate());
    }
  }

  onWindowResize() {
    if (!this.isInitialized || !this.canvas) return;
    
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  showError(message) {
    const loading = document.querySelector('.fence-3d-loading');
    if (loading) {
      loading.classList.remove('hidden');
      loading.innerHTML = `<p style="color: #dc2626;">${message}</p>`;
    }
  }

  dispose() {
    if (this.controls) {
      this.controls.dispose();
    }
    
    // Dispose all shared materials
    Object.values(this.materials).forEach(material => {
      if (material.dispose) {
        material.dispose();
      } else if (typeof material === 'object') {
        Object.values(material).forEach(m => m.dispose && m.dispose());
      }
    });
    
    // Clear all groups
    if (this.fenceGroup) this.clearGroup(this.fenceGroup);
    if (this.poolGroup) this.clearGroup(this.poolGroup);  
    if (this.groundGroup) this.clearGroup(this.groundGroup);
    
    if (this.renderer) {
      this.renderer.dispose();
    }
    
    if (this.scene) {
      this.scene.clear();
    }
    
    this.isInitialized = false;
  }

  toggleAutoRotate(enable = null) {
    if (!this.controls) return;
    
    if (enable === null) {
      // Toggle current state
      this.controls.autoRotate = !this.controls.autoRotate;
    } else {
      this.controls.autoRotate = enable;
    }
    
    
  }
}

// Main Calculator Class
class GlassFencingCalculator {
  constructor() {
    this.state = {
      currentTab: 'add-sections',
      sectionsCount: 0,
      currentSection: 1,
      sections: {},
      optimizedConfigs: {}, // NEW: Store optimized configurations
      components: {},
      quote: 0,
      cartItems: [],
      quoteBreakdown: [],
      pricingType: 'diy',  // 'diy' or 'install'
      includeDelivery: false,
      panelDesign: 'none',  // selected design panel: 'none' | 'gum-leaves' | 'palm' | 'bird-nest'
      // ── Per-section redesign ──
      sameForAll: true,    // default = all sections the same (shared colour/height/mount)
      shared: { color: 'satin-black', fenceHeight: '1200', groundPost: false }  // used when sameForAll
    };
    
    // Store the HTML content for PDF generation
    this.savedQuoteBreakdownHTML = null;
    
    this.domCache = new DOMCache();
    this.shopifyData = this.loadShopifyData();
    this.changeHandlers = this.createChangeHandlers();
    this.fenceRenderer3D = null;
    
    this.init();
  }

  // Load Shopify data from script tag
  // ── Data-load failure alarm ────────────────────────────────────────────────
  // If the template's JSON blob fails to parse, loadShopifyData() falls back to
  // the hardcoded defaults below. That fallback used to be SILENT: the page
  // looked fine while every price was a frozen constant. Flat Top ran that way
  // for months. Anything that lands here now says so.
  _cfDataAlarm(reason, detail) {
    var label = 'Blade';
    var msg = '[Click Fencing] ' + label + ' calculator is NOT using live Shopify prices — '
            + reason + '. Every price on this page is a hardcoded fallback and may be out of date.';
    try { console.error(msg, detail || ''); } catch (e) {}
    try {
      window.CF_DATA_FALLBACK = { calculator: label, reason: reason, detail: String(detail || ''), at: new Date().toISOString() };
    } catch (e) {}
    // Visible only to staff: the theme editor/preview, or ?cfdebug=1 in the URL.
    var staff = false;
    try {
      staff = (window.Shopify && window.Shopify.designMode) || /[?&]cfdebug=1/.test(window.location.search);
    } catch (e) {}
    if (!staff) return;
    try {
      var show = function () {
        if (document.getElementById('cf-data-alarm')) return;
        var b = document.createElement('div');
        b.id = 'cf-data-alarm';
        b.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483647;background:#b3261e;color:#fff;'
          + 'font:600 13px/1.45 system-ui,sans-serif;padding:10px 16px;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,.35)';
        b.textContent = msg;
        document.body.appendChild(b);
      };
      if (document.body) show(); else document.addEventListener('DOMContentLoaded', show);
    } catch (e) {}
  }

  loadShopifyData() {
    try {
      const dataElement = this.domCache.get('shopify-calculator-data');
      
      
      if (dataElement) {
        
        // Check if the content looks like valid JSON
        const content = dataElement.textContent.trim();
        if (!content) {
          this._cfDataAlarm('the product data block on this page is empty');
          return this.getDefaultData();
        }
        
        // Try to identify common JSON issues
        if (content.includes('"id": ,')) {
        }
        
        // Look for other common issues
        const doubleCommas = /,\s*,/g;
        const trailingCommas = /,\s*(\}|\])/g;
        if (doubleCommas.test(content)) {
        }
        if (trailingCommas.test(content)) {
        }
      }
      
      // Try to parse, but catch and handle malformed JSON
      let data = {};
      if (dataElement) {
        try {
          data = JSON.parse(dataElement.textContent);
        } catch (parseError) {
          
          // Try to fix common JSON issues
          let fixedContent = dataElement.textContent.trim();
          
          // Fix empty ID values
          fixedContent = fixedContent.replace(/"id"\s*:\s*,/g, '"id": null,');
          fixedContent = fixedContent.replace(/"id"\s*:\s*}/g, '"id": null}');
          
          // Fix double commas
          fixedContent = fixedContent.replace(/,\s*,/g, ',');
          
          // Fix trailing commas
          fixedContent = fixedContent.replace(/,\s*(\}|\])/g, '$1');
          
          
          try {
            data = JSON.parse(fixedContent);
            this._cfDataAlarm('its product data block is malformed and had to be repaired to load — fix the template', parseError && parseError.message);
          } catch (secondError) {
            this._cfDataAlarm('its product data block could not be parsed', (parseError && parseError.message) + ' | ' + (secondError && secondError.message));
            return this.getDefaultData();
          }
        }
      }
      
      // Comprehensive logging of Shopify data structure
      
      
      if (data.products) {
        
        Object.keys(data.products).forEach(productKey => {
          
          const product = data.products[productKey];
          
          
          if (product.variants) {
            
            
            
            // Log unique variant properties
            const variantProps = new Set();
            product.variants.forEach(v => {
              Object.keys(v).forEach(key => variantProps.add(key));
            });
            
            
            // Log unique values for key properties
            if (product.variants.some(v => v.color)) {
              const colors = [...new Set(product.variants.map(v => v.color).filter(Boolean))];
              
            }
            if (product.variants.some(v => v.size)) {
              const sizes = [...new Set(product.variants.map(v => v.size).filter(Boolean))];
              
            }
            if (product.variants.some(v => v.type)) {
              const types = [...new Set(product.variants.map(v => v.type).filter(Boolean))];
              
            }
            if (product.variants.some(v => v.position)) {
              const positions = [...new Set(product.variants.map(v => v.position).filter(Boolean))];
              
            }
          }
        });
        
        // Specific logging for hinge and latch products
        
        if (data.products.hingeStandard) {
          
        }
        if (data.products.hingeSoftClose) {
          
        }
        
        
        if (data.products.gateLatch) {
          
        }
      }
      
      
      
      return data;
    } catch (error) {
      return this.getDefaultData();
    }
  }

  // Fallback data if Shopify data unavailable — James Glenn Blade Fencing price list
  getDefaultData() {
    // Prices stored in cents (x100) to match Shopify variant format.
    // All prices are RRP (AUD incl. GST) from the master price list.
    // Panels: always 2400mm wide. Heights: 1000, 1200, 1500, 1800.
    // Colours: satin-black, white, monument.
    // Gates: 920mm leaf, priced per height x colour.
    // Rake: 1252mm wide, heights 1300-1800mm, LEFT HIGH / RIGHT HIGH.
    // SKU reference: see James Glenn master price list.
    const p = (dollars) => Math.round(dollars * 100);

    const makePanelProduct = (sku, height, colour, priceDollars) => ({
      id: sku, sku,
      variants: [{ id: sku, available: true, price: p(priceDollars), size: '2400', height: String(height), color: colour }]
    });
    const makeGateProduct = (sku, height, colour, priceDollars) => ({
      id: sku, sku,
      variants: [{ id: sku, available: true, price: p(priceDollars), height: String(height), color: colour, gate: '960' }]
    });
    const makeRakeProduct = (sku, side, rakeHeight, colour, priceDollars) => ({
      id: sku, sku,
      variants: [{ id: sku, available: true, price: p(priceDollars), height: String(rakeHeight), side, color: colour }]
    });

    return {
      shop: { currency: 'AUD', moneyFormat: '${{amount}}' },
      settings: {
        maxPanelVariance: CONFIG.DEFAULTS.MAX_PANEL_VARIANCE,
        maxGapVariance: CONFIG.DEFAULTS.MAX_GAP_VARIANCE,
        maxCalculatedPanelVariance: CONFIG.DEFAULTS.MAX_CALCULATED_PANEL_VARIANCE,
        showOptimizationNotes: true,
        showQuoteBreakdown: true,
        // ── Blade Fencing — May 2026 Price Guide ─────────────────────────
        // Supply & Install rates (AUD incl. GST)
        blade_install_per_lm:      370,
        blade_install_curved_lm:   585,
        blade_install_gate:        510,
        blade_install_rake:        370,
        blade_install_hinges:      265,
        // DIY rates
        blade_diy_per_lm:          180,
        blade_diy_curved_lm:       350,
        blade_diy_gate:            350,
        blade_diy_rake:            300,
        blade_diy_hinges:          180,
        // Job size surcharges
        blade_surcharge_4lm:       0.40,   // +40% for ≤4Lm
        blade_surcharge_8lm:       0.30,   // +30% for ≤8Lm
        // Minimum quote (excl. GST base → displayed incl. GST)
        blade_minimum_quote:       2425,
      },
      products: {
        // 1000H Garden panels
        bladePanelH1000_satinblack: makePanelProduct(49975, 1000, 'satin-black', 258.44),
        bladePanelH1000_white:      makePanelProduct(50073, 1000, 'white',        258.44),
        // 1200H Pool panels — RRP per master price list
        bladePanelH1200_satinblack: makePanelProduct(49978, 1200, 'satin-black', 287.30),
        bladePanelH1200_white:      makePanelProduct(50071, 1200, 'white',        287.30),
        bladePanelH1200_monument:   makePanelProduct(50072, 1200, 'monument',     287.30),
        // 1500H Pool/Garden panels
        bladePanelH1500_satinblack: makePanelProduct(50450, 1500, 'satin-black', 318.50),
        bladePanelH1500_monument:   makePanelProduct(50451, 1500, 'monument',     318.50),
        bladePanelH1500_white:      makePanelProduct(50452, 1500, 'white',        318.50),
        // 1800H Pool/Garden panels
        bladePanelH1800_satinblack: makePanelProduct(50453, 1800, 'satin-black', 362.50),
        bladePanelH1800_monument:   makePanelProduct(50454, 1800, 'monument',     362.50),
        bladePanelH1800_white:      makePanelProduct(50455, 1800, 'white',        362.50),
        // Gates 1000H
        bladeGateH1000_satinblack:  makeGateProduct(50354, 1000, 'satin-black', 192.83),
        bladeGateH1000_white:       makeGateProduct(50355, 1000, 'white',        192.83),
        // Gates 1200H — RRP per master price list
        bladeGateH1200_satinblack:  makeGateProduct(50357, 1200, 'satin-black', 208.00),
        bladeGateH1200_white:       makeGateProduct(50358, 1200, 'white',        208.00),
        bladeGateH1200_monument:    makeGateProduct(50359, 1200, 'monument',     208.00),
        // Gates 1500H
        bladeGateH1500_satinblack:  makeGateProduct(50456, 1500, 'satin-black', 297.70),
        bladeGateH1500_monument:    makeGateProduct(50457, 1500, 'monument',     297.70),
        bladeGateH1500_white:       makeGateProduct(50458, 1500, 'white',        297.70),
        // Gates 1800H
        bladeGateH1800_satinblack:  makeGateProduct(50459, 1800, 'satin-black', 348.70),
        bladeGateH1800_monument:    makeGateProduct(50460, 1800, 'monument',     348.70),
        bladeGateH1800_white:       makeGateProduct(50461, 1800, 'white',        348.70),
        // Rake RIGHT HIGH — RRP per master price list; white/monument share price with satin-black per height
        bladeRakeH1300_right_satinblack: makeRakeProduct(49904, 'right', 1300, 'satin-black', 358.38),
        bladeRakeH1300_right_white:      makeRakeProduct('NEW', 'right', 1300, 'white',        420.83),
        bladeRakeH1300_right_monument:   makeRakeProduct('NEW', 'right', 1300, 'monument',     420.83),
        bladeRakeH1400_right_satinblack: makeRakeProduct(49906, 'right', 1400, 'satin-black', 370.66),
        bladeRakeH1400_right_white:      makeRakeProduct('NEW', 'right', 1400, 'white',        438.74),
        bladeRakeH1400_right_monument:   makeRakeProduct('NEW', 'right', 1400, 'monument',     438.74),
        bladeRakeH1500_right_satinblack: makeRakeProduct(49908, 'right', 1500, 'satin-black', 382.68),
        bladeRakeH1500_right_white:      makeRakeProduct('NEW', 'right', 1500, 'white',        456.17),
        bladeRakeH1500_right_monument:   makeRakeProduct('NEW', 'right', 1500, 'monument',     456.17),
        bladeRakeH1600_right_satinblack: makeRakeProduct(49910, 'right', 1600, 'satin-black', 396.06),
        bladeRakeH1600_right_white:      makeRakeProduct('NEW', 'right', 1600, 'white',        475.17),
        bladeRakeH1600_right_monument:   makeRakeProduct('NEW', 'right', 1600, 'monument',     475.17),
        bladeRakeH1700_right_satinblack: makeRakeProduct(49912, 'right', 1700, 'satin-black', 410.40),
        bladeRakeH1700_right_white:      makeRakeProduct('NEW', 'right', 1700, 'white',        491.65),
        bladeRakeH1700_right_monument:   makeRakeProduct('NEW', 'right', 1700, 'monument',     491.65),
        bladeRakeH1800_right_satinblack: makeRakeProduct(49914, 'right', 1800, 'satin-black', 423.86),
        bladeRakeH1800_right_white:      makeRakeProduct('NEW', 'right', 1800, 'white',        501.79),
        bladeRakeH1800_right_monument:   makeRakeProduct('NEW', 'right', 1800, 'monument',     501.79),
        // Rake LEFT HIGH — RRP per master price list; white/monument share price with satin-black per height
        bladeRakeH1300_left_satinblack:  makeRakeProduct(50341, 'left', 1300, 'satin-black', 358.38),
        bladeRakeH1300_left_white:       makeRakeProduct('NEW', 'left', 1300, 'white',        420.83),
        bladeRakeH1300_left_monument:    makeRakeProduct('NEW', 'left', 1300, 'monument',     420.83),
        bladeRakeH1400_left_satinblack:  makeRakeProduct(50342, 'left', 1400, 'satin-black', 370.66),
        bladeRakeH1400_left_white:       makeRakeProduct('NEW', 'left', 1400, 'white',        438.74),
        bladeRakeH1400_left_monument:    makeRakeProduct('NEW', 'left', 1400, 'monument',     438.74),
        bladeRakeH1500_left_satinblack:  makeRakeProduct(50345, 'left', 1500, 'satin-black', 382.68),
        bladeRakeH1500_left_white:       makeRakeProduct('NEW', 'left', 1500, 'white',        456.17),
        bladeRakeH1500_left_monument:    makeRakeProduct('NEW', 'left', 1500, 'monument',     456.17),
        bladeRakeH1600_left_satinblack:  makeRakeProduct(50347, 'left', 1600, 'satin-black', 396.06),
        bladeRakeH1600_left_white:       makeRakeProduct('NEW', 'left', 1600, 'white',        475.17),
        bladeRakeH1600_left_monument:    makeRakeProduct('NEW', 'left', 1600, 'monument',     475.17),
        bladeRakeH1700_left_satinblack:  makeRakeProduct(50349, 'left', 1700, 'satin-black', 410.40),
        bladeRakeH1700_left_white:       makeRakeProduct('NEW', 'left', 1700, 'white',        491.65),
        bladeRakeH1700_left_monument:    makeRakeProduct('NEW', 'left', 1700, 'monument',     491.65),
        bladeRakeH1800_left_satinblack:  makeRakeProduct(50351, 'left', 1800, 'satin-black', 423.86),
        bladeRakeH1800_left_white:       makeRakeProduct('NEW', 'left', 1800, 'white',        501.79),
        bladeRakeH1800_left_monument:    makeRakeProduct('NEW', 'left', 1800, 'monument',     501.79),
        // Post brackets — 40×40mm, sold as a PACK OF 4 ($15.40/pack); one pack per
        // non-gate panel (4 brackets each). SKUs: 49870 SB / 50077 white / 50078 monument.
        bladePostBracket: {
          id: 49870,
          variants: [
            { id: 49870, available: true, price: p(14.32), color: 'satin-black' },
            { id: 50078, available: true, price: p(14.32), color: 'monument'    },
            { id: 50077, available: true, price: p(14.32), color: 'white'       }
          ]
        },
        // Posts — Blade fixes to the same 50×50 aluminium posts as Flat Top.
        // Ground Post ON (core-drill) → 50×50×1800 in-ground post, $39.50
        //   (price INCLUDES cap + dress ring — not billed as separate lines).
        // Ground Post OFF (base plate) → 50×50×1300 post w/ base + cap, $45.60
        //   (price INCLUDES cap + base cover).
        bladePostInGround: {
          variants: [
            { id: 42792780267614, sku: '49321', available: true, price: p(42.41), color: 'satin-black' },
            { id: 42792780333150, sku: '49926', available: true, price: p(42.41), color: 'white'       },
            { id: 42792780300382, sku: '49927', available: true, price: p(42.41), color: 'monument'    }
          ]
        },
        bladePostBaseMount: {
          variants: [
            { id: 42792780398686, sku: '49838', available: true, price: p(44.16), color: 'satin-black' },
            { id: 42792780464222, sku: '49941', available: true, price: p(44.16), color: 'white'       },
            { id: 42792780431454, sku: '49942', available: true, price: p(44.16), color: 'monument'    }
          ]
        },
        // Adjustable brackets 40x40 — RRP per master price list
        bladeAdjBracket: {
          id: 50523,
          variants: [
            { id: 50523, available: true, price: p(24.65), color: 'satin-black' },
            { id: 50524, available: true, price: p(24.65), color: 'white'       },
            { id: 50525, available: true, price: p(24.65), color: 'monument'    }
          ]
        },
        // Slat plastic caps — RRP per master price list
        bladeSlatCap: {
          id: 49868,
          variants: [
            { id: 49868, available: true, price: p(1.26), color: 'satin-black' },
            { id: 49886, available: true, price: p(1.26), color: 'monument'    },
            { id: 49883, available: true, price: p(1.26), color: 'white'       }
          ]
        },
        // Safetech Hinges & Latches — ALI Range
        hingeStandard: {
          id: 'SHF-90L',
          variants: [{ id: 'SHF-90L', available: true, price: p(18.20), position: 'standard' }]
        },
        hingeSoftClose: {
          id: 'SHG-90L',
          variants: [{ id: 'SHG-90L', available: true, price: p(35.58), position: 'soft-close' }]
        },
        hingePolaris: {
          id: 'PHS',
          variants: [{ id: 'PHS', available: true, price: p(79.30), position: 'premium-soft-close' }]
        },
        // Safetech Latches
        gateLatch: {
          id: 'SL-50K',
          variants: [
            { id: 'SL-50K', available: true, price: p(55.64),  color: 'standard'     },
            { id: 'SL-50H', available: true, price: p(67.99),  color: 'key-lockable' }
          ]
        },
        // D & D Hinges & Latch Kits
        hingeDDTruClose: {
          id: 'TCA1L2S3BT',
          variants: [{ id: 'TCA1L2S3BT', available: true, price: p(49.88), color: 'black' }]
        },
        gateLatchDDKit: {
          id: 'MTSTDKS3LLA',
          variants: [
            { id: 'MTSTDKS3LLA', available: true, price: p(171.79), color: 'black', legs: 'with-legs'    },
            { id: 'MTSTDKS3LA',  available: true, price: p(171.79), color: 'black', legs: 'without-legs' }
          ]
        }
      },
      // Cap counts per item type (2400mm panel=25, 970=10, gate 920=9, rake=13)
      bladeCaps: { panel2400: 25, panel970: 10, gate960: 9, rake: 13 },
      collections: { addOns: [], postType: '', finishes: ['satin-black', 'white', 'monument'] }
    };
  }

  // Log product summary for debugging
  logProductSummary() {
    
    
    if (!this.shopifyData.products) {
      
      return;
    }
    
    // Hinge products
    
    if (this.shopifyData.products.hingeSoftClose) {
      const product = this.shopifyData.products.hingeSoftClose;
      if (product.variants) {
        // NOTE: Colors are in 'position' field for hinges
        const colors = [...new Set(product.variants.filter(v => v.position).map(v => v.position))];
        
      } else {
        
      }
    } else {
      
    }
    
    if (this.shopifyData.products.hingeStandard) {
      const product = this.shopifyData.products.hingeStandard;
      if (product.variants) {
        // NOTE: Colors are in 'position' field for hinges
        const colors = [...new Set(product.variants.filter(v => v.position).map(v => v.position))];
        
      } else {
        
      }
    } else {
      
    }
    
    // Latch products
    
    if (this.shopifyData.products.gateLatch) {
      const product = this.shopifyData.products.gateLatch;
      if (product.variants) {
        // Latch types are correctly in position field, colors are correctly in color field
        const types = [...new Set(product.variants.filter(v => v.position).map(v => v.position))];
        const colors = [...new Set(product.variants.filter(v => v.color).map(v => v.color))];
        
        
        
        // Log color availability per type
        types.forEach(type => {
          const typeColors = [...new Set(product.variants
            .filter(v => v.position === type && v.color)
            .map(v => v.color))];
          
        });
      } else {
        
      }
    } else {
      
    }
    
    
  }

  // Initialize calculator
  init() {
    // DOM diagnostic — logs to console so we can identify ID mismatches with Liquid template
    console.log('[ullc] DOM check on init:');
    console.log('  [data-tab] elements:', Array.from(document.querySelectorAll('[data-tab]')).map(el => el.dataset.tab));
    console.log('  .tab-content IDs:', Array.from(document.querySelectorAll('.tab-content')).map(el => el.id));
    console.log('  #sections-count:', !!document.getElementById('sections-count'));
    console.log('  #nav-next:', !!document.getElementById('nav-next'));
    console.log('  #nav-back:', !!document.getElementById('nav-back'));
    console.log('  #sections-next-btn:', !!document.getElementById('sections-next-btn'));

    // Log a summary of available products
    this.logProductSummary();
    
    this.setupEventListeners();
    this.setupAddSectionsControls();
    this.setupSectionCountDropdown();
    this.removeOrphanToggle();
    [150, 500, 1200].forEach(ms => setTimeout(() => this.removeOrphanToggle(), ms));
    this.populateComponentDropdowns();
    this.populateAddOns();
    this.updateQuoteDisplay();
    this.updateBladeHeightLabel();
    this._hideCheckoutUnlessReview();

    // Quote-page return (Edit Quote button)
    try {
      const isReturn = sessionStorage.getItem('blade_quote_return');
      if (isReturn) {
        sessionStorage.removeItem('blade_quote_return');
        const stored = sessionStorage.getItem('ullc_quote');
        if (stored) {
          const quote = JSON.parse(stored);
          if (Array.isArray(quote.breakdown) && quote.breakdown.length) {
            this.state.quoteBreakdown = quote.breakdown;
            this.state.quote          = quote.grandTotal || 0;
            if (quote.pricingType) this.state.pricingType = quote.pricingType;
          }
        }
        setTimeout(() => this.switchTab('review-checkout'), 100);
      }
    } catch (e) {
      console.warn('[calc] Could not restore quote state on return:', e);
    }

    
    // ── Panel preview height measurement ─────────────────────────────────
    // Sets --site-header-height CSS var so configure-sections fills the
    // remaining viewport height correctly (preview fixed, forms scrollable).
    this._setupStickyPreview();

    window.addEventListener('resize', this.debounce(() => {
      this._updateStickyPreview();
      this.recalculatePreviewOnResize();
      if (this.fenceRenderer3D) {
        this.fenceRenderer3D.onWindowResize();
      }
    }, 250));
    
    // Don't initialize 3D renderer here - wait for Add Components tab
  }

  // Create change handlers map for event delegation
  createChangeHandlers() {
    return {
      'sections-count': (e) => { this.handleSectionsChange(parseInt(e.target.value)); if (e.target && e.target.blur) e.target.blur(); },
      'pricing-diy': () => this.handlePricingTypeChange('diy'),
      'pricing-install': () => this.handlePricingTypeChange('install'),
      'delivery-no': () => this.handleDeliveryChange(false),
      'delivery-yes': () => this.handleDeliveryChange(true),
      'spigot-type': () => this.handleSpigotChange(),
      'spigot-shape': () => this.handleSpigotChange(),
      'spigot-shape-select': () => this.handleSpigotChange(),
      'spigot-finish': () => this.handleSpigotChange(),
      'global-hinge-color': () => this.handleGlobalColorChange(),
      'global-latch-color': () => this.handleGlobalColorChange(),
      'glass-type': () => this.handleGlassTypeChange(),
      'post-color': () => { this.updateQuoteDisplay(); this.updateComponentInfo(); this.updatePostPreview(); },
      'mesh-color': () => { this.updateQuoteDisplay(); this.updateComponentInfo(); }
    };
  }

  // Sync global hinge/latch color to all sections
  handleGlobalColorChange() {
    const latchColor = document.getElementById('global-latch-color')?.value;
    Object.keys(this.state.sections).forEach(sectionNum => {
      if (latchColor) this.state.sections[sectionNum].latchColor = latchColor;
      if (latchColor) this.state.sections[sectionNum].hingeColor = latchColor;
      const latchEl = document.getElementById(`section-${sectionNum}-latch-color`);
      const hingeEl = document.getElementById(`section-${sectionNum}-hinge-color`);
      if (latchEl && latchColor) latchEl.value = latchColor;
      if (hingeEl && latchColor) hingeEl.value = latchColor;
    });
    this.updateQuoteDisplay();
    this.updateHardwarePreviews();
    this.updateComponentInfo();
  }

  // Handle glass type selection change
  handleGlassTypeChange() {
    this.updateQuoteDisplay();
    this.updateComponentsQuote();
    this.updateComponentInfo();
    if (this.shopifyData.settings?.showQuoteBreakdown !== false) {
      this.updateQuoteBreakdown();
    }
  }

  // Handle post type selection change
  // Bolt-Down Posts → show coach screws in add-ons
  // Core Drill Posts → show cement grout in add-ons
  handlePostTypeChange() {
    this.state.postType = document.getElementById('post-type')?.value || '';
    this.populateAddOns();
    this.updateComponentsQuote();
    this.updateComponentInfo();
  }

  // Resolve a section's colour → 'satin-black' | 'white' | 'monument'.
  // Per-section redesign: prefer the section's own colour; fall back to the
  // shared job colour, then the legacy global mesh-color control, then default.
  resolveSectionColour(section = null) {
    const raw = (
      section?.color
      || (this.state.sameForAll ? this.state.shared?.color : null)
      || document.getElementById('mesh-color')?.value
      || 'satin-black'
    ).toString().toLowerCase();
    return raw.includes('white') ? 'white'
         : raw.includes('monument') ? 'monument'
         : 'satin-black';
  }

  // Returns the active glass panel product based on the Glass Type dropdown
  getActiveMeshSheet(section = null) {
    // Resolve Blade panel product by fence height + colour.
    // Height is read from the section state (fenceHeight) or the global fence-height selector.
    // Colour is read per-section (section.color), falling back to the global control.
    const colour = this.resolveSectionColour(section);

    const heightRaw = section?.fenceHeight
                   || document.getElementById('fence-height')?.value
                   || '1200';
    const height = parseInt(heightRaw) || 1200;

    // Build product key: bladePanelH{height}_{colour-slug}
    const colourSlug = colour.replace(/-/g, ''); // e.g. 'satin-black' → 'satinblack'
    const key = `bladePanelH${height}_${colourSlug}`;
    const product = this.shopifyData.products?.[key];
    // Return even if id is null — price from default data is still valid for quote
    if (product && product.variants?.length) return product;

    // Fallback: try satin-black at same height
    const fallbackKey = `bladePanelH${height}_satinblack`;
    const fallback = this.shopifyData.products?.[fallbackKey];
    if (fallback && fallback.variants?.length) return fallback;

    // No product found for this height/colour combination
    return null;
  }

  // Resolve the active Blade gate product by height + colour
  getActiveBladeGate(section = null) {
    const colour = this.resolveSectionColour(section); // mill-finish not available for gates

    const heightRaw = section?.fenceHeight
                   || document.getElementById('fence-height')?.value
                   || '1200';
    const height = parseInt(heightRaw) || 1200;

    const key = `bladeGateH${height}_${colour.replace('-', '')}`;
    const product = this.shopifyData.products?.[key];
    if (product && product.variants?.length) return product;

    // Fallback: satin-black at same height
    const fallbackKey = `bladeGateH${height}_satinblack`;
    const fb = this.shopifyData.products?.[fallbackKey];
    return (fb && fb.variants?.length) ? fb : null;
  }

  // Resolve the active Blade rake product by rake height + side + colour
  getActiveBladeRake(section = null) {
    // All three colours now have rake SKUs (white/monument are NEW pending assignment).
    const colour = this.resolveSectionColour(section);

    const side = section?.rakedSide || 'right'; // 'left' or 'right'
    const rakeHeightRaw = section?.retainedHeight || '1300';
    // Round to nearest supported step (1300–1800 in 100mm increments)
    let rakeH = Math.round(parseInt(rakeHeightRaw) / 100) * 100;
    rakeH = Math.max(1300, Math.min(1800, rakeH));

    const colourSlug = colour.replace(/-/g, ''); // consistent with panel key format (Bug 7 fix)
    const key = `bladeRakeH${rakeH}_${side}_${colourSlug}`;
    const product = this.shopifyData.products?.[key];
    if (product && product.variants?.length) return product;

    // Fallback: satin-black same height same side
    const fbKey = `bladeRakeH${rakeH}_${side}_satinblack`;
    const fb = this.shopifyData.products?.[fbKey];
    return (fb && fb.variants?.length) ? fb : null;
  }

  updateHingeBaseDisplay() {
    // Hinge Base display removed from Color Finishes tab (Fix #7)
    // Hinge type is managed per-section in Configure Sections
  }
  
  // Handle pricing type change (DIY vs Supply & Install)
  handlePricingTypeChange(type) {
    this.state.pricingType = type;
    // Supply & Install: the fixings are ours to supply, so the Optional Fixing Materials panel is hidden
    // and nothing is charged for it (Pete, 5 Oct 2026: "hide fixings in supply and install").
    setTimeout(() => { try { this.renderReviewFixings(); } catch (e) {} }, 0);

    const diyLabel     = document.getElementById('pricing-diy-label');
    const installLabel = document.getElementById('pricing-install-label');
    const note         = document.getElementById('pricing-type-note');

    if (diyLabel)     { diyLabel.style.background = 'white';     diyLabel.style.color = '#64748b'; }
    if (installLabel) { installLabel.style.background = 'white'; installLabel.style.color = '#64748b'; }

    if (type === 'install') {
      if (installLabel) { installLabel.style.background = '#0e9ed5'; installLabel.style.color = 'white'; }
      if (note) note.textContent = 'Supply & Install: includes professional installation by our team.';
    } else {
      if (diyLabel) { diyLabel.style.background = '#0e9ed5'; diyLabel.style.color = 'white'; }
      if (note) note.textContent = 'DIY Supply Only: pick up or have products delivered for self-installation.';
    }

    this.updateQuoteDisplay();
    this.updateComponentsQuote();
    this.updateNavigationButtons();

    // Hide & reset delivery for S&I (not applicable — install team brings materials)
    const deliveryWrap = document.getElementById('delivery-toggle-wrap');
    if (deliveryWrap) deliveryWrap.style.display = type === 'install' ? 'none' : 'flex';
    if (type === 'install') this.state.includeDelivery = false;
  }

  // Handle delivery option change
  handleDeliveryChange(includeDelivery) {
    this.state.includeDelivery = includeDelivery;

    const noLabel  = document.getElementById('delivery-no-label');
    const yesLabel = document.getElementById('delivery-yes-label');
    const note     = document.getElementById('delivery-note');

    if (noLabel && yesLabel) {
      if (includeDelivery) {
        noLabel.style.background  = 'white';
        noLabel.style.color       = '#64748b';
        yesLabel.style.background = '#0e9ed5';
        yesLabel.style.color      = 'white';
        if (note) note.textContent = 'Delivery to your door — $99 flat rate Perth metro, GST included.';
      } else {
        noLabel.style.background  = '#0e9ed5';
        noLabel.style.color       = 'white';
        yesLabel.style.background = 'white';
        yesLabel.style.color      = '#64748b';
        if (note) note.textContent = 'Pick up from our warehouse at no extra cost.';
      }
    }

    this.updateQuoteDisplay();
    this.updateComponentsQuote();
  }

  // Handle spigot configuration change
  handleSpigotChange() {
    this.populateAddOns();
    this.updateQuoteDisplay();
    this.updateComponentsQuote();
    if (this.fenceRenderer3D) {
      const spigotFinish = this.domCache.get('spigot-shape')?.value;
      this.fenceRenderer3D.updateSpigotColor(spigotFinish);
    }
    this.updatePostPreview();
    this.syncSpigotTypeDisplay();
    this.updateComponentInfo();
  }

  // syncSpigotTypeDisplay — no-op, spigot-type is now a real dropdown populated by populateComponentDropdowns()
  syncSpigotTypeDisplay() {}

  // Update Component Information panel with current Components & Finishes selections
  updateComponentInfo() {
    const panel = document.getElementById('component-info');
    if (!panel) return;

    const spigotType      = document.getElementById('spigot-type')?.value || '';
    const spigotShape     = document.getElementById('spigot-shape')?.value || '';
    const spigotBaseShape = document.getElementById('spigot-shape-select')?.value || '';
    const latchColor      = document.getElementById('global-latch-color')?.value || '';
    const glassTypeEl     = document.getElementById('glass-type');
    const glassTypeVal    = glassTypeEl?.value || 'toughened';
    const glassTypeLabel  = glassTypeEl?.options[glassTypeEl.selectedIndex]?.text || 'Toughened Frameless Glass';

    // Checked add-ons
    const addOns = Array.from(document.querySelectorAll('#addon-list input[type="checkbox"]:checked'))
      .map(cb => cb.closest('.addon-item')?.querySelector('span:not(.addon-price)')?.textContent?.trim())
      .filter(Boolean);

    // Get glass panel price per panel (cheapest available variant as base)
    const glassProduct = this.getActiveMeshSheet();
    const cheapestVariant = glassProduct?.variants
      ?.filter(v => v.available)
      ?.sort((a, b) => a.price - b.price)[0];
    const enduroMultiplier = 1; // perfmesh: no enduroshield variant
    const glassPriceDisplay = cheapestVariant
      ? `${PriceHelper.format(PriceHelper.fromCents(cheapestVariant.price) * enduroMultiplier, this.shopifyData)} / panel`
      : '';

    // Get spigot price
    const spigotProduct = this.shopifyData.products?.spigot;
    const spigotVariant = spigotProduct?.variants?.find(v =>
      v.available &&
      (!spigotType   || v.description === spigotType) &&
      (!spigotShape  || v.color === spigotShape)
    ) || spigotProduct?.variants?.find(v => v.available);
    const spigotPriceDisplay = spigotVariant
      ? `${PriceHelper.format(PriceHelper.fromCents(spigotVariant.price), this.shopifyData)} each`
      : '';

    const row = (label, value, price) => {
      if (!value) return '';
      return `
      <div style="display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #e2e8f0; font-size:1.58rem;">
        <span style="color:#64748b; font-weight:500;">${label}</span>
        <div style="text-align:right;">
          <span style="color:#1e293b; font-weight:600;">${value}</span>
          ${price ? `<div style="color:#059669; font-size:1.35rem; font-weight:600;">${price}</div>` : ''}
        </div>
      </div>`;
    };

    // Post & Mesh colour labels
    const postColorEl  = document.getElementById('post-color');
    const meshColorEl  = document.getElementById('mesh-color');
    const postColorLabel = postColorEl?.options[postColorEl.selectedIndex]?.text || postColorEl?.value || '';
    const meshColorLabel = meshColorEl?.options[meshColorEl.selectedIndex]?.text || meshColorEl?.value || '';

    // Add-ons with quantities
    const addOnItems = Array.from(document.querySelectorAll('#addon-list input[type="checkbox"]:checked'))
      .map(cb => {
        const item  = cb.closest('.addon-item');
        const label = item?.querySelector('span:not(.addon-price)')?.textContent?.trim();
        const qty   = item?.querySelector('input[type="number"]')?.value || '1';
        return label ? `${label} × ${qty}` : null;
      })
      .filter(Boolean);

    let html = `<div style="padding:12px;">`;
    html += `<div style="font-size:1.23rem; font-weight:700; color:#374151; margin-bottom:10px; text-transform:uppercase; letter-spacing:0.05em;">Selected Configuration</div>`;
    // html += row('Glass Type',          glassTypeLabel,  glassPriceDisplay); // hidden from UI
    const anyGate = Object.values(this.state.sections || {}).some(s => s.hasGate);
    html += row('Post Colour',         postColorLabel,  '');
    if (anyGate) {
      html += row('Hinge & Latch Colour', latchColor ? latchColor.charAt(0).toUpperCase() + latchColor.slice(1) : '', '');
    }
    if (this.state.panelDesign && this.state.panelDesign !== 'none') {
      const designLabels = { 'gum-leaves': 'Gum Leaves', 'palm': 'Palm', 'bird-nest': 'Bird Nest' };
      html += row('Panel Design', designLabels[this.state.panelDesign] || this.state.panelDesign, '');
    }
    if (addOnItems.length > 0) {
      addOnItems.forEach(addon => { html += row('Add-on', addon, ''); });
    }
    html += `<div style="margin-top:10px; padding-top:8px; font-size:1.23rem; color:#64748b;">All components are compliant with Australian Standards AS1288, AS2208, AS192.</div>`;
    html += `</div>`;

    panel.innerHTML = html;
  }

  // Handle section configuration changes that affect product previews
  handleSectionProductChange() {
    this.updateHardwarePreviews();
  }

  // Accordion functionality removed
  setupAccordion(retryCount = 0) {
    
    
    
    
    const headers = document.querySelectorAll('.accordion-header');
    
    
    if (headers.length === 0 && retryCount < 3) {
      const addComponentsTab = document.getElementById('add-components');
      
      if (addComponentsTab) {
        const accordionsInTab = addComponentsTab.querySelectorAll('.accordion-header');
        
      }
      
      // Try again after a delay in case DOM isn't fully ready
      
      setTimeout(() => this.setupAccordion(retryCount + 1), 1000);
      return;
    } else if (headers.length === 0) {
      return;
    }
    
    headers.forEach((header, index) => {
      
      header.addEventListener('click', (e) => {
        
        const target = header.dataset.target;
        const section = header.closest('.accordion-section');
        const content = document.getElementById(target);
        const toggle = header.querySelector('.accordion-toggle');
        
        
        
        if (content && section) {
          // Toggle the hidden class on content
          const isCurrentlyHidden = content.classList.contains('hidden');
          
          
          if (isCurrentlyHidden) {
            // Show content
            content.classList.remove('hidden');
            section.classList.add('open');
            if (toggle) toggle.textContent = '−';
            
            
            // Initialize 3D renderer if this is the 3D preview accordion
            if (target === '3d-preview' && !this.fenceRenderer3D) {
              
              this.init3DRenderer();
            }
          } else {
            // Hide content
            content.classList.add('hidden');
            section.classList.remove('open');
            if (toggle) toggle.textContent = '+';
            
          }
        }
      });
    });
  }

  // Initialize 3D renderer
  init3DRenderer() {
    
    
    // Check if canvas is actually visible and accessible
    const canvas = document.getElementById('fence-3d-canvas');
    if (!canvas) {
      return;
    }
    
    // Check if canvas is visible (not hidden by CSS)
    const canvasRect = canvas.getBoundingClientRect();

    
    if (canvasRect.width === 0 || canvasRect.height === 0) {
      setTimeout(() => this.init3DRenderer(), 500);
      return;
    }
    
    try {
      
      this.fenceRenderer3D = new FenceRenderer3D('fence-3d-canvas');
      
      
      // Update preview after a short delay to ensure everything is ready
      setTimeout(() => {
        
        this.update3DPreview();
        // Enable a brief auto-rotate when first loaded to show the layout
        if (this.fenceRenderer3D && this.fenceRenderer3D.controls) {
          this.fenceRenderer3D.toggleAutoRotate(true);
          setTimeout(() => {
            this.fenceRenderer3D.toggleAutoRotate(false);
          }, 3000); // Auto-rotate for 3 seconds
        }
      }, 500); // Increased delay to give more time
    } catch (error) {
    }
  }

  // Validate and restore section data if needed
  validateAndRestoreSectionData() {
    
    
    // Check if we have sections count but no section data
    if (this.state.sectionsCount > 0) {
      let hasValidSections = false;
      
      for (let i = 1; i <= this.state.sectionsCount; i++) {
        const section = this.state.sections[i];
        if (section && section.length && parseFloat(section.length) >= 100) {
          hasValidSections = true;
          break;
        }
      }
      
      if (!hasValidSections) {
        
        
        // First try to restore from backup
        if (this.sectionStateBackup && this.sectionsCountBackup) {
          
          this.state.sections = JSON.parse(JSON.stringify(this.sectionStateBackup));
          this.state.sectionsCount = this.sectionsCountBackup;
        } else {
          // Fallback to form data
          
          this.restoreSectionDataFromForms();
        }
      } else {
        
      }
    } else {
      
    }
  }

  // Save current section state before tab switching
  saveCurrentSectionState() {
    
    
    // Create a backup of current section data
    if (this.state.sections && Object.keys(this.state.sections).length > 0) {
      this.sectionStateBackup = JSON.parse(JSON.stringify(this.state.sections));
      this.sectionsCountBackup = this.state.sectionsCount;

    }
  }

  // Restore section data from form inputs
  restoreSectionDataFromForms() {
    
    
    // Find all section forms in the DOM (excluding gate forms)
    const sectionForms = document.querySelectorAll('.section-form');
    
    
    sectionForms.forEach(form => {
      const formId = form.id;
      const sectionMatch = formId.match(/section-(\d+)-form/);
      if (sectionMatch) {
        const sectionNum = parseInt(sectionMatch[1]);
        
        
        // Get form values from section form
        const lengthInput = form.querySelector(`#section-${sectionNum}-length`);
        const panelSelect = form.querySelector(`#section-${sectionNum}-panel`);
        const gapSelect = form.querySelector(`#section-${sectionNum}-gap`);

        // Get rake form values from separate rake form
        const rakeForm = document.getElementById(`section-${sectionNum}-rake-form`);
        const rakedToggle = rakeForm ? rakeForm.querySelector(`#section-${sectionNum}-raked`) : null;
        
        // Get gate form values from separate gate form
        const gateForm = document.getElementById(`section-${sectionNum}-gate-form`);
        const clampToggle = gateForm ? gateForm.querySelector(`#section-${sectionNum}-clamp`) : null;
        const gateToggle = gateForm ? gateForm.querySelector(`#section-${sectionNum}-gate`) : null;
        const gateLengthSelect = gateForm ? gateForm.querySelector(`#section-${sectionNum}-gate-length`) : null;
        const hingeMountChecked = gateForm ? gateForm.querySelector(`input[name="section-${sectionNum}-gate-hinge-type"]:checked`) : null;
        
        if (lengthInput && lengthInput.value) {
          // Initialize section if it doesn't exist
          if (!this.state.sections[sectionNum]) {
            this.state.sections[sectionNum] = {};
          }
          
          const section = this.state.sections[sectionNum];
          // Strip any "mm" suffix — the input displays "5985mm" but state must store "5985"
          section.length = String(parseInt(lengthInput.value) || 0) || lengthInput.value;
          section.panelLength = panelSelect ? panelSelect.value : this.getDefaultPanelLength(section.fenceHeight);
          section.gapSpacing = gapSelect ? gapSelect.value : CONFIG.DEFAULTS.GAP_SPACING;
          section.hasRaked = rakedToggle ? rakedToggle.checked : false;
          section.hasClamp = clampToggle ? clampToggle.checked : false;
          section.hasGate = gateToggle ? gateToggle.checked : false;
          section.gateLength = (gateLengthSelect && section.hasGate) ? gateLengthSelect.value : this.getDefaultGateLength();
          section.gateHingeType = hingeMountChecked ? hingeMountChecked.value : (section.gateHingeType || 'glass-to-glass');
          
          
          
          // Update sectionsCount if needed
          if (sectionNum > this.state.sectionsCount) {
            this.state.sectionsCount = sectionNum;
          }
        }
      }
    });
  }

  // Generate explicit panel layout for a section
  generateSectionPanelLayout(section, calculation) {
    const panels = [];
    const gapSpacingsArr = calculation.gapSpacings || Array(calculation.totalGaps || calculation.numPanels).fill(calculation.gapSpacing || parseFloat(section.gapSpacing) || 25);
    const panelWidthsArr = calculation.panelWidths || Array(calculation.regularPanels || calculation.numPanels).fill(calculation.panelWidth);
    
    // Boss rule: adjusted panels (±50mm from preferred) display on the RIGHT.
    // Sort preferred panels first, adjusted panels last — display only, solver unchanged.
    const preferredW = calculation.panelWidth;
    const sortedWidths = [...panelWidthsArr].sort((a, b) => {
      const aIsPreferred = a === preferredW;
      const bIsPreferred = b === preferredW;
      if (aIsPreferred && !bIsPreferred) return -1;
      if (!aIsPreferred && bIsPreferred) return 1;
      return 0;
    });
    let gapIndex = 0;
    let regularPanelIndex = 0;

    // Check if section has custom panel order from user modifications
    if (section.customPanelOrder && section.customPanelOrder.length > 0) {

      // Use the custom order and rebuild panels with correct dimensions
      section.customPanelOrder.forEach((customPanel, idx) => {
        let length, height, type;

        switch (customPanel.type) {
          case 'hinge-panel':
          case 'hinge':
          case 'reg':
            length = (sortedWidths[idx] !== undefined) ? sortedWidths[idx] : calculation.panelWidth;
            height = 1200;
            type = customPanel.type;
            break;
          case 'gate':
            length = parseInt(section.gateLength) || calculation.gateLength || 920;
            height = 1200;
            type = 'gate';
            break;
          case 'raked':
            // Rake panels are their own width (1252 on Blade), NOT the regular
            // panel width - this drew and labelled the rake at 2400.
            length = CONFIG.DEFAULTS.RAKED_PANEL_WIDTH;
            height = parseInt(section.fenceHeight) || 1200;
            type = 'raked';
            break;
          default:
            length = (sortedWidths[idx] !== undefined) ? sortedWidths[idx] : calculation.panelWidth;
            height = 1200;
            type = 'reg';
        }

        panels.push({
          id: customPanel.id,
          length: length,
          height: height,
          type: type,
          gap: gapSpacingsArr[idx] !== undefined ? gapSpacingsArr[idx] : (gapSpacingsArr[0] || 25),
          position: customPanel.position
        });
      });

      return panels;
    }

    // Default layout logic: [Regular panels] → [Gate] → [Raked]

    // Add regular panels first
    // numPanels = regularPanels + hingePanels (hinge is last when gate present)
    for (let i = 0; i < calculation.numPanels; i++) {
      const isHingePanel = (calculation.hingePanels > 0) && (i === calculation.numPanels - 1);
      let panelWidth;
      if (isHingePanel) {
        panelWidth = calculation.hingeWidth
          || (sortedWidths.length > 0 ? sortedWidths[sortedWidths.length - 1] : calculation.panelWidth);
      } else {
        panelWidth = (sortedWidths[regularPanelIndex] !== undefined)
          ? sortedWidths[regularPanelIndex]
          : calculation.panelWidth;
      }

      const panel = {
        id: isHingePanel ? `hinge-${i}` : `reg-${i}`,
        length: panelWidth,
        height: parseInt(section.fenceHeight || 1200),
        type: isHingePanel ? 'hinge' : 'reg',
        gap: gapSpacingsArr[gapIndex] !== undefined ? gapSpacingsArr[gapIndex] : (gapSpacingsArr[0] || 25),
        flipped: false,
        position: panels.length
      };
      panels.push(panel);
      gapIndex++;
      if (!isHingePanel) regularPanelIndex++;
    }

    // Add gate panel if required
    if (section.hasGate) {
      const gateLength = parseInt(section.gateLength) || this.getDefaultGateLength();
      const panel = {
        id: 'gate-0',
        length: gateLength,
        height: parseInt(section.fenceHeight || 1200),
        type: 'gate',
        gap: gapSpacingsArr[gapIndex] !== undefined ? gapSpacingsArr[gapIndex] : (gapSpacingsArr[0] || 25),
        flipped: section.gateFlipped || false,
        position: panels.length
      };
      panels.push(panel);
      gapIndex++;
    }

    // Add raked panel if required
    if (section.hasRaked) {
      // Blade rake panels are 1252mm wide - NOT the same width as a regular panel.
      // This previously used calculation.panelWidth (2400 on a 1200h fence), so the
      // drawing showed a rake 1148mm wider than the part actually ordered, and the
      // derived rake height came out at 3000mm - outside the 1300-1800 range James
      // Glen actually makes. Solver and drawing now agree.
      const rakedPanelWidth = this.getRakedPanelWidth(); // 1252
      // Rake HEIGHT comes from the customer's "Retained Height" selection
      // (1300-1800 in 100mm steps) - the SAME value already used to pick the
      // rake SKU (bladeRakeH<h>_<side>_<colour>). It used to be derived as
      // width x 1.25, so the drawing ignored the selection completely and drew
      // every rake the same, at a height that is not even manufactured.
      let _rakeH = Math.round((parseInt(section.retainedHeight) || 1300) / 100) * 100;
      _rakeH = Math.max(1300, Math.min(1800, _rakeH));
      const proportionalRakeSize = _rakeH;
      const panel = {
        id: 'raked-0',
        length: rakedPanelWidth,
        height: parseInt(section.fenceHeight || 1200),
        type: 'raked',
        size: proportionalRakeSize, // Use proportional calculation instead of fixed value
        gap: gapSpacingsArr[gapIndex] !== undefined ? gapSpacingsArr[gapIndex] : (gapSpacingsArr[0] || 25),
        flipped: section.rakedSide === 'right',
        position: panels.length
      };
      panels.push(panel);
      gapIndex++;
    }
    
    
    panels.forEach((panel, index) => {
      
    });
    
    
    
    return panels;
  }

  // Build 3D fence data structure
  build3DFenceData() {
    const fence3D = [];
    
    // First, validate and restore section data if needed
    this.validateAndRestoreSectionData();
    
    
    
    
    // If sectionsCount is 0, there might be no sections configured
    if (!this.state.sectionsCount || this.state.sectionsCount === 0) {
      
      return fence3D;
    }
    
    for (let i = 1; i <= this.state.sectionsCount; i++) {
      const section = this.state.sections[i];
      
      if (!section || !section.length || parseFloat(section.length) < 100) {
        
        continue;
      }
      
      // Use the main calculation function to get accurate panel data
      const calculation = this.calculateOptimalPanels(section);
      if (!calculation || calculation.numPanels === 0) continue;
      
      // Generate explicit panel layout using new structure
      const panels = this.generateSectionPanelLayout(section, calculation);
      
      
      
      
      
      
      
      
      
      
      
      fence3D.push({
        totalLength: parseFloat(section.length),
        panels: panels
      });
    }
    
    return fence3D;
  }

  // Update 3D preview with current sections
  update3DPreview() {
    
    

    
    if (!this.fenceRenderer3D) {
      
      return;
    }
    
    if (!this.fenceRenderer3D.isInitialized) {
      
      return;
    }
    
    const fence3DData = this.build3DFenceData();
    
    
    if (fence3DData.length > 0) {
      
      this.fenceRenderer3D.updateFence(fence3DData);
    } else {
      
      // Still show the basic scene (ground, etc.)
      this.fenceRenderer3D.updateFence([]);
    }
  }

  // Trigger 3D preview update when section data changes
  trigger3DUpdate() {
    // Only update if we're currently on the add-components tab and 3D renderer exists
    if (this.state.currentTab === 'add-components' && this.fenceRenderer3D && this.fenceRenderer3D.isInitialized) {
      
      // Force recalculation of pool dimensions by resetting the flag
      if (this.fenceRenderer3D.rectangleCorners) {
        this.fenceRenderer3D.rectangleCorners = null;
      }
      this.update3DPreview();
    } else {
      
      // Still mark that dimensions need recalculation when we return to the tab
      if (this.fenceRenderer3D) {
        this.fenceRenderer3D.poolDimensionsChanged = true;
      }
    }
  }

  // Populate component dropdowns from Shopify data
  populateComponentDropdowns() {
    this.populateDropdown('spigot-type', this.getSpigotTypes(), 'Select spigot type...');
    this.populateDropdown('spigot-shape', this.getSpigotFinishes(), 'Select finish...');
    this.populateDropdown('spigot-finish', this.getSpigotShapes(), 'Select shape...');

    // Populate global hinge/latch color dropdowns in Add Components
    this.populateGlobalColorDropdowns();

    // ── Post Colour and Mesh Colour — fixed options: Black, White, Monument ──
    const COLOUR_OPTIONS = [
      { value: 'black',    label: 'Satin Black' },
      { value: 'white',    label: 'Satin White' },
      { value: 'monument', label: 'Monument' }
    ];
    this.populateDropdown('post-color',  COLOUR_OPTIONS, 'Select colour...', 'black');
    this.populateDropdown('mesh-color',  COLOUR_OPTIONS, 'Select colour...', 'black');

    // Initialize post preview after dropdowns are populated
    setTimeout(() => this.updatePostPreview(), 100);
  }

  // Populate global hinge and latch color dropdowns from product data
  populateGlobalColorDropdowns() {
    const hingeColors = [];
    const hingeProduct = this.shopifyData.products?.hingeSoftClose || this.shopifyData.products?.hingeStandard;
    if (hingeProduct?.variants) {
      const seen = new Set();
      hingeProduct.variants.filter(v => v.available && v.position).forEach(v => {
        if (!seen.has(v.position)) { seen.add(v.position); hingeColors.push(v.position); }
      });
    }
    if (!hingeColors.length) hingeColors.push('Black', 'White');

    const latchColors = [];
    const latchProduct = this.shopifyData.products?.latchGlassToGlass;
    if (latchProduct?.variants) {
      const seen = new Set();
      latchProduct.variants.filter(v => v.available && v.color).forEach(v => {
        if (!seen.has(v.color)) { seen.add(v.color); latchColors.push(v.color); }
      });
    }
    if (!latchColors.length) latchColors.push('Black', 'White');

    const hingeEl = document.getElementById('global-hinge-color');
    if (hingeEl) {
      hingeEl.innerHTML = '';
      hingeColors.forEach(c => {
        const opt = document.createElement('option');
        opt.value = c.toLowerCase();
        opt.textContent = c;
        hingeEl.appendChild(opt);
      });
    }

    const latchEl = document.getElementById('global-latch-color');
    if (latchEl) {
      latchEl.innerHTML = '';
      latchColors.forEach(c => {
        const opt = document.createElement('option');
        opt.value = c.toLowerCase();
        opt.textContent = c;
        latchEl.appendChild(opt);
      });
    }
  }

  // Generic dropdown population method
  populateDropdown(elementId, items, placeholder, defaultValue = null) {
    const select = this.domCache.get(elementId);
    if (!select) return;

    // Placeholder option — disabled so it shows as prompt but can't be re-selected
    const placeholderOpt = document.createElement('option');
    placeholderOpt.value = '';
    placeholderOpt.textContent = placeholder;
    placeholderOpt.disabled = true;
    if (!defaultValue) placeholderOpt.selected = true;
    select.innerHTML = '';
    select.appendChild(placeholderOpt);

    items.forEach(item => {
      const option = document.createElement('option');
      option.value = item.value;
      option.textContent = item.label;
      option.style.fontSize = '16px';
      option.style.padding = '10px';
      if (defaultValue && item.value === defaultValue) option.selected = true;
      select.appendChild(option);
    });
  }

  // Get spigot types from product data
  getSpigotTypes() {
    const spigotProduct = this.shopifyData.products?.spigot;
    if (!spigotProduct || !spigotProduct.variants) return [];

    const descriptions = [...new Set(spigotProduct.variants
      .filter(v => v.available && v.description)
      .map(v => v.description))];

    return descriptions.map(desc => ({ value: desc, label: desc }));
  }

  // Get spigot shapes from product data
  getSpigotShapes() {
    return [
      { value: 'Square', label: 'Square' }
    ];
  }

  // Get spigot finishes from product data
  getSpigotFinishes() {
    const spigotProduct = this.shopifyData.products?.spigot;
    if (!spigotProduct || !spigotProduct.variants) return [];

    const colors = [...new Set(spigotProduct.variants
      .filter(v => v.available && v.color)
      .map(v => v.color))];

    return colors.map(color => ({
      value: color,
      label: color.charAt(0).toUpperCase() + color.slice(1)
    }));
  }

  // Get spigot image URL based on current configuration
  getSpigotImageUrl() {
    const spigotProduct = this.shopifyData.products?.spigot;
    if (!spigotProduct || !spigotProduct.variants) {
      return 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/spigot-default.svg?v=1739010000';
    }

    const spigotType   = document.getElementById('spigot-type')?.value;
    const spigotShape  = document.getElementById('spigot-shape')?.value;       // Finish (color)
    const spigotBaseShape = document.getElementById('spigot-shape-select')?.value; // Square or Round

    // Match on type + color (finish) + base shape
    const matchingVariant =
      spigotProduct.variants.find(v =>
        (!spigotType      || v.description === spigotType) &&
        (!spigotShape     || v.color === spigotShape) &&
        (!spigotBaseShape || v.shape === spigotBaseShape)
      ) ||
      spigotProduct.variants.find(v =>
        (!spigotType  || v.description === spigotType) &&
        (!spigotShape || v.color === spigotShape)
      ) ||
      spigotProduct.variants.find(v =>
        (!spigotType || v.description === spigotType)
      ) ||
      spigotProduct.variants.find(v => v.available);

    return matchingVariant?.image ||
           spigotProduct.image ||
           'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/spigot-default.svg?v=1739010000';
  }

  // Friendly display label for a hinge SKU
  getHingeLabel(sku) {
    const hinges = this.shopifyData.collections?.perfmeshProducts?.hinges;
    if (hinges) {
      const match = hinges.find(h => h.sku === sku);
      if (match) return match.title;
    }
    const labels = {
      'SHF-90L':     'Fixed Tension',
      'SHG-90L':     'Adjustable Tension',
      'PHS':         'Soft Close Premium',
      'TCA1L2S3BT':  'Adjustable Tension With Legs'
    };
    return labels[sku] || sku || 'Unknown';
  }

  // Friendly display label for a latch SKU
  getLatchLabel(sku) {
    const latches = this.shopifyData.collections?.perfmeshProducts?.latches;
    if (latches) {
      const match = latches.find(l => l.sku === sku);
      if (match) return match.title;
    }
    const labels = {
      'SL-50K': 'Top Pull MagnaLatch Keyless',
      'SL-50H': 'Top Pull MagnaLatch Lockable'
    };
    return labels[sku] || sku || 'Unknown';
  }

  // Get hinge image URL based on current configuration
  getHingeImageUrl(sectionNum = 1) {
    const section = this.state.sections[sectionNum];

    // Read live dropdown value first to avoid stale state
    const hingeTypeSelect = document.getElementById(`section-${sectionNum}-hinge-color`);
    const hingeType = (hingeTypeSelect?.value) || section?.hingeType || 'SHG-90L';

    // SKU-based image map
    const skuImages = {
      'SHF-90L':     'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/hingefixetension.jpg?v=1780488431',
      'SHG-90L':     'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/Safetech-Hardware-TopKlik-Self-Closing-Gate-Hinges-Self-Aligning-Duty-Spring-Hinges-Adjustable-Tension-Pool-Safety-Hinges-Square-Post-Aluminum-Gates_af5c8a3f-9312-4208-a28b-983a1f765b.webp?v=1780941474',
      'PHS':         'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/61qbJD1PXGL._AC_SY355.jpg?v=1780941825',
      'TCA1L2S3BT':  'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/D_DTruCloseSelfClosingHingeswithLegs.png?v=1780432161'
    };
    if (skuImages[hingeType]) return skuImages[hingeType];
    // Legacy normalisation: treat old WT SKU as BT
    const normHinge = hingeType === 'TCA1L2S3WT' ? 'TCA1L2S3BT' : hingeType;
    if (skuImages[normHinge]) return skuImages[normHinge];

    // Fallback: check perfmeshProducts.hinges array for image field
    const hinges = this.shopifyData.collections?.perfmeshProducts?.hinges;
    if (hinges) {
      const match = hinges.find(h => h.sku === hingeType);
      if (match?.image) return match.image;
    }

    return 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/hinge-default.svg?v=1739010000';
  }

  // Get latch image URL based on current configuration
  getLatchImageUrl(sectionNum = 1) {
    const section   = this.state.sections[sectionNum];
    // Read live dropdown value first — catches cases where state wasn't synced yet
    const latchTypeSelect = document.getElementById(`section-${sectionNum}-latch-type`);
    const latchType = (latchTypeSelect?.value) || section?.latchType || 'SL-50K';

    // SKU-based options (Magna Latch products) — return CDN image directly
    const skuImages = {
      'SL-50K': 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/SL-50K-Front-View.png?v=1780430939',
      'SL-50H': 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/SL-50TRI-Front-View_ed932b9e-9bad-4856-a77c-57f5785d200d.png?v=1780431097'
    };
    if (skuImages[latchType]) return skuImages[latchType];

    // Position-based options (Glass to Glass, Wall to Glass, etc.) — use gateLatch variants
    const latchColor = (document.getElementById('global-latch-color')?.value || section?.latchColor || 'black').toLowerCase();
    const latchProduct = this.shopifyData.products?.gateLatch;
    if (!latchProduct || !latchProduct.variants) {
      return 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/latch-default.svg?v=1739010000';
    }
    const matchingVariant =
      latchProduct.variants.find(v =>
        v.available &&
        v.position?.toLowerCase() === latchType.toLowerCase() &&
        v.color?.toLowerCase()    === latchColor
      ) ||
      latchProduct.variants.find(v => v.available && v.color?.toLowerCase() === latchColor) ||
      latchProduct.variants.find(v => v.available);
    return matchingVariant?.image || latchProduct.image ||
           'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/latch-default.svg?v=1739010000';
  }

  // Get clamp image URL based on current configuration
  getClampImageUrl(sectionNum = 1) {
    const section = this.state.sections[sectionNum];
    const clampColor    = (section?.clampColor    || 'black').toLowerCase();
    const clampPosition = (section?.clampPosition || '').toLowerCase();

    const clampProduct = this.shopifyData.products?.clampStandard;

    if (clampProduct) {
      if (clampProduct.variants && clampProduct.variants.length > 0) {
        // Best match: colour AND position
        const matchingVariant =
          clampProduct.variants.find(v =>
            v.available &&
            v.color?.toLowerCase()    === clampColor &&
            v.position?.toLowerCase() === clampPosition
          ) ||
          // Fallback: colour only
          clampProduct.variants.find(v =>
            v.available &&
            v.color?.toLowerCase() === clampColor
          ) ||
          clampProduct.variants.find(v => v.available);

        if (matchingVariant?.image) return matchingVariant.image;
      }
      if (clampProduct.image) return clampProduct.image;
    }

    return '';
  }

  getRakedImageUrl(sectionNum = 1) {
    const section = this.state.sections[sectionNum];
    const rakedSide = section?.rakedSide || 'left';
    const rakedPanel = this.shopifyData.products?.rakedPanel;
    
    if (!rakedPanel || !rakedPanel.variants) {
      // Fallback to hardcoded URLs if no Shopify data
      return rakedSide === 'left'
        ? (this.shopifyData?.settings?.rakedImages?.left || '')
        : (this.shopifyData?.settings?.rakedImages?.right || '');
    }
    
    // Look for variants that match the selected side (left or right)
    const matchingVariant = rakedPanel.variants.find(variant => {
      const description = (variant.description || variant.option1 || '').toLowerCase();
      const sideMatch = description.includes(rakedSide);
      return variant.available && sideMatch;
    });
    
    if (matchingVariant && matchingVariant.image) {
      return matchingVariant.image;
    }
    
    // Fallback: return product image or hardcoded URL
    return rakedPanel.image || (rakedSide === 'left'
      ? (this.shopifyData?.settings?.rakedImages?.left || '')
      : (this.shopifyData?.settings?.rakedImages?.right || ''));
  }
    

  // Open image modal for magnified view
  openImageModal(src, alt) {
    const modal = document.getElementById('image-modal');
    const modalImage = document.getElementById('modal-image');
    
    if (!modal || !modalImage) return;
    
    modalImage.src = src;
    modalImage.alt = alt;
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
  
  // Close image modal
  closeImageModal() {
    const modal = document.getElementById('image-modal');
    if (modal) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  // Update all hardware preview images
  updateHardwarePreviews() {
    const currentSectionNum = this.getCurrentSectionNumber();
    const section = this.state.sections[currentSectionNum];

    // Update Hinge Base display (Glass to Glass / Wall to Glass)
    this.updateHingeBaseDisplay();

    // Update "Latch Selected" preview box
    const latchSelectedBox = document.getElementById('latch-selected-preview');
    if (latchSelectedBox) {
      if (section?.hasGate) {
        const latchUrl = this.getLatchImageUrl(currentSectionNum);
        latchSelectedBox.innerHTML = latchUrl
          ? `<img src="${latchUrl}" alt="Latch" style="max-height:110px;object-fit:contain;">`
          : `<div class="preview-placeholder"><p>No latch image</p></div>`;
      } else {
        latchSelectedBox.innerHTML = `<div class="preview-placeholder"><p>No gate configured</p></div>`;
      }
    }

    // Update "Hinge Selected" preview box
    const hingeSelectedBox = document.getElementById('hinge-selected-preview');
    if (hingeSelectedBox) {
      if (section?.hasGate) {
        const hingeUrl = this.getHingeImageUrl(currentSectionNum);
        hingeSelectedBox.innerHTML = hingeUrl
          ? `<img src="${hingeUrl}" alt="Hinge" style="max-height:110px;object-fit:contain;">`
          : `<div class="preview-placeholder"><p>No hinge image</p></div>`;
      } else {
        hingeSelectedBox.innerHTML = `<div class="preview-placeholder"><p>No gate configured</p></div>`;
      }
    }

    // Update small inline hinge thumbnail next to Hinge Type dropdown
    const hingeThumb = document.getElementById(`section-${currentSectionNum}-hinge-thumb`);
    if (hingeThumb && section?.hasGate) {
      const hingeUrl = this.getHingeImageUrl(currentSectionNum);
      if (hingeUrl) { hingeThumb.src = hingeUrl; hingeThumb.style.display = 'block'; }
    } else if (hingeThumb) {
      hingeThumb.style.display = 'none';
    }

    // Update small inline latch thumbnail next to Latch Type dropdown
    const latchThumb = document.getElementById(`section-${currentSectionNum}-latch-thumb`);
    if (latchThumb && section?.hasGate) {
      const latchUrl = this.getLatchImageUrl(currentSectionNum);
      if (latchUrl) { latchThumb.src = latchUrl; latchThumb.style.display = 'block'; }
    } else if (latchThumb) {
      latchThumb.style.display = 'none';
    }

    // Update visibility of entire preview container based on hardware selection
    const previewContainer = document.getElementById('product-images-preview');
    if (previewContainer) {
      const hasGate = section?.hasGate;
      const hasClamp = section?.hasClamp;
      const hasAnyHardware = hasGate || hasClamp;
      
      // Hide entire container if no hardware is selected
      previewContainer.style.display = hasAnyHardware ? 'block' : 'none';
    }
    
    // Only update individual hardware items if the container is visible
    if (previewContainer && previewContainer.style.display !== 'none') {
      // Update hinge preview
      const hingePreviewItem = document.getElementById('hinge-preview-item');
      const hingePreviewImg = document.getElementById('hinge-preview-img');
      if (hingePreviewItem && hingePreviewImg) {
        if (section?.hasGate) {
          hingePreviewItem.style.display = 'block';
          const hingeImageUrl = this.getHingeImageUrl(currentSectionNum);
          hingePreviewImg.src = hingeImageUrl;
        } else {
          hingePreviewItem.style.display = 'none';
        }
      }
      
      // Update latch preview
      const latchPreviewItem = document.getElementById('latch-preview-item');
      const latchPreviewImg = document.getElementById('latch-preview-img');
      if (latchPreviewItem && latchPreviewImg) {
        if (section?.hasGate) {
          latchPreviewItem.style.display = 'block';
          const latchImageUrl = this.getLatchImageUrl(currentSectionNum);
          latchPreviewImg.src = latchImageUrl;
        } else {
          latchPreviewItem.style.display = 'none';
        }
      }
      
      // Update clamp preview
      const clampPreviewItem = document.getElementById('clamp-preview-item');
      const clampPreviewImg = document.getElementById('clamp-preview-img');
      if (clampPreviewItem && clampPreviewImg) {
        if (section?.hasClamp) {
          clampPreviewItem.style.display = 'block';
          const clampImageUrl = this.getClampImageUrl(currentSectionNum);
          clampPreviewImg.src = clampImageUrl;
        } else {
          clampPreviewItem.style.display = 'none';
        }
      }
    }
  }

  // Update clamp preview image
  updateClampPreview() {
    // Find or create the clamp preview container
    let clampPreviewContainer = document.querySelector('.clamp-product-preview');
    if (!clampPreviewContainer) {
      const gateFormsContainer = document.getElementById('gate-forms-container');
      if (!gateFormsContainer) return;

      clampPreviewContainer = document.createElement('div');
      clampPreviewContainer.className = 'clamp-product-preview';
      clampPreviewContainer.style.cssText = `
        margin-top: 16px;
        padding: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        background: #f8fafc;
        text-align: center;
      `;
      gateFormsContainer.appendChild(clampPreviewContainer);
    }

    // Check if clamp is enabled for current section
    const currentSectionNum = this.getCurrentSectionNumber();
    const clampCheckbox = document.getElementById(`section-${currentSectionNum}-clamp`);
    const isClampEnabled = clampCheckbox?.checked || false;

    if (isClampEnabled) {
      const clampImageUrl = this.getClampImageUrl(currentSectionNum);

      clampPreviewContainer.style.display = 'block';
      clampPreviewContainer.innerHTML = `
        <div style="font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 8px;">Clamp Preview</div>
        <img src="${clampImageUrl}" alt="Clamp Preview" style="width: 100%; height: auto; max-height: 150px; object-fit: contain; border-radius: 4px; background: white; padding: 8px; border: 1px solid #e2e8f0;" 
             onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDIwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSIxMDAiIHk9IjUwIiBmaWxsPSIjNjQ3NDhCIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkNsYW1wPC90ZXh0Pjwvc3ZnPg=='">
      `;
    } else {
      clampPreviewContainer.style.display = 'none';
    }
  }

  // Update spigot preview image in component preview
  updateSpigotPreview() {
    const componentPreview = document.getElementById('component-preview');
    if (!componentPreview) return;

    const imageUrl = this.getSpigotImageUrl();
    
    // Replace the entire component preview with magnifiable image
    componentPreview.innerHTML = `
      <div class="image-wrapper magnify-wrapper" style="position: relative; width: 100%; height: 100%; overflow: hidden; cursor: zoom-in;">
        <img src="${imageUrl}" alt="Spigot Preview" class="hardware-image magnifiable" 
             style="width: 100%; height: auto; object-fit: contain; border-radius: 4px; transition: transform 0.3s ease;" 
             onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxNTAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSIxMDAiIHk9Ijc1IiBmaWxsPSIjNjQ3NDhCIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlNwaWdvdCBJbWFnZTwvdGV4dD48L3N2Zz4='">
      </div>
    `;
    
    // Setup click event for magnify modal
    const img = componentPreview.querySelector('.magnifiable');
    if (img) {
      img.addEventListener('click', this.openImageModal.bind(this, img.src, img.alt));
    }
  }

  // Update the Post Selected preview based on ground post state
  // Ground Post ON → ground post image; Ground Post OFF → base plate image
  updatePostPreview() {
    const componentPreview = document.getElementById('component-preview');
    if (!componentPreview) return;

    const postColor = (document.getElementById('post-color')?.value || 'black').toLowerCase();

    const fenceColourImages = (window.CF_SWATCH_IMAGES) || {
      'black':    'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeBlk.png?v=1783627985',
      'white':    'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeWht.png?v=1783627986',
      'monument': 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeGry.png?v=1783627986'
    };

    const imageUrl = fenceColourImages[postColor] || fenceColourImages['black'];
    const label = `Blade Fence – ${postColor.charAt(0).toUpperCase() + postColor.slice(1)}`;

    componentPreview.innerHTML = `
      <div class="image-wrapper magnify-wrapper" style="position: relative; width: 100%; height: 100%; overflow: hidden; cursor: zoom-in;">
        <img src="${imageUrl}" alt="${label}" class="hardware-image magnifiable"
             style="width: 100%; height: auto; object-fit: contain; border-radius: 4px; transition: transform 0.3s ease;">
      </div>
    `;
    const img = componentPreview.querySelector('.magnifiable');
    if (img) img.addEventListener('click', this.openImageModal.bind(this, img.src, img.alt));
  }

  // DEPRECATED: Replaced by updateHardwarePreviews()
  // Update section product preview (hinge and latch images)
  /* updateSectionProductPreview() {
    // Find or create the preview container below section forms
    let previewContainer = document.querySelector('.section-product-preview');
    if (!previewContainer) {
      const sectionFormsContainer = document.getElementById('section-forms-container');
      if (!sectionFormsContainer) return;

      previewContainer = document.createElement('div');
      previewContainer.className = 'section-product-preview';
      previewContainer.style.cssText = `
        margin-top: 16px;
        padding: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        background: #f8fafc;
        display: flex;
        flex-direction: column;
        gap: 16px;
      `;
      sectionFormsContainer.parentNode.insertBefore(previewContainer, sectionFormsContainer.nextSibling);
    }

    // Check if gate is enabled for current section
    const currentSectionNum = this.getCurrentSectionNumber();
    const gateCheckbox = document.getElementById(`section-${currentSectionNum}-gate`);
    const isGateEnabled = gateCheckbox?.checked || false;

    if (isGateEnabled) {
      const hingeImageUrl = this.getHingeImageUrl(currentSectionNum);
      const latchImageUrl = this.getLatchImageUrl(currentSectionNum);

      previewContainer.style.display = 'flex';
      previewContainer.innerHTML = `
        <div style="text-align: center;">
          <div style="font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 8px;">Hinge</div>
          <img src="${hingeImageUrl}" alt="Hinge Preview" style="width: 100%; height: auto; max-height: 180px; object-fit: contain; border-radius: 4px; background: white; padding: 8px; border: 1px solid #e2e8f0;" 
               onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDIwMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSIxMDAiIHk9IjYwIiBmaWxsPSIjNjQ3NDhCIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkhpbmdlPC90ZXh0Pjwvc3ZnPg=='">
        </div>
        <div style="text-align: center;">
          <div style="font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 8px;">Latch</div>
          <img src="${latchImageUrl}" alt="Latch Preview" style="width: 100%; height: auto; max-height: 180px; object-fit: contain; border-radius: 4px; background: white; padding: 8px; border: 1px solid #e2e8f0;" 
               onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDIwMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSIxMDAiIHk9IjYwIiBmaWxsPSIjNjQ3NDhCIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkxhdGNoPC90ZXh0Pjwvc3ZnPg=='">
        </div>
      `;
    } else {
      previewContainer.style.display = 'none';
    }
  } */

  // Get current section number (helper method)
  getCurrentSectionNumber() {
    // Find the currently visible section form
    const activeSectionForm = document.querySelector('.section-form[style*="display: block"]');
    if (activeSectionForm) {
      const match = activeSectionForm.id.match(/section-(\d+)-form/);
      return match ? parseInt(match[1]) : 1;
    }
    return 1;
  }

  // Populate add-ons from collection
  // Driven by Ground Post toggle across all sections:
  //   Ground Post OFF (base plate) → show coach screws
  //   Ground Post ON  (core drill) → show Sika grout
  populateAddOns() {
    const container = this.domCache.get('addon-list');
    if (!container) return;

    const addOns = this.shopifyData.collections?.addOns || [];

    // Determine ground post state — true if ANY section has groundPost ON
    const isGroundPost = Object.values(this.state.sections || {}).some(s => s.groundPost === true);

    // Save currently checked state AND quantity so selections survive a refresh
    const checked = {};
    const quantities = {};
    container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      checked[cb.dataset.productId] = cb.checked;
    });
    container.querySelectorAll('input[type="number"]').forEach(qty => {
      // Only preserve a quantity the user actually changed — otherwise let the
      // recommended amount refresh as the job (post count) changes.
      if (qty.dataset.userSet === '1') quantities[qty.dataset.productId] = parseInt(qty.value) || 1;
    });

    container.innerHTML = '';

    let groutRendered = false;
    addOns.forEach(product => {
      const title = product.title.toLowerCase();

      const isGrout  = title.includes('grout') || title.includes('construction') || title.includes('sika') || title.includes('bostik');
      const isScrews = title.includes('coach') || title.includes('screw') || title.includes('bolt') || title.includes('anchor') || title.includes('fixing');

      // Ground Post ON → show grout only; Ground Post OFF → show screws only
      if (isGrout  && !isGroundPost) return;
      if (isScrews &&  isGroundPost) return;

      // Only render the second grout product — skip the first (Sika), show Bostik
      if (isGrout) {
        if (!groutRendered) { groutRendered = true; return; }
      }

      const price = PriceHelper.format(PriceHelper.fromCents(product.price), this.shopifyData);
      const displayTitle = isGrout ? '20kg Sika High Early Strength Construction Grout' : product.title;
      const productImage = product.variants[0]?.image || product.image || '';

      // ── Recommended quantity (customer can adjust) ────────────────────────
      // Total posts across the job — used to recommend both grout bags and fixings.
      const totalPosts = Object.entries(this.state.sections || {}).reduce((sum, [num, section]) => {
        const calc = this.state.optimizedConfigs?.[num];
        if (!calc) return sum;
        const rp = calc.regularPanels || 0;
        const gp = (calc.gatePanels || 0) > 0 ? 1 : 0;
        const rkp = (calc.rakedPanels || 0) > 0 ? 1 : 0;
        return sum + rp + 1 + gp + rkp;
      }, 0);

      let suggestedQty = 1;
      let qtyHint = '';
      let qtyLabel = 'Qty:';
      if (isGrout) {
        qtyLabel = 'Bags:';
        suggestedQty = totalPosts > 0 ? Math.ceil(totalPosts / 4) : 1;
        if (totalPosts > 0) qtyHint = `Recommended: ${suggestedQty} bag${suggestedQty !== 1 ? 's' : ''} (${totalPosts} posts ÷ 4) — adjust as needed`;
      } else if (isScrews) {
        qtyLabel = 'Qty:';
        // Base plates take 4 fixings each — recommend 4 per post; customer can change.
        suggestedQty = totalPosts > 0 ? totalPosts * 4 : 4;
        if (totalPosts > 0) qtyHint = `Recommended: ${suggestedQty} (${totalPosts} posts × 4 per base plate) — adjust as needed`;
      }
      const savedQty = quantities[product.id] || suggestedQty;
      const showQty  = isGrout || isScrews;

      // ── M8 / masonry fixing advisory (Pete 19/07) ─────────────────────────
      const advisoryHTML = isScrews
        ? `<div style="font-size:1.2rem; color:#92400e; background:#fef3c7; border:1px solid #fcd34d; border-radius:6px; padding:8px 10px; line-height:1.45;">
             ⚠ <strong>Fixing note:</strong> These bolts are for fixing base plates to <strong>concrete or masonry walls</strong> — they are <strong>not</strong> for timber. Fixing to timber or decking varies case by case, so please source the correct fixings to suit your specific surface.
           </div>`
        : '';

      const addonItem = document.createElement('div');
      addonItem.className = 'addon-item';
      addonItem.style.cssText = 'display: flex; flex-direction: column; padding: 10px; border: 1px solid #e2e8f0; background: white; margin-bottom: 6px; gap: 10px;';

      addonItem.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <input type="checkbox" data-product-id="${product.id}" data-variant-id="${product.variants[0]?.id}"
            style="margin: 0; accent-color: #0e9ed5; width: 16px; height: 16px; flex-shrink: 0;">
          <span style="flex: 1; font-size: 1.58rem; color: #334155;">${displayTitle}</span>
          ${showQty ? `
          <div style="display: flex; align-items: center; gap: 4px; flex-shrink: 0;">
            <label style="font-size: 1.35rem; color: #64748b; margin: 0;">${qtyLabel}</label>
            <input type="number"
              data-product-id="${product.id}"
              class="addon-qty"
              min="1" max="999" value="${savedQty}"
              style="width: 60px; padding: 4px 6px; border: 1px solid #cbd5e1; font-size: 1.35rem; text-align: center;">
          </div>` : ''}
          <span class="addon-price" style="font-weight: 600; color: #059669; font-size: 1.58rem; flex-shrink: 0;">${price}</span>
        </div>
        ${qtyHint ? `<div style="font-size:1.23rem; color:#64748b; padding: 0 2px;">${qtyHint}</div>` : ''}
        ${advisoryHTML}
        ${productImage ? `
        <div style="width: 100%; display: flex; justify-content: center;">
          <img src="${productImage}" alt="${displayTitle}"
            style="width: 100%; max-width: 200px; height: 130px; object-fit: contain; border: 1px solid #e2e8f0; border-radius: 6px; background: #f8fafc; padding: 8px; box-sizing: border-box;">
        </div>` : ''}
      `;

      // Restore checked state
      const checkbox = addonItem.querySelector('input[type="checkbox"]');
      if (checked[product.id]) checkbox.checked = true;

      // Quantity change triggers quote recalc
      const qtyInput = addonItem.querySelector('.addon-qty');
      if (qtyInput) {
        qtyInput.addEventListener('change', () => { qtyInput.dataset.userSet = '1'; this.updateComponentsQuote(); this.updateComponentInfo(); });
        qtyInput.addEventListener('input',  () => { qtyInput.dataset.userSet = '1'; this.updateComponentsQuote(); this.updateComponentInfo(); });
      }

      container.appendChild(addonItem);
    });

    if (container.children.length === 0) {
      const msg = document.createElement('p');
      msg.style.cssText = 'color: #64748b; font-size: 13px; margin: 4px 0; padding: 4px 0;';
      msg.textContent = isGroundPost
        ? 'No construction grout found in add-ons collection.'
        : 'No coach screws / fixings found in add-ons collection.';
      container.appendChild(msg);
    }

    // Panel Design section
    this.renderPanelDesignSection();
  }

  renderPanelDesignSection() {
    // Remove existing panel design section if any
    const existing = document.getElementById('panel-design-section');
    if (existing) existing.remove();

    const addonList = this.domCache.get('addon-list');
    if (!addonList) return;

    const designs = [
      { value: 'gum-leaves',  label: 'Gum Leaves',  image: 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/gumleaves.webp?v=1778176934',  id: this.shopifyData.collections?.perfmeshProducts?.panels?.design?.find(d => d.title === 'Gum Leaves')?.id  || null, price: this.shopifyData.collections?.perfmeshProducts?.panels?.design?.find(d => d.title === 'Gum Leaves')?.price  || 0 },
      { value: 'palm',        label: 'Palm',         image: 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/Palm.webp?v=1778176934',         id: this.shopifyData.collections?.perfmeshProducts?.panels?.design?.find(d => d.title === 'Palm')?.id         || null, price: this.shopifyData.collections?.perfmeshProducts?.panels?.design?.find(d => d.title === 'Palm')?.price         || 0 },
      { value: 'bird-nest',   label: 'Bird Nest',    image: 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/Birdnest.webp?v=1778176934',      id: this.shopifyData.collections?.perfmeshProducts?.panels?.design?.find(d => d.title === 'Bird Nest')?.id   || null, price: this.shopifyData.collections?.perfmeshProducts?.panels?.design?.find(d => d.title === 'Bird Nest')?.price   || 0 }
    ];

    const savedDesign = this.state.panelDesign || 'none';

    const section = document.createElement('div');
    section.id = 'panel-design-section';
    section.style.cssText = 'margin-top: 16px;';

    section.innerHTML = `
      <h4 style="font-size: 1.58rem; font-weight: 700; color: #1e293b; margin: 0 0 10px 0;">Panel Design <span style="font-weight:400; color:#64748b; font-size:1.35rem;">(Optional)</span></h4>
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <label style="display: flex; align-items: center; justify-content: space-between; padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; background: white; cursor: pointer; gap: 10px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <input type="radio" name="panel-design" value="none" ${savedDesign === 'none' ? 'checked' : ''} style="accent-color: #0e9ed5; width: 16px; height: 16px; flex-shrink: 0;">
            <span style="font-size: 1.58rem; color: #334155; font-weight: 500;">None</span>
          </div>
        </label>
        ${designs.map(d => `
        <label style="display: flex; align-items: center; justify-content: space-between; padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; background: white; cursor: pointer; gap: 10px; transition: border-color 0.2s;" 
               onmouseover="this.style.borderColor='#0e9ed5'" onmouseout="this.style.borderColor=this.querySelector('input').checked ? '#0e9ed5' : '#e2e8f0'">
          <div style="display: flex; align-items: center; gap: 8px;">
            <input type="radio" name="panel-design" value="${d.value}" ${savedDesign === d.value ? 'checked' : ''} style="accent-color: #0e9ed5; width: 16px; height: 16px; flex-shrink: 0;">
            <span style="font-size: 1.58rem; color: #334155; font-weight: 500;">${d.label}</span>
          </div>
          <img src="${d.image}" alt="${d.label}" style="width: 80px; height: 50px; object-fit: cover; border-radius: 4px; border: 1px solid #e2e8f0; flex-shrink: 0;">
        </label>`).join('')}
      </div>
    `;

    // Wire up radio change events
    section.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.state.panelDesign = e.target.value;
        // Highlight selected border
        section.querySelectorAll('label').forEach(lbl => {
          lbl.style.borderColor = lbl.querySelector('input').checked ? '#0e9ed5' : '#e2e8f0';
        });
        this.updateComponentInfo();
        this.updateComponentsQuote();
      });
    });

    // Insert right after addon-list
    addonList.parentNode.insertBefore(section, addonList.nextSibling);

    // Apply saved border highlights
    section.querySelectorAll('label').forEach(lbl => {
      if (lbl.querySelector('input')?.checked) lbl.style.borderColor = '#0e9ed5';
    });
  }

  // Setup all event listeners
  setupEventListeners() {
    // Tab navigation
    document.querySelectorAll('.tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        this.switchTab(e.target.dataset.tab);
      });
    });

    // Click outside to clear panel selections
    document.addEventListener('click', (e) => {
      // Check if click is outside any panel or control
      if (!e.target.closest('.panel-preview') && !e.target.closest('.panel-controls')) {
        this.clearAllSelections();
      }
    });

    // Event delegation for change events
    document.addEventListener('change', (e) => {
      const handler = this.changeHandlers[e.target.id];
      if (handler) {
        handler(e);
      } else if (e.target.matches('#addon-list input[type="checkbox"]')) {
        this.updateComponentsQuote();
        this.updateComponentInfo();
      }
    });

    // Direct fallback listener for sections-count in case ID differs in Liquid template
    const sectionsSelect = document.getElementById('sections-count')
      || document.querySelector('select[name="sections-count"]')
      || document.querySelector('.sections-count-select');
    if (sectionsSelect && !this.changeHandlers[sectionsSelect.id]) {
      sectionsSelect.addEventListener('change', (e) => {
        this.handleSectionsChange(parseInt(e.target.value));
        if (e.target && e.target.blur) e.target.blur();  // close the dropdown on keyboard selection
      });
    }
    // Keyboard: pressing a valid number picks it and closes the dropdown, like a click.
    if (sectionsSelect) {
      sectionsSelect.addEventListener('keydown', (e) => {
        if (/^[1-9]$/.test(e.key)) {
          const opt = Array.from(sectionsSelect.options).find(o => o.value === e.key);
          if (opt) {
            e.preventDefault();
            sectionsSelect.value = e.key;
            sectionsSelect.dispatchEvent(new Event('change', { bubbles: true }));
            sectionsSelect.blur();
          }
        }
      });
    }
    // FIX: If the dropdown already has a default value on load (e.g. "1"),
    // handleSectionsChange is never triggered by a change event, leaving
    // state.sectionsCount unset and sections-next-btn without its listener.
    if (sectionsSelect && sectionsSelect.value && parseInt(sectionsSelect.value) > 0) {
      this.handleSectionsChange(parseInt(sectionsSelect.value));
    }

    // Navigation buttons
    this.setupNavigationButtons();

    // Quote actions
    this.setupQuoteActions();
  }

  // Setup navigation button listeners
  setupNavigationButtons() {
    const backBtn = this.domCache.get('nav-back')
      || document.querySelector('[data-action="nav-back"]')
      || Array.from(document.querySelectorAll('button')).find(b => b.textContent.trim() === '← Back');
    const nextBtn = this.domCache.get('nav-next')
      || document.querySelector('[data-action="nav-next"]')
      || Array.from(document.querySelectorAll('button')).find(b => /^Next\s*→/.test(b.textContent.trim()));
    const addToCartBtn = this.domCache.get('add-to-cart');

    if (backBtn) {
      backBtn.addEventListener('click', () => this.navigateBack());
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.navigateNext());
    }

    // Floating "Next" button on the preview box — same behaviour as the main Next.
    const previewNextBtn = document.getElementById('preview-next-btn');
    if (previewNextBtn) previewNextBtn.addEventListener('click', () => this.navigateNext());

    const diyBtn = document.getElementById('btn-diy-checkout');
    const siBtn  = document.getElementById('btn-si-enquiry');
    if (diyBtn) diyBtn.addEventListener('click', () => this.proceedToQuotePage());
    if (siBtn)  siBtn.addEventListener('click',  () => this.addSiteInspectionToCart());

    // The green quote box on the Review step doubles as the primary CTA button.
    // It triggers the same action as the currently-relevant checkout button
    // (DIY → proceed to quote/cart; Supply & Install → book site inspection).
    const finalQuoteBox = document.getElementById('final-quote-box');
    if (finalQuoteBox) {
      const triggerCheckout = () => {
        // Ignore clicks until a section is completed (box acts as plain price display).
        if (finalQuoteBox.dataset.ctaEnabled !== '1' || !this.hasCompletedSection()) return;
        if (this.state.pricingType === 'install') this.addSiteInspectionToCart();
        else this.proceedToQuotePage();
      };
      finalQuoteBox.addEventListener('click', triggerCheckout);
      finalQuoteBox.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); triggerCheckout(); }
      });
    }
  }

  // ── Per-section redesign: Add Sections "same vs individual" + shared options ──
  setupAddSectionsControls() {
    const shared = this.state.shared || (this.state.shared = { color: 'satin-black', fenceHeight: '1200', groundPost: false });

    // Mode switch (same / individual)
    const modeInputs = document.querySelectorAll('input[name="add-mode"]');
    const panel      = document.getElementById('shared-options-panel');
    const note       = document.getElementById('add-individual-note');
    const sameLabel  = document.getElementById('add-mode-same-label');
    const indivLabel = document.getElementById('add-mode-individual-label');
    const syncModePills = () => {
      const same = this.state.sameForAll;
      if (sameLabel)  { sameLabel.style.background  = same ? '#0e9ed5' : 'white'; sameLabel.style.color  = same ? 'white'   : '#64748b'; }
      if (indivLabel) { indivLabel.style.background = same ? 'white'   : '#0e9ed5'; indivLabel.style.color = same ? '#64748b' : 'white'; }
      if (panel) panel.style.display = same ? 'block' : 'none';
      if (note)  note.style.display  = same ? 'none'  : 'block';
    };
    modeInputs.forEach(inp => {
      inp.checked = (inp.value === (this.state.sameForAll ? 'same' : 'individual'));
      inp.addEventListener('change', () => {
        if (!inp.checked) return;
        this.state.sameForAll = (inp.value === 'same');
        syncModePills();
        if (this.state.sameForAll) this.applySharedToAllSections();
        this.applySectionModeVisibility();
        this.updateQuoteDisplay();
      });
    });
    syncModePills();

    // Shared Fence Colour swatches
    const colorInputs = document.querySelectorAll('#shared-color-swatches input[name="shared-color"]');
    const syncSharedColor = (val) => {
      colorInputs.forEach(inp => {
        const dot = inp.parentElement.querySelector('.cf-swatch-dot');
        const sel = inp.value === val;
        if (dot) { dot.style.border = sel ? '3px solid #0e9ed5' : '3px solid transparent'; dot.style.boxShadow = sel ? '0 0 0 1px #0e9ed5' : '0 0 0 1px #cbd5e1'; }
        inp.checked = sel;
      });
    };
    colorInputs.forEach(inp => inp.addEventListener('change', () => {
      if (!inp.checked) return;
      shared.color = inp.value;
      syncSharedColor(inp.value);
      if (this.state.sameForAll) { this.applySharedToAllSections(); this.updateQuoteDisplay(); }
    }));
    syncSharedColor(shared.color);

    // Shared Height
    const heightSel = document.getElementById('shared-height');
    if (heightSel) {
      heightSel.value = shared.fenceHeight || '1200';
      heightSel.addEventListener('change', () => {
        shared.fenceHeight = heightSel.value;
        if (this.state.sameForAll) { this.applySharedToAllSections(); this.updateQuoteDisplay(); }
      });
    }

    // Shared Mount (base / inground)
    const mountInputs = document.querySelectorAll('input[name="shared-mount"]');
    const baseLbl = document.getElementById('shared-mount-base-label');
    const ingLbl  = document.getElementById('shared-mount-inground-label');
    const syncMount = () => {
      const ing = !!shared.groundPost;
      if (baseLbl) { baseLbl.style.background = ing ? 'white'   : '#0e9ed5'; baseLbl.style.color = ing ? '#64748b' : 'white'; }
      if (ingLbl)  { ingLbl.style.background  = ing ? '#0e9ed5' : 'white';   ingLbl.style.color  = ing ? 'white'   : '#64748b'; }
    };
    mountInputs.forEach(inp => {
      inp.checked = (inp.value === (shared.groundPost ? 'inground' : 'base'));
      inp.addEventListener('change', () => {
        if (!inp.checked) return;
        shared.groundPost = (inp.value === 'inground');
        syncMount();
        if (this.state.sameForAll) { this.applySharedToAllSections(); this.updateQuoteDisplay(); }
      });
    });
    syncMount();
  }

  // Write the shared colour/height/mount into every section (all-same mode).
  applySharedToAllSections() {
    const sh = this.state.shared || {};
    Object.keys(this.state.sections).forEach(n => {
      const s = this.state.sections[n];
      if (!s) return;
      s.color = sh.color || 'satin-black';
      s.fenceHeight = sh.fenceHeight || '1200';
      s.panelLength = this.getPanelLengthForHeight(s.fenceHeight);
      s.groundPost = !!sh.groundPost;
      delete this.state.optimizedConfigs[n];
    });
    this.updateBladeHeightLabel();
  }

  // Show/hide the per-section colour/height/mount groups based on mode.
  applySectionModeVisibility() {
    const hide = !!this.state.sameForAll;
    document.querySelectorAll('.cf-persec-shared').forEach(el => {
      el.style.display = hide ? 'none' : '';
    });
  }

  // "Blade Fencing 1500 High Panels" line above the Section Configuration heading.
  updateBladeHeightLabel(sectionNum) {
    const el = document.getElementById('blade-height-line');
    if (!el) return;
    let h;
    if (this.state.sameForAll) {
      h = (this.state.shared && this.state.shared.fenceHeight) || '1200';
    } else {
      const n = sectionNum || this.state.currentSection || 1;
      h = (this.state.sections[n] && this.state.sections[n].fenceHeight) || '1200';
    }
    el.textContent = `${h}mm high Blade Fencing`;
  }

  // Custom "How many sections" dropdown. Native <select> popups (Windows/Chrome) swallow
  // key presses while open, so pressing a number doesn't close them. This div-based list
  // keeps focus on the trigger button, so a number key selects + closes like a click. The
  // hidden native #sections-count stays the source of truth (all existing code reads it).
  setupSectionCountDropdown() {
    const sel = document.getElementById('sections-count');
    if (!sel || sel.dataset.cfCustom) return;
    sel.dataset.cfCustom = '1';
    sel.style.display = 'none';
    const opts = Array.from(sel.options).filter(o => o.value !== '');
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:relative; max-width:360px;';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.style.cssText = 'width:100%; text-align:left; padding:9px 12px; border:1px solid #cbd5e1; border-radius:6px; background:#fff; font-size:16px; cursor:pointer; display:flex; justify-content:space-between; align-items:center;';
    trigger.innerHTML = '<span class="cf-val"></span><span style="color:#64748b;">▾</span>';
    const valEl = trigger.querySelector('.cf-val');
    const setTriggerText = () => { valEl.textContent = sel.value ? sel.value : 'Select…'; valEl.style.color = sel.value ? '#334155' : '#94a3b8'; };
    const list = document.createElement('div');
    list.setAttribute('role', 'listbox');
    list.style.cssText = 'position:absolute; top:calc(100% + 4px); left:0; right:0; background:#fff; border:1px solid #cbd5e1; border-radius:6px; box-shadow:0 6px 18px rgba(0,0,0,.12); z-index:50; overflow:hidden; display:none;';
    const close = () => { list.style.display = 'none'; };
    const open  = () => { list.style.display = 'block'; };
    const choose = (val) => {
      sel.value = val;
      setTriggerText();
      close();
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    };
    opts.forEach(o => {
      const item = document.createElement('div');
      item.textContent = o.textContent;
      item.setAttribute('role', 'option');
      item.style.cssText = 'padding:9px 12px; cursor:pointer; font-size:16px; color:#334155;';
      item.addEventListener('mouseenter', () => item.style.background = '#f1f5f9');
      item.addEventListener('mouseleave', () => item.style.background = '#fff');
      item.addEventListener('click', () => choose(o.value));
      list.appendChild(item);
    });
    trigger.addEventListener('click', () => { list.style.display === 'block' ? close() : open(); });
    trigger.addEventListener('keydown', (e) => {
      if (/^[1-9]$/.test(e.key)) { const o = opts.find(x => x.value === e.key); if (o) { e.preventDefault(); choose(e.key); } }
      else if (e.key === 'Escape') close();
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); list.style.display === 'block' ? close() : open(); }
    });
    document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(); });
    sel.parentNode.insertBefore(wrap, sel.nextSibling);
    wrap.appendChild(trigger);
    wrap.appendChild(list);
    setTriggerText();
  }

  // Remove the orphaned "Finishing touches for each section" toggle injected by an
  // old inline page patch (name="claude-same-for-all"). It does nothing now — it's
  // superseded by the Add Sections "All sections the same / Configure individually"
  // switch. Runs a few times in case the inline patch injects it after our init.
  removeOrphanToggle() {
    try {
      const scope = document.getElementById('add-sections') || document;
      scope.querySelectorAll('input[name="claude-same-for-all"]').forEach(r => {
        const lbl = r.closest('label');
        (lbl || r).remove();
      });
      scope.querySelectorAll('h1,h2,h3,h4,h5,h6,label,p,span,div').forEach(el => {
        if (el.childElementCount === 0 && /^\s*Finishing touches for each section\s*$/i.test(el.textContent || '')) {
          el.remove();
        }
      });
    } catch (e) {}
  }

  // Setup quote action listeners
  setupQuoteActions() {
    const downloadBtn = this.domCache.get('download-quote');
    const emailBtn = this.domCache.get('email-quote');

    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => this.downloadQuote());
    }

    if (emailBtn) {
      emailBtn.addEventListener('click', () => this.emailQuote());
    }
  }

  // Switch between tabs
  switchTab(targetTab) {
    // Finishing Touches was removed from the flow — never allow navigation to it.
    // This is the single choke-point for all tab changes, so any lingering path
    // (or stale-cached navigateNext) that targets it lands on Review instead.
    if (targetTab === 'add-components') targetTab = 'review-checkout';

    // Before switching, save current state
    this.saveCurrentSectionState();

    // ── PRESERVE CALCULATIONS: snapshot optimizedConfigs before leaving ──
    // Without this, navigating to Color Finishes then back causes
    // optimizedConfigs to be empty, which forces updateCalculationResults
    // to recalc from scratch — often producing a blank preview if the tab
    // DOM is not yet fully visible when the recalc fires.
    if (this.state.currentTab === 'configure-sections') {
      this._optimizedConfigsSnapshot = JSON.parse(
        JSON.stringify(this.state.optimizedConfigs || {})
      );
    }
    // Restore snapshot when returning to configure-sections
    if (targetTab === 'configure-sections' && this._optimizedConfigsSnapshot) {
      // Only restore entries that are still valid and haven't been replaced
      Object.entries(this._optimizedConfigsSnapshot).forEach(([k, v]) => {
        if (!this.state.optimizedConfigs[k]) {
          this.state.optimizedConfigs[k] = v;
        }
      });
    }

    // Remove active class from all tabs and content
    document.querySelectorAll('.tab').forEach(tab => tab.classList.remove('active'));
    document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));

    // Activate tab nav element — won't crash if missing
    const tabEl = document.querySelector(`[data-tab="${targetTab}"]`);
    if (tabEl) {
      tabEl.classList.add('active');
    } else {
      console.warn('[ullc] switchTab: no [data-tab="' + targetTab + '"] found. Available:',
        Array.from(document.querySelectorAll('[data-tab]')).map(el => el.dataset.tab));
    }

    // Activate tab content panel — won't crash if missing
    const contentEl = document.getElementById(targetTab);
    if (contentEl) {
      contentEl.classList.add('active');
    } else {
      const fallback = document.querySelector(`[data-tab-content="${targetTab}"]`);
      if (fallback) {
        fallback.classList.add('active');
      } else {
        console.warn('[ullc] switchTab: no #' + targetTab + ' content found. Available IDs:',
          Array.from(document.querySelectorAll('.tab-content')).map(el => el.id));
      }
    }

    this.state.currentTab = targetTab;
    // Rebuild the breakdown for the tab we just moved to. Only review-checkout and
    // add-components did this, so once updateQuoteBreakdown started clearing the inactive
    // containers, coming back to configure would have found an empty one.
    try { this.updateQuoteBreakdown(); } catch (e) {}
    this.updateNavigationButtons();
    
    // Special handling for configure-sections tab — show section 1 and render preview
    if (targetTab === 'configure-sections') {
      const currentSection = this.state.currentSection || 1;
      this.showSection(currentSection);
      // Recheck sticky state after tab switch
      this._updateStickyPreview();
      // Refresh breakdown for this tab's container
      this.updateQuoteBreakdown();
    }

    // Special handling for review tab
    if (targetTab === 'review-checkout') {
      // Recalculate on arrival: a fixing change made just before left the parts list stale (break test 5 Oct 2026).
      try { this.updateQuoteDisplay(); } catch (e) {}
      this.updateQuoteSummary();
      this.updateFinalSectionsPreview();
      // The preview above redraws every section (a section edited while another was on screen is only
      // redrawn here), and Blade bills the posts it drew - so price it again from that drawing
      // (break test 5 Oct 2026: 3 sections, 'No Post Both Ends' on section 1 billed 14 posts, drew 12).
      try { this.updateQuoteDisplay(); this.updateQuoteSummary(); } catch (e) {}
      this.updateQuoteBreakdown();
      this.renderReviewOptions();
    }
    
    // Special handling for add-components tab
    if (targetTab === 'add-components') {
      this.syncSpigotTypeDisplay();
      this.updateQuoteBreakdown();
      this.updateComponentInfo();
      this.updatePostPreview();
      this.updateAddComponentsPreview();
      
      // Initialize 3D renderer only when this tab becomes active
      if (!this.fenceRenderer3D) {
        
        this.init3DRenderer();
      } else {

        // Always recalculate and update 3D preview with current section data
        this.update3DPreview();
      }
    }

    this._hideCheckoutUnlessReview();

    // Scroll to the top on every screen change — Next, Back, or a direct tab click
    // all route through switchTab (Pete 19/07). Instant + deferred: the smooth
    // variant silently no-ops on these pages, so a plain jump is used, repeated
    // after layout settles.
    try { window.scrollTo(0, 0); } catch (e) {}
    setTimeout(() => { try { window.scrollTo(0, 0); } catch (e) {} }, 80);
  }

  // Handle sections count change
  // Show the "Are all sections the same?" toggle only when there is more than one
  // section — with a single section there is nothing to compare, so the question is
  // noise. Mirrors the frameless glass calculator, which already behaved this way.
  // Pete, 29 Jul 2026.
  applySectionsCountUi(count) {
    const n = parseInt(count) || 0;
    const multi = n > 1;
    const modeGroup = document.getElementById('add-mode-group');
    if (modeGroup) modeGroup.style.display = multi ? 'block' : 'none';
    if (!multi) {
      // Single section (or none): collapse back to shared/same mode so the shared
      // finishes panel configures that lone section.
      this.state.sameForAll = true;
      const sameRadio = document.getElementById('add-mode-same');
      if (sameRadio) sameRadio.checked = true;
      const panel = document.getElementById('shared-options-panel');
      if (panel) panel.style.display = 'block';
      const note = document.getElementById('add-individual-note');
      if (note) note.style.display = 'none';
      if (typeof this.applySectionModeVisibility === 'function') this.applySectionModeVisibility();
    }
  }

  handleSectionsChange(count) {
    this.applySectionsCountUi(count);
    const sectionsNextBtn = this.domCache.get('sections-next-btn');
    
    if (!count) {
      if (sectionsNextBtn) {
        sectionsNextBtn.disabled = true;
      }
      return;
    }
    
    this.state.sectionsCount = count;
    this.state.sections = {};
    
    // Initialize sections
    const sh = this.state.shared || {};
    const sameAll = !!this.state.sameForAll;
    for (let i = 1; i <= count; i++) {
      // Seed the panel sheet width from the section's own height (Blade panel width
      // is fully determined by height: 1200→2400, 1500→1825, 1800→1632). Hardcoding
      // '2400' left 1500/1800 sections with a stale sheet until a height-change event
      // fired, undercounting posts in the configure preview.
      const _seedHeight = sameAll ? (sh.fenceHeight || '1200') : '1200';
      this.state.sections[i] = {
        length: '',
        // In "all sections the same" mode, seed shared height/mount/colour; else defaults.
        fenceHeight: _seedHeight,
        color: sameAll ? (sh.color || 'satin-black') : 'satin-black',
        panelLength: this.getPanelLengthForHeight(_seedHeight),
        gapSpacing: String(CONFIG.DEFAULTS.GAP_SPACING),
        hasRaked: false,
        hasGate: false,
        gateHingeType: 'glass-to-glass',
        gateSide: 'right',
        gatePosition: 0,
        gateLength: String(this.getDefaultGateLength()),
        hingeBase: false,
        groundPost: sameAll ? !!sh.groundPost : false,
        panelMode: 'cut'  // 'cut' = remainder panel, 'even' = evenly distributed panels
      };
    }
    
    this.generateSectionTabs();
    this.generateSectionForms();
    // Refresh add-ons filter
    this.populateAddOns();
    this.hideError('sections-error');
    
    // Enable the next button
    if (sectionsNextBtn) {
      sectionsNextBtn.disabled = false;
      sectionsNextBtn.removeEventListener('click', this.handleFirstTabNext);
      sectionsNextBtn.addEventListener('click', this.handleFirstTabNext.bind(this));
    }
  }

  // Get default panel length from product variants
  getDefaultPanelLength(fenceHeight) {
    // Returns the correct panel sheet width for the given fence height.
    // 1200mm (default) → 2400mm panel
    // 1500mm           → 1825mm panel
    // 1800mm           → 1632mm panel
    return this.getPanelLengthForHeight(fenceHeight || '1200');
  }

  getPanelLengthForHeight(fenceHeight) {
    const h = parseInt(fenceHeight) || 1200;
    if (h === 1500) return '1825';
    if (h === 1800) return '1632';
    return '2400';
  }

  // Get default gate length from product variants
  getDefaultGateLength(hingeType = 'soft-close') {
    // Blade gate leaf is fixed at 920mm.
    return 920;
  }

  // Get raked panel width from product properties, height-aware.
  // Height → rake width mapping:
  //   1500mm fence → 1825mm rake panel
  //   1800mm fence → 1632mm rake panel
  //   1200mm fence → 1200mm rake panel (default)
  getRakedPanelWidth() {
    // Blade RAKE panels are 1252mm wide at EVERY height (Pete, 4 Aug 2026, and the
    // James Glen Sept-2025 list: 1252x1300 .. 1252x1800, Left High and Right High).
    // This used to return 1825 for 1500h and 1632 for 1800h - but those are the
    // widths of the REGULAR 1500h / 1800h blade panels, not rake widths, so every
    // raked 1500/1800 job was drawn and ordered at the wrong size.
    // Height no longer affects rake WIDTH, only which rake SKU is picked.
    return CONFIG.DEFAULTS.RAKED_PANEL_WIDTH;   // 1252
  }

  // Find optimal sheet size when gate is added: smallest sheet that leaves ≥500mm filler
  // Iterative calculation: estimate panels, calc posts, refine panels, return filler
  findOptimalSheetForGate(sectionNum) {
    const section = this.state.sections[sectionNum];
    if (!section) return '1000';

    const sectionLength = parseInt(section.length || 0);
    const postSize      = parseInt(section.gapSpacing || 50);
    // Use actual configured gate size — not hardcoded 1010
    const gateSize      = parseInt(section.gateLength) || this.getDefaultGateLength();
    // Rake panels are not offered on 1500mm / 1800mm heights (custom-manufacture only) —
    // never lay one out regardless of stale state (Pete 19/07).
    const _rakeHeightOK = !(String(section.fenceHeight) === '1500' || String(section.fenceHeight) === '1800');
    const hasRake       = !!section.hasRaked && _rakeHeightOK;
    const rakeSize      = hasRake ? this.getRakedPanelWidth(section.fenceHeight) : 0;
    const MIN_FILLER    = 100;
    const _defSheet = parseInt(this.getPanelLengthForHeight(section?.fenceHeight || '1200'));
    const STANDARD_SHEETS = [_defSheet];

    for (const sheetSize of STANDARD_SHEETS) {
      // Iterative: estimate panels, then refine
      let panelCount = 0;
      let filler = 0;

      // Loop: add panels one at a time, accounting for posts
      // Use PanelCalculator.estimatePosts for canonical post count — consistent with solveFenceLayout
      while (true) {
        const testPanels   = panelCount + 1;
        const postCount    = PanelCalculator.estimatePosts(testPanels, true, hasRake);
        const usedByPosts  = postCount * postSize;
        const usedByPanels = testPanels * sheetSize;
        const totalUsed    = gateSize + rakeSize + usedByPosts + usedByPanels;

        if (totalUsed > sectionLength) break;
        panelCount++;
      }

      // Calculate final filler using same canonical post count
      const postCount    = PanelCalculator.estimatePosts(panelCount, true, hasRake);
      const usedByPosts  = postCount * postSize;
      const usedByPanels = panelCount * sheetSize;
      filler = sectionLength - gateSize - rakeSize - usedByPosts - usedByPanels;

      // Accept this sheet size if filler >= 500mm or clean fit
      if (filler >= MIN_FILLER || filler === 0) {
        return String(sheetSize);
      }
    }

    // Fallback
    return this.getPanelLengthForHeight(section?.fenceHeight || '1200');
  }

  // Calculate proportional rake height based on panel width
  // Formula: Rake Height = Panel Width × 1.25
  // Example: 1200mm panel → 1500mm rake, 1800mm panel → 2250mm rake, 625mm panel → 781mm rake
  getProportionalRakeHeight(panelWidth) {
    const width = panelWidth || this.getRakedPanelWidth();
    return Math.round(width * 1.25);
  }

  // Handle first tab next button click
  handleFirstTabNext() {
    if (this.validateAddSections()) {
      this.switchTab('configure-sections');
    }
  }

  // Generate section tabs
  generateSectionTabs() {
    const container = this.domCache.get('section-tabs-container');
    if (!container) return;
    
    container.innerHTML = '';
    
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display: flex; align-items: center; gap: 15px; margin-bottom: 18px;';
    
    const tabsContainer = document.createElement('div');
    tabsContainer.style.cssText = 'display: flex; gap: 8px; flex-wrap: wrap;';
    
    for (let i = 1; i <= this.state.sectionsCount; i++) {
      const tab = document.createElement('button');
      tab.className = `section-tab ${i === 1 ? 'active' : ''}`;
      tab.textContent = `Section ${i}`;
      tab.addEventListener('click', () => this.showSection(i));
      
      const section = this.state.sections[i];
      if (section && section.length) {
        tab.style.background = '#059669';
        tab.style.color = 'white';
        tab.style.borderColor = '#059669';
      }

      tabsContainer.appendChild(tab);
    }
    
    const sectionNextBtn = document.createElement('button');
    sectionNextBtn.className = 'btn btn-primary';
    sectionNextBtn.textContent = 'Next →';
    sectionNextBtn.style.cssText = 'padding: 8px 16px; font-size: 1.2rem;';
    sectionNextBtn.addEventListener('click', () => this.handleSectionNextClick());
    
    wrapper.appendChild(tabsContainer);
    wrapper.appendChild(sectionNextBtn);
    container.appendChild(wrapper);
  }

  // Handle section next button click
  handleSectionNextClick() {
    const currentSection = this.state.currentSection;
    
    if (!this.validateSection(currentSection)) {
      this.showSectionValidationError(currentSection);
      return;
    }
    
    // Always save current panel data before navigation
    console.log(`Next button clicked: saving panel data for section ${currentSection}`);
    this.updatePanelData();
    
    if (currentSection < this.state.sectionsCount) {
      const nextSection = currentSection + 1;
      this.showSection(nextSection);
      // Scroll to top when advancing to the next section (Pete 19/07). Instant +
      // a deferred repeat — smooth scroll doesn't take on these pages and the section
      // re-render can interrupt an in-flight smooth animation.
      try { window.scrollTo(0, 0); } catch (e) {}
      setTimeout(() => { try { window.scrollTo(0, 0); } catch (e) {} }, 80);
    } else {
      const incompleteSection = this.findFirstIncompleteSection();
      if (incompleteSection) {
        this.showSection(incompleteSection);
        this.showSectionValidationError(incompleteSection);
      } else {
        this.switchTab('review-checkout');
      }
    }
  }

  // Validate if a section has required data
  validateSection(sectionNum) {
    const section = this.state.sections[sectionNum];
    
    // Check state first
    let hasValidLength = section && section.length && parseInt(section.length) >= CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH;
    
    // If not in state, check DOM as fallback
    if (!hasValidLength) {
      const lengthInput = document.getElementById(`section-${sectionNum}-length`);
      if (lengthInput && lengthInput.value) {
        const inputValue = parseInt(lengthInput.value);
        hasValidLength = inputValue >= CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH;
        
        // If DOM has valid value but state doesn't, update state
        if (hasValidLength) {
          this.updateSectionData(sectionNum, 'length', lengthInput.value);
        }
      }
    }

    // Boss 22 Jul: also reject a section whose computed layout can't physically fit — e.g. a
    // gate/rake reduced below the length it needs, or a too-short cut-mode run. The preview warns,
    // but validateSection must block it too so an unbuildable section can't reach Review &
    // Checkout with a quote. Recompute when the cached layout is absent or stale (its length no
    // longer matches), then reject if no valid layout results. An impossible layout surfaces as
    // null (blade), valid:false, or total:0 depending on the path; a valid one has total > 0.
    if (hasValidLength) {
      const curLen = parseInt(section && section.length) || 0;
      let calc = this.state.optimizedConfigs[sectionNum];
      if (calc === undefined || (calc && calc.originalSectionLength && calc.originalSectionLength !== curLen)) {
        try { this.updateCalculationResults(sectionNum); } catch (e) {}
        calc = this.state.optimizedConfigs[sectionNum];
      }
      // Block ONLY when there is no physical layout at all: null config (blade nulls unsolvable
      // sections) or total === 0 (nothing fits). Do NOT block on valid:false alone — a large
      // buildable section can be flagged valid:false purely for missing the ≥500mm-remainder
      // preference while still summing to its length; blocking those would reject real orders.
      if (!calc || calc.total === 0) return false;
    }

    return hasValidLength;
  }

  // Show validation error for incomplete section
  showSectionValidationError(sectionNum) {
    const section = this.state.sections[sectionNum];
    let message = `Section ${sectionNum} is incomplete. `;
    
    if (!section || !section.length) {
      message += 'Please enter the section length.';
    } else if (parseInt(section.length) < CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH) {
      message += `Section length must be at least ${CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH}mm.`;
    }
    
    this.showValidationPopup(message, () => {
      const lengthInput = this.domCache.get(`section-${sectionNum}-length`);
      if (lengthInput) {
        lengthInput.focus();
        lengthInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  // Generate section forms
  generateSectionForms() {
    
    const sectionContainer = this.domCache.get('section-forms-container');
    const gateContainer = this.domCache.get('gate-forms-container');
    const rakeContainer = document.getElementById('rake-forms-container');
    
    
    if (sectionContainer) {
      sectionContainer.innerHTML = '';
      
      for (let i = 1; i <= this.state.sectionsCount; i++) {
        const form = this.createSectionForm(i);
        form.style.display = i === 1 ? 'block' : 'none';
        sectionContainer.appendChild(form);
      }
    } else {
    }
    
    if (gateContainer) {
      gateContainer.innerHTML = '';
      
      for (let i = 1; i <= this.state.sectionsCount; i++) {
        const gateForm = this.createGateConfigForm(i);
        gateForm.style.display = i === 1 ? 'block' : 'none';
        gateContainer.appendChild(gateForm);
      }
    } else {
      // Try to find it with direct DOM query as fallback
      const fallbackGateContainer = document.getElementById('gate-forms-container');
      
      if (fallbackGateContainer) {
        fallbackGateContainer.innerHTML = '';
        
        for (let i = 1; i <= this.state.sectionsCount; i++) {
          try {
            const gateForm = this.createGateConfigForm(i);
            gateForm.style.display = i === 1 ? 'block' : 'none';
            fallbackGateContainer.appendChild(gateForm);
          } catch (error) {
          }
        }
        
        // Clear and reset DOMCache for gate container
        this.domCache.clear('gate-forms-container');
      } else {
      }
    }
    
    // Populate rake-forms-container (Rake Configuration column)
    if (rakeContainer) {
      rakeContainer.innerHTML = '';
      for (let i = 1; i <= this.state.sectionsCount; i++) {
        const rakeForm = this.createRakeConfigForm(i);
        rakeForm.style.display = i === 1 ? 'block' : 'none';
        rakeContainer.appendChild(rakeForm);
      }
    }

    this.state.currentSection = 1;

    // Per-section redesign: newly built forms must respect the current mode
    // (hide colour/height/mount inside sections when "all sections the same").
    this.applySectionModeVisibility();
  }

  // Generic options generator
  generateOptions(items, config = {}) {
    const { 
      valueKey = 'value', 
      labelKey = 'label', 
      defaultValue = null, 
      transformer = null,
      placeholder = null 
    } = config;
    
    let options = placeholder ? `<option value="" style="font-size:16px;padding:10px;">${placeholder}</option>` : '';
    
    items.forEach(item => {
      const value = typeof item === 'object' ? item[valueKey] : item;
      const label = typeof item === 'object' 
        ? (transformer ? transformer(item[labelKey]) : item[labelKey])
        : item;
      const selected = value == defaultValue ? 'selected' : '';
      options += `<option value="${value}" ${selected} style="font-size:16px;padding:10px;">${label}</option>`;
    });
    
    return options;
  }

  // Generate panel length options — default is 2400mm
  generatePanelOptions() {
    const options = [
      { value: 2400, label: '2400mm' }
    ];
    return this.generateOptions(options, { defaultValue: 2400 });
  }

  // Generate gap spacing options — post size is fixed at 50mm
  generateGapOptions() {
    const options = [
      { value: 50, label: '50mm' }
    ];
    return this.generateOptions(options, { defaultValue: 50 });
  }

  // Generate retained height options
  generateRetainedHeightOptions() {
    const options = [
      { value: 1300, label: '1300mm' },
      { value: 1400, label: '1400mm' },
      { value: 1500, label: '1500mm' },
      { value: 1600, label: '1600mm' },
      { value: 1700, label: '1700mm' },
      { value: 1800, label: '1800mm' },
    ];
    return this.generateOptions(options, { placeholder: 'Select height...' });
  }

  // Generate gate length options
  generateGateLengthOptions(hingeType = 'soft-close') {
    // 2026-08-30: blade sells ONE gate - 920W (SKUs 50357/50358/50359, also 1500H and 1800H).
    // 800/850/900 were listed here but have never been buyable.
    const GATE_OPTIONS = [
      { value: 920,  label: '920mm'  }
    ];
    return this.generateOptions(GATE_OPTIONS, { defaultValue: 920 });
  }

  // Generate hinge type options. Honours the section's saved choice so a rebuilt
  // form pre-selects the customer's hinge rather than resetting to the default.
  generateHingeTypeOptions(selectedSku = 'SHG-90L', fenceHeight = null) {
    // Check bladeHinges first (loaded from blade collection — has real variant IDs)
    const hinges = this.shopifyData.products?.bladeHinges
                || this.shopifyData.collections?.perfmeshProducts?.hinges;

    // Only show Adjustable Tension (SHG-90L) and Soft Close Premium (PHS)
    const ALLOWED_SKUS = ['SHG-90L', 'PHS'];

    // Heavy-duty hinges — offered on the 1500 and 1800 gates only (the taller leaf
    // needs the heavier hinge). Gated on the product actually resolving in the live
    // catalogue: CF_VARIANT returns null while TCHD1AL2S3BT is a draft or out of the
    // online-store channel, so the option cannot appear before it is orderable, and
    // appears by itself the moment it is activated. No republish needed.
    const HD_SKU    = 'TCHD1AL2S3BT';
    const HD_OPTION = { value: HD_SKU, label: 'TruClose Heavy Duty (Black)' };
    const hdEligible = Number(fenceHeight) >= 1500
                    && !!(window.CF_VARIANT && window.CF_VARIANT(HD_SKU));

    if (hinges && hinges.length > 0) {
      const filtered = hinges.filter(h => ALLOWED_SKUS.includes(h.sku));
      const options = (filtered.length > 0 ? filtered : hinges).map(h => ({ value: h.sku, label: h.title }));
      if (hdEligible && !options.some(o => o.value === HD_SKU)) options.push(HD_OPTION);
      return this.generateOptions(options, { defaultValue: selectedSku });
    }

    // Fallback if no live data
    const fallbackOptions = [
      { value: 'SHG-90L', label: 'Adjustable Tension' },
      { value: 'PHS',     label: 'Soft Close Premium' }
    ];
    if (hdEligible) fallbackOptions.push(HD_OPTION);
    return this.generateOptions(fallbackOptions, { defaultValue: selectedSku });
  }

  // Generate hinge color options based on selected hinge type
  generateHingeColorOptions(hingeType = 'soft-close') {
    
    
    // Get the specific hinge product based on type
    const hingeProduct = hingeType === 'soft-close' 
      ? this.shopifyData.products?.hingeSoftClose
      : this.shopifyData.products?.hingeStandard;
    
    
    
    let colors = new Set();
    
    // Collect colors from the selected hinge product variants
    // NOTE: In Shopify data, color is stored in 'position' field
    if (hingeProduct && hingeProduct.variants) {
      
      hingeProduct.variants
        .filter(v => v.available && v.position) // Using position for color
        .forEach(v => {
          
          colors.add(v.position);
        });
    } else {
      
    }
    
    // Convert to array and sort
    const colorArray = Array.from(colors).sort();
    
    
    if (colorArray.length === 0) {
      
      // Fallback colors if no product data
      return this.generateOptions([
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' }
      ], { defaultValue: 'black' });
    }
    
    const colorOptions = colorArray.map(color => ({
      value: color.toLowerCase(),
      label: color.charAt(0).toUpperCase() + color.slice(1)
    }));
    
    return this.generateOptions(colorOptions, {
      defaultValue: 'black'
    });
  }

  // Generate latch type options
  // Generate latch type options — 3 Magna Latch products only
  generateLatchTypeOptions(selectedSku = 'SL-50K') {
    // Check bladeLatches first (loaded from blade collection — has real variant IDs)
    const latches = this.shopifyData.products?.bladeLatches
                 || this.shopifyData.collections?.perfmeshProducts?.latches;
    if (latches && latches.length > 0) {
      return this.generateOptions(latches.map(l => ({ value: l.sku, label: l.title })), { defaultValue: selectedSku });
    }
    return this.generateOptions([
      { value: 'SL-50K', label: 'Top Pull MagnaLatch Keyless'  },
      { value: 'SL-50H', label: 'Top Pull MagnaLatch Lockable' }
    ], { defaultValue: selectedSku });
  }

  // Generate clamp color options
  generateClampColorOptions() {
    const clampProduct = this.shopifyData.products?.clampStandard;
    if (!clampProduct || !clampProduct.variants) {
      return this.generateOptions([
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' }
      ], { defaultValue: 'black' });
    }

    const colors = [...new Set(clampProduct.variants
      .filter(v => v.available && v.color)
      .map(v => v.color.trim()))].sort();

    if (colors.length === 0) {
      return this.generateOptions([
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' }
      ], { defaultValue: 'black' });
    }

    const options = colors.map(color => ({
      value: color.toLowerCase(),
      label: color.charAt(0).toUpperCase() + color.slice(1)
    }));

    return this.generateOptions(options, { defaultValue: 'black' });
  }

  // Generate clamp position options
  generateClampPositionOptions() {
    const clampProduct = this.shopifyData.products?.clampStandard;
    if (!clampProduct || !clampProduct.variants) {
      return this.generateOptions([
        { value: 'glass-to-corner', label: 'Glass to Corner' },
        { value: 'glass-to-glass', label: 'P2P' },
        { value: 'wall-to-glass', label: 'W2P' }
      ]);
    }

    const positions = [...new Set(clampProduct.variants
      .filter(v => v.available && v.position)
      .map(v => v.position.trim()))].sort();

    if (positions.length === 0) {
      return this.generateOptions([
        { value: 'glass-to-corner', label: 'Glass to Corner' },
        { value: 'glass-to-glass', label: 'P2P' },
        { value: 'wall-to-glass', label: 'W2P' }
      ]);
    }

    const options = positions.map(position => ({
      value: position.toLowerCase(),
      label: position.charAt(0).toUpperCase() + position.slice(1)
    }));

    return this.generateOptions(options);
  }

  // Generate latch color options based on selected latch type
  generateLatchColorOptions(latchType = null) {
    
    
    const gateLatch = this.shopifyData.products?.gateLatch;
    
    
    if (!gateLatch || !gateLatch.variants) {
      
      return this.generateOptions([
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' }
      ], { defaultValue: 'black' });
    }

    
    
    // Filter variants by latch type if specified
    let relevantVariants = gateLatch.variants.filter(v => v.available);
    
    
    if (latchType) {
      relevantVariants = relevantVariants.filter(v => {
        const matches = v.position === latchType; // Type is in position field
        if (matches) {
          
        }
        return matches;
      });
      
    }
    
    // Get unique colors from relevant variants
    const colors = [...new Set(relevantVariants
      .filter(v => v.color)
      .map(v => v.color))].sort();
    
    
    
    if (colors.length === 0) {
      
      // Fallback colors
      return this.generateOptions([
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' }
      ], { defaultValue: 'black' });
    }
    
    const options = colors.map(color => ({
      value: color.toLowerCase(),
      label: color.charAt(0).toUpperCase() + color.slice(1)
    }));

    return this.generateOptions(options, { defaultValue: 'black' });
  }

  // Create individual section form
  createSectionForm(sectionNum) {
    const form = document.createElement('div');
    form.id = `section-${sectionNum}-form`;
    form.className = 'section-form';
    
    // Section configuration HTML (without gate toggle)
    const sectionConfigHTML = `
      <div class="form-group">
        ${this.createLabelWithTooltip('Section Length (mm)', 'Enter the full length of this section end to end.')}
        <input type="text"
               class="form-control"
               id="section-${sectionNum}-length"
               placeholder="Enter length in mm"
               inputmode="numeric"
               autocomplete="off"
               data-info="sectionLength">
      </div>

      <div class="form-group cf-persec-shared">
        ${this.createLabelWithTooltip('Section Height (mm)', 'Select the fence panel height. 1200mm is standard (2400mm wide panels). 1500mm uses 1825mm wide panels. 1800mm uses 1632mm wide panels.')}
        <select class="form-control"
                id="section-${sectionNum}-fence-height"
                data-info="sectionHeight">
          <option value="1200" selected>1200mm (Pool Compliant)</option>
          <option value="1500">1500mm</option>
          <option value="1800">1800mm</option>
        </select>
      </div>

      <div class="form-group cf-persec-shared">
        <label style="font-size:14px; font-weight:600; color:#374151; display:block; margin-bottom:8px;">Fence Colour</label>
        <div class="cf-section-color" data-section="${sectionNum}" style="display:flex; gap:10px; align-items:flex-start;">
          <label style="flex:1; cursor:pointer; text-align:center;">
            <input type="radio" name="section-${sectionNum}-color" value="satin-black" checked style="display:none;">
            <span class="cf-dot" style="display:block; width:100%; border-radius:9px; overflow:hidden; border:3px solid #0e9ed5; box-shadow:0 0 0 1px #0e9ed5;">
              <img src="${(window.CF_SWATCH_IMAGES&&window.CF_SWATCH_IMAGES['satin-black'])||'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeBlk.png?v=1783627985'}" alt="Satin Black" style="width:100%; height:auto; display:block;">
            </span>
            <span style="display:block; font-size:11.5px; color:#374151; margin-top:5px; font-weight:600;">Satin Black</span>
          </label>
          <label style="flex:1; cursor:pointer; text-align:center;">
            <input type="radio" name="section-${sectionNum}-color" value="white" style="display:none;">
            <span class="cf-dot" style="display:block; width:100%; border-radius:9px; overflow:hidden; border:3px solid transparent; box-shadow:0 0 0 1px #cbd5e1;">
              <img src="${(window.CF_SWATCH_IMAGES&&window.CF_SWATCH_IMAGES['white'])||'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeWht.png?v=1783627986'}" alt="Satin White" style="width:100%; height:auto; display:block;">
            </span>
            <span style="display:block; font-size:11.5px; color:#374151; margin-top:5px; font-weight:600;">Satin White</span>
          </label>
          <label style="flex:1; cursor:pointer; text-align:center;">
            <input type="radio" name="section-${sectionNum}-color" value="monument" style="display:none;">
            <span class="cf-dot" style="display:block; width:100%; border-radius:9px; overflow:hidden; border:3px solid transparent; box-shadow:0 0 0 1px #cbd5e1;">
              <img src="${(window.CF_SWATCH_IMAGES&&window.CF_SWATCH_IMAGES['monument'])||'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeGry.png?v=1783627986'}" alt="Monument" style="width:100%; height:auto; display:block;">
            </span>
            <span style="display:block; font-size:11.5px; color:#374151; margin-top:5px; font-weight:600;">Monument</span>
          </label>
        </div>
      </div>

      <div class="form-group">
        <label style="font-size: 14px; font-weight: 600; color: #374151; display: block; margin-bottom: 8px;">Panel Layout Mode</label>
        <div style="display: flex; gap: 0; border: 2px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
          <label id="section-${sectionNum}-panel-mode-cut-label" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 8px; cursor: pointer; background: #0e9ed5; color: white; font-weight: 600; font-size: 13px; transition: all 0.2s; user-select: none;">
            <input type="radio" name="section-${sectionNum}-panel-mode" id="section-${sectionNum}-panel-mode-cut" value="cut" checked style="display: none;">
            ✂️ Cut Panel
          </label>
          <label id="section-${sectionNum}-panel-mode-even-label" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 8px; cursor: pointer; background: white; color: #64748b; font-weight: 600; font-size: 13px; transition: all 0.2s; user-select: none;">
            <input type="radio" name="section-${sectionNum}-panel-mode" id="section-${sectionNum}-panel-mode-even" value="even" style="display: none;">
            ⚖️ Even Panels
          </label>
        </div>
      </div>




      <div class="form-group" style="display:none;">
        ${this.createLabelWithTooltip('Fence Colour', 'Select the colour for your blade fence panels and posts. This determines which product is priced.')}
        <select class="form-control" id="glass-type">
          <option value="toughened">Toughened Frameless Glass</option>

        </select>
      </div>

      <div class="form-group cf-persec-shared">
        ${this.createLabelWithTooltip('Installation Method', 'Here you can choose your installation type. Base Mount is the easiest DIY method and is the most common method for installing into decking. In Ground Posts are used for core drilling holes into concrete and securing them with grout, or straight into sand or grass using rapid set.')}
        <div style="display: flex; gap: 0; border: 2px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
          <label id="section-${sectionNum}-ground-post-base-label" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 8px; cursor: pointer; background: #0e9ed5; color: white; font-weight: 600; font-size: 13px; transition: all 0.2s; user-select: none;">
            <input type="radio" name="section-${sectionNum}-ground-post-type" id="section-${sectionNum}-ground-post-base" value="base" checked style="display: none;">
            🔩 Base Mount
          </label>
          <label id="section-${sectionNum}-ground-post-inground-label" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 8px; cursor: pointer; background: white; color: #64748b; font-weight: 600; font-size: 13px; transition: all 0.2s; user-select: none;">
            <input type="radio" name="section-${sectionNum}-ground-post-type" id="section-${sectionNum}-ground-post-inground" value="inground" style="display: none;">
            ⛏️ In Ground Post
          </label>
        </div>
      </div>

      <div class="form-group">
        ${this.createLabelWithTooltip('Side End Post Setup', 'Choose the amount of posts required on the ends and corners. If this section carries on from another section around a corner, you might want to remove a post. You can also attach to walls or patio posts without needing a 50x50 post.')}
        <select class="form-control" id="section-${sectionNum}-end-post-setup">
          <option value="both">Post Both Sides</option>
          <option value="no-left">No Post Left</option>
          <option value="no-right">No Post Right</option>
          <option value="no-both">No Post Both Ends</option>
        </select>
      </div>

    `;
    
    form.innerHTML = sectionConfigHTML;
    
    this.setupSectionFormListeners(form, sectionNum);
    
    // Setup tooltips for this form
    setTimeout(() => {
      if (window.setupTooltips) window.setupTooltips();
    }, 10);
    
    return form;
  }
  
  // Helper function to create label with tooltip
  createLabelWithTooltip(text, tooltipText) {
    return `
      <label style="display: flex; align-items: center; gap: 8px;">
        <span>${text}</span>
        <div class="tooltip-container" style="position: relative; display: inline-block;">
          <span class="tooltip-icon" style="
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            height: 16px;
            background: #000;
            color: white;
            border-radius: 50%;
            font-size: 11px;
            font-weight: bold;
            cursor: help;
            user-select: none;
          ">i</span>
          <div class="tooltip-text" style="
            visibility: hidden;
            opacity: 0;
            position: absolute;
            bottom: 125%;
            left: 50%;
            transform: translateX(-50%);
            background: #1f2937;
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 13px;
            line-height: 1.4;
            white-space: normal;
            width: 280px;
            z-index: 1000;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            transition: opacity 0.3s, visibility 0.3s;
          ">${tooltipText}
            <div style="
              position: absolute;
              top: 100%;
              left: 50%;
              transform: translateX(-50%);
              border: 5px solid transparent;
              border-top-color: #1f2937;
            "></div>
          </div>
        </div>
      </label>
    `;
  }

  // Create gate configuration form
  createGateConfigForm(sectionNum) {
    const gateForm = document.createElement('div');
    gateForm.id = `section-${sectionNum}-gate-form`;
    gateForm.className = 'gate-form';
    const hingeMountType = this.state.sections[sectionNum]?.gateHingeType || 'glass-to-glass';
    
    const gateConfigHTML = `
      <div class="form-group" style="display:none;">
        <label class="toggle-label" style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 4px;">
            <span>Clamp Required?</span>
            <div class="tooltip-container" style="position: relative; display: inline-block;">
              <span class="tooltip-icon" style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 16px;
                height: 16px;
                background: #000;
                color: white;
                border-radius: 50%;
                font-size: 11px;
                font-weight: bold;
                cursor: help;
                user-select: none;
              ">i</span>
              <div class="tooltip-text" style="
                visibility: hidden;
                opacity: 0;
                position: absolute;
                bottom: 125%;
                left: 50%;
                transform: translateX(-50%);
                background: #000;
                color: white;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 13px;
                line-height: 1.4;
                white-space: normal;
                width: 280px;
                z-index: 1000;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                transition: opacity 0.3s, visibility 0.3s;
              ">Clamps secure fence panels to walls or posts. Select this if your fence section connects to an existing structure.
                <div style="
                  position: absolute;
                  top: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  border: 5px solid transparent;
                  border-top-color: #000;
                "></div>
              </div>
            </div>
          </div>
          <div class="toggle-switch">
            <input type="checkbox" id="section-${sectionNum}-clamp">
            <span class="slider"></span>
          </div>
        </label>
      </div>

      <div id="section-${sectionNum}-clamp-fields" class="conditional-fields-no hidden">
        <div class="form-group">
          ${this.createLabelWithTooltip('Clamp Colour', 'Select the finish color for your clamp hardware to match your aesthetic.')}
          <select class="form-control" id="section-${sectionNum}-clamp-position">
            ${this.generateClampPositionOptions()}
          </select>
        </div>

        <div class="form-group">
          ${this.createLabelWithTooltip('Clamp Position', 'Choose where the clamp will be positioned on the panel - top, middle, or bottom.')}
          <select class="form-control" id="section-${sectionNum}-clamp-color">
            ${this.generateClampColorOptions()}
          </select>
        </div>
      </div>

      <div class="form-group">
        <label class="toggle-label" style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 4px;">
            <span>Gate Required?</span>
            <div class="tooltip-container" style="position: relative; display: inline-block;">
              <span class="tooltip-icon" style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 16px;
                height: 16px;
                background: #000;
                color: white;
                border-radius: 50%;
                font-size: 11px;
                font-weight: bold;
                cursor: help;
                user-select: none;
              ">i</span>
              <div class="tooltip-text" style="
                visibility: hidden;
                opacity: 0;
                position: absolute;
                bottom: 125%;
                left: 50%;
                transform: translateX(-50%);
                background: #000;
                color: white;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 13px;
                line-height: 1.4;
                white-space: normal;
                width: 280px;
                z-index: 1000;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                transition: opacity 0.3s, visibility 0.3s;
              ">Ever wondered the best way to get into your pool area? Well here it is! Remember, council require all pool gates to open away from the pool.
                <div style="
                  position: absolute;
                  top: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  border: 5px solid transparent;
                  border-top-color: #0e9ed5;
                "></div>
              </div>
            </div>
          </div>
          <div class="toggle-switch">
            <input type="checkbox" id="section-${sectionNum}-gate">
            <span class="slider"></span>
          </div>
        </label>
      </div>

      <!-- Gate orientation controls — only visible when Gate Required is ON -->
      <div id="section-${sectionNum}-gate-orientation-row" class="conditional-fields-no hidden" style="margin-bottom: 16px;">

        <!-- 1. Gate Hinge Side (LHS/RHS Hinge) -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label style="font-size: 14px; font-weight: 600; color: #374151; display: block; margin-bottom: 8px;">Gate Hinge Side</label>
          <div style="display: flex; gap: 24px; align-items: center;">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 14px; color: #374151;">
              <input type="radio" name="section-${sectionNum}-gate-side" id="section-${sectionNum}-gate-side-left" value="left"
                style="width: 16px; height: 16px; accent-color: #0e9ed5; cursor: pointer;">
              LHS Hinge
            </label>
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 14px; color: #374151;">
              <input type="radio" name="section-${sectionNum}-gate-side" id="section-${sectionNum}-gate-side-right" value="right" checked
                style="width: 16px; height: 16px; accent-color: #0e9ed5; cursor: pointer;">
              RHS Hinge
            </label>
          </div>
        </div>

        <!-- 2. Gate Placing buttons -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label style="font-size: 14px; font-weight: 600; color: #374151; display: block; margin-bottom: 8px;">Gate Placing</label>
          <div style="display: flex; gap: 8px; align-items: center;">
            <button type="button" id="section-${sectionNum}-gate-move-left" style="flex: 1; padding: 8px 12px; background: #0e9ed5; color: white; border: none; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer;">&#8592; Move</button>
            <button type="button" id="section-${sectionNum}-gate-move-middle" style="flex: 1; padding: 8px 12px; background: #0e9ed5; color: white; border: none; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer;">Middle</button>
            <button type="button" id="section-${sectionNum}-gate-move-right" style="flex: 1; padding: 8px 12px; background: #0e9ed5; color: white; border: none; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer;">Move &#8594;</button>
          </div>
        </div>

        <!-- 2b. Exact gate position — distance of the gate's closest edge from one side -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label style="font-size: 14px; font-weight: 600; color: #374151; display: block; margin-bottom: 8px;">Exact Gate Position (mm)</label>
          <div style="display: flex; gap: 8px; align-items: center;">
            <input type="number" inputmode="numeric" min="0" step="10" id="section-${sectionNum}-gate-exact" placeholder="e.g. 2000"
              style="flex: 1; padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px;">
            <button type="button" id="section-${sectionNum}-gate-exact-side" data-side="left"
              style="flex-shrink: 0; padding: 8px 12px; background: #0e9ed5; color: white; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; min-width: 64px;">From LHS</button>
            <button type="button" id="section-${sectionNum}-gate-exact-apply"
              style="flex-shrink: 0; padding: 8px 14px; background: #1a6b23; color: white; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;">Set</button>
          </div>
          <div style="font-size: 12px; color: #64748b; margin-top: 4px;">Distance from the chosen side to the gate's closest edge. Rake stays at the ends.</div>
        </div>

        <!-- 3. Hinge Mount — toggle switch; W2P radio only available when gate is at an end -->
        <!-- UI hidden: W2P toggle kept in DOM for logic compatibility, not shown to user -->
        <div class="form-group" style="margin-bottom: 12px; display: none;">
          <label class="toggle-label" style="display: flex; align-items: center; justify-content: space-between;">
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-size: 14px; font-weight: 600; color: #374151;">Wall to Fence</span>
              <div class="tooltip-container" style="position: relative; display: inline-block;">
                <span class="tooltip-icon" style="
                  display: inline-flex; align-items: center; justify-content: center;
                  width: 16px; height: 16px; background: #000; color: white;
                  border-radius: 50%; font-size: 11px; font-weight: bold;
                  cursor: help; user-select: none;">i</span>
                <div class="tooltip-text" style="
                  visibility: hidden; opacity: 0; position: absolute;
                  bottom: 125%; left: 50%; transform: translateX(-50%);
                  background: #000; color: white; padding: 8px 12px;
                  border-radius: 6px; font-size: 13px; line-height: 1.4;
                  white-space: normal; width: 280px; z-index: 1000;
                  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                  transition: opacity 0.3s, visibility 0.3s;">
                  Off = Fence to Fence (default). Turn on to hinge or latch the gate directly to a wall — only available when the gate is at the start or end of the panel run.
                  <div style="position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
                    border: 5px solid transparent; border-top-color: #000;"></div>
                </div>
              </div>
            </div>
            <div class="toggle-switch">
              <input type="checkbox" id="section-${sectionNum}-hinge-mount-toggle"
                ${hingeMountType === 'wall-to-glass' ? 'checked' : ''}>
              <span class="slider"></span>
            </div>
          </label>
          <!-- Hidden radio inputs — kept for downstream logic compatibility -->
          <input type="radio" name="section-${sectionNum}-gate-hinge-type" id="section-${sectionNum}-gate-hinge-type-glass" value="glass-to-glass"
            ${hingeMountType === 'glass-to-glass' ? 'checked' : ''} style="display:none;">
          <input type="radio" name="section-${sectionNum}-gate-hinge-type" id="section-${sectionNum}-gate-hinge-type-wall" value="wall-to-glass"
            ${hingeMountType === 'wall-to-glass' ? 'checked' : ''} style="display:none;">
          <div id="section-${sectionNum}-hinge-mount-hint" style="display:none; margin-top:6px; font-size:12px; color:#dc2626;"></div>
        </div>

      </div>

      <!-- Gate detail fields — only visible when Gate Required is ON -->
      <div id="section-${sectionNum}-gate-fields" class="conditional-fields-no hidden">
        <div class="form-group">
          ${this.createLabelWithTooltip('Hinge Type', 'Select the hinge type for your gate. Adjustable Tension hinges are self-closing spring hinges with adjustable closing force. Soft Close Premium hinges close the gate gently and quietly. Both are self-closing for pool safety compliance.')}
          <div style="display: flex; align-items: center; gap: 10px;">
            <select class="form-control" id="section-${sectionNum}-hinge-color" style="flex: 1;">
              ${this.generateHingeTypeOptions(this.state.sections[sectionNum]?.hingeType || 'SHG-90L', this.state.sections[sectionNum]?.fenceHeight)}
            </select>
            <div style="width: 48px; height: 48px; flex-shrink: 0; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; background: #f8fafc; display: flex; align-items: center; justify-content: center;">
              <img id="section-${sectionNum}-hinge-thumb" src="" alt="Hinge" style="width: 100%; height: 100%; object-fit: contain; display: none;">
            </div>
          </div>
        </div>

        <div class="form-group">
          ${this.createLabelWithTooltip('Latch Type', 'Select the latch configuration based on your gate installation. Fence to Fence is for gates between fence panels. Wall to Fence is for gates latching to a wall or post. Corner Gate is used when the gate latches at an angled (non-straight) corner.')}
          <div style="display: flex; align-items: center; gap: 10px;">
            <select class="form-control" id="section-${sectionNum}-latch-type" style="flex: 1;">
              ${this.generateLatchTypeOptions(this.state.sections[sectionNum]?.latchType || 'SL-50K')}
            </select>
            <div style="width: 48px; height: 48px; flex-shrink: 0; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; background: #f8fafc; display: flex; align-items: center; justify-content: center;">
              <img id="section-${sectionNum}-latch-thumb" src="" alt="Latch" style="width: 100%; height: 100%; object-fit: contain; display: none;">
            </div>
          </div>
        </div>
      </div>
    `;
    
    gateForm.innerHTML = gateConfigHTML;
    
    // Setup gate-specific listeners
    this.setupGateFormListeners(gateForm, sectionNum);
    
    // Setup tooltips for this form
    setTimeout(() => {
      if (window.setupTooltips) window.setupTooltips();
    }, 10);
    
    return gateForm;
  }

  // Create rake configuration form (Rake Configuration column)
  createRakeConfigForm(sectionNum) {
    const rakeForm = document.createElement('div');
    rakeForm.id = `section-${sectionNum}-rake-form`;
    rakeForm.className = 'rake-form';

    // Rake panels are NOT offered on 1500mm / 1800mm heights (custom-manufacture only,
    // Pete 19/07). Hide the toggle for those heights and clear any stale rake state.
    const _h = String(this.state.sections[sectionNum]?.fenceHeight || '1200');
    const _rakeRestrictedNow = (_h === '1500' || _h === '1800');
    if (_rakeRestrictedNow && this.state.sections[sectionNum]) {
      this.state.sections[sectionNum].hasRaked = false;
    }

    const rakeConfigHTML = `
      <div class="form-group" id="section-${sectionNum}-rake-toggle-group"${_rakeRestrictedNow ? ' style="display:none;"' : ''}>
        <label class="toggle-label" style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 4px;">
            <span>Raked Panel Required?</span>
            <div class="tooltip-container" style="position: relative; display: inline-block;">
              <span class="tooltip-icon" style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 16px;
                height: 16px;
                background: #000;
                color: white;
                border-radius: 50%;
                font-size: 11px;
                font-weight: bold;
                cursor: help;
                user-select: none;
              ">i</span>
              <div class="tooltip-text" style="
                visibility: hidden;
                opacity: 0;
                position: absolute;
                bottom: 125%;
                left: 50%;
                transform: translateX(-50%);
                background: #000;
                color: white;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 13px;
                line-height: 1.4;
                white-space: normal;
                width: 280px;
                z-index: 1000;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                transition: opacity 0.3s, visibility 0.3s;
              ">Rake panels are higher on one side to account for step ups in your fencing. Remember to measure the height of your step up and enter it into the field below. Raked panels are available on 1200mm high blade fences only.
                <div style="
                  position: absolute;
                  top: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  border: 5px solid transparent;
                  border-top-color: #000;
                "></div>
              </div>
            </div>
          </div>
          <div class="toggle-switch">
            <input type="checkbox" id="section-${sectionNum}-raked">
            <span class="slider"></span>
          </div>
        </label>
      </div>

      <div id="section-${sectionNum}-rake-custom-note" style="display: ${_rakeRestrictedNow ? 'block' : 'none'}; margin-top: 8px; padding: 8px 12px; background: #fff8e1; border-left: 3px solid #f59e0b; border-radius: 4px; font-size: 13px; color: #92400e; line-height: 1.5;">
        ⚠️ Rake panels for 1.5m and 1.8m panels require custom manufacturing. Please <a href="mailto:enquiries@clickfencing.com.au" style="color: #92400e; font-weight: 600;">email us your designs</a> if required.
      </div>

      <div id="section-${sectionNum}-raked-fields" class="conditional-fields-no hidden">
        <div class="form-group">
          <label>Raked Side</label>
          <div class="radio-group" style="display:flex; gap:24px; align-items:center;">
            <label style="display:flex; align-items:center; gap:8px; cursor:pointer;"><input type="radio" name="section-${sectionNum}-raked-side" value="left" style="width:16px; height:16px; accent-color:#0e9ed5; cursor:pointer;" ${(this.state.sections[sectionNum]?.rakedSide || 'left') === 'left' ? 'checked' : ''}> Left-hand</label>
            <label style="display:flex; align-items:center; gap:8px; cursor:pointer;"><input type="radio" name="section-${sectionNum}-raked-side" value="right" style="width:16px; height:16px; accent-color:#0e9ed5; cursor:pointer;" ${this.state.sections[sectionNum]?.rakedSide === 'right' ? 'checked' : ''}> Right-hand</label>
          </div>
        </div>
        <div class="form-group">
          <label>Retained Height (mm)</label>
          <select class="form-control" id="section-${sectionNum}-retained-height">
            ${this.generateRetainedHeightOptions()}
          </select>
        </div>
      </div>
    `;

    rakeForm.innerHTML = rakeConfigHTML;

    // Wire up the raked toggle (mirrors the listener originally in setupSectionFormListeners)
    const rakedToggle = rakeForm.querySelector(`#section-${sectionNum}-raked`);
    if (rakedToggle) {
      rakedToggle.addEventListener('change', (e) => {
        const currentValue = this.state.sections[sectionNum]?.hasRaked;
        const checked = e.target.checked;
        if (checked === currentValue) return;
        this.state.sections[sectionNum].hasRaked = checked;
        const rakedFields = rakeForm.querySelector(`#section-${sectionNum}-raked-fields`);
        if (rakedFields) {
          rakedFields.classList.toggle('hidden', !checked);
        }
        // AUTO-SET: default retained height to 1300mm when rake is enabled
        if (checked && !this.state.sections[sectionNum]?.retainedHeight) {
          const retainedHeightSelect = rakeForm.querySelector(`#section-${sectionNum}-retained-height`);
          if (retainedHeightSelect) {
            retainedHeightSelect.value = '1300';
            this.state.sections[sectionNum].retainedHeight = '1300';
          }
        }
        // AUTO-SET: default raked side to LHS when rake is enabled, and show it
        // as the selected (blue) radio so the auto-choice is visible.
        if (checked && !this.state.sections[sectionNum]?.rakedSide) {
          this.state.sections[sectionNum].rakedSide = 'left';
        }
        if (checked) {
          const _side = this.state.sections[sectionNum].rakedSide || 'left';
          const _sideRadio = rakeForm.querySelector(`input[name="section-${sectionNum}-raked-side"][value="${_side}"]`);
          if (_sideRadio) _sideRadio.checked = true;
        }
        this.updateWallToGlassAvailability(sectionNum);
        this.updateCalculationResults(sectionNum);
        this.trigger3DUpdate();
      });
    }

    // Wire up raked side radios
    const rakedSideRadios = rakeForm.querySelectorAll(`input[name="section-${sectionNum}-raked-side"]`);
    rakedSideRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const newSide = e.target.value;
        if (newSide === this.state.sections[sectionNum]?.rakedSide) return;
        this.updateSectionData(sectionNum, 'rakedSide', newSide);
        this.updateWallToGlassAvailability(sectionNum);
      });
    });

    // Wire up retained height select
    const retainedHeight = rakeForm.querySelector(`#section-${sectionNum}-retained-height`);
    if (retainedHeight) {
      retainedHeight.addEventListener('change', (e) => {
        const currentValue = this.state.sections[sectionNum]?.retainedHeight;
        const newValue = e.target.value;
        if (newValue === currentValue) return;
        this.updateSectionData(sectionNum, 'retainedHeight', newValue);
      });
    }

    setTimeout(() => {
      if (window.setupTooltips) window.setupTooltips();
    }, 10);

    return rakeForm;
  }

  // Setup section form listeners
  setupSectionFormListeners(form, sectionNum) {
    // Glass Type dropdown
    const glassTypeSelect = form.querySelector('#glass-type');
    if (glassTypeSelect) {
      glassTypeSelect.addEventListener('change', () => {
        this.handleGlassTypeChange();
        this.updateComponentInfo();
      });
    }

    // Length input
    const lengthInput = form.querySelector(`#section-${sectionNum}-length`);

    // Helper: extract raw numeric value from field (strips "mm" suffix)
    const getRawLength = (val) => parseInt(val.toString().replace(/[^0-9]/g, '')) || 0;

    lengthInput.addEventListener('input', (e) => {
      // Strip non-numeric chars immediately
      const raw = e.target.value.replace(/[^0-9]/g, '');
      e.target.value = raw;
      if (raw === '') return;

      const val = parseInt(raw);

      // Only compute if value is a plausible section length (≥ 500mm).
      // While user is mid-type (e.g. typing "5600" and currently at "5"),
      // skip silently — no error, no stale preview.
      if (val < CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH) return;

      // Compute immediately — no debounce
      const currentValue = this.state.sections[sectionNum]?.length;
      if (currentValue !== raw) {
        this.updateSectionData(sectionNum, 'length', raw);
        
        // panelLength is determined by fenceHeight
        const _h = this.state.sections[sectionNum]?.fenceHeight || '1200';
        this.state.sections[sectionNum].panelLength = this.state.sections[sectionNum].panelLength || this.getPanelLengthForHeight(_h);
      }
    });

    lengthInput.addEventListener('blur', () => {
      // On blur: append "mm" suffix and ensure final value is computed
      const raw = getRawLength(lengthInput.value);
      if (raw > 0) {
        lengthInput.value = raw + 'mm';
        const rawStr = String(raw);
        const currentValue = this.state.sections[sectionNum]?.length;
        if (currentValue !== rawStr) {
          this.updateSectionData(sectionNum, 'length', rawStr);
        } else {
          this.updateCalculationResults(sectionNum);
        }
      }
      this.trigger3DUpdate();
    });

    lengthInput.addEventListener('focus', () => {
      // On focus: strip "mm" so user can edit the number cleanly
      const raw = getRawLength(lengthInput.value);
      lengthInput.value = raw > 0 ? String(raw) : '';
      this.showInfo('sectionLength');
    });

    // If section already has a length (e.g. restored state), show with mm suffix
    setTimeout(() => {
      const existingVal = this.state.sections[sectionNum]?.length;
      if (existingVal && parseInt(existingVal) > 0 && document.activeElement !== lengthInput) {
        lengthInput.value = parseInt(existingVal) + 'mm';
      }
    }, 50);

    // ── Section Height dropdown ──────────────────────────────────────────────────────────
    const heightSelect = form.querySelector(`#section-${sectionNum}-fence-height`);
    if (heightSelect) {
      heightSelect.value = this.state.sections[sectionNum]?.fenceHeight || '1200';
      heightSelect.addEventListener('focus', () => { this.showInfo('sectionHeight'); });
      heightSelect.addEventListener('change', (e) => {
        if (!this.state.sections[sectionNum]) return;
        const newHeight = e.target.value;
        this.state.sections[sectionNum].fenceHeight = newHeight;
        this.updateBladeHeightLabel(sectionNum);
        this.state.sections[sectionNum].panelLength = this.getPanelLengthForHeight(newHeight);
        if (this.state.sections[sectionNum].hasGate) {
          this.state.sections[sectionNum].panelLength = this.findOptimalSheetForGate(sectionNum);
        }
        // Remove the rake panel option entirely for 1500/1800mm heights (custom-manufacture
        // only, Pete 19/07) — hide the toggle and show the custom-manufacturing note.
        const _rakedToggle  = document.getElementById(`section-${sectionNum}-raked`);
        const _rakeNote     = document.getElementById(`section-${sectionNum}-rake-custom-note`);
        const _rakedFields  = document.getElementById(`section-${sectionNum}-raked-fields`);
        const _rakeToggleGrp = document.getElementById(`section-${sectionNum}-rake-toggle-group`);
        const _rakeRestricted = (newHeight === '1500' || newHeight === '1800');
        if (_rakeToggleGrp) _rakeToggleGrp.style.display = _rakeRestricted ? 'none' : '';
        if (_rakeRestricted) {
          if (_rakedToggle) _rakedToggle.checked = false;
          this.state.sections[sectionNum].hasRaked = false;
          if (_rakedFields) _rakedFields.classList.add('hidden');
        }
        if (_rakeNote) _rakeNote.style.display = _rakeRestricted ? 'block' : 'none';
        delete this.state.optimizedConfigs[sectionNum];
        this.updateCalculationResults(sectionNum);
        this.renderSectionPreview(sectionNum);
        this.trigger3DUpdate();
      });
    }

    // ── Per-section Fence Colour swatches ────────────────────────────────
    const colorInputs = form.querySelectorAll(`input[name="section-${sectionNum}-color"]`);
    if (colorInputs.length) {
      const savedColor = this.state.sections[sectionNum]?.color || 'satin-black';
      const syncColorSwatches = (val) => {
        colorInputs.forEach(inp => {
          const dot = inp.parentElement.querySelector('.cf-dot');
          const sel = inp.value === val;
          if (dot) {
            dot.style.border = sel ? '3px solid #0e9ed5' : '3px solid transparent';
            dot.style.boxShadow = sel ? '0 0 0 1px #0e9ed5' : '0 0 0 1px #cbd5e1';
          }
          inp.checked = sel;
        });
      };
      syncColorSwatches(savedColor);
      colorInputs.forEach(inp => {
        inp.addEventListener('change', () => {
          if (!this.state.sections[sectionNum]) return;
          this.state.sections[sectionNum].color = inp.value;
          syncColorSwatches(inp.value);
          delete this.state.optimizedConfigs[sectionNum];
          this.updateCalculationResults(sectionNum);
          this.renderSectionPreview(sectionNum);
          this.updateQuoteDisplay();
          this.trigger3DUpdate();
        });
      });
    }

    // panelLength derived from fenceHeight — no standalone listener needed.

    // Post size is fixed at 50mm — no dropdown, no listener needed.

    // Rest of the listeners remain the same...
    const rakedToggle = form.querySelector(`#section-${sectionNum}-raked`);
    if (rakedToggle) {
      rakedToggle.addEventListener('change', (e) => {
        const currentValue = this.state.sections[sectionNum]?.hasRaked;
        const newValue = e.target.checked;
        
        // Only update if the value has actually changed
        if (currentValue !== newValue) {
          this.toggleRakedPanel(sectionNum, newValue);
        }
      });
    }

    this.setupRakedFieldListeners(form, sectionNum);

    // ── Ground Post pill selector (Base Mount / In Ground Post) ──────────
    const groundPostRadios = form.querySelectorAll(`input[name="section-${sectionNum}-ground-post-type"]`);
    if (groundPostRadios.length) {
      // Init from state — true = in-ground, false = base mount
      const isInGround = this.state.sections[sectionNum]?.groundPost || false;
      const baseRadio     = form.querySelector(`#section-${sectionNum}-ground-post-base`);
      const ingroundRadio = form.querySelector(`#section-${sectionNum}-ground-post-inground`);
      const baseLabel     = form.querySelector(`#section-${sectionNum}-ground-post-base-label`);
      const ingroundLabel = form.querySelector(`#section-${sectionNum}-ground-post-inground-label`);

      // Helper to sync pill colours
      const syncGroundPostPill = (inGround) => {
        if (baseLabel && ingroundLabel) {
          baseLabel.style.background     = inGround ? 'white'    : '#0e9ed5';
          baseLabel.style.color          = inGround ? '#64748b'  : 'white';
          ingroundLabel.style.background = inGround ? '#0e9ed5'  : 'white';
          ingroundLabel.style.color      = inGround ? 'white'    : '#64748b';
        }
      };

      // Set initial state
      if (baseRadio && ingroundRadio) {
        baseRadio.checked     = !isInGround;
        ingroundRadio.checked = isInGround;
      }
      syncGroundPostPill(isInGround);

      groundPostRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (!this.state.sections[sectionNum]) return;
          const inGround = e.target.value === 'inground';
          this.state.sections[sectionNum].groundPost = inGround;
          syncGroundPostPill(inGround);
          this.renderSectionPreview(sectionNum);
          // Refresh add-ons (grout vs coach screws) and post preview image
          this.populateAddOns();
          this.updatePostPreview();
        });
      });
    }

    // ── Panel Mode pill selector (Cut Panel / Even Panels) ──────
    const panelModeRadios = form.querySelectorAll(`input[name="section-${sectionNum}-panel-mode"]`);
    if (panelModeRadios.length) {
      const currentMode = this.state.sections[sectionNum]?.panelMode || 'cut';
      const cutLabel    = form.querySelector(`#section-${sectionNum}-panel-mode-cut-label`);
      const evenLabel   = form.querySelector(`#section-${sectionNum}-panel-mode-even-label`);
      const cutRadio    = form.querySelector(`#section-${sectionNum}-panel-mode-cut`);
      const evenRadio   = form.querySelector(`#section-${sectionNum}-panel-mode-even`);

      const syncPanelModePill = (mode) => {
        if (cutLabel && evenLabel) {
          cutLabel.style.background  = mode === 'cut'  ? '#0e9ed5' : 'white';
          cutLabel.style.color       = mode === 'cut'  ? 'white'   : '#64748b';
          evenLabel.style.background = mode === 'even' ? '#0e9ed5' : 'white';
          evenLabel.style.color      = mode === 'even' ? 'white'   : '#64748b';
        }
      };

      if (cutRadio && evenRadio) {
        cutRadio.checked  = currentMode === 'cut';
        evenRadio.checked = currentMode === 'even';
      }
      syncPanelModePill(currentMode);

      panelModeRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (!this.state.sections[sectionNum]) return;
          // Fast path: set mode, clear cached config, recompute + re-render in one pass
          this.state.sections[sectionNum].panelMode = e.target.value;
          syncPanelModePill(e.target.value);
          delete this.state.optimizedConfigs[sectionNum];
          this._recalcAndRender(sectionNum);
        });
      });
    }

    // ── Side End Post Setup dropdown ──────────────────────────────────────
    const endPostSetup = form.querySelector(`#section-${sectionNum}-end-post-setup`);
    if (endPostSetup) {
      // Init from state. The end-post on/off choice stays user-selectable even when a
      // gate is present (a gate no longer force-resets it to "both").
      endPostSetup.value = this.state.sections[sectionNum]?.endPostSetup || 'both';
      endPostSetup.disabled = false;
      endPostSetup.addEventListener('change', (e) => {
        if (!this.state.sections[sectionNum]) return;
        this.state.sections[sectionNum].endPostSetup = e.target.value;
        this.updateCalculationResults(sectionNum);
      });
    }
  }
  
  // Setup gate form listeners
  setupGateFormListeners(gateForm, sectionNum) {
    // ── Hinge Type dropdown + thumbnail ─────────────────────────────────
    const updateHingeThumb = (type) => {
      const thumb = document.getElementById(`section-${sectionNum}-hinge-thumb`);
      if (!thumb) return;
      const product = type === 'soft-close'
        ? this.shopifyData.products?.hingeSoftClose
        : this.shopifyData.products?.hingeStandard;
      const url = product?.variants?.find(v => v.available)?.image || product?.image || '';
      if (url) { thumb.src = url; thumb.style.display = 'block'; }
      else { thumb.style.display = 'none'; }
    };

    const hingeColorSelect = gateForm.querySelector(`#section-${sectionNum}-hinge-color`);
    if (hingeColorSelect) {
      const savedHingeType = this.state.sections[sectionNum]?.hingeType || 'SHG-90L';
      hingeColorSelect.value = savedHingeType;
      updateHingeThumb(savedHingeType);
      hingeColorSelect.addEventListener('change', (e) => {
        const type = e.target.value;
        this.state.sections[sectionNum].hingeType = type;
        updateHingeThumb(type);
        const gateLengthSelect = document.getElementById(`section-${sectionNum}-gate-length`);
        if (gateLengthSelect) gateLengthSelect.innerHTML = this.generateGateLengthOptions(type);
        this.updateHardwarePreviews();
        this.updateQuoteDisplay();
      });
    }
    // ────────────────────────────────────────────────────────────────────

    // Clamp toggle listener
    const clampToggle = gateForm.querySelector(`#section-${sectionNum}-clamp`);
    if (clampToggle) {
      clampToggle.addEventListener('change', (e) => {
        const currentValue = this.state.sections[sectionNum]?.hasClamp;
        const newValue = e.target.checked;
        
        // Only update if the value has actually changed
        if (currentValue !== newValue) {
          this.toggleClamp(sectionNum, newValue);
        }
      });
    }

    // Gate toggle listener
    const gateToggle = gateForm.querySelector(`#section-${sectionNum}-gate`);
    if (gateToggle) {
      gateToggle.addEventListener('change', (e) => {
        const currentValue = this.state.sections[sectionNum]?.hasGate;
        const newValue = e.target.checked;
        
        // Only update if the value has actually changed
        if (currentValue !== newValue) {
          this.toggleGate(sectionNum, newValue);
          // Keep the end-post on/off control usable while a gate is present — the
          // user's selection persists rather than being reset to "both".
          const endPostSel = document.querySelector(`#section-${sectionNum}-end-post-setup`);
          if (endPostSel) {
            endPostSel.disabled = false;
            endPostSel.value = this.state.sections[sectionNum]?.endPostSetup || 'both';
          }
        }
      });
    }

    // Hinge Base toggle listener
    const hingeBaseToggle = gateForm.querySelector(`#section-${sectionNum}-hinge-base`);
    if (hingeBaseToggle) {
      hingeBaseToggle.addEventListener('change', (e) => {
        if (!this.state.sections[sectionNum]) return;
        this.state.sections[sectionNum].hingeBase = e.target.checked;
        this.updateQuoteDisplay();
      });
    }

    // Gate side radio button listeners (LHS / RHS Hinge)
    // IMPORTANT: changing hinge side must NOT reset gatePosition.
    // calculatePanelsLayout handles enforcing rake to opposite end automatically.
    // We only need to update gateSide in state and re-render.
    const gateRadios = gateForm.querySelectorAll(`input[name="section-${sectionNum}-gate-side"]`);
    gateRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        // Suppress programmatic changes (e.g. from applyHingeMountType) to prevent
        // the radio change event from re-entering and overwriting wallGateEnd/gateSide.
        if (this._suppressGateSideEvent) return;
        const side = e.target.value; // 'left' or 'right'
        const section = this.state.sections[sectionNum];
        if (!section) return;
        if (section.gateHingeType === 'W2G-REMOVED-2026-08-30') {
          // Wall-to-glass: user can still move the gate along the run.
          // gateSide is the hinge side selection; wallGateEnd becomes active only when
          // the gate is actually at an end.
          section.gateSide = side;
          const calc = this.state.optimizedConfigs[sectionNum];
          const maxPos = calc ? (calc.regularPanels || 0) : 0;
          const currentPos = section.gatePosition !== undefined
            ? Math.max(0, Math.min(section.gatePosition, maxPos))
            : null;
          if (currentPos === 0) {
            section.wallGateEnd = 'left';
            // Force inward-facing hinge side at wall end
            section.gateSide = 'right';
          } else if (currentPos === maxPos) {
            section.wallGateEnd = 'right';
            section.gateSide = 'left';
          } else {
            section.wallGateEnd = null;
          }
          const lhsR = document.getElementById(`section-${sectionNum}-gate-side-left`);
          const rhsR = document.getElementById(`section-${sectionNum}-gate-side-right`);
          if (lhsR) lhsR.checked = (section.gateSide === 'left');
          if (rhsR) rhsR.checked = (section.gateSide === 'right');
          delete this.state.optimizedConfigs[sectionNum];
          this.updateCalculationResults(sectionNum);
          this.renderSectionPreview(sectionNum);
          this.trigger3DUpdate();
          return;
        }

        // gateSide change requires a full recalc — buildTrailingGaps and buildStartGap
        // both read calculation.gateSide from the stored optimizedConfig. If we only
        // update section.gateSide without invalidating, the stale calc.gateSide
        // causes gaps and panel order to render with the old side.
        const savedPos = section.gatePosition !== undefined ? section.gatePosition : 0;
        section.gateSide     = side;
        section.gatePosition = savedPos;

        delete this.state.optimizedConfigs[sectionNum];
        this.updateCalculationResults(sectionNum);
        this.updateGateSideImage(sectionNum, side);
        this.renderSectionPreview(sectionNum);
        this.updateWallToGlassAvailability(sectionNum);
        this.trigger3DUpdate();
      });
    });

    // Hinge Mount toggle — OFF=glass-to-glass (default), ON=wall-to-glass
    // W2G is only functional when gate is at the start or end of the panel run.
    const hingeMountToggle = gateForm.querySelector(`#section-${sectionNum}-hinge-mount-toggle`);
    const applyHingeMountType = (newType) => {
      const section = this.state.sections[sectionNum];
      if (!section) return;
      const glassR = document.getElementById(`section-${sectionNum}-gate-hinge-type-glass`);
      const wallR  = document.getElementById(`section-${sectionNum}-gate-hinge-type-wall`);

      const oldType = section.gateHingeType || 'glass-to-glass';
      if (oldType === newType) return;
      section.gateHingeType = newType;
      this.updateHingeBaseDisplay();

      if (newType === 'wall-to-glass') {
        if (wallR)  wallR.checked  = true;
        if (glassR) glassR.checked = false;
        // Derive wallGateEnd and gateSide directly from gatePosition — do NOT recalculate
        // here because wallGateEnd is not yet set, so peterCalculateLayout Patch 1 cannot
        // correctly identify the gate as at-edge, and may wrongly override W2G → G2G or
        // flip the gate to the wrong side. The toggle handler already set gatePosition
        // to the correct wall-end value (0 or maxPos) before calling us.
        // W2G left:  gate at pos 0   → wall on left,  hinge faces inward (gateSide='right')
        // W2G right: gate at pos maxPos → wall on right, hinge faces inward (gateSide='left')
        // Read maxPos from job cut plan (same source as updateWallToGlassAvailability)
        // so gatePosition clamping is consistent and wallGateEnd is set correctly.
        const _jobPlanApply = this.calculateJobCutPlan();
        const _secPlanApply = _jobPlanApply[sectionNum];
        const _existingCalc = this.state.optimizedConfigs[sectionNum];
        const maxPos = _secPlanApply
          ? _secPlanApply.panels.filter(p => !p.is_gate && !p.is_hinge).length
          : (_existingCalc ? (_existingCalc.regularPanels || 0) : 0);
        const currentPos = section.gatePosition !== undefined
          ? Math.max(0, Math.min(section.gatePosition, maxPos)) : 0;
        section.gatePosition = currentPos;
        if (currentPos <= 0) {
          section.wallGateEnd = 'left';
          section.gateSide    = 'right';
        } else if (currentPos >= maxPos) {
          section.wallGateEnd = 'right';
          section.gateSide    = 'left';
        } else {
          section.wallGateEnd = null;
        }
        // Suppress gate-side radio change events while updating checked state
        // programmatically — the change listener would otherwise re-enter and
        // overwrite wallGateEnd / gateSide before the final recalc at the end.
        this._suppressGateSideEvent = true;
        const lhsR = document.getElementById(`section-${sectionNum}-gate-side-left`);
        const rhsR = document.getElementById(`section-${sectionNum}-gate-side-right`);
        if (lhsR) lhsR.checked = (section.gateSide === 'left');
        if (rhsR) rhsR.checked = (section.gateSide === 'right');
        this._suppressGateSideEvent = false;
      } else {
        if (glassR) glassR.checked = true;
        if (wallR)  wallR.checked  = false;
        section.wallGateEnd = null;
        section.gateMaxPos  = undefined;
      }

      delete this.state.optimizedConfigs[sectionNum];
      this.updateCalculationResults(sectionNum);
      this.renderSectionPreview(sectionNum);
      this.updateWallToGlassAvailability(sectionNum);
      this.trigger3DUpdate();
    };

    if (hingeMountToggle) {
      hingeMountToggle.addEventListener('change', (e) => {
        const section = this.state.sections[sectionNum];
        if (!section) return;
        const wantW2G = e.target.checked;

        if (wantW2G) {
          // Get a fresh calc to read the correct maxPos. We must temporarily clear
          // gateMaxPos so peterCalculateLayout does NOT fire gateAtRakeEnd — if it did,
          // it would use a hinge-less layout with regularPanels = n-1, causing the clamp
          // in updateCalculationResults to reduce gatePosition by 1. That makes the gate
          // appear to be one slot away from the right wall end, so nearRightEnd = false
          // and W2G right never fires (or worse, nearLeftEnd fires on short runs).
          const _savedGateMaxPos = section.gateMaxPos;
          section.gateMaxPos = undefined; // prevent gateAtRakeEnd from firing
          delete this.state.optimizedConfigs[sectionNum];
          this.updateCalculationResults(sectionNum);
          section.gateMaxPos = _savedGateMaxPos; // restore after calc

          // Use job cut plan panel count (same source as updateWallToGlassAvailability)
          // so maxPos is consistent between the toggle handler and the availability check.
          // calc.regularPanels (Mode B) can differ from the Mode A cut plan count when
          // a cut panel is present, causing gatePosition to be clamped to the wrong value
          // and W2P to be immediately re-disabled after enabling.
          const jobPlanForToggle = this.calculateJobCutPlan();
          const secPlanForToggle = jobPlanForToggle[sectionNum];
          const calc   = this.state.optimizedConfigs[sectionNum];
          const maxPos = secPlanForToggle
            ? secPlanForToggle.panels.filter(p => !p.is_gate && !p.is_hinge).length
            : (calc ? (calc.regularPanels || 0) : 0);
          // Re-clamp gatePosition against the correct maxPos now that we have it
          if (section.gatePosition !== undefined && section.gatePosition > maxPos) {
            section.gatePosition = maxPos;
          }
          const pos    = section.gatePosition !== undefined ? section.gatePosition : 0;
          const rakedSide   = section.rakedSide || 'left';
          const rakedActive = !!section.hasRaked;
          const gateSide    = section.gateSide || 'right';

          // Rake only blocks W2G on its OWN end.
          // If rake is on left  → W2G left  is blocked, W2G right is always free.
          // If rake is on right → W2G right is blocked, W2G left  is always free.
          const rakeBlocksLeft  = rakedActive && rakedSide === 'left';
          const rakeBlocksRight = rakedActive && rakedSide === 'right';

          // Exact wall-end check — no ±1 slop.
          const nearLeftEnd  = (pos <= 0);
          const nearRightEnd = (pos >= maxPos);

          // W2G is allowed whenever the gate is at a wall end that the rake does NOT occupy.
          // Hinge side (LHS/RHS) does not restrict W2G — any hinge at the right end can use W2G.
          const canW2GLeft  = nearLeftEnd  && !rakeBlocksLeft;
          const canW2GRight = nearRightEnd && !rakeBlocksRight;

          if (canW2GLeft) {
            // W2G LEFT — gate flush to left wall, hinge faces inward (RHS)
            section.gatePosition = 0;
            section.gateSide     = 'right';
            applyHingeMountType('wall-to-glass');
          } else if (canW2GRight) {
            // W2G RIGHT — gate flush to right wall, hinge faces inward (LHS)
            section.gatePosition = maxPos;
            section.gateSide     = 'left';
            applyHingeMountType('wall-to-glass');
          } else {
            // Gate and hinge are both mid-run — revert toggle, inform user
            e.target.checked = false;
            const hint = document.getElementById(`section-${sectionNum}-hinge-mount-hint`);
            if (hint) {
              hint.textContent = 'Move gate to the start or end of the run first.';
              hint.style.display = 'block';
              setTimeout(() => { hint.style.display = 'none'; }, 3000);
            }
            return;
          }
        } else {
          applyHingeMountType('glass-to-glass');
        }
      });
    }

    // Gate placing move buttons
    const moveLeftBtn   = gateForm.querySelector(`#section-${sectionNum}-gate-move-left`);
    const moveRightBtn  = gateForm.querySelector(`#section-${sectionNum}-gate-move-right`);
    const moveMiddleBtn = gateForm.querySelector(`#section-${sectionNum}-gate-move-middle`);

    // Move gate to a position. When rake is present and gate reaches the rake's end,
    // auto-flip gate side so it faces/latches onto the rake.
    // Moving away from rake restores the original gate side.
    const applyGatePosition = (newPos) => {
      const section = this.state.sections[sectionNum];
      if (!section) return;
      // Ensure we always have a valid calculation before reading maxPos
      if (!this.state.optimizedConfigs[sectionNum]) {
        this.updateCalculationResults(sectionNum);
      }
      // Use job cut plan panel count (includes cut panel) as maxPos
      // so the gate block can move through ALL panels including the cut panel
      const jobPlan    = this.calculateJobCutPlan();
      const sectionPlan = jobPlan[sectionNum];
      const maxPos = sectionPlan
        ? sectionPlan.panels.filter(p => !p.is_gate && !p.is_hinge).length
        : (this.state.optimizedConfigs[sectionNum]?.regularPanels || 0);
      if (section.gateHingeType === 'W2G-REMOVED-2026-08-30') {
        section.gatePosition = Math.max(0, Math.min(newPos, maxPos));
        const rakedSide = section.rakedSide || 'left';
        const rakeBlocksLeft  = section.hasRaked && rakedSide === 'left';
        const rakeBlocksRight = section.hasRaked && rakedSide === 'right';

        if (section.gatePosition <= 0 && !rakeBlocksLeft) {
          section.wallGateEnd = 'left';
          section.gateSide    = 'right';
        } else if (section.gatePosition >= maxPos && !rakeBlocksRight) {
          section.wallGateEnd = 'right';
          section.gateSide    = 'left';
        } else if (
          (section.gatePosition <= 0 && rakeBlocksLeft) ||
          (section.gatePosition >= maxPos && rakeBlocksRight)
        ) {
          // Tried to move to the rake end with W2G — revert to G2G
          section.wallGateEnd = null;
          section.gateHingeType = 'glass-to-glass';
          const hingeGlassRadio = document.getElementById(`section-${sectionNum}-gate-hinge-type-glass`);
          const hingeWallRadio  = document.getElementById(`section-${sectionNum}-gate-hinge-type-wall`);
          if (hingeGlassRadio) hingeGlassRadio.checked = true;
          if (hingeWallRadio)  hingeWallRadio.checked  = false;
          const w2gToggle = document.getElementById(`section-${sectionNum}-hinge-mount-toggle`);
          if (w2gToggle) w2gToggle.checked = false;
          this.updateHingeBaseDisplay();
        } else {
          // Gate moved away from wall end → auto-switch to glass-to-glass
          section.wallGateEnd = null;
          section.gateHingeType = 'glass-to-glass';
          const hingeGlassRadio = document.getElementById(`section-${sectionNum}-gate-hinge-type-glass`);
          const hingeWallRadio  = document.getElementById(`section-${sectionNum}-gate-hinge-type-wall`);
          if (hingeGlassRadio) hingeGlassRadio.checked = true;
          if (hingeWallRadio)  hingeWallRadio.checked  = false;
          const w2gToggle = document.getElementById(`section-${sectionNum}-hinge-mount-toggle`);
          if (w2gToggle) w2gToggle.checked = false;
          this.updateHingeBaseDisplay();
        }
        const lhsR = document.getElementById(`section-${sectionNum}-gate-side-left`);
        const rhsR = document.getElementById(`section-${sectionNum}-gate-side-right`);
        if (lhsR) lhsR.checked = (section.gateSide === 'left');
        if (rhsR) rhsR.checked = (section.gateSide === 'right');
        delete this.state.optimizedConfigs[sectionNum];
        this.updateCalculationResults(sectionNum);
        this.renderSectionPreview(sectionNum);
        this.trigger3DUpdate();
        return;
      }
      section.gatePosition = Math.max(0, Math.min(newPos, maxPos));

      if (section.hasRaked) {
        const rakedSide     = section.rakedSide || 'left';
        const gateSide      = section.gateSide  || 'right'; // hinge side

        // ── FORBIDDEN POSITION RULE ───────────────────────────────────────
        // Hinge panel can NEVER be adjacent to the rake panel.
        // Gate CAN latch off rake (7mm gap), but hinge CANNOT be beside rake.
        //
        // Rake RIGHT + RHS hinge (gateSide='right'):
        //   Block order: [hinge][gate]
        //   At maxPos:   [...panels][hinge][gate][rake] ← hinge beside rake = FORBIDDEN
        //   Max allowed: maxPos - 1 → [...panels][hinge][gate] - 7mm - [rake] ✓
        //
        // Rake LEFT + LHS hinge (gateSide='left'):
        //   Block order: [gate][hinge]
        //   At pos 0:    [rake][gate][hinge][panels...] ← hinge beside rake = FORBIDDEN
        //   Min allowed: pos 1 → [rake] - 7mm - [gate][hinge][panels...] ✓
        //
        // Rake RIGHT + LHS hinge (gateSide='left'):
        //   Block order: [gate][hinge]
        //   At maxPos:   [...panels][gate][hinge][rake] ← hinge beside rake = FORBIDDEN
        //   Max allowed: maxPos - 1 → [...panels][gate] - 7mm - [rake] ← gate latches rake ✓
        //   Wait — at maxPos-1: [...panels][gate][hinge] then hinge at maxPos-1+1=maxPos
        //   beside rake. Max allowed: maxPos - 2 so hinge lands at maxPos-1, one away from rake.
        //   Actually: block occupies positions [clampedPos, clampedPos+1].
        //   hinge is at clampedPos+1 (LHS: gate=clampedPos, hinge=clampedPos+1)
        //   Forbidden: clampedPos+1 === maxPos (hinge beside rake on right)
        //   Max allowed clampedPos = maxPos - 2 (gate latches rake at maxPos-1, hinge at maxPos-2+1=maxPos-1... 
        //   Simplest: for LHS hinge + rake right, max pos = maxPos - 1
        //   giving layout: [...panels][gate][hinge] - post - [rake]. Gate does NOT latch rake.
        //   To allow gate latch rake: pos = maxPos, but then hinge at maxPos+1 which doesn't exist.
        //   So: LHS hinge + rake right → max allowed = maxPos - 1 (gate one away from rake)
        //
        // Rake LEFT + RHS hinge (gateSide='right'):
        //   Block order: [hinge][gate]
        //   At pos 0:    [rake][hinge][gate][panels...] ← hinge beside rake = FORBIDDEN
        //   Min allowed: pos 1 → [rake] - post - [hinge][gate][panels...] ✓

        // Clamping: gate can reach any position 0..maxPos.
        // When it arrives at a rake end the auto-flip below forces the correct hinge side.
        // No pre-clamping based on hinge side needed — the flip handles the correction.
        const minAllowed = 0;
        const maxAllowed = maxPos;

        section.gatePosition = Math.max(minAllowed, Math.min(section.gatePosition, maxAllowed));

        // ── Auto-convert hinge side when gate reaches rake end ────────────
        // Rule:
        //   LHS hinge moving left  → hits left rake  → convert to RHS hinge
        //   RHS hinge moving right → hits right rake → convert to LHS hinge
        // This ensures gate always LATCHES off rake (7mm on rake side, 10mm hinge away from rake)
        const atLeftRake  = rakedSide === 'left'  && section.gatePosition <= minAllowed;
        const atRightRake = rakedSide === 'right' && section.gatePosition >= maxAllowed;
        const gateAtRakeEnd = atLeftRake || atRightRake;

        const prevGateAtRakeEnd = !!(this.state.optimizedConfigs[sectionNum]?.gateAtRakeEnd);
        const prevGateSide      = section.gateSide;

        if (atLeftRake) {
          // Gate meets LEFT rake — always force LHS hinge regardless of current hinge side.
          // Rule: gate LATCHES off rake → latch faces rake, hinge faces away.
          // Left rake latch side = left → hinge must be on LEFT (gateSide='left')
          // Layout: [rake]-7mm-[gate]-10mm-[hinge_LEFT]-panels
          // Covers: RHS hinge at left end (was blocked, now auto-flips to LHS) ✓
          //         LHS hinge at left end (already correct) ✓
          section.gateSide = 'left';
        } else if (atRightRake) {
          // Gate meets RIGHT rake — always force RHS hinge regardless of current hinge side.
          // Rule: gate LATCHES off rake → latch faces rake, hinge faces away.
          // Right rake latch side = right → hinge must be on RIGHT (gateSide='right')
          // Layout: panels-[hinge_RIGHT]-10mm-[gate]-7mm-[rake]
          // Covers: LHS hinge at right end (was blocked, now auto-flips to RHS) ✓
          //         RHS hinge at right end (already correct) ✓
          section.gateSide = 'right';
        }
        // Gate not at rake end — hinge side stays as user selected (no forced flip)

        // Update LHS/RHS radio buttons
        const lhsR = document.getElementById(`section-${sectionNum}-gate-side-left`);
        const rhsR = document.getElementById(`section-${sectionNum}-gate-side-right`);
        if (lhsR) lhsR.checked = (section.gateSide === 'left');
        if (rhsR) rhsR.checked = (section.gateSide === 'right');
        this.updateGateSideImage(sectionNum, section.gateSide);

        // Recalculate whenever rake-end status CHANGES (arriving OR leaving) or gateSide flipped.
        // This ensures gateAtRakeEnd flag in optimizedConfigs is always current.
        const rakeEndChanged = gateAtRakeEnd !== prevGateAtRakeEnd;
        const gateSideChanged = section.gateSide !== prevGateSide;
        if (rakeEndChanged || gateSideChanged) {
          delete this.state.optimizedConfigs[sectionNum];
          // Reset stale gateMaxPos so peterCalculateLayout sees correct gateAtRakeEnd on first recalc.
          if (!gateAtRakeEnd) section.gateMaxPos = undefined;
          this.updateCalculationResults(sectionNum);
          this.updateWallToGlassAvailability(sectionNum);
          this.trigger3DUpdate();
          return;
        }
      }

      // No structural change — re-render preview only
      this.renderSectionPreview(sectionNum);
      this.updateWallToGlassAvailability(sectionNum);
      this.trigger3DUpdate();
    };

    // Gate moves in 500mm increments per click.
    // gatePosition is a panel-slot index. We calculate how many slots = 500mm
    // based on the current panel width, always stepping at least 1 slot.
    //   1000mm panels → 500mm = 1 slot  (500/1000 = 0.5 → rounds to 1)
    //   2000mm panels → 500mm = 1 slot  (500/2000 = 0.25 → rounds to 1)
    //   3000mm panels → 500mm = 1 slot  (500/3000 = 0.17 → rounds to 1)
    // One slot is the minimum granularity — so all panel sizes move 1 slot per click,
    // which equals the panel width in real mm. This is Peter's intent.
    // Gate moves half a panel width per click (e.g. 500mm for 1000mm panels).
    // gateOffsetMm accumulates the sub-slot shift. When it reaches a full panel
    // width the slot index advances and offset resets. The filler panel absorbs
    // the difference in renderSectionPreview.
    const applyGateStep = (direction) => {
      const section = this.state.sections[sectionNum];
      if (!section || !section.hasGate) return;
      const panelWidth = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));

      // Resolve a not-yet-placed gate (sentinel 9999 / _placeAtMiddle) to its actual
      // displayed middle slot FIRST, so the very first Move click steps from the real
      // position instead of the sentinel. Fixes the "have to press Move twice" bug.
      if (section.gatePosition === undefined || section.gatePosition >= 9999 || section._placeAtMiddle) {
        let mp = this.state.optimizedConfigs[sectionNum]?.regularPanels;
        try {
          const jp = this.calculateJobCutPlan()[sectionNum];
          if (jp && jp.valid) mp = jp.panels.filter(p => !p.is_gate && !p.is_hinge).length;
        } catch (e) {}
        section.gatePosition = Math.floor((mp || 0) / 2);
        section._placeAtMiddle = false;
        section.gateOffsetMm = 0;
      }

      // Move the gate by ONE WHOLE PANEL per click, so it lands on the panel edge
      // (post) every time. No sub-slot offset is kept, so a single click always
      // moves the gate a clear step (fixes the "takes two clicks" feel that the
      // old 1m-snapping caused).
      section.gateOffsetMm = 0;
      let offset = 0;
      let pos    = (section.gatePosition || 0) + direction;

      // ── Filler-skip + 500mm hinge-follower sensor ─────────────────────────
      // Read the current cut plan to identify which slot is the filler panel.
      // The filler is the panel whose width < actual_sheet (i.e. not a full sheet).
      // Rules:
      //   1. FILLER SKIP: if the resolved slot lands on the filler slot, skip it
      //      by stepping one more slot in the same direction.
      //   2. 500MM FOLLOWER: when the gate sits next to a full-sheet panel,
      //      that panel is split: 500mm stays as a hinge-follower (on the hinge
      //      side of the gate), and the remainder merges into the filler panel.
      //      The follower travels with the gate whenever the gate moves.
      // ──────────────────────────────────────────────────────────────────────
      try {
        const jobPlan     = this.calculateJobCutPlan();
        const sectionPlan = jobPlan[sectionNum];
        if (sectionPlan && sectionPlan.valid) {
          const stripPanels = sectionPlan.panels.filter(p => !p.is_gate && !p.is_hinge);
          const actual_sheet = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
          const HINGE_FOLLOWER_MM = 500;

          // Identify the filler slot index — the panel whose sheet < actual_sheet.
          // There is at most one filler per section.
          const fillerSlotIdx = stripPanels.findIndex(p => p.sheet < actual_sheet);
          const fillerWidth   = fillerSlotIdx >= 0 ? stripPanels[fillerSlotIdx].sheet : 0;
          const maxPos        = stripPanels.length;

          // Clamp pos to valid range before filler-skip
          pos = Math.max(0, Math.min(pos, maxPos));

          // ── Rule 1: Filler skip ──────────────────────────────────────────
          // gatePosition is the slot index to the LEFT of where the gate block
          // is inserted. If pos lands on the filler slot, skip it in one clean
          // jump — and reset offset to 0 so the half-step residue doesn't cause
          // the skip to re-fire on the very next button press.
          if (fillerSlotIdx >= 0 && pos === fillerSlotIdx) {
            pos = Math.max(0, Math.min(pos + direction, maxPos));
            offset = 0; // absorb residue — next click starts from a clean slot boundary
            section.gateOffsetMm = 0;
          }

          // ── Rule 2: 500mm hinge-follower ─────────────────────────────────
          // Determine the hinge side so we know which adjacent slot to split.
          // G2G LHS (gateSide='left'):  hinge is to the RIGHT of gate → split slot at pos
          //                             (rightPanels[0] is the panel right of gate)
          // G2G RHS (gateSide='right'): hinge is to the LEFT of gate  → split slot at pos-1
          //                             (leftPanels[pos-1] is the panel left of gate)
          // W2G: no glass hinge panel; follower still applies — treat as hinge side = gate open direction.
          //   W2G left  (wallGateEnd='left',  gate faces right): follower on right → slot at pos
          //   W2G right (wallGateEnd='right', gate faces left):  follower on left  → slot at pos-1
          const isW2G     = section.gateHingeType === 'W2G-REMOVED-2026-08-30';
          const gateSide  = isW2G
            ? (section.wallGateEnd === 'left' ? 'right' : 'left')
            : (section.gateSide || 'right');

          // hingeSlotIdx: the regularStripPanel slot that is immediately on the hinge side of the gate.
          // gateSide='left'  → hinge is LEFT of gate  → slot = pos - 1
          // gateSide='right' → hinge is RIGHT of gate → slot = pos
          // (G2G RHS layout: [hinge][gate] — hinge occupies the slot just before pos)
          // (G2G LHS layout: [gate][hinge] — hinge occupies the slot at pos)
          const hingeSlotIdx = (gateSide === 'right') ? pos : pos - 1;

          const hingeSlotInRange = hingeSlotIdx >= 0 && hingeSlotIdx < stripPanels.length;
          const slotIsFullSheet  = hingeSlotInRange && stripPanels[hingeSlotIdx].sheet === actual_sheet;

          if (hingeSlotInRange && slotIsFullSheet && actual_sheet > HINGE_FOLLOWER_MM) {
            // Split: 500mm follower stays, remainder merges into filler.
            const splitRemainder = actual_sheet - HINGE_FOLLOWER_MM;
            const mergedFiller   = fillerWidth + splitRemainder; // old filler + new remainder
            section.hingeFollowerActive  = true;
            section.hingeFollowerMm      = HINGE_FOLLOWER_MM;
            section.hingeFollowerMergedFiller = mergedFiller;
            section.hingeFollowerSlotIdx = hingeSlotIdx; // which slot was split (for renderSectionPreview)
          } else {
            // Gate is not next to a full sheet (e.g. at filler or at rake end) — no follower.
            section.hingeFollowerActive  = false;
            section.hingeFollowerMm      = 0;
            section.hingeFollowerMergedFiller = 0;
            section.hingeFollowerSlotIdx = -1;
          }
        }
      } catch (e) {
        // Sensor is non-critical — if anything throws, let gate movement proceed normally.
        console.warn('[hingeFollower sensor]', e);
      }

      applyGatePosition(pos);
    };

    if (moveLeftBtn) {
      moveLeftBtn.addEventListener('click', () => {
        this.state.sections[sectionNum].gateMoved = true;
        applyGateStep(-1);
      });
    }

    if (moveRightBtn) {
      moveRightBtn.addEventListener('click', () => {
        this.state.sections[sectionNum].gateMoved = true;
        applyGateStep(1);
      });
    }

    if (moveMiddleBtn) {
      moveMiddleBtn.addEventListener('click', () => {
        this.state.sections[sectionNum].gateMoved = true;
        const section = this.state.sections[sectionNum];
        if (!section || !section.hasGate) return;
        const jobPlan     = this.calculateJobCutPlan();
        const sectionPlan = jobPlan[sectionNum];
        const maxPos      = sectionPlan
          ? sectionPlan.panels.filter(p => !p.is_gate && !p.is_hinge).length
          : (this.state.optimizedConfigs[sectionNum]?.regularPanels || 0);
        section.gateOffsetMm = 0; // reset sub-slot offset on middle
        // Clear hinge-follower state — middle placement re-evaluates from scratch via applyGateStep sensor
        section.hingeFollowerActive       = false;
        section.hingeFollowerMm           = 0;
        section.hingeFollowerMergedFiller = 0;
        section.hingeFollowerSlotIdx      = -1;
        applyGatePosition(Math.floor(maxPos / 2));
      });
    }

    // ── Exact gate position (item 8) ──────────────────────────────────────
    // Places the gate so its closest edge sits a typed distance from one side.
    // Default reference = LHS; a toggle button switches to RHS. The rake is kept
    // at the ends by applyGatePosition's own clamping.
    const exactInput   = gateForm.querySelector(`#section-${sectionNum}-gate-exact`);
    const exactSideBtn = gateForm.querySelector(`#section-${sectionNum}-gate-exact-side`);
    const exactApplyBtn= gateForm.querySelector(`#section-${sectionNum}-gate-exact-apply`);

    if (exactSideBtn) {
      exactSideBtn.addEventListener('click', () => {
        const cur = exactSideBtn.getAttribute('data-side') === 'right' ? 'right' : 'left';
        const next = cur === 'left' ? 'right' : 'left';
        exactSideBtn.setAttribute('data-side', next);
        exactSideBtn.textContent = next === 'left' ? 'From LHS' : 'From RHS';
      });
    }

    const applyExactPosition = () => {
      const section = this.state.sections[sectionNum];
      if (!section || !section.hasGate || !exactInput) return;
      const targetMm = parseInt((exactInput.value || '').replace(/[^0-9]/g, ''));
      if (!Number.isFinite(targetMm)) return;
      const side = exactSideBtn && exactSideBtn.getAttribute('data-side') === 'right' ? 'right' : 'left';

      const panelWidth = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
      const totalLen   = parseInt(section.length) || 0;
      const gateWidth  = parseInt(section.gateLength) || this.getDefaultGateLength();

      // Distance from the LEFT end to the gate's LEFT edge.
      let leftEdgeFromLeft = side === 'left'
        ? targetMm
        : Math.max(0, totalLen - targetMm - gateWidth);

      // Determine how many regular slots exist so we can clamp within the run.
      const jobPlan     = this.calculateJobCutPlan();
      const sectionPlan = jobPlan[sectionNum];
      const maxPos      = sectionPlan
        ? sectionPlan.panels.filter(p => !p.is_gate && !p.is_hinge).length
        : (this.state.optimizedConfigs[sectionNum]?.regularPanels || 0);

      leftEdgeFromLeft = Math.max(0, Math.min(leftEdgeFromLeft, Math.max(0, totalLen - gateWidth)));

      let pos    = Math.floor(leftEdgeFromLeft / panelWidth);
      let offset = leftEdgeFromLeft - (pos * panelWidth);
      pos = Math.max(0, Math.min(pos, maxPos));

      section.gateMoved = true;
      section.gateOffsetMm = offset;
      // Reset hinge-follower state so the placement re-evaluates cleanly.
      section.hingeFollowerActive       = false;
      section.hingeFollowerMm           = 0;
      section.hingeFollowerMergedFiller = 0;
      section.hingeFollowerSlotIdx      = -1;
      applyGatePosition(pos);
    };

    if (exactApplyBtn) exactApplyBtn.addEventListener('click', applyExactPosition);
    if (exactInput) exactInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); applyExactPosition(); }
    });

    // Set initial gate image on form creation
    setTimeout(() => {
      const currentSide = this.state.sections[sectionNum]?.gateSide || 'right';
      this.updateGateSideImage(sectionNum, currentSide);
    }, 0);

    this.setupClampFieldListeners(gateForm, sectionNum);
    this.setupGateFieldListeners(gateForm, sectionNum);

  }

  // Update gate side state — the 2D panel preview re-renders automatically via renderSectionPreview
  updateGateSideImage(sectionNum, side) {
    // Preview box removed — gate image is now shown inline in the section panel strip.
    // This method intentionally left as a no-op so existing callers don't break.
  }

  // ── Cut panel inline editor ───────────────────────────────────────────────
  // Shows a floating input overlay when Peter clicks the dashed cut panel.
  // Saves value to section.overrideCutMm and re-renders the preview.
  showCutPanelEditor(sectionNum, currentWidthMm, panelContainer, hitRect) {
    // Remove any existing editor
    const existing = document.getElementById('cut-panel-editor-overlay');
    if (existing) existing.remove();

    // Position overlay relative to panelContainer
    const containerRect = panelContainer.getBoundingClientRect();
    const hitRectBBox   = hitRect.getBoundingClientRect();

    const overlay = document.createElement('div');
    overlay.id = 'cut-panel-editor-overlay';
    overlay.style.cssText = `
      position: fixed;
      left: ${hitRectBBox.left + hitRectBBox.width / 2 - 90}px;
      top:  ${hitRectBBox.top - 72}px;
      width: 180px;
      background: #fff;
      border: 2px solid #0e9ed5;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.18);
      padding: 10px 12px 10px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 8px;
      font-family: sans-serif;
    `;

    const label = document.createElement('div');
    label.textContent = 'Cut panel size (min 500mm)';
    label.style.cssText = 'font-size:12px;font-weight:600;color:#374151;';

    const input = document.createElement('input');
    input.type = 'number';
    input.min  = '500';
    input.max  = '9999';
    input.step = '1';
    input.value = currentWidthMm;
    input.style.cssText = `
      width: 100%;
      box-sizing: border-box;
      padding: 6px 8px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
      outline: none;
    `;
    input.addEventListener('focus', () => { input.style.borderColor = '#0e9ed5'; });
    input.addEventListener('blur',  () => { input.style.borderColor = '#cbd5e1'; });

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:6px;';

    const applyBtn = document.createElement('button');
    applyBtn.type = 'button';
    applyBtn.textContent = 'Apply';
    applyBtn.style.cssText = `
      flex:1;padding:6px 0;background:#0e9ed5;color:#fff;
      border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;
    `;

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.textContent = 'Reset';
    clearBtn.style.cssText = `
      flex:1;padding:6px 0;background:#f1f5f9;color:#374151;
      border:1px solid #cbd5e1;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;
    `;

    const apply = () => {
      const val = parseInt(input.value, 10);
      if (!isNaN(val) && val >= 500) {
        this.state.sections[sectionNum].overrideCutMm = val;
      } else if (!isNaN(val) && val < 500) {
        // Below minimum — snap to 500 and flash the input
        input.value = 500;
        input.style.borderColor = '#f59e0b';
        setTimeout(() => { input.style.borderColor = '#cbd5e1'; }, 800);
        return; // don't close — let Peter fix it
      }
      overlay.remove();
      this.renderSectionPreview(sectionNum);
    };

    const reset = () => {
      delete this.state.sections[sectionNum].overrideCutMm;
      overlay.remove();
      this.renderSectionPreview(sectionNum);
    };

    applyBtn.addEventListener('click', apply);
    clearBtn.addEventListener('click', reset);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') apply();
      if (e.key === 'Escape') { overlay.remove(); }
    });

    // Click outside to dismiss without saving
    const dismiss = (e) => {
      if (!overlay.contains(e.target)) {
        overlay.remove();
        document.removeEventListener('click', dismiss, true);
      }
    };
    // Defer so the originating click doesn't instantly dismiss
    setTimeout(() => document.addEventListener('click', dismiss, true), 0);

    btnRow.appendChild(applyBtn);
    btnRow.appendChild(clearBtn);
    overlay.appendChild(label);
    overlay.appendChild(input);
    overlay.appendChild(btnRow);
    document.body.appendChild(overlay);
    input.select();
  }
  // ─────────────────────────────────────────────────────────────────────────

  // Setup clamp field listeners
  setupClampFieldListeners(gateForm, sectionNum) {
    const clampFieldsContainer = gateForm.querySelector(`#section-${sectionNum}-clamp-fields`);
    if (!clampFieldsContainer) return;
    
    const clampSelectFields = clampFieldsContainer.querySelectorAll('select');
    
    clampSelectFields.forEach(field => {
      field.addEventListener('change', (e) => {
        const fieldId = field.id;
        const fieldParts = fieldId.split('-');
        const fieldName = fieldParts.slice(2).join('-');
        const dataField = fieldName.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
        
        const currentValue = this.state.sections[sectionNum]?.[dataField];
        const newValue = e.target.value;
        
        // Only update if the value has actually changed
        if (currentValue !== newValue) {
          this.updateSectionData(sectionNum, dataField, newValue);
          
          // Update prices for clamp field changes that affect pricing
          if (fieldName === 'clamp-color' || fieldName === 'clamp-position') {
            this.updateQuoteDisplay();
            this.updateComponentsQuote();
          }
          
          // Update hardware previews
          this.updateHardwarePreviews();
        }
      });
    });
  }

  // Setup gate field listeners
  setupGateFieldListeners(gateForm, sectionNum) {
    const gateFieldsContainer = gateForm.querySelector(`#section-${sectionNum}-gate-fields`);
    if (!gateFieldsContainer) return;

    // Hinge type radio buttons (now live in gate-orientation-row, above gate-fields)
    const hingeTypeRadios = gateForm.querySelectorAll(`input[name="section-${sectionNum}-hinge-type-radio"]`);
    hingeTypeRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (!e.target.checked) return;
        const newHingeType = e.target.value; // 'soft-close' or 'spring'
        const currentValue = this.state.sections[sectionNum]?.hingeType;
        if (currentValue === newHingeType) return;
        this.updateSectionData(sectionNum, 'hingeType', newHingeType);
        this.updateGateLengthDropdown(sectionNum, newHingeType);
        this.updateHingeColorDropdown(sectionNum, newHingeType);
        this.updateQuoteDisplay();
        this.updateComponentsQuote();
        this.updateHardwarePreviews();
        if (this.state.currentSection === sectionNum) this.renderSectionPreview(sectionNum);
      });
    });

    // Sync hinge-type radios to saved state on form rebuild
    const savedHingeType = this.state.sections[sectionNum]?.hingeType || 'SHG-90L';
    const softCloseRadio = gateForm.querySelector(`#section-${sectionNum}-hinge-type-soft-close`);
    const springRadio    = gateForm.querySelector(`#section-${sectionNum}-hinge-type-spring`);
    if (softCloseRadio) softCloseRadio.checked = (savedHingeType === 'soft-close');
    if (springRadio)    springRadio.checked    = (savedHingeType === 'spring');

    const gateSelectFields = gateFieldsContainer.querySelectorAll('select');
    
    gateSelectFields.forEach(field => {
      field.addEventListener('change', (e) => {
        const fieldId = field.id;
        const fieldParts = fieldId.split('-');
        const fieldName = fieldParts.slice(2).join('-');
        const dataField = fieldName.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
        
        const currentValue = this.state.sections[sectionNum]?.[dataField];
        const newValue = e.target.value;
        
        // Only update if the value has actually changed
        if (currentValue !== newValue) {
          this.updateSectionData(sectionNum, dataField, newValue);
          
          // hinge-type select removed — handled by radios above
          if (fieldName === 'hinge-type') {
            this.updateGateLengthDropdown(sectionNum, e.target.value);
            this.updateHingeColorDropdown(sectionNum, e.target.value);
          }
          
          if (fieldName === 'hinge-color') {
            this.state.sections[sectionNum].hingeType = e.target.value;
            const gateLengthSelect = document.getElementById(`section-${sectionNum}-gate-length`);
            if (gateLengthSelect) gateLengthSelect.innerHTML = this.generateGateLengthOptions(e.target.value);
          }

          if (fieldName === 'latch-type') {
            this.state.sections[sectionNum].latchType = e.target.value;
            this.updateLatchColorDropdown(sectionNum, e.target.value);
          }
          
          // Update prices for gate field changes that affect pricing
          if (fieldName === 'hinge-type' || fieldName === 'hinge-color' || fieldName === 'latch-type' || fieldName === 'latch-color' || fieldName === 'gate-length') {
            this.updateQuoteDisplay();
            this.updateComponentsQuote();
          }
          
          // Update section product preview when hinge or latch changes
          if (fieldName === 'hinge-type' || fieldName === 'hinge-color' || fieldName === 'latch-type' || fieldName === 'latch-color') {
            // Log latch-specific changes
            if (fieldName === 'latch-type' || fieldName === 'latch-color') {
              
              
              
              
              
              const imageUrl = this.getLatchImageUrl(sectionNum);
              
              
              // Log available color options if latch color changed
              if (fieldName === 'latch-color') {
                
              }
            }
            
            this.updateHardwarePreviews();
          }
          
          // Update calculation results when gate length changes — full interaction
          if (fieldName === 'gate-length') {
            // Gate width changed: recalculate from scratch, then sync both dropdowns
            delete this.state.optimizedConfigs[sectionNum];
            this.updateCalculationResults(sectionNum, false, false);
            this.syncPanelDropdown(sectionNum);
            this.syncGapDropdown(sectionNum);
          }
          
          if (this.state.currentSection === sectionNum) {
            this.renderSectionPreview(sectionNum);
          }
        }
      });
    });
  }


  // Setup raked field listeners
  setupRakedFieldListeners(form, sectionNum) {
  const rakedSideRadios = form.querySelectorAll(`input[name="section-${sectionNum}-raked-side"]`);
  rakedSideRadios.forEach(radio => {
    radio.addEventListener('change', (e) => {
      if (e.target.checked) {
        const oldSide  = this.state.sections[sectionNum]?.rakedSide;
        const newValue = e.target.value; // 'left' or 'right'
        const section  = this.state.sections[sectionNum];
        if (!section || oldSide === newValue) return;

        // When rake changes side: always back gate up 1 panel from its current position.
        // This ensures the gate never immediately sits at the new rake end after the switch.
        if (section.hasGate) {
          const existingConfig = this.state.optimizedConfigs[sectionNum];
          const gatePos = section.gatePosition !== undefined ? section.gatePosition : 0;
          const maxPos  = existingConfig ? (existingConfig.regularPanels || 0) : 0;

          // ── W2G + RAKE SIDE CHANGE RULE ───────────────────────────────────
          // If W2G is active and rake is switching TO the same side as W2G → conflict.
          // Revert W2G to G2G and move gate one slot inward, then update UI radios.
          if (section.gateHingeType === 'W2G-REMOVED-2026-08-30' && section.wallGateEnd === newValue) {
            section.gateHingeType = 'glass-to-glass';
            section.wallGateEnd   = null;
            section.gateMaxPos    = undefined;
            section.gatePosition  = 9999; // park during recalc

            // In G2G the hinge must face AWAY from the rake.
            // Rake on right → hinge on left (gateSide='left')
            // Rake on left  → hinge on right (gateSide='right')
            section.gateSide = (newValue === 'right') ? 'left' : 'right';

            // Recalc to get fresh G2G maxPos with corrected gateSide
            delete this.state.optimizedConfigs[sectionNum];
            this.updateSectionDataSilently(sectionNum, 'rakedSide', newValue);
            this.updateCalculationResults(sectionNum);
            const freshCalc = this.state.optimizedConfigs[sectionNum];
            const newMaxPos = freshCalc ? (freshCalc.numPanels ?? freshCalc.regularPanels ?? 0) : 0;

            // Move gate one slot inward from the conflicting wall end
            section.gatePosition = (newValue === 'left') ? 1 : Math.max(0, newMaxPos - 1);

            // Final recalc with correct gate position
            delete this.state.optimizedConfigs[sectionNum];
            this.updateCalculationResults(sectionNum);

            // Update UI — all radios
            const toggle = document.getElementById(`section-${sectionNum}-hinge-mount-toggle`);
            const glassR = document.getElementById(`section-${sectionNum}-gate-hinge-type-glass`);
            const wallR  = document.getElementById(`section-${sectionNum}-gate-hinge-type-wall`);
            const lhsR   = document.getElementById(`section-${sectionNum}-gate-side-left`);
            const rhsR   = document.getElementById(`section-${sectionNum}-gate-side-right`);
            if (toggle) toggle.checked = false;
            if (glassR) glassR.checked = true;
            if (wallR)  wallR.checked  = false;
            if (lhsR)   lhsR.checked   = (section.gateSide === 'left');
            if (rhsR)   rhsR.checked   = (section.gateSide === 'right');

            this.updateQuoteDisplay();
            this.renderSectionPreview(sectionNum);
            this.updateWallToGlassAvailability(sectionNum);
            this.updateHingeBaseDisplay();
            this.trigger3DUpdate();
            return;
          }
          // ── END W2G + RAKE SIDE CHANGE RULE ──────────────────────────────

          if (newValue === 'right') {
            // Rake moving to right — gate backs up left by 1 (away from new right end)
            section.gatePosition = Math.max(0, gatePos - 1);
          } else {
            // Rake moving to left — gate backs up right by 1 (away from new left end)
            section.gatePosition = Math.min(maxPos, gatePos + 1);
          }
          // Reset gateMaxPos so gateAtRakeEnd recalculates cleanly
          section.gateMaxPos = undefined;
        }

        // Update rakedSide and recalculate — gateAtRakeEnd status may have changed.
        this.updateSectionDataSilently(sectionNum, 'rakedSide', newValue);
        delete this.state.optimizedConfigs[sectionNum];
        this.updateCalculationResults(sectionNum);
        this.updateQuoteDisplay();
        this.updateWallToGlassAvailability(sectionNum);
        this.trigger3DUpdate();
      }
    });
  });

    const retainedHeight = form.querySelector(`#section-${sectionNum}-retained-height`);
    if (retainedHeight) {
      retainedHeight.addEventListener('change', (e) => {
        const currentValue = this.state.sections[sectionNum]?.retainedHeight;
        const newValue = e.target.value;
        
        // Only update if the value has actually changed
        if (currentValue !== newValue) {
          this.updateSectionData(sectionNum, 'retainedHeight', newValue);
          this.updateQuoteDisplay();
          
          if (this.state.currentSection === sectionNum) {
            this.renderSectionPreview(sectionNum);
          }
        }
      });
    }
  }

  // Update gate length dropdown based on hinge type
  updateGateLengthDropdown(sectionNum, hingeType) {
    const gateLengthSelect = document.getElementById(`section-${sectionNum}-gate-length`);
    if (!gateLengthSelect) return;

    const currentValue = gateLengthSelect.value;
    gateLengthSelect.innerHTML = this.generateGateLengthOptions(hingeType);
    
    const options = Array.from(gateLengthSelect.options);
    const matchingOption = options.find(option => option.value === currentValue);
    
    if (matchingOption) {
      gateLengthSelect.value = currentValue;
    } else {
      this.updateSectionData(sectionNum, 'gateLength', gateLengthSelect.value);
    }
    
    this.renderSectionPreview(sectionNum);
  }

  // Update hinge color dropdown based on hinge type
  updateHingeColorDropdown(sectionNum, hingeType) {
    const hingeColorSelect = document.getElementById(`section-${sectionNum}-hinge-color`);
    if (!hingeColorSelect) return;

    const currentValue = hingeColorSelect.value;
    
    // Clear existing options
    while (hingeColorSelect.firstChild) {
      hingeColorSelect.removeChild(hingeColorSelect.firstChild);
    }
    
    // Add new options
    const newOptionsHTML = this.generateHingeColorOptions(hingeType);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newOptionsHTML;
    
    Array.from(tempDiv.children).forEach(option => {
      hingeColorSelect.appendChild(option);
    });
    
    // Try to maintain the current selection if it exists in the new options
    const options = Array.from(hingeColorSelect.options);
    const matchingOption = options.find(option => option.value === currentValue);
    
    if (matchingOption) {
      hingeColorSelect.value = currentValue;
    } else {
      // Update the state with the new default value
      this.updateSectionData(sectionNum, 'hingeColor', hingeColorSelect.value);
    }
    
    // Update the preview image
    this.updateHardwarePreviews();
    
    
  }

  // Update latch color dropdown based on latch type
  updateLatchColorDropdown(sectionNum, latchType) {
    
    
    
    const latchColorSelect = document.getElementById(`section-${sectionNum}-latch-color`);
    if (!latchColorSelect) {
      
      return;
    }

    const currentValue = latchColorSelect.value;
    
    
    // Clear existing options
    while (latchColorSelect.firstChild) {
      latchColorSelect.removeChild(latchColorSelect.firstChild);
    }
    
    // Add new options
    const newOptionsHTML = this.generateLatchColorOptions(latchType);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newOptionsHTML;
    
    Array.from(tempDiv.children).forEach(option => {
      latchColorSelect.appendChild(option);
    });
    
    // Try to maintain the current selection if it exists in the new options
    const options = Array.from(latchColorSelect.options);
    const matchingOption = options.find(option => option.value === currentValue);
    
    
    
    if (matchingOption) {
      latchColorSelect.value = currentValue;
      
    } else {
      // Update the state with the new default value
      this.updateSectionData(sectionNum, 'latchColor', latchColorSelect.value);
      
    }
    
    const finalImageUrl = this.getLatchImageUrl(sectionNum);
    
    
    // Update the preview image
    this.updateHardwarePreviews();
    
    
  }

  // Toggle raked panel
  // ── Master Gate UI State Controller ─────────────────────────────────────
  // Enforces the full state flowchart on every gate position change:
  //
  //  Near Rake:    Force G2G, lock LHS/RHS to rake-opposite, disable W2G
  //  At Wall End:  Enable W2G (user choice), enable LHS/RHS
  //  Mid-run:      Disable W2G, enable LHS/RHS, keep G2G
  // ─────────────────────────────────────────────────────────────────────────
  updateWallToGlassAvailability(sectionNum) {
    // Re-entrancy guard — prevent infinite loop from mid-run revert calling updateCalculationResults
    if (this._updatingW2G) return;
    this._updatingW2G = true;
    try {
      this._updateWallToGlassAvailabilityInner(sectionNum);
    } finally {
      this._updatingW2G = false;
    }
  }

  _updateWallToGlassAvailabilityInner(sectionNum) {
    const section = this.state.sections[sectionNum];
    if (!section || !section.hasGate) return;

    const wallR   = document.getElementById(`section-${sectionNum}-gate-hinge-type-wall`);
    const glassR  = document.getElementById(`section-${sectionNum}-gate-hinge-type-glass`);
    const lhsR    = document.getElementById(`section-${sectionNum}-gate-side-left`);
    const rhsR    = document.getElementById(`section-${sectionNum}-gate-side-right`);
    const toggle  = document.getElementById(`section-${sectionNum}-hinge-mount-toggle`);
    if (!wallR) return;

    // Use existing calc if available; only recalculate if none exists.
    // Do NOT force-delete here — applyHingeMountType already runs a fresh calc before
    // calling this function. Force-deleting here triggers an extra recalc that re-enters
    // peterCalculateLayout Patch 1 and can wrongly classify the gate as mid-run.
    let calc = this.state.optimizedConfigs[sectionNum];
    if (!calc) {
      this.updateCalculationResults(sectionNum);
      calc = this.state.optimizedConfigs[sectionNum];
    }
    // Use job cut plan panel count (includes cut panel) for correct maxPos
    const jobPlan2    = this.calculateJobCutPlan();
    const secPlan2    = jobPlan2[sectionNum];
    const maxPos  = secPlan2
      ? secPlan2.panels.filter(p => !p.is_gate && !p.is_hinge).length
      : (calc ? (calc.regularPanels || 0) : 0);
    const gatePos = section.gatePosition !== undefined ? section.gatePosition : 0;
    const rakedSide = section.rakedSide || 'left';

    const gateAtRakeEnd = section.hasRaked && (
      (rakedSide === 'left'  && gatePos <= 0) ||
      (rakedSide === 'right' && gatePos >= maxPos)
    );
    // gateAtWallEnd: gate is flush at a wall end (pos 0 = left wall, pos maxPos = right wall)
    const gateAtWallEnd = !gateAtRakeEnd && (gatePos <= 0 || gatePos >= maxPos);

    const rakeBlocksLeft  = section.hasRaked && rakedSide === 'left';
    const rakeBlocksRight = section.hasRaked && rakedSide === 'right';

    const gateSide = section.gateSide || 'right';

    // Check wall proximity purely by gate position — independent of gateSide.
    // Rake forces gateSide but the FREE opposite wall is always valid for W2G.
    // Near left:  gate+hinge block within 1 slot of left wall
    // Near right: gate+hinge block within 1 slot of right wall
    // Exact wall-end proximity — no ±1 slop (glassWidths now correctly excludes rake from regularPanels).
    // blockTouchesLeft:  gate block is at position 0 (flush to left wall)
    // blockTouchesRight: gate block is at position maxPos (flush to right wall)
    const blockTouchesLeft  = (gatePos <= 0);
    const blockTouchesRight = (gatePos >= maxPos);

    // W2P is available at any wall end that is NOT occupied by the rake.
    // No restriction on current gateHingeType — W2P can be toggled on or off freely.
    const hingeAtOppositeWallEnd = gateAtRakeEnd && (
      (rakedSide === 'left'  && !rakeBlocksRight) ||
      (rakedSide === 'right' && !rakeBlocksLeft)
    );
    const hingeAtWallEnd = (
      (blockTouchesLeft  && !rakeBlocksLeft)  ||
      (blockTouchesRight && !rakeBlocksRight) ||
      hingeAtOppositeWallEnd
    );

    // Also keep toggle checked/enabled when W2G is already active and gate is at wall end
    const w2gActive = section.gateHingeType === 'W2G-REMOVED-2026-08-30' && (
      (section.wallGateEnd === 'left'  && gatePos <= 0)  ||
      (section.wallGateEnd === 'right' && gatePos >= maxPos)
    );

    // At rake end: force G2G, auto-set gate side to match rake side, disable W2G toggle.
    // EXCEPTION: if the hinge panel is at the OPPOSITE wall end (hingeAtWallEnd for the other side),
    // keep the toggle enabled so the user can switch to W2G on the non-rake side.
    if (gateAtRakeEnd) {
      // Gate is at the rake end — force G2G on that end, gate side matches rake
      if (section.gateHingeType === 'W2G-REMOVED-2026-08-30') {
        section.gateHingeType = 'glass-to-glass';
        section.wallGateEnd   = null;
        section.gateMaxPos    = undefined;
        if (glassR) { glassR.checked = true; wallR.checked = false; }
        if (toggle) toggle.checked = false;
      }
      // Latch faces the rake — hinge is on the OPPOSITE side to the rake.
      const rakeSide = rakedSide === 'left' ? 'left' : 'right';
      const hingeAwayFromRake = rakeSide === 'left' ? 'right' : 'left';
      section.gateSide = hingeAwayFromRake;
      if (lhsR) { lhsR.checked = hingeAwayFromRake === 'left'; lhsR.disabled = true; }
      if (rhsR) { rhsR.checked = hingeAwayFromRake === 'right'; rhsR.disabled = true; }
      // W2P toggle: enable only if the OPPOSITE wall end is free (not rake end)
      if (hingeAtWallEnd) {
        wallR.disabled = false;
        if (toggle) { toggle.disabled = false; }
      } else {
        wallR.disabled = true;
        if (toggle) { toggle.disabled = true; toggle.checked = false; }
      }
      this.updateHingeBaseDisplay();
      return;
    }

    if (lhsR) lhsR.disabled = false;
    if (rhsR) rhsR.disabled = false;

    // At wall end, hinge at wall end, or W2G already active at wall end: enable toggle
    if (gateAtWallEnd || hingeAtWallEnd || w2gActive) {
      wallR.disabled = false;
      if (toggle) {
        toggle.disabled = false;
        toggle.checked = (section.gateHingeType === 'W2G-REMOVED-2026-08-30');
      }
      return;
    }

    // Mid-run: disable toggle, revert W2G to G2G if active
    wallR.disabled = true;
    if (toggle) { toggle.disabled = true; toggle.checked = false; }
    if (section.gateHingeType === 'W2G-REMOVED-2026-08-30') {
      section.gateHingeType = 'glass-to-glass';
      section.wallGateEnd   = null;
      section.gateMaxPos    = undefined;
      if (glassR) { glassR.checked = true; wallR.checked = false; }
      delete this.state.optimizedConfigs[sectionNum];
      this.updateCalculationResults(sectionNum);
      this.renderSectionPreview(sectionNum);
    }
    this.updateHingeBaseDisplay();
  }  // end _updateWallToGlassAvailabilityInner


  toggleRakedPanel(sectionNum, checked) {
    const fields = this.domCache.get(`section-${sectionNum}-raked-fields`);
    if (fields) {
      fields.classList.toggle('hidden', !checked);
    }
    
    if (this.state.sections[sectionNum]) {
      this.state.sections[sectionNum].hasRaked = checked;
      
      if (checked) {
        // RULE: Rake panels always go to ends only (left or right) — never middle
        if (!this.state.sections[sectionNum].rakedSide) {
          this.state.sections[sectionNum].rakedSide = 'left';
        }
        // Ensure rakedSide is always 'left' or 'right'
        const rakedSide = this.state.sections[sectionNum].rakedSide;
        if (rakedSide !== 'left' && rakedSide !== 'right') {
          this.state.sections[sectionNum].rakedSide = 'left';
        }

        const leftRadio = document.querySelector(`input[name="section-${sectionNum}-raked-side"][value="${this.state.sections[sectionNum].rakedSide}"]`);
        if (leftRadio) leftRadio.checked = true;

        // AUTO-FIND optimal sheet size when rake is added.
        // IMPORTANT: read fenceHeight FIRST so panelLength is correct before any calc runs.
        const _rakeSection = this.state.sections[sectionNum];
        const _rakeFenceH  = _rakeSection.fenceHeight || '1200';
        if (_rakeSection.hasGate) {
          // Gate+rake: find optimal sheet for this height
          _rakeSection.panelLength = this.findOptimalSheetForGate(sectionNum);
        } else {
          // Rake only: set height-correct panel width immediately
          _rakeSection.panelLength = this.getPanelLengthForHeight(_rakeFenceH);
        }
        // Clear cache NOW so updateCalculationResults uses the correct panelLength
        delete this.state.optimizedConfigs[sectionNum];

        // ── W2G + RAKE RULE ─────────────────────────────────────────────────
        // Rake and W2G only conflict when on the SAME end.
        //   Same end  → revert W2G to G2G, move gate one slot inward.
        //   Other end → W2G stays untouched, rake coexists on the opposite end.
        const section = this.state.sections[sectionNum];
        if (section.hasGate && section.gateHingeType === 'W2G-REMOVED-2026-08-30') {
          const wallEnd    = section.wallGateEnd; // 'left' or 'right'
          const newRakeSide = section.rakedSide || 'left';

          if (wallEnd === newRakeSide) {
            // Conflict — same end: revert W2G to G2G
            section.gateHingeType = 'glass-to-glass';
            section.wallGateEnd   = null;
            section.gateMaxPos    = undefined;
            section.gatePosition  = 9999; // park off-screen during recalc

            delete this.state.optimizedConfigs[sectionNum];
            this.updateCalculationResults(sectionNum);
            const freshCalc = this.state.optimizedConfigs[sectionNum];
            const newMaxPos = freshCalc ? (freshCalc.numPanels ?? freshCalc.regularPanels ?? 0) : 0;

            // Move gate one slot inward from the conflicting wall end
            section.gatePosition = (wallEnd === 'left') ? 1 : Math.max(0, newMaxPos - 1);

            const toggle = document.getElementById(`section-${sectionNum}-hinge-mount-toggle`);
            const glassR = document.getElementById(`section-${sectionNum}-gate-hinge-type-glass`);
            const wallR  = document.getElementById(`section-${sectionNum}-gate-hinge-type-wall`);
            if (toggle) toggle.checked = false;
            if (glassR) glassR.checked = true;
            if (wallR)  wallR.checked  = false;
            this.updateHingeBaseDisplay();
          }
          // Opposite end: W2G stays — rake and W2G coexist on different ends
        }
        // ── END W2G + RAKE RULE ─────────────────────────────────────────────
      }
      
      // Save gate position before recalculation so it never moves when rake is toggled
      const savedGatePos = this.state.sections[sectionNum].gatePosition;
      // Reset gateMaxPos so gateAtRakeEnd is recalculated cleanly after rake toggle
      this.state.sections[sectionNum].gateMaxPos = undefined;

      delete this.state.optimizedConfigs[sectionNum];
      this.updateQuoteDisplay();
      this.updateCalculationResults(sectionNum);

      // Restore gate position after recalculation, then clamp to new maxPos.
      // Without clamping, a stale position (e.g. 9999 or old regularPanels count)
      // causes w2gActive to evaluate false in updateWallToGlassAvailability,
      // which silently disables the W2G toggle even when gate is still at the right wall end.
      if (savedGatePos !== undefined && this.state.sections[sectionNum].hasGate) {
        const freshCalcForClamp = this.state.optimizedConfigs[sectionNum];
        // numPanels includes filler panel = matches strip length in renderSectionPreview
        const clampedMaxPos = freshCalcForClamp ? (freshCalcForClamp.numPanels ?? freshCalcForClamp.regularPanels ?? 0) : 0;
        this.state.sections[sectionNum].gatePosition = Math.min(savedGatePos, clampedMaxPos);
      }

      if (this.state.currentSection === sectionNum) {
        this.renderSectionPreview(sectionNum);
      }
      this.updateWallToGlassAvailability(sectionNum);
      
      // Update 3D preview
      this.trigger3DUpdate();
    }
  }

  // Toggle clamp
  toggleClamp(sectionNum, checked) {
    const fields = document.getElementById(`section-${sectionNum}-clamp-fields`);
    if (fields) {
      fields.classList.toggle('hidden', !checked);
    }
    
    if (this.state.sections[sectionNum]) {
      const section = this.state.sections[sectionNum];
      section.hasClamp = checked;
      
      if (checked) {
        const clampColorSelect = document.getElementById(`section-${sectionNum}-clamp-color`);
        const clampPositionSelect = document.getElementById(`section-${sectionNum}-clamp-position`);
        
        if (clampColorSelect && clampColorSelect.value) {
          section.clampColor = clampColorSelect.value;
        }
        
        if (clampPositionSelect && clampPositionSelect.value) {
          section.clampPosition = clampPositionSelect.value;
        }
      }
      
      this.updateQuoteDisplay();
      this.updateComponentsQuote();
      
      // Update hardware previews
      this.updateHardwarePreviews();
    }
  }

  // Toggle gate
  toggleGate(sectionNum, checked) {
    // ── Gate parameter validation ─────────────────────────────────────────
    // W2P: gate bolts to wall — only 1 post needed on the free side → min = gate + 1 post
    // P2P: gate needs a post on each side → min = gate + 2 posts
    if (checked) {
      const section = this.state.sections[sectionNum];
      const sectionLength = parseInt(section?.length || 0);
      // 2026-08-30 (Pete): aluminium minimum = gate + 2 posts + 30mm hinge/latch clearance.
      // Aluminium gates CANNOT be wall-hinged - no wall-mount hinge is sold - so it is always 2 posts.
      const POST_SIZE      = 50;
      const GATE_CLEARANCE = 30;
      const gateW          = parseInt(section?.gateLength) || 920;
      const RAKE_W         = section?.hasRaked ? (this.getRakedPanelWidth ? this.getRakedPanelWidth() : 0) : 0;
      const RAKE_POST      = section?.hasRaked ? POST_SIZE : 0;
      const minRequired    = gateW + RAKE_W + (2 * POST_SIZE) + RAKE_POST + GATE_CLEARANCE;   // 920 -> 1050
      // 2026-08-30 (Pete): "make all calculators snap up to minimum". Rather than refusing a short
      // run, PROBE the solver for the smallest length it can actually build and set the section to
      // it. Probing (not re-deriving) is deliberate: every gate defect found this session came from
      // a validator formula that had drifted away from the solver's real rules.
      // This also closes the dead band where the validator accepted a length the solver could not
      // draw, because the snap target is by definition drawable.
      let minBuildable = null;
      try {
        const probe = Object.assign({}, section, { hasGate: true });
        for (let L = minRequired; L <= minRequired + 900; L++) {
          probe.length = String(L);
          if (this.guaranteedSolve(probe)) { minBuildable = L; break; }
        }
      } catch (e) { minBuildable = null; }

      if (minBuildable && sectionLength < minBuildable) {
        this.updateSectionData(sectionNum, 'length', String(minBuildable));
        const lenInput = document.getElementById(`section-${sectionNum}-length`);
        if (lenInput) lenInput.value = String(minBuildable);
        this.showValidationPopup(
          `Your section was ${sectionLength}mm, which is too short for a ${gateW}mm gate.\n\n` +
          `We have set it to ${minBuildable}mm — this is the minimum build that will work for your section.`
        );
      } else if (!minBuildable) {
        const gateToggle = document.getElementById(`section-${sectionNum}-gate`);
        if (gateToggle) gateToggle.checked = false;
        this.showValidationPopup(
          `We could not find a workable layout for a ${gateW}mm gate near ${sectionLength}mm.\n\n` +
          `Send us a drawing on the "Send us your plans" page and we will advise.`
        );
        return;
      }
    }
    // ── End gate validation ───────────────────────────────────────────────

    const fields = document.getElementById(`section-${sectionNum}-gate-fields`);
    if (fields) {
      fields.classList.toggle('hidden', !checked);
    }

    // Show/hide gate orientation radio row together with gate fields
    const orientationRow = document.getElementById(`section-${sectionNum}-gate-orientation-row`);
    if (orientationRow) {
      orientationRow.classList.toggle('hidden', !checked);
      // When turning gate ON, set the preview image to match current selection
      if (checked) {
        const currentSide = this.state.sections[sectionNum]?.gateSide || 'right';
        // Ensure the correct radio is checked
        const checkedRadio = document.getElementById(`section-${sectionNum}-gate-side-${currentSide}`);
        if (checkedRadio) checkedRadio.checked = true;
        this.updateGateSideImage(sectionNum, currentSide);
      }
    }
    
    if (this.state.sections[sectionNum]) {
      const section = this.state.sections[sectionNum];
      section.hasGate = checked;
      
      if (checked) {
        const gateLengthSelect = document.getElementById(`section-${sectionNum}-gate-length`);
        if (gateLengthSelect && gateLengthSelect.value) {
          section.gateLength = gateLengthSelect.value;
        } else {
          section.gateLength = this.getDefaultGateLength('soft-close').toString();
        }
        
        // Default gate hinge to opposite side of rake (rake=latch side, hinge=opposite).
        // If no rake, default LHS hinge (Peter's rule).
        const _defaultRakedSide = section.hasRaked ? (section.rakedSide || 'left') : null;
        section.gateSide = _defaultRakedSide === 'left' ? 'right' : (_defaultRakedSide === 'right' ? 'left' : 'left');
        section.gateHingeType = section.gateHingeType || 'glass-to-glass';
        // Sync hingeBase checkbox to saved state
        const hingeBaseEl = document.getElementById(`section-${sectionNum}-hinge-base`);
        if (hingeBaseEl) hingeBaseEl.checked = !!section.hingeBase;
        section.wallGateEnd = null;
        if (section.gateHingeType === 'W2G-REMOVED-2026-08-30') {
          section.gateSide = section.gateSide || 'right';
        }
        // Default gate position:
        // If rake present → place gate at the rake end so it latches immediately
        // If no rake → place at middle (9999 sentinel resolved after first calc)
        if (section.hasRaked) {
          const rakeSide = section.rakedSide || 'left';
          section.gatePosition = rakeSide === 'left' ? 0 : 9999; // 0=left end, 9999=right end sentinel
          section._placeAtMiddle = false;
        } else {
          section.gatePosition = 9999; // sentinel — resolved to Math.floor(maxPos/2) after first calc
          section._placeAtMiddle = true;
        }
        // Reset gateMaxPos so gateAtRakeEnd cannot misfire on first calc when gate is enabled.
        section.gateMaxPos = undefined;
        // AUTO-FIND optimal sheet size: find the smallest sheet that gives ≥500mm filler
        // This removes the need for a separate cut panel when gate is present
        const optimalSheet = this.findOptimalSheetForGate(sectionNum);
        section.panelLength = optimalSheet;
        const _panelSelGate = document.getElementById(`section-${sectionNum}-panel`);
        if (_panelSelGate) _panelSelGate.value = '2400';
        // Sync the RHS radio button to match
        const rhsRadio = document.getElementById(`section-${sectionNum}-gate-side-right`);
        if (rhsRadio) rhsRadio.checked = section.gateSide === 'right';
        const lhsRadio = document.getElementById(`section-${sectionNum}-gate-side-left`);
        if (lhsRadio) lhsRadio.checked = section.gateSide === 'left';
        const hingeGlassRadio = document.getElementById(`section-${sectionNum}-gate-hinge-type-glass`);
        const hingeWallRadio = document.getElementById(`section-${sectionNum}-gate-hinge-type-wall`);
        if (hingeGlassRadio) hingeGlassRadio.checked = section.gateHingeType === 'glass-to-glass';
        if (hingeWallRadio) hingeWallRadio.checked = section.gateHingeType === 'W2G-REMOVED-2026-08-30';

        // State is authoritative. Only adopt the live dropdown when state is unset
        // (fresh gate), otherwise push the saved choice back onto the select so a
        // rebuilt/defaulted dropdown never clobbers the customer's hinge selection.
        const hingeTypeSelectEl = document.getElementById(`section-${sectionNum}-hinge-color`);
        if (!section.hingeType) {
          section.hingeType = (hingeTypeSelectEl && hingeTypeSelectEl.value) || 'SHG-90L';
        }
        if (hingeTypeSelectEl) hingeTypeSelectEl.value = section.hingeType;
        // Sync legacy radio buttons
        const htSoftClose = document.getElementById(`section-${sectionNum}-hinge-type-soft-close`);
        const htSpring    = document.getElementById(`section-${sectionNum}-hinge-type-spring`);
        if (htSoftClose) htSoftClose.checked = (section.hingeType !== 'spring');
        if (htSpring)    htSpring.checked    = (section.hingeType === 'spring');
        
        // Sync latchType from the dropdown — always read the live DOM value so
        // the selected option (e.g. 'SL-50H') is stored rather than a stale default.
        const latchTypeSelect = document.getElementById(`section-${sectionNum}-latch-type`);
        if (!section.latchType) {
          section.latchType = (latchTypeSelect && latchTypeSelect.value) || 'SL-50K'; // default: Magna Latch Keyless
        }
        if (latchTypeSelect) latchTypeSelect.value = section.latchType;
        
        // Initialize latch color with default
        const latchColorSelect = document.getElementById(`section-${sectionNum}-latch-color`);
        if (latchColorSelect && latchColorSelect.value) {
          section.latchColor = latchColorSelect.value;
        }
      }
      
      // When gate is turned OFF, reset its state so next enable starts fresh
      if (!checked) {
        section.gateSide     = 'right';
        section.gatePosition = 9999;
        section.gateMaxPos   = undefined;
        section.wallGateEnd  = null;
        section.gateMoved    = false; // reset — cut panel only shows after user moves gate
        delete section.overrideCutMm;
      }

      this.removeOptimizationIndicators(sectionNum);
      delete this.state.optimizedConfigs[sectionNum];
      
      if (section.length && parseInt(section.length) >= CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH) {
        this.updateCalculationResults(sectionNum);

        // After calculation, read back the recommended sheet the solver resolved.
        // Cascade may have moved from 1000 → 2000 → 3000mm to satisfy the ≥500mm
        // remainder rule. Sync section.panelLength and the dropdown so the preview
        // and quote both show the recommended sheet, not the 1000mm starting placeholder.
        if (checked && section.hasGate) {
          const resolvedCalc = this.state.optimizedConfigs[sectionNum];
          if (resolvedCalc && resolvedCalc.panelWidth) {
            const recommendedSheet = String(resolvedCalc.panelWidth);
            section.panelLength = recommendedSheet;
            const _panelSelResolved = document.getElementById(`section-${sectionNum}-panel`);
            if (_panelSelResolved) _panelSelResolved.value = '2400';
          }
        }

        this.updateQuoteDisplay();
        
        if (this.state.currentSection === sectionNum) {
          this.renderSectionPreview(sectionNum);
        }
        
        // Update 3D preview
        this.trigger3DUpdate();
      }
      
      // Update hardware previews when gate is toggled
      this.updateHardwarePreviews();
    }
  }

  // Update section data
  updateSectionData(sectionNum, field, value) {
    if (!this.state.sections[sectionNum]) {
      this.state.sections[sectionNum] = {};
    }
    this.state.sections[sectionNum][field] = value;
    
    // Clear optimized config when user changes values
    // Save gatePosition first so it survives recalculation
    if (field === 'length' || field === 'panelLength' || field === 'gapSpacing' || field === 'panelMode') {
      const savedGatePos = this.state.sections[sectionNum].gatePosition;
      delete this.state.optimizedConfigs[sectionNum];
      this.updateQuoteDisplay();
      this.updateCalculationResults(sectionNum);
      // Restore gatePosition after recalc — clamp happens in renderSectionPreview
      if (savedGatePos !== undefined && this.state.sections[sectionNum]?.hasGate) {
        this.state.sections[sectionNum].gatePosition = savedGatePos;
      }
      if (field === 'length') {
        this.updateSectionTabState(sectionNum);
      }
      if (['length', 'hasGate', 'hasRaked', 'gateLength'].includes(field)) {
        try { this.update3DPreview(); } catch(e) {}
      }
      return;
    }
    
    this.updateQuoteDisplay();
    this.updateCalculationResults(sectionNum);
    
    if (field === 'length') {
      this.updateSectionTabState(sectionNum);
    }
    
    // Update 3D preview if values that affect layout change
    if (['length', 'hasGate', 'hasRaked', 'gateLength'].includes(field)) {
      try { this.update3DPreview(); } catch(e) {}
    }
  }

  // Update section data without triggering calculations
  updateSectionDataSilently(sectionNum, field, value) {
    if (!this.state.sections[sectionNum]) {
      this.state.sections[sectionNum] = {};
    }
    this.state.sections[sectionNum][field] = value;
  }

  // Show info panel
  showInfo(infoType) {
    const infoTexts = {
      sectionLength: this.shopifyData?.settings?.infoTexts?.sectionLength || 
        "Enter the full length of this section measured from centreline to centreline. Exclude any gates or openings.",
      sectionHeight: "Select the fence panel height. 1200mm standard (2400mm wide panels). 1500mm uses 1825mm wide panels. 1800mm uses 1632mm wide panels.",
      panelLength: this.shopifyData?.settings?.infoTexts?.panelLength || 
        "Blade panels come in a fixed width for each height — 2400mm wide at 1200mm high, 1825mm at 1500mm, 1632mm at 1800mm. The calculator works out the best panel layout for your section length.",
      gapSpacing: this.shopifyData?.settings?.infoTexts?.gapSpacing || 
        "Select the post profile size. This is the width of the steel post used between panels."
    };
    
    const infoPanel = this.domCache.get('section-info');
    if (infoPanel && infoTexts[infoType]) {
      infoPanel.innerHTML = `<div class="info-content"><p>${infoTexts[infoType]}</p></div>`;
    }
  }

  // Calculate optimal panels
  calculateOptimalPanels(section, prioritizeGap = false, prioritizePanel = false) {
    const sectionLength = parseInt(section.length);
    // User is the boss — use exactly what they selected in the dropdowns
    const preferredPanelLength = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
    const preferredGapSpacing  = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
    const maxVariance = this.shopifyData.settings?.maxPanelVariance || CONFIG.DEFAULTS.MAX_PANEL_VARIANCE;
    
    if (sectionLength < CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH || sectionLength > CONFIG.CONSTRAINTS.MAX_SECTION_LENGTH) {
      console.warn(`calculateOptimalPanels: Section length ${sectionLength}mm is out of range`);
      return null;
    }

    // Calculate fixed special panel space (raked only, gates are handled as variable panels)
    let fixedSpecialWidth = 0;
    let specialPanelComponents = [];
    let gateLength = 0;
    
    if (section.hasGate && section.gateLength) {
      gateLength = parseInt(section.gateLength);
      specialPanelComponents.push(`Gate: ${gateLength}mm`);
      
    }
    
    if (section.hasRaked) {
      const rakedWidth = this.getRakedPanelWidth(section.fenceHeight);
      fixedSpecialWidth += rakedWidth;
      specialPanelComponents.push(`Raked Panel: ${rakedWidth}mm`);
    }


    const availableSpaceForVariablePanels = sectionLength - fixedSpecialWidth;
    

    // Step 1: Calculate minimum number of panels needed
    const minPanels = Math.floor(availableSpaceForVariablePanels / preferredPanelLength);
    
    
    if (minPanels < 1) {
      console.warn(`calculateOptimalPanels: Not enough space for even one ${preferredPanelLength}mm panel in ${availableSpaceForVariablePanels}mm available space (section: ${sectionLength}mm, fixed: ${fixedSpecialWidth}mm)`);
      return null; // Not enough space for even one panel
    }

    // ── Deterministic solver — derive panel count via solveFenceLayout ──────
    // PanelCalculator.solveFenceLayout places fixed elements first (rake, gate),
    // fills panels left → right, then places the cut panel at the right end only.
    // postSize = 50mm is included in the accounting so the panel count is exact.
    //
    // FLEXIBLE MERGE: if maxSheetLength is set, the filler is absorbed into the
    // last panel that falls below max, avoiding a separate cut panel.
    const solverResult = PanelCalculator.solveFenceLayout({
      total:     sectionLength,
      panelSize: preferredPanelLength,
      postSize:  50,
      gateSize:  gateLength || (section.gateLength ? parseInt(section.gateLength) : 920),
      hasGate:   !!(section.hasGate && section.gateLength),
      hasRake:   !!section.hasRaked,
      rakeSize:  this.getRakedPanelWidth(section.fenceHeight),
      maxSheetLength: preferredPanelLength  // Enable flexible merge: filler merges into last panel below max
    });

    // solverResult.panelCount = full regular panels (excludes gate/rake fixed elements).
    // solverResult.isFlexed = true if filler was merged into a panel (cut panel removed).
    // Add 1 for the hinge panel when a gate is present (glass-to-glass), matching the
    // existing buildPanelConfiguration convention.
    let numPanels = solverResult.panelCount;
    if (section.hasGate && section.gateLength) {
      numPanels = Math.max(1, numPanels) + 1; // +1 for hinge panel slot
    }
    numPanels = Math.max(1, numPanels);

    // Build the configuration directly with user's exact values
    let bestConfig = this.buildPanelConfiguration(
      preferredPanelLength,
      numPanels,
      preferredGapSpacing,
      section,
      specialPanelComponents,
      sectionLength,
      availableSpaceForVariablePanels
    );

    if (!bestConfig) {
      console.warn(`calculateOptimalPanels: Could not build config. Sheet: ${preferredPanelLength}mm, post: ${preferredGapSpacing}mm, section: ${sectionLength}mm`);
      return null;
    }
    
    
    
    
    
    
    
    
    
    
    // Calculate panel breakdown for the new algorithm
    const panelBreakdown = this.calculateSimplePanelBreakdown(bestConfig.numVariablePanels, section, bestConfig.panelWidth);
    
    // Always try variable panel/gap refinement to minimize remaining space
    const maxGapVariance = this.shopifyData.settings?.maxGapVariance || CONFIG.DEFAULTS.MAX_GAP_VARIANCE;
    const maxCalcPanelVariance = this.shopifyData.settings?.maxCalculatedPanelVariance || CONFIG.DEFAULTS.MAX_CALCULATED_PANEL_VARIANCE;
    const preferredGap = preferredGapSpacing;
    
    // Try variable refinement to achieve better fit (closer to preferred gap or smaller remaining space)
    const effectivePanelVariance = prioritizePanel ? 0 : maxCalcPanelVariance;
    const variableResult = this.tryVariablePanelConfiguration(
      sectionLength, bestConfig, section, preferredPanelLength, preferredGap,
      effectivePanelVariance, maxGapVariance
    );
    if (variableResult) {
      bestConfig = variableResult;
    }

    // Ensure panelWidths and gapSpacings arrays exist
    const panelWidths = bestConfig.panelWidths || Array(panelBreakdown.regularPanels).fill(bestConfig.panelWidth);
    const gapSpacings = bestConfig.gapSpacings || Array(bestConfig.totalGaps).fill(bestConfig.gapSpacing);

    return {
      panelWidth: bestConfig.panelWidth,
      gapSpacing: bestConfig.gapSpacing,
      panelWidths: panelWidths,
      gapSpacings: gapSpacings,
      numPanels: panelBreakdown.regularPanels + panelBreakdown.hingePanels,
      totalPanels: bestConfig.totalPanelCount,
      regularPanels: panelBreakdown.regularPanels,
      hingePanels: panelBreakdown.hingePanels,
      gatePanels: panelBreakdown.gatePanels,
      rakedPanels: panelBreakdown.rakedPanels,
      specialPanelComponents,
      originalSectionLength: sectionLength,
      totalGaps: bestConfig.totalGaps,
      totalGapWidth: bestConfig.totalGapWidth,
      totalPanelWidth: bestConfig.variablePanelWidth,
      remaining: bestConfig.remaining,
      variance: bestConfig.variance || 0,
      gapVariance: bestConfig.gapVariance || 0,
      withinVarianceLimit: bestConfig.withinVarianceLimit,
      optimized: bestConfig.optimized || false,
      optimizationType: bestConfig.optimizationType,
      score: bestConfig.score
    };

  }

  // Generate panel sizes
  generatePanelSizes(preferredLength) {
    const maxVariance = this.shopifyData.settings?.maxPanelVariance || CONFIG.DEFAULTS.MAX_PANEL_VARIANCE;
    
    const poolPanel = this.getActiveMeshSheet();
    
    // Always prioritize Shopify product data
    if (poolPanel && poolPanel.variants && poolPanel.variants.length > 0) {
      const allAvailableSizes = VariantHelper.getAvailableSizes(poolPanel);
      
      // Sort by distance from preferred length with weighting
      const sizesWithWeight = allAvailableSizes.map(size => {
        const variance = Math.abs(size - preferredLength);
        // Calculate weight - closer to preferred = higher weight
        const weight = variance <= maxVariance 
          ? 1 - (variance / maxVariance) // Linear weight from 1 to 0
          : 0; // Outside variance gets 0 weight
        
        return { size, variance, weight };
      });
      
      // Sort by weight (descending) then by variance (ascending)
      sizesWithWeight.sort((a, b) => {
        if (b.weight !== a.weight) return b.weight - a.weight;
        return a.variance - b.variance;
      });
      
      // Return sizes that have weight > 0 (within variance)
      const validSizes = sizesWithWeight
        .filter(item => item.weight > 0)
        .map(item => item.size);
      
      if (validSizes.length === 0) {
        // Return the closest size even if outside variance
        return [sizesWithWeight[0].size];
      }
      
      return validSizes;
    }
    
    // Fallback only if no Shopify data
    const fallbackSizes = new Set();
    fallbackSizes.add(preferredLength);
    
    for (let offset = 50; offset <= maxVariance; offset += 50) {
      if (preferredLength + offset <= CONFIG.CONSTRAINTS.MAX_PANEL_WIDTH) {
        fallbackSizes.add(preferredLength + offset);
      }
      if (preferredLength - offset >= CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH) {
        fallbackSizes.add(preferredLength - offset);
      }
    }
    
    return Array.from(fallbackSizes).sort((a, b) => {
      const distA = Math.abs(a - preferredLength);
      const distB = Math.abs(b - preferredLength);
      return distA - distB;
    });
  }

  generateGapSpacings(preferredGap) {
    // Post sizes — fixed options only, no free range
    const validPostSizes = [50, 65, 75];
    // Return sorted by closeness to preferred
    return validPostSizes.sort((a, b) => Math.abs(a - preferredGap) - Math.abs(b - preferredGap));
  }


  // Add optimization indicators
  addOptimizationIndicators(sectionNum, calculation) {
    this.removeOptimizationIndicators(sectionNum);
    
    if (!calculation.optimized) return;

    if (calculation.optimizationType === 'panel-size-increased' || calculation.optimizationType === 'both-optimized') {
      const panelSelect = this.domCache.get(`section-${sectionNum}-panel`);
      if (panelSelect) {
        this.addOptimizedIndicator(panelSelect, 'Panel size increased to utilize space');
      }
    }

    if (calculation.optimizationType === 'gap-spacing-increased' || calculation.optimizationType === 'both-optimized') {
      const gapSelect = this.domCache.get(`section-${sectionNum}-gap`);
      if (gapSelect) {
        this.addOptimizedIndicator(gapSelect, 'Gap spacing increased to utilize space');
      }
    }
  }

  // Add optimized indicator
  addOptimizedIndicator(element, tooltip) {
    this.removeOptimizedIndicator(element);
    
    const indicator = document.createElement('span');
    indicator.className = 'optimization-indicator';
    indicator.innerHTML = '✨';
    indicator.style.cssText = `
      margin-left: 5px;
      color: #7c3aed;
      font-size: 14px;
      cursor: help;
      animation: pulse 2s infinite;
    `;
    indicator.title = tooltip;
    
    element.parentNode.insertBefore(indicator, element.nextSibling);
  }

  // Remove optimization indicator
  removeOptimizedIndicator(element) {
    const existingIndicator = element.parentNode.querySelector('.optimization-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }
  }

  // Remove all optimization indicators
  removeOptimizationIndicators(sectionNum) {
    const panelSelect = this.domCache.get(`section-${sectionNum}-panel`);
    const gapSelect = this.domCache.get(`section-${sectionNum}-gap`);
    
    if (panelSelect) this.removeOptimizedIndicator(panelSelect);
    if (gapSelect) this.removeOptimizedIndicator(gapSelect);
  }

  // Handle gap select blur event - remove optimized values and select closest existing
  handleGapSelectBlur(sectionNum, gapSelect) {
    const currentValue = parseInt(gapSelect.value);

    // Remove transient options added by the solver (optimized / solved)
    Array.from(gapSelect.options)
      .filter(o => o.textContent.includes('(optimized)') || o.textContent.includes('(solved)'))
      .forEach(o => o.remove());

    const availableValues = Array.from(gapSelect.options).map(o => parseInt(o.value));

    if (!availableValues.includes(currentValue)) {
      const closestValue = availableValues.reduce((closest, cur) =>
        Math.abs(cur - currentValue) < Math.abs(closest - currentValue) ? cur : closest
      );
      gapSelect.value = closestValue.toString();
      if (this.state.sections[sectionNum]) {
        this.state.sections[sectionNum].gapSpacing = closestValue.toString();
        delete this.state.optimizedConfigs[sectionNum];
        this.removeOptimizationIndicators(sectionNum);
        this.updateCalculationResults(sectionNum);
        // Re-sync panel dropdown after gap snapped to closest standard value
        this.syncPanelDropdown(sectionNum);
      }
    }
  }

  // PerfoMesh: post size is a fixed dropdown — no sync needed
  syncGapDropdown(sectionNum) {}

  // PerfoMesh: sheet length is a fixed dropdown — no sync needed
  syncPanelDropdown(sectionNum) {}

  // Get available panel sizes sorted by preference
  getAvailablePanelSizes(preferredLength) {
    const poolPanel = this.getActiveMeshSheet();
    
    
    
    
    if (poolPanel && poolPanel.variants) {
      
      
      
      // Try different property names that might contain the length
      const availableSizes = [];
      poolPanel.variants.forEach((v, index) => {
        
        
        let length = null;
        // Try different possible property names
        if (v.length) length = parseInt(v.length);
        else if (v.size) length = parseInt(v.size);
        else if (v.width) length = parseInt(v.width);
        else if (v.title && v.title.match(/(\d+)mm/)) {
          const match = v.title.match(/(\d+)mm/);
          length = parseInt(match[1]);
        }
        
        if (length && !isNaN(length) && length >= 100 && length <= CONFIG.CONSTRAINTS.MAX_PANEL_WIDTH) {
          availableSizes.push(length);
        }
      });

      // 
      
      if (availableSizes.length > 0) {
        const sortedSizes = this.sortPanelSizesByPreference([...new Set(availableSizes)], preferredLength);
        // 
        return sortedSizes;
      }
    }
    
    // Fallback to generated sizes
    
    const sizes = [];
    for (let i = 100; i <= CONFIG.CONSTRAINTS.MAX_PANEL_WIDTH; i += 50) {
      sizes.push(i);
    }
    const sortedSizes = this.sortPanelSizesByPreference(sizes, preferredLength);
    
    return sortedSizes;
  }

  // Sort panel sizes by preference (preferred first, then closest)
  sortPanelSizesByPreference(sizes, preferredLength) {
    return sizes.sort((a, b) => {
      if (a === preferredLength) return -1;
      if (b === preferredLength) return 1;
      return Math.abs(a - preferredLength) - Math.abs(b - preferredLength);
    });
  }

  // Try a specific panel configuration
  tryPanelConfiguration(availableSpace, numPanels, panelSizes, preferredLength, section, specialPanelComponents, sectionLength, prioritizeGap = false, preferredGapSpacing = null, prioritizePanel = false) {

    // If prioritizing panel width, lock to the preferred panel size first
    if (prioritizePanel) {
      const panelSize = preferredLength;
      let totalPanelWidth, actualNumRegularPanels;
      if (section.hasGate && section.gateLength) {
        actualNumRegularPanels = Math.max(0, numPanels - 1);
        totalPanelWidth = (actualNumRegularPanels * panelSize) + parseInt(section.gateLength);
      } else {
        actualNumRegularPanels = numPanels;
        totalPanelWidth = panelSize * numPanels;
      }
      const remainingSpace = availableSpace - totalPanelWidth;
      let numGaps = numPanels;
      if (section.hasRaked) numGaps += 1;
      const roundedGapSize = Math.round(remainingSpace / numGaps);
      if (roundedGapSize >= CONFIG.CONSTRAINTS.MIN_GAP && roundedGapSize <= CONFIG.CONSTRAINTS.MAX_GAP) {
        return this.buildPanelConfiguration(panelSize, numPanels, roundedGapSize, section, specialPanelComponents, sectionLength, availableSpace);
      }
      // Fall through to normal logic if gap out of valid range
    }

    for (let panelSize of panelSizes) {
      // Calculate panel width accounting for gate replacement
      let totalPanelWidth;
      let actualNumRegularPanels;
      
      if (section.hasGate && section.gateLength) {
        // Gate replaces one regular panel
        actualNumRegularPanels = Math.max(0, numPanels - 1);
        const gateLength = parseInt(section.gateLength);
        totalPanelWidth = (actualNumRegularPanels * panelSize) + gateLength;
      } else {
        actualNumRegularPanels = numPanels;
        totalPanelWidth = panelSize * numPanels;
      }
      
      // Calculate how many gaps we need (panels + 1 for raked panel if present)
      let numGaps = numPanels;
      if (section.hasRaked) numGaps += 1;

      // If user specified a preferred gap, use it directly — don't calculate from remainder
      let roundedGapSize;
      if (preferredGapSpacing) {
        roundedGapSize = preferredGapSpacing;
      } else {
        const remainingSpace = availableSpace - totalPanelWidth;
        roundedGapSize = Math.round(remainingSpace / numGaps);
      }
      
      
      
      // Check if gap size is within acceptable range
      if (roundedGapSize >= CONFIG.CONSTRAINTS.MIN_GAP && roundedGapSize <= CONFIG.CONSTRAINTS.MAX_GAP) {
        
        
        // If prioritizing gap spacing, try to get as close as possible to preferred gap
        if (prioritizeGap && preferredGapSpacing) {
          const gapDifference = Math.abs(roundedGapSize - preferredGapSpacing);
          
          
          // Try different panel sizes to get closer to preferred gap
          let bestGapMatch = { panelSize, numPanels, gapSize: roundedGapSize, difference: gapDifference };
          
          // Try smaller panels for larger gaps
          if (roundedGapSize < preferredGapSpacing) {
            const smallerPanelSize = this.getPanelSizeDown(panelSize, panelSizes);
            if (smallerPanelSize) {
              const smallerTotalPanelWidth = smallerPanelSize * numPanels;
              const smallerRemainingSpace = availableSpace - smallerTotalPanelWidth;
              const smallerCalculatedGapSize = smallerRemainingSpace / numPanels;
              const smallerRoundedGapSize = Math.round(smallerCalculatedGapSize);
              
              if (smallerRoundedGapSize >= CONFIG.CONSTRAINTS.MIN_GAP && smallerRoundedGapSize <= CONFIG.CONSTRAINTS.MAX_GAP) {
                const smallerGapDifference = Math.abs(smallerRoundedGapSize - preferredGapSpacing);
                
                
                if (smallerGapDifference < bestGapMatch.difference) {
                  bestGapMatch = { panelSize: smallerPanelSize, numPanels, gapSize: smallerRoundedGapSize, difference: smallerGapDifference };
                  
                }
              }
            }
          }
          
          // Try larger panels for smaller gaps — max +50mm above preferred
          if (roundedGapSize > preferredGapSpacing) {
            const largerPanelSize = this.getNextPanelSizeUp(panelSize, panelSizes);
            if (largerPanelSize && largerPanelSize <= preferredLength + 50) {
              const largerTotalPanelWidth = largerPanelSize * numPanels;
              const largerRemainingSpace = availableSpace - largerTotalPanelWidth;
              const largerCalculatedGapSize = largerRemainingSpace / numPanels;
              const largerRoundedGapSize = Math.round(largerCalculatedGapSize);
              
              if (largerRoundedGapSize >= CONFIG.CONSTRAINTS.MIN_GAP && largerRoundedGapSize <= CONFIG.CONSTRAINTS.MAX_GAP) {
                const largerGapDifference = Math.abs(largerRoundedGapSize - preferredGapSpacing);
                
                
                if (largerGapDifference < bestGapMatch.difference) {
                  bestGapMatch = { panelSize: largerPanelSize, numPanels, gapSize: largerRoundedGapSize, difference: largerGapDifference };
                  
                }
              }
            }
          }
          
          
          return this.buildPanelConfiguration(bestGapMatch.panelSize, bestGapMatch.numPanels, bestGapMatch.gapSize, section, specialPanelComponents, sectionLength, availableSpace);
        }
        
        // If gap is above the user's preferred gap, try next panel size up to get closer
        if (preferredGapSpacing && roundedGapSize > preferredGapSpacing) {
          const nextPanelSizeUp = this.getNextPanelSizeUp(panelSize, panelSizes);
          // Boss rule: max +50mm above preferred only
          if (nextPanelSizeUp && nextPanelSizeUp !== panelSize && nextPanelSizeUp <= preferredLength + 50) {
            const newTotalPanelWidth = nextPanelSizeUp * numPanels;
            const newRemainingSpace = availableSpace - newTotalPanelWidth;
            const newCalculatedGapSize = newRemainingSpace / numGaps;
            const newRoundedGapSize = Math.round(newCalculatedGapSize);
            
            // Use the larger panel only if it gets us closer to the preferred gap
            if (newRoundedGapSize >= CONFIG.CONSTRAINTS.MIN_GAP && newRoundedGapSize <= CONFIG.CONSTRAINTS.MAX_GAP &&
                Math.abs(newRoundedGapSize - preferredGapSpacing) < Math.abs(roundedGapSize - preferredGapSpacing)) {
              return this.buildPanelConfiguration(nextPanelSizeUp, numPanels, newRoundedGapSize, section, specialPanelComponents, sectionLength, availableSpace);
            }
          }
        }

        // Try one panel size down if it produces a gap closer to the user's preferred gap
        if (preferredGapSpacing && roundedGapSize > preferredGapSpacing) {
          const panelSizeDown = this.getPanelSizeDown(panelSize, panelSizes);
          if (panelSizeDown && panelSizeDown !== panelSize) {
            const smallerTotalPanelWidth = panelSizeDown * numPanels;
            const smallerRemainingSpace = availableSpace - smallerTotalPanelWidth;
            const smallerCalculatedGapSize = smallerRemainingSpace / numGaps;
            const smallerRoundedGapSize = Math.round(smallerCalculatedGapSize);
            
            // Use the smaller panel only if it gets us closer to the preferred gap
            if (smallerRoundedGapSize >= CONFIG.CONSTRAINTS.MIN_GAP && 
                smallerRoundedGapSize <= CONFIG.CONSTRAINTS.MAX_GAP &&
                Math.abs(smallerRoundedGapSize - preferredGapSpacing) < Math.abs(roundedGapSize - preferredGapSpacing)) {
              return this.buildPanelConfiguration(panelSizeDown, numPanels, smallerRoundedGapSize, section, specialPanelComponents, sectionLength, availableSpace);
            }
          }
        }

        // Use current configuration
        
        return this.buildPanelConfiguration(panelSize, numPanels, roundedGapSize, section, specialPanelComponents, sectionLength, availableSpace);
      } else {
        
      }
    }
    
    return null;
  }

  // Get next panel size up from available sizes
  getNextPanelSizeUp(currentSize, availableSizes) {
    const largerSizes = availableSizes.filter(size => size > currentSize).sort((a, b) => a - b);
    return largerSizes.length > 0 ? largerSizes[0] : null;
  }

  // Get panel size down from available sizes
  getPanelSizeDown(currentSize, availableSizes) {
    const smallerSizes = availableSizes.filter(size => size < currentSize).sort((a, b) => b - a);
    return smallerSizes.length > 0 ? smallerSizes[0] : null;
  }

  // Variable panel/gap refinement: allows individual panels and gaps to differ
  // so that the overall layout better matches the customer's preferred values.
  tryVariablePanelConfiguration(sectionLength, uniformConfig, section, preferredPanelLength, preferredGap, maxCalcPanelVariance, maxGapVariance) {
    const numRegularPanels = uniformConfig.numVariablePanels;
    if (numRegularPanels < 1) return null;

    // Get available panel sizes from Shopify variants, filtered by maxCalcPanelVariance
    const allAvailableSizes = this.getAvailablePanelSizes(preferredPanelLength);
    const candidateSizes = allAvailableSizes.filter(
      s => Math.abs(s - preferredPanelLength) <= maxCalcPanelVariance
      // allows ±50mm both directions (maxCalcPanelVariance = 50)
    );
    if (candidateSizes.length === 0) return null;

    // Sort candidates: preferred size first, then by distance from preferred
    candidateSizes.sort((a, b) => {
      if (a === preferredPanelLength) return -1;
      if (b === preferredPanelLength) return 1;
      return Math.abs(a - preferredPanelLength) - Math.abs(b - preferredPanelLength);
    });

    // Calculate fixed widths (gate + raked) that don't change
    let fixedWidth = 0;
    let gateWidth = 0;
    const numGaps = uniformConfig.totalGaps;

    if (section.hasGate && section.gateLength) {
      gateWidth = parseInt(section.gateLength);
    }
    if (section.hasRaked) {
      fixedWidth += this.getRakedPanelWidth(section.fenceHeight);
    }

    // Start with all panels at preferred size
    const panelWidths = Array(numRegularPanels).fill(preferredPanelLength);

    // Helper: calculate gap size given current panel widths
    const calcGapSize = (widths) => {
      const totalPanelWidth = widths.reduce((sum, w) => sum + w, 0) + gateWidth + fixedWidth;
      const remainingSpace = sectionLength - totalPanelWidth;
      if (numGaps === 0) return preferredGap;
      return remainingSpace / numGaps;
    };

    // Check if the preferred-size-for-all solution already works
    let baseGap = calcGapSize(panelWidths);
    if (Math.abs(baseGap - preferredGap) <= maxGapVariance &&
        baseGap >= CONFIG.CONSTRAINTS.MIN_GAP && baseGap <= CONFIG.CONSTRAINTS.MAX_GAP) {
      // Uniform preferred-size solution works within gap variance
      const roundedGap = Math.round(baseGap);
      const gapSpacings = Array(numGaps).fill(roundedGap);
      // Distribute any rounding remainder across individual gaps
      const totalRoundedGaps = roundedGap * numGaps;
      const totalNeededGaps = sectionLength - panelWidths.reduce((s, w) => s + w, 0) - gateWidth - fixedWidth;
      const gapRemainder = totalNeededGaps - totalRoundedGaps;
      if (Math.abs(gapRemainder) > 0 && numGaps > 0) {
        // Distribute remainder 1mm at a time across gaps
        const direction = gapRemainder > 0 ? 1 : -1;
        for (let r = 0; r < Math.abs(gapRemainder); r++) {
          gapSpacings[r % numGaps] += direction;
        }
      }
      // Validate all gaps are within constraints
      if (gapSpacings.every(g => g >= CONFIG.CONSTRAINTS.MIN_GAP && g <= CONFIG.CONSTRAINTS.MAX_GAP && Math.abs(g - preferredGap) <= maxGapVariance)) {
        return this.buildVariableConfiguration(panelWidths, gapSpacings, section, sectionLength, preferredPanelLength, preferredGap);
      }
    }

    // Greedy approach: adjust panels one at a time to bring gaps closer to preferred
    // Determine direction: do we need to make panels bigger (to reduce gaps) or smaller (to increase gaps)?
    const needBiggerPanels = baseGap > preferredGap; // gaps too large → use bigger panels
    const needSmallerPanels = baseGap < preferredGap; // gaps too small → use smaller panels

    // Peter's rule: "To adjust for gaps we can have a couple of panels change size by 50mm"
    // Pick the adjustment size at EXACTLY preferred ±50mm. If that exact step is not
    // stocked in Shopify variants, fall back to the next-closest larger/smaller size.
    const PETER_STEP = 50;
    const targetUp   = preferredPanelLength + PETER_STEP;
    const targetDown = preferredPanelLength - PETER_STEP;

    let adjustSize = null;
    if (needBiggerPanels) {
      // Prefer exact +50mm step; otherwise fall back to closest larger stocked size
      adjustSize = candidateSizes.includes(targetUp)
        ? targetUp
        : candidateSizes.find(s => s > preferredPanelLength);
    } else if (needSmallerPanels) {
      // Prefer exact -50mm step; otherwise fall back to closest smaller stocked size
      adjustSize = candidateSizes.includes(targetDown)
        ? targetDown
        : candidateSizes.find(s => s < preferredPanelLength);
    }

    if (!adjustSize) return null;

    // Peter's rule: "To adjust for gaps we can have a couple of panels change size by 50mm"
    // Cap variable adjustments at 2 panels max — all other panels stay at the preferred size.
    const MAX_ADJUSTABLE_PANELS = 2;
    const maxAdjustablePrimary = Math.min(MAX_ADJUSTABLE_PANELS, numRegularPanels);

    // Try adjusting 1, 2 panels until gaps are within tolerance
    const bestWidths = [...panelWidths];
    for (let numAdjusted = 1; numAdjusted <= maxAdjustablePrimary; numAdjusted++) {
      bestWidths[numAdjusted - 1] = adjustSize;
      const newGap = calcGapSize(bestWidths);

      if (newGap >= CONFIG.CONSTRAINTS.MIN_GAP && newGap <= CONFIG.CONSTRAINTS.MAX_GAP) {
        if (Math.abs(newGap - preferredGap) <= maxGapVariance) {
          // Found a working variable configuration
          const roundedGap = Math.round(newGap);
          const gapSpacings = Array(numGaps).fill(roundedGap);
          // Distribute rounding remainder
          const totalNeededGaps = sectionLength - bestWidths.reduce((s, w) => s + w, 0) - gateWidth - fixedWidth;
          const gapRemainder = totalNeededGaps - (roundedGap * numGaps);
          if (Math.abs(gapRemainder) > 0 && numGaps > 0) {
            const direction = gapRemainder > 0 ? 1 : -1;
            for (let r = 0; r < Math.abs(gapRemainder); r++) {
              gapSpacings[r % numGaps] += direction;
            }
          }
          if (gapSpacings.every(g => g >= CONFIG.CONSTRAINTS.MIN_GAP && g <= CONFIG.CONSTRAINTS.MAX_GAP && Math.abs(g - preferredGap) <= maxGapVariance)) {
            return this.buildVariableConfiguration([...bestWidths], gapSpacings, section, sectionLength, preferredPanelLength, preferredGap);
          }
        }
        // If we overshot (gap went past preferred in the other direction), stop
        if ((needBiggerPanels && newGap < preferredGap - maxGapVariance) ||
            (needSmallerPanels && newGap > preferredGap + maxGapVariance)) {
          break;
        }
      }
    }

    // Try the opposite direction too (e.g., if bigger didn't work, try smaller panels or vice versa)
    // Same Peter rule: prefer exact ±50mm step, fall back to nearest stocked size.
    let altAdjustSize = null;
    if (needBiggerPanels) {
      altAdjustSize = candidateSizes.includes(targetDown)
        ? targetDown
        : candidateSizes.find(s => s < preferredPanelLength);
    } else {
      altAdjustSize = candidateSizes.includes(targetUp)
        ? targetUp
        : candidateSizes.find(s => s > preferredPanelLength);
    }

    if (altAdjustSize) {
      const altWidths = Array(numRegularPanels).fill(preferredPanelLength);
      const maxAdjustableAlt = Math.min(MAX_ADJUSTABLE_PANELS, numRegularPanels);
      for (let numAdjusted = 1; numAdjusted <= maxAdjustableAlt; numAdjusted++) {
        altWidths[numAdjusted - 1] = altAdjustSize;
        const newGap = calcGapSize(altWidths);
        if (newGap >= CONFIG.CONSTRAINTS.MIN_GAP && newGap <= CONFIG.CONSTRAINTS.MAX_GAP &&
            Math.abs(newGap - preferredGap) <= maxGapVariance) {
          const roundedGap = Math.round(newGap);
          const gapSpacings = Array(numGaps).fill(roundedGap);
          const totalNeededGaps = sectionLength - altWidths.reduce((s, w) => s + w, 0) - gateWidth - fixedWidth;
          const gapRemainder = totalNeededGaps - (roundedGap * numGaps);
          if (Math.abs(gapRemainder) > 0 && numGaps > 0) {
            const direction = gapRemainder > 0 ? 1 : -1;
            for (let r = 0; r < Math.abs(gapRemainder); r++) {
              gapSpacings[r % numGaps] += direction;
            }
          }
          if (gapSpacings.every(g => g >= CONFIG.CONSTRAINTS.MIN_GAP && g <= CONFIG.CONSTRAINTS.MAX_GAP && Math.abs(g - preferredGap) <= maxGapVariance)) {
            return this.buildVariableConfiguration([...altWidths], gapSpacings, section, sectionLength, preferredPanelLength, preferredGap);
          }
        }
      }
    }

    return null; // No variable configuration found within tolerances
  }

  // Build a configuration object from variable panel widths and gap spacings
  buildVariableConfiguration(panelWidths, gapSpacings, section, sectionLength, preferredPanelLength, preferredGap) {
    const numRegularPanels = panelWidths.length;
    let gateWidth = 0;
    let fixedWidth = 0;

    if (section.hasGate && section.gateLength) {
      gateWidth = parseInt(section.gateLength);
    }
    if (section.hasRaked) {
      fixedWidth += this.getRakedPanelWidth(section.fenceHeight);
    }

    const totalPanelWidth = panelWidths.reduce((s, w) => s + w, 0) + gateWidth;
    const totalGapWidth = gapSpacings.reduce((s, g) => s + g, 0);
    const totalUsedSpace = totalPanelWidth + totalGapWidth + fixedWidth;
    const remaining = sectionLength - totalUsedSpace;

    // Use most common panel width as the primary panelWidth for backward compat
    const sizeCounts = {};
    panelWidths.forEach(w => { sizeCounts[w] = (sizeCounts[w] || 0) + 1; });
    const primaryPanelWidth = parseInt(Object.entries(sizeCounts).sort((a, b) => b[1] - a[1])[0][0]);

    // Use most common gap as the primary gapSpacing
    const gapCounts = {};
    gapSpacings.forEach(g => { gapCounts[g] = (gapCounts[g] || 0) + 1; });
    const primaryGapSpacing = parseInt(Object.entries(gapCounts).sort((a, b) => b[1] - a[1])[0][0]);

    const panelBreakdown = this.calculateSimplePanelBreakdown(numRegularPanels + (section.hasGate ? 1 : 0), section, primaryPanelWidth);

    let totalGaps = numRegularPanels + (section.hasGate ? 1 : 0);
    if (section.hasRaked) totalGaps += 1;

    return {
      panelWidth: primaryPanelWidth,
      numVariablePanels: panelBreakdown.regularPanels,
      gapSpacing: primaryGapSpacing,
      panelWidths: panelWidths,
      gapSpacings: gapSpacings,
      totalRequired: totalUsedSpace,
      totalGaps: totalGaps,
      totalGapWidth: totalGapWidth,
      totalPanelCount: panelBreakdown.totalPanels,
      variablePanelWidth: totalPanelWidth,
      remaining: remaining,
      variance: Math.max(...panelWidths.map(w => Math.abs(w - preferredPanelLength))),
      gapVariance: Math.max(...gapSpacings.map(g => Math.abs(g - preferredGap))),
      withinVarianceLimit: true,
      optimized: true,
      optimizationType: 'variable-panel-gap',
      score: 1.0,
      panelWeight: 1.0,
      gapWeight: 1.0,
      remainingScore: 1.0
    };
  }

  // Build the final panel configuration object
  buildPanelConfiguration(panelSize, numPanels, roundedGapSize, section, specialPanelComponents, sectionLength, availableSpace) {
    const fixedSpecialWidth = sectionLength - availableSpace;

    // ── Peter's formula ──────────────────────────────────────────────────────
    // fullPanels = floor(available / preferredPanel)
    // remainder  = available - (fullPanels × preferredPanel)
    // If (remainder - one gap) >= MIN_PANEL → add as extra panel
    // ─────────────────────────────────────────────────────────────────────────
    let spaceForPanels = availableSpace;

    // If gate: subtract gate + one gap from available first
    let gateLength = 0;
    if (section.hasGate && section.gateLength) {
      gateLength = parseInt(section.gateLength);
      spaceForPanels = availableSpace - gateLength - roundedGapSize;
    }

    // ── Peter's formula ──────────────────────────────────────────────────────
    // No gate: full panels + remainder panel fills section exactly
    // With gate: full panels + hinge panel (absorbs remainder) + gate fills section exactly
    //   hinge = section - full*panel - gate - (full+2)*gap   [full+2 slots = full+hinge+gate]
    // ─────────────────────────────────────────────────────────────────────────

    let panelWidthsArr;
    let totalGaps;

    if (section.hasGate && section.gateLength) {
      // HARDCODED PATTERN:
      // LEFT GATE:  [Regular] - 7mm - [GATE] - 10mm - [HINGE] - regular
      // RIGHT GATE: [Regular] - regular - [HINGE] - 10mm - [GATE] - 7mm - regular
      
      const gateSide = section.gateSide || 'right';
      const fullPanels = Math.max(1, Math.floor(spaceForPanels / panelSize));
      
      // Calculate regular gaps based on gate side
      let regularGapsBeforeFirst, regularGapsAfterLast;
      if (gateSide === 'left') {
        // LEFT: [regular gaps] - 7mm - [gate] - 10mm - [hinge] - regular
        regularGapsBeforeFirst = fullPanels > 1 ? fullPanels - 2 : 0; // gaps before 7mm
        regularGapsAfterLast = 1; // one gap after hinge
      } else {
        // RIGHT: [regular gaps] - regular - [hinge] - 10mm - [gate] - 7mm - regular
        regularGapsBeforeFirst = fullPanels > 1 ? fullPanels - 1 : 0; // includes one before hinge
        regularGapsAfterLast = 1; // one gap after gate
      }
      const totalRegularGapCount = regularGapsBeforeFirst + regularGapsAfterLast;
      
      // Calculate hinge size — Peter's rule: hinge panel = same size as regular panels
      const finalHingeSize = panelSize;
      
      // Calculate actual regular gap size using fixed hinge size
      const totalPanelSpace = fullPanels * panelSize + gateLength + finalHingeSize;
      const remainingForGaps = availableSpace - totalPanelSpace;
      const spaceForRegularGaps = remainingForGaps - 7 - 10;
      const actualRegularGap = totalRegularGapCount > 0 ? Math.round(spaceForRegularGaps / totalRegularGapCount) : roundedGapSize;
      
      // Update roundedGapSize with calculated regular gap
      roundedGapSize = actualRegularGap;
      
      // Total gaps
      totalGaps = totalRegularGapCount + 2; // regular + 7mm + 10mm
      if (section.hasRaked) totalGaps += 1;
      
      // Build panel array
      // LEFT:  [regular, GATE, HINGE]
      // RIGHT: [regular, HINGE, GATE]
      panelWidthsArr = Array(fullPanels).fill(panelSize);
      if (gateSide === 'left') {
        panelWidthsArr.push(gateLength);
        panelWidthsArr.push(finalHingeSize);
      } else {
        panelWidthsArr.push(finalHingeSize);
        panelWidthsArr.push(gateLength);
      }
      
      // Store gate side for gap array
      section._gateSide = gateSide;
    } else {
      // ✅ PETER'S NEW RULE (Apr 2026): All panels must be same size. No ugly remainder
      // panels. If the uniform layout leaves a large remainder, try panel counts N, N+1,
      // N+2 at base sizes snapped to the 50mm grid, pick the config with the smallest
      // deviation from preferred that keeps gaps inside [MIN_GAP, MAX_GAP].
      // Up to 2 panels may deviate by ±50mm from the chosen base to close the gap.
      const rakedGapExtra = section.hasRaked ? 1 : 0;

      // Baseline uniform calculation at preferred size
      const fullPanels = Math.max(1, Math.floor(availableSpace / panelSize));
      const baselineGaps = Math.max(0, fullPanels - 1) + rakedGapExtra;
      const baselineGapWidth = baselineGaps * roundedGapSize;
      const baselineRemainder = availableSpace - (fullPanels * panelSize) - baselineGapWidth;

      // Check if baseline uniform fits within acceptable gap variance
      // (i.e., remainder is small enough to either ignore or distribute into gaps)
      const maxGapV = this.shopifyData.settings?.maxGapVariance || CONFIG.DEFAULTS.MAX_GAP_VARIANCE;
      const baselineGapAbsorbed = baselineGaps > 0
        ? roundedGapSize + (baselineRemainder / baselineGaps)
        : roundedGapSize;
      const baselineWorks = Math.abs(baselineRemainder) < 50 ||
        (baselineGapAbsorbed >= CONFIG.CONSTRAINTS.MIN_GAP &&
         baselineGapAbsorbed <= CONFIG.CONSTRAINTS.MAX_GAP &&
         Math.abs(baselineGapAbsorbed - roundedGapSize) <= maxGapV);

      if (baselineWorks) {
        // Baseline uniform layout at preferred size fits — use it, let gap refinement
        // in tryVariablePanelConfiguration polish it later.
        panelWidthsArr = Array(fullPanels).fill(panelSize);
        totalGaps = baselineGaps;
      } else {
        // Baseline doesn't fit cleanly — search for a uniform rescue.
        // Try N-1, N, N+1, N+2 panel counts at 50mm-snapped base sizes.
        let bestRescue = null;
        let bestScore = Infinity;

        for (let n = Math.max(1, fullPanels - 1); n <= fullPanels + 2; n++) {
          const nGaps = Math.max(0, n - 1) + rakedGapExtra;
          if (nGaps === 0 && n > 1) continue;

          // Target base panel size for this count, allowing preferred gap
          const targetBase = (availableSpace - nGaps * roundedGapSize) / n;
          if (targetBase < CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH) continue;

          // Try 50mm-snapped candidates around targetBase
          const snapDown = Math.floor(targetBase / 50) * 50;
          const snapUp = Math.ceil(targetBase / 50) * 50;
          const candidates = [...new Set([snapDown, snapUp, snapDown - 50, snapUp + 50])]
            .filter(s => s >= CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH && s <= CONFIG.CONSTRAINTS.MAX_PANEL_WIDTH);

          for (const base of candidates) {
            // Try base uniform, then 1 or 2 panels shifted ±50mm to fine-tune
            for (let numAdj = 0; numAdj <= Math.min(2, n); numAdj++) {
              for (const delta of (numAdj === 0 ? [0] : [50, -50])) {
                const widths = Array(n).fill(base);
                for (let i = 0; i < numAdj; i++) widths[i] = base + delta;
                if (widths.some(w => w < CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH)) continue;

                const panelsSum = widths.reduce((a, b) => a + b, 0);
                const spaceForGaps = availableSpace - panelsSum;
                if (nGaps === 0) {
                  if (Math.abs(spaceForGaps) < 10) {
                    const panelDev = Math.abs(base - panelSize) + (numAdj * 50);
                    if (panelDev < bestScore) {
                      bestScore = panelDev;
                      bestRescue = { widths, gapSize: roundedGapSize, nGaps };
                    }
                  }
                  continue;
                }
                const gapSize = spaceForGaps / nGaps;
                if (gapSize < CONFIG.CONSTRAINTS.MIN_GAP || gapSize > CONFIG.CONSTRAINTS.MAX_GAP) continue;

                // Score: weight panel deviation more heavily than gap deviation
                const panelDev = Math.abs(base - panelSize) + (numAdj * 25);
                const gapDev = Math.abs(gapSize - roundedGapSize);
                const score = panelDev * 3 + gapDev * 10;

                if (score < bestScore) {
                  bestScore = score;
                  bestRescue = { widths: [...widths], gapSize: Math.round(gapSize), nGaps };
                }
              }
            }
          }
        }

        if (bestRescue) {
          panelWidthsArr = bestRescue.widths;
          totalGaps = bestRescue.nGaps;
          roundedGapSize = bestRescue.gapSize;
        } else {
          // Absolute last resort: keep old remainder logic so the quote at least renders
          panelWidthsArr = Array(fullPanels).fill(panelSize);
          if (baselineRemainder >= CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH) {
            panelWidthsArr.push(baselineRemainder);
            totalGaps = baselineGaps + 1;
          } else {
            totalGaps = baselineGaps;
          }
          if (section.hasRaked && baselineRemainder < CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH) {
            totalGaps = Math.max(0, panelWidthsArr.length - 1) + rakedGapExtra;
          }
        }
      }
    }

    // Total panel width
    const totalPanelWidth = panelWidthsArr.reduce((s, w) => s + w, 0);

    const totalGapWidth  = roundedGapSize * totalGaps;
    const totalUsedSpace = totalPanelWidth + totalGapWidth + fixedSpecialWidth;
    const actualRemaining = sectionLength - totalUsedSpace;

    // Remove gate slot from panelWidths
    // LEFT: [regular, gate, hinge] → [regular, hinge]
    // RIGHT: [regular, hinge, gate] → [regular, hinge]
    let glassWidths;
    if (section.hasGate && section.gateLength) {
      const gateSide = section.gateSide || 'right';
      if (gateSide === 'left') {
        // LEFT: remove gate (second to last)
        const regular = panelWidthsArr.slice(0, -2);
        const hinge = panelWidthsArr[panelWidthsArr.length - 1];
        glassWidths = [...regular, hinge];
      } else {
        // RIGHT: remove gate (last)
        glassWidths = panelWidthsArr.slice(0, -1);
      }
    } else {
      glassWidths = [...panelWidthsArr];
    }

    // Panel breakdown — pass full slot count so hingePanels=1 and regularPanels are correct
    // numVariablePanels = full regular panels only (not hinge, not gate)
    const numRegularOnly = section.hasGate && section.gateLength
      ? panelWidthsArr.length - 2   // strip hinge + gate
      : panelWidthsArr.length;

    // Determine primary panel width — usually the preferred (panelSize), but if a rescue
    // used a different base (e.g. 1750mm instead of 1850mm), report the most common
    // actual size so the quote display reflects reality.
    let primaryPanelWidth = panelSize;
    const nonGateWidths = section.hasGate && section.gateLength
      ? panelWidthsArr.slice(0, -2)   // exclude gate + hinge slots
      : panelWidthsArr;
    if (nonGateWidths.length > 0) {
      const sizeCounts = {};
      nonGateWidths.forEach(w => { sizeCounts[w] = (sizeCounts[w] || 0) + 1; });
      const mostCommon = parseInt(Object.entries(sizeCounts).sort((a, b) => b[1] - a[1])[0][0]);
      if (mostCommon !== panelSize) {
        primaryPanelWidth = mostCommon;
      }
    }

    const panelBreakdown = this.calculateSimplePanelBreakdown(
      numRegularOnly, section, primaryPanelWidth
    );

    const spaceCalc = {
      totalRequired: totalUsedSpace,
      totalGaps: totalGaps,
      totalGapWidth: totalGapWidth,
      totalPanelCount: panelBreakdown.totalPanels,
      variablePanelWidth: totalPanelWidth
    };

    // Build gap spacings array
    let gapSpacings;
    if (section.hasGate && section.gateLength) {
      // HARDCODED: LEFT = 7mm before gate, 10mm middle; RIGHT = regular, 10mm middle, 7mm after
      const gateSide = section._gateSide || section.gateSide || 'right';
      const numRegularPanels = panelWidthsArr.length - 2;
      
      gapSpacings = [];
      if (gateSide === 'left') {
        // LEFT: [regular gaps] - 7mm - [gate] - 10mm - [hinge] - regular
        const regularBefore = numRegularPanels > 1 ? numRegularPanels - 2 : 0;
        for (let i = 0; i < regularBefore; i++) gapSpacings.push(roundedGapSize);
        gapSpacings.push(7);
        gapSpacings.push(10);
        gapSpacings.push(roundedGapSize);
      } else {
        // RIGHT: [regular gaps] - regular - [hinge] - 10mm - [gate] - 7mm - regular
        const regularBefore = numRegularPanels > 1 ? numRegularPanels - 1 : 0;
        for (let i = 0; i < regularBefore; i++) gapSpacings.push(roundedGapSize);
        gapSpacings.push(10);
        gapSpacings.push(7);
        gapSpacings.push(roundedGapSize);
      }
      if (section.hasRaked) gapSpacings.push(roundedGapSize);
    } else {
      gapSpacings = Array(totalGaps).fill(roundedGapSize);
    }

    // Guarantee total == sectionLength exactly — cycle ±1mm through variable gaps
    let corrRemaining = sectionLength - (totalPanelWidth + gapSpacings.reduce((a,b)=>a+b,0) + fixedSpecialWidth);
    if (corrRemaining !== 0) {
      const corrDelta = corrRemaining > 0 ? +1 : -1;
      let toFix = Math.abs(corrRemaining);
      // For gate sections, fixed gap indices are the 7mm and 10mm hardware gaps
      const fixedGateGapIndices = new Set();
      if (section.hasGate && section.gateLength) {
        const gateSide = section._gateSide || section.gateSide || 'right';
        const numRegularPanels = panelWidthsArr.length - 2;
        if (gateSide === 'left') {
          const regularBefore = numRegularPanels > 1 ? numRegularPanels - 2 : 0;
          fixedGateGapIndices.add(regularBefore);     // 7mm
          fixedGateGapIndices.add(regularBefore + 1); // 10mm
        } else {
          const regularBefore = numRegularPanels > 1 ? numRegularPanels - 1 : 0;
          fixedGateGapIndices.add(regularBefore);     // 10mm
          fixedGateGapIndices.add(regularBefore + 1); // 7mm
        }
      }
      const varIdx = [];
      for (let i = 0; i < gapSpacings.length; i++) {
        if (!fixedGateGapIndices.has(i)) varIdx.push(i);
      }
      if (varIdx.length > 0) {
        let vi = 0;
        while (toFix > 0) {
          gapSpacings[varIdx[vi % varIdx.length]] += corrDelta;
          vi++; toFix--;
        }
      }
      corrRemaining = 0;
    }

    return {
      panelWidth: primaryPanelWidth,
      numVariablePanels: panelBreakdown.regularPanels,
      gapSpacing: roundedGapSize,
      panelWidths: glassWidths,
      gapSpacings: gapSpacings,
      ...spaceCalc,
      remaining: corrRemaining,
      variance: Math.abs(panelSize - (parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight)))),
      gapVariance: 0,
      withinVarianceLimit: true,
      optimized: false,
      score: 1.0,
      panelWeight: 1.0,
      gapWeight: 1.0,
      remainingScore: 1.0
    };
  }

  // Simple panel breakdown calculation
  calculateSimplePanelBreakdown(numVariablePanels, section, actualPanelWidth) {
    let regularGlassPanels = numVariablePanels;
    let hingePanels = 0;
    let gatePanels = 0;
    let rakedPanels = 0;

    if (section.hasGate) {
      gatePanels = 1;
      const gateHingeType = section.gateHingeType || 'glass-to-glass';
      const isWallToGlass = gateHingeType === 'W2G-REMOVED-2026-08-30' && !!(section.wallGateEnd);

      if (isWallToGlass) {
        // Wall-to-glass: no glass hinge panel. Keep all regular glass panels
        // (this is the "replacement panel" behaviour).
        hingePanels = 0;
        regularGlassPanels = Math.max(0, numVariablePanels);
      } else {
        // Glass-to-glass: include a hinge panel and gate replaces one regular panel
        hingePanels = 1;
        regularGlassPanels = Math.max(0, numVariablePanels - 1);
      }
    }

    if (section.hasRaked) {
      rakedPanels = 1;
    }

    const totalPanels = regularGlassPanels + hingePanels + gatePanels + rakedPanels;

    // Calculate total panel width including special panels
    let totalPanelWidth;
    
    if (section.hasGate && section.gateLength) {
      // Gate replaces one regular panel
      const actualNumRegularPanels = Math.max(0, numVariablePanels - 1);
      totalPanelWidth = (actualNumRegularPanels * actualPanelWidth) + parseInt(section.gateLength);
    } else {
      totalPanelWidth = numVariablePanels * actualPanelWidth;
    }
    
    if (section.hasRaked) {
      totalPanelWidth += this.getRakedPanelWidth(section.fenceHeight);
    }

    return {
      numPanels: numVariablePanels,
      regularPanels: regularGlassPanels,
      hingePanels,
      gatePanels,
      rakedPanels,
      totalPanels,
      totalPanelWidth: totalPanelWidth
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PETER'S ROUNDING RULE
  // Round raw width DOWN to the nearest 50mm boundary.
  // ─────────────────────────────────────────────────────────────────────────
  peterRoundDown(value) {
    return Math.floor(value / 50) * 50;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PETER'S CORE LAYOUT ENGINE
  //
  // RULE 1: All panels must be the same size.
  // RULE 2: To adjust for gaps, up to 2 panels may change size by exactly ±50mm.
  // RULE 3: When the user changes the panel length → honour that panel size,
  //         solve the gap to make everything fit.
  // RULE 4: When the user changes the gap → honour that exact gap,
  //         solve the panel size (all same), allow ±50mm on up to 2 panels.
  //
  // Gap layout (start gap + end gap always present):
  //   No gate : [gap][panel…][raked?][gap]
  //   Left    : [gap][hinge][10mm][gate][7mm][panel…][raked?][gap]
  //   Right   : [gap][panel…][raked?][7mm][gate][10mm][hinge][gap]
  // ─────────────────────────────────────────────────────────────────────────
  peterCalculateLayout(section, preferredPanelLength, preferredGapSpacing, options = {}) {
    const sectionLength = parseInt(section.length);
    if (!sectionLength || sectionLength < CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH) return null;

    // ── PATCH 1: Auto-switch W2G → G2G when gate is not at a section edge ──────
    // If the user has wall-to-glass selected but the gate is mid-run, we must use
    // G2G for this calculation pass. A mid-run W2G is structurally invalid (no wall
    // to hinge off). We spread-copy so live section state is never mutated here;
    // updateWallToGlassAvailability() handles resetting the UI toggle separately.
    if (section.gateHingeType === 'W2G-REMOVED-2026-08-30') {
      const _estPanelUnit = (preferredPanelLength || 2400) + (preferredGapSpacing || 50);
      const _estGateWidth = 1000;
      // Subtract rakedWidth AND hinge panel from net space so _estMaxPos matches actual regularPanels.
      // Without subtracting rake: left-rake sections produce _estMaxPos = n+1 → gate at n wrongly
      //   classified as mid-run → W2G blocked.
      // Without subtracting hinge: G2G sections produce _estMaxPos = n+1 → gate at n wrongly
      //   classified as mid-run → W2G blocked.
      const _estRakedWidth = section.hasRaked
        ? (this.getRakedPanelWidth ? this.getRakedPanelWidth(section.fenceHeight) : CONFIG.DEFAULTS.RAKED_PANEL_WIDTH)
        : 0;
      // Hinge panel always present for G2G — same size as a regular panel
      const _estHingeWidth = _estPanelUnit - (preferredGapSpacing || 50); // = preferredPanelLength
      const _estNetSpace  = sectionLength - _estGateWidth - (preferredGapSpacing || 50) - _estRakedWidth - _estHingeWidth - (preferredGapSpacing || 50);
      const _estMaxPos    = Math.max(1, Math.floor(
        (_estNetSpace + (preferredGapSpacing || 50)) / _estPanelUnit
      ));
      const _gatePos = section.gatePosition !== undefined ? section.gatePosition : 0;
      const _atEdge  = _gatePos <= 0 || _gatePos >= _estMaxPos;
      if (!_atEdge) {
        // Gate is mid-run — override to G2G for this pass only (spread copy, no mutation)
        section = { ...section, gateHingeType: 'glass-to-glass', wallGateEnd: null };
      }
    }
    // ── END PATCH 1 ──────────────────────────────────────────────────────────────

    const hasGate    = !!section.hasGate;
    const hasRaked   = !!section.hasRaked;
    const gateHingeType = section.gateHingeType || 'glass-to-glass';
    const wallGateEnd = gateHingeType === 'W2G-REMOVED-2026-08-30' ? (section.wallGateEnd || null) : null;
    const effectiveWallToGlass = !!wallGateEnd;
    // W2G: gateSide maps directly from wallGateEnd (no flip).
    // wall-left → gateSide='left' → W2G-left solver path
    // wall-right → gateSide='right' → W2G-right solver path
    // G2G: use section.gateSide (user-selected hinge side).
    const gateSide  = effectiveWallToGlass
      ? (wallGateEnd === 'left' ? 'left' : 'right')
      : (section.gateSide || 'right');

    // When glass-to-glass gate is moved to the rake end, the rake acts as the hinge panel.
    // No separate hinge panel is needed — gate latches off the rake with a baseGap between them.
    // gatePosition=0 = leftmost slot; gateMaxPos (stored after last calc) = rightmost slot.
    const rakedSideForGate = section.rakedSide || 'left';
    const currentGatePos   = section.gatePosition !== undefined ? section.gatePosition : 9999;
    const gateMaxPos       = section.gateMaxPos !== undefined ? section.gateMaxPos : Infinity; // Infinity = not yet established, never triggers gateAtRakeEnd
    const gateAtRakeEnd    = hasGate && hasRaked && gateHingeType === 'glass-to-glass' && !effectiveWallToGlass && (
      (rakedSideForGate === 'left'  && currentGatePos <= 0) ||
      (rakedSideForGate === 'right' && currentGatePos >= gateMaxPos)
    );

    // hasHingePanel: ALWAYS true for G2G and W2G — hinge panel is always a regular glass panel.
    // W2G only changes RENDERING (no hinge dots, wall edge marker) — NOT the solver layout.
    // This keeps maxPos, panel count and gaps identical between G2G and W2G.
    const hasHingePanel = hasGate && !gateAtRakeEnd;
    const hasWallGateReplacementPanel = false; // retired — W2G is now render-only
    const gateWidth  = hasGate  ? (parseInt(section.gateLength) || 920) : 0;  // 2026-08-30: was hardcoded 1000; blade gates are 800/850/900/920
    const rakedWidth = hasRaked
      ? (this.getRakedPanelWidth ? this.getRakedPanelWidth(section.fenceHeight) : CONFIG.DEFAULTS.RAKED_PANEL_WIDTH)
      : 0;
    const LATCH_GAP = 70;  // 2026-08-30: 50mm gate post + 20mm latch clearance
    const HINGE_GAP = 60;  // 2026-08-30: 50mm gate post + 10mm hinge clearance
    const MIN_GAP   = CONFIG.CONSTRAINTS.MIN_GAP;
    const MAX_GAP   = CONFIG.CONSTRAINTS.MAX_GAP;
    const MIN_PANEL = CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH;
    const MAX_PANEL = CONFIG.CONSTRAINTS.MAX_PANEL_WIDTH;
    const rd        = (v) => Math.floor(v / 50) * 50;
    // Boss rule: hinge panel minimum 1000mm — never go below that.
    // Regular panels can go below 1000mm freely.
    const HINGE_MIN  = 1000;
    const clampHinge = (w) => Math.max(w || 0, HINGE_MIN);

    // Strip fixed hardware from net length.
    // Normal glass-to-glass: strip gateWidth + LATCH_GAP(7) + HINGE_GAP(10)
    // Gate-at-rake-end: strip gateWidth + LATCH_GAP(7) only — rake IS the hinge, rake→gate gap is variable
    // Wall-to-glass: strip gateWidth + LATCH_GAP(7) + HINGE_GAP(10)
    let netLength = sectionLength;
    if (hasGate) {
      // gateAtRakeEnd: rake IS the hinge — only the latch gap (7mm) is fixed; hinge→panel gap is variable.
      // Normal G2G and W2G: both LATCH_GAP(7) and HINGE_GAP(10) are fixed hardware gaps.
      netLength -= gateWidth + LATCH_GAP;
      if (!gateAtRakeEnd) netLength -= HINGE_GAP;
    }
    if (hasRaked) netLength -= rakedWidth;
    if (netLength <= 0) return null;

    // W2G is now render-only — solver uses same G2G layout for both modes.
    const wallToGlassLeft = effectiveWallToGlass && gateSide === 'left';
    const vc = (n) => {
      if (gateAtRakeEnd) {
        return rakedSideForGate === 'left' ? n + 1 : n + 2;
      }
      if (!hasGate) {
        return n + 1 + (hasRaked ? 2 : 0);
      }
      // RHS hinge + left rake:
      // G2G: [rake]-rakeGap-[panels]-[hinge]-10f-[gate]-7f-endGap = n+1
      // W2G right: [startGap]-[rake]-rakeGap-[panels]-[plain]-7f-[gate]-10f-endGap = n+2
      if (hasGate && hasHingePanel && gateSide === 'right' && hasRaked && rakedSideForGate === 'left') {
        return effectiveWallToGlass ? n + 2 : n + 1;
      }
      // LHS hinge + right rake:
      // G2G left+rake right: [startGap]-[hinge]-10f-[gate]-7f-[panels]-rakeGap-[rake]-endGap = n+3
      // W2G left+rake right: [wall]-10f-[gate]-7f-[plain]-var-[panels]-rakeGap-[rake]-endGap = n+2
      //   startGap(10mm fixed) not counted as variable gap slot
      if (hasGate && hasHingePanel && gateSide === 'left' && hasRaked && rakedSideForGate === 'right') {
        return effectiveWallToGlass ? n + 2 : n + 3;
      }
      // LHS hinge: n+1 no rake, n+3 with rake
      if (hasGate && hasHingePanel && gateSide === 'left') {
        return n + 1 + (hasRaked ? 2 : 0);
      }
      // RHS hinge + right rake or no rake
      return n + (hasRaked ? 1 : 0) + 1;
    };

        // ── Build final result object ─────────────────────────────────────────
    const buildResult = (n, widths, hingeW, baseGap, endGap, rem = 0) => {
      const gapSpacings    = [];
      const panelWidthsArr = [];
      // Track which gap indices are FIXED (HINGE_GAP / LATCH_GAP) by position, not by value.
      // Value-based detection breaks when baseGap happens to equal 7 or 10 — those variable
      // gaps would be incorrectly skipped, leaving rem units unspread → total short by rem mm.
      const fixedGapIndices = new Set();

      // pushPanels: pushes regular panels with inter-panel baseGaps.
      // If hasRaked, also appends [baseGap][rake] — but does NOT push the gap after the rake.
      // The caller is responsible for the gap that follows (fixed or variable depending on context).
      const pushPanels = (trailingRakeGap = true) => {
        for (let i = 0; i < n; i++) {
          panelWidthsArr.push(widths[i]);
          if (i < n - 1) gapSpacings.push(baseGap);
        }
        if (hasRaked) {
          gapSpacings.push(baseGap); // gap: last regular panel → rake (always baseGap)
          panelWidthsArr.push(rakedWidth);
          if (trailingRakeGap) gapSpacings.push(baseGap); // gap: rake → next (variable, default)
        }
      };

      if (hasGate) {
        if (gateAtRakeEnd) {
          // Peter's rule: gate LATCHES off rake (never hinges). Hinge panel always on opposite side.
          // rakedSideForGate tells us which end the rake is on.
          // Left rake:  [rake]-7mm(latch)-[gate]-10mm(hinge)-[hinge]-var-[p1..pN]-endGap
          // Right rake: [startGap]-[p1..pN]-var-[hinge]-10mm(hinge)-[gate]-7mm(latch)-[rake]-endGap
          if (rakedSideForGate === 'left') {
            panelWidthsArr.push(rakedWidth);
            fixedGapIndices.add(gapSpacings.length); gapSpacings.push(LATCH_GAP);   // rake→gate: 7mm latch
            panelWidthsArr.push(gateWidth);
            fixedGapIndices.add(gapSpacings.length); gapSpacings.push(HINGE_GAP);   // gate→hinge: 10mm
            panelWidthsArr.push(hingeW);
            for (let i = 0; i < n; i++) {
              gapSpacings.push(baseGap);
              panelWidthsArr.push(widths[i]);
            }
            gapSpacings.push(endGap);
          } else {
            gapSpacings.push(baseGap);
            for (let i = 0; i < n; i++) {
              panelWidthsArr.push(widths[i]);
              if (i < n - 1) gapSpacings.push(baseGap);
            }
            gapSpacings.push(baseGap);                                               // panels→hinge: variable
            panelWidthsArr.push(hingeW);
            fixedGapIndices.add(gapSpacings.length); gapSpacings.push(HINGE_GAP);   // hinge→gate: 10mm
            panelWidthsArr.push(gateWidth);
            fixedGapIndices.add(gapSpacings.length); gapSpacings.push(LATCH_GAP);   // gate→rake: 7mm latch
            panelWidthsArr.push(rakedWidth);
            gapSpacings.push(endGap);
          }
        } else if (hasHingePanel) {
          if (gateSide === 'left') {
            // LHS hinge: [startGap][hinge][10mm][gate][7mm][panels][raked?][endGap]
            gapSpacings.push(baseGap);
            panelWidthsArr.push(hingeW);
            fixedGapIndices.add(gapSpacings.length); gapSpacings.push(HINGE_GAP);   // hinge→gate: 10mm
            panelWidthsArr.push(gateWidth);
            fixedGapIndices.add(gapSpacings.length); gapSpacings.push(LATCH_GAP);   // gate→panels: 7mm
            pushPanels();
            gapSpacings.push(endGap);
          } else {
            // RHS hinge — original working layout:
            // No rake:    [startGap][panels][7mm fixed][gate][10mm fixed][hinge][endGap]
            // Right rake: [startGap][panels][rakeGap][rake][7mm fixed][gate][10mm fixed][hinge][endGap]
            // Left rake:  [rake][rakeGap][panels][7mm fixed][gate][10mm fixed][hinge][endGap]
            if (hasRaked && rakedSideForGate === 'left') {
              if (effectiveWallToGlass) {
                // W2G right + left rake: [startGap][rake][rakeGap][panels][plain panel][7f][gate][10f][wall]
                gapSpacings.push(baseGap);                                             // startGap
                panelWidthsArr.push(rakedWidth);
                gapSpacings.push(baseGap);                                             // rake→p0
                for (let i = 0; i < n; i++) {
                  panelWidthsArr.push(widths[i]);
                  if (i < n - 1) gapSpacings.push(baseGap);
                }
                panelWidthsArr.push(hingeW);                                           // plain panel (renders as plain in W2G)
                fixedGapIndices.add(gapSpacings.length); gapSpacings.push(LATCH_GAP); // plain→gate: 7mm latch
                panelWidthsArr.push(gateWidth);
                fixedGapIndices.add(gapSpacings.length); gapSpacings.push(HINGE_GAP); // gate→wall: 10mm hinge
                gapSpacings.push(endGap);
              } else {
                // G2G RHS + left rake: [rake]-rakeGap-[panels]-[hinge]-10mm-[gate]-7mm-endGap
                panelWidthsArr.push(rakedWidth);
                gapSpacings.push(baseGap);                                             // rake→p0: variable
                for (let i = 0; i < n; i++) {
                  panelWidthsArr.push(widths[i]);
                  if (i < n - 1) gapSpacings.push(baseGap);                           // inter-panel gaps
                }
                panelWidthsArr.push(hingeW);                                           // hinge panel
                fixedGapIndices.add(gapSpacings.length); gapSpacings.push(HINGE_GAP); // hinge→gate: 10mm
                panelWidthsArr.push(gateWidth);
                fixedGapIndices.add(gapSpacings.length); gapSpacings.push(LATCH_GAP); // gate→wall: 7mm latch
                gapSpacings.push(endGap);
              }
            } else {
              gapSpacings.push(baseGap);                                             // startGap
              if (hasRaked) {
                // Right rake RHS: [panels]-rakeGap-[rake]-[hinge]-10mm-[gate]-7mm-endGap
                pushPanels(false); // [panels][inter-gaps][rakeGap][rake] — no gap after rake
              } else {
                // No rake RHS: [panels]-[hinge]-10mm-[gate]-7mm-endGap
                pushPanels(false);
              }
              panelWidthsArr.push(hingeW);                                           // hinge panel
              fixedGapIndices.add(gapSpacings.length); gapSpacings.push(HINGE_GAP); // hinge→gate: 10mm
              panelWidthsArr.push(gateWidth);
              fixedGapIndices.add(gapSpacings.length); gapSpacings.push(LATCH_GAP); // gate→wall: 7mm latch
              gapSpacings.push(endGap);
            }
          }
        }
      } else {
        gapSpacings.push(baseGap);
        pushPanels();
        gapSpacings.push(endGap);
      }

      // ── Remainder spread ─────────────────────────────────────────────────
      // Distribute rem units of ±1mm across VARIABLE gaps only.
      // Fixed gaps (HINGE_GAP, LATCH_GAP) are identified by their insertion index — NOT
      // by value — so this is safe even when baseGap == 7 or baseGap == 10.
      //
      // rem > 0 → subtract 1mm from rem variable gaps (baseGap was ceil, need to reduce)
      // rem < 0 → add    +1mm to |rem| variable gaps (baseGap was floor, need to fill)
      //
      // If abs(rem) > available variable gaps (rare edge: very short section with gate+raked),
      // do a second pass to guarantee total == sectionLength exactly.
      if (rem !== 0) {
        const absRem = Math.abs(rem);
        const delta  = rem > 0 ? +1 : -1;
        let spread = 0;
        // First pass
        for (let i = 0; i < gapSpacings.length && spread < absRem; i++) {
          if (!fixedGapIndices.has(i)) {
            gapSpacings[i] += delta;
            spread++;
          }
        }
        // Second pass — only fires when absRem > variable gap count (e.g. gate+raked with 2 var gaps, rem=3)
        // Cycles back through variable gaps to absorb the leftover
        if (spread < absRem) {
          let leftover = absRem - spread;
          for (let i = 0; i < gapSpacings.length && leftover > 0; i++) {
            if (!fixedGapIndices.has(i)) {
              gapSpacings[i] += delta;
              leftover--;
            }
          }
        }
      }

      // glassWidths = regular panels only — excludes gate, hinge panel, and rake.
      let glassWidths;
      if (hasGate) {
        if (gateAtRakeEnd) {
          // Left rake:  [rake(0), gate(1), hinge(2), p1..pN] — exclude rake, gate, hinge
          // Right rake: [p1..pN, hinge(n), gate(n+1), rake(n+2)] — exclude hinge, gate, rake
          if (rakedSideForGate === 'left') {
            glassWidths = panelWidthsArr.filter((_, i) => i !== 0 && i !== 1 && i !== 2);
          } else {
            const hingeIdx = n;
            const gateIdx  = n + 1;
            const rakeIdx  = n + 2;
            glassWidths = panelWidthsArr.filter((_, i) => i !== hingeIdx && i !== gateIdx && i !== rakeIdx);
          }
        } else if (hasHingePanel) {
          if (gateSide === 'left') {
            // LHS: [hinge(0), gate(1), p1..pN, rake?(last)] — exclude hinge, gate, and rake (if present)
            // Without excluding the rake, regularPanels = n+1 instead of n, which causes
            // gateAtWallEnd to never be true when gate is at the right wall end with a left rake,
            // silently blocking the W2G toggle.
            const lhsRakeIdx = hasRaked ? panelWidthsArr.length - 1 : -1;
            glassWidths = panelWidthsArr.filter((_, i) => i !== 0 && i !== 1 && i !== lhsRakeIdx);
          } else {
            // RHS no rake:    [p1..pN, hinge(n), gate(n+1)]
            // RHS right rake: [p1..pN, rake(n), hinge(n+1), gate(n+2)]
            // RHS left rake:  [rake(0), p1..pN, hinge(n+1), gate(n+2)]
            if (hasRaked && rakedSideForGate === 'left') {
              glassWidths = panelWidthsArr.filter((_, i) => i !== 0 && i !== n + 1 && i !== n + 2);
            } else if (hasRaked) {
              glassWidths = panelWidthsArr.filter((_, i) => i !== n && i !== n + 1 && i !== n + 2);
            } else {
              glassWidths = panelWidthsArr.filter((_, i) => i !== n && i !== n + 1);
            }
          }
        } else {
          const gateIdx = gateSide === 'left' ? 0 : panelWidthsArr.length - 1;
          glassWidths = panelWidthsArr.filter((_, i) => i !== gateIdx);
        }
      } else {
        glassWidths = [...panelWidthsArr];
      }

      const allPanelsSum = panelWidthsArr.reduce((a, b) => a + b, 0);
      // gapSpacings[] contains ALL gaps inline including HINGE_GAP(10) and LATCH_GAP(7).
      const fixedGapsSum = hasGate ? (HINGE_GAP + LATCH_GAP) : 0;
      const varGapsSum   = gapSpacings.reduce((a, b) => a + b, 0) - fixedGapsSum;
      const total        = allPanelsSum + varGapsSum + fixedGapsSum;
      const primaryW     = widths[0];

      // Hard correction: guarantee total == sectionLength exactly — no remainder ever.
      // Distribute ±1mm across variable gaps cycling through them until exact.
      // Each variable gap may receive more than 1mm total across passes — this is intentional
      // and always produces a valid fence that adds up to exactly the section length entered.
      let actualRemaining = sectionLength - total;
      if (actualRemaining !== 0) {
        const corrDelta  = actualRemaining > 0 ? +1 : -1;
        let   toCorrect  = Math.abs(actualRemaining);
        const varIndices = [];
        for (let i = 0; i < gapSpacings.length; i++) {
          if (!fixedGapIndices.has(i)) varIndices.push(i);
        }
        if (varIndices.length > 0) {
          let vi = 0;
          while (toCorrect > 0) {
            gapSpacings[varIndices[vi % varIndices.length]] += corrDelta;
            vi++;
            toCorrect--;
          }
        }
        // Recompute — must now be exactly zero
        const corrVarGapsSum = gapSpacings.reduce((a, b) => a + b, 0) - fixedGapsSum;
        actualRemaining = sectionLength - (allPanelsSum + corrVarGapsSum + fixedGapsSum);
      }

      return {
        panelWidth:         primaryW,
        // W2G: no glass hinge panel, but hingeW IS the extra replacement panel on the latch side.
        // Store hingeWidth so renderers know the hinge panel size.
        hingeWidth:         hasHingePanel ? hingeW : 0,
        hasWallGateReplacement: false,
        numVariablePanels:  n,
        numPanels:          n + (hasHingePanel ? 1 : 0),
        regularPanels:      n,
        hingePanels:        hasHingePanel ? 1 : 0,
        gateAtRakeEnd:      gateAtRakeEnd || false,
        gatePanels:         hasGate  ? 1 : 0,
        rakedPanels:        hasRaked ? 1 : 0,
        gateSide:           hasGate  ? gateSide : null,
        gateHingeType:      hasGate  ? gateHingeType : null,
        wallGateEnd:        effectiveWallToGlass ? wallGateEnd : null,
        rakedSide:          hasRaked ? (section.rakedSide || 'left') : null,
        gapSpacing:         baseGap,
        panelWidths:        glassWidths,
        gapSpacings:        gapSpacings,
        fixedGapIndices:    [...fixedGapIndices], // positions of LATCH/HINGE gaps in gapSpacings[]
        originalSectionLength: sectionLength,
        totalRequired:      total,
        totalGaps:          vc(n),
        totalGapWidth:      varGapsSum,
        totalPanelCount:    n + (hasGate ? (hasHingePanel ? 2 : 1) : 0) + (hasRaked ? 1 : 0),
        variablePanelWidth: allPanelsSum,
        remaining:          actualRemaining,
        variance:           Math.abs(primaryW - preferredPanelLength),
        gapVariance:        Math.abs(baseGap - preferredGapSpacing),
        withinVarianceLimit: true,
        optimized:          false,
        score:              1.0,
        panelWeight:        1.0,
        gapWeight:          1.0,
        remainingScore:     1.0
      };
    };

    // ── PREFERRED PANEL SOLVER ───────────────────────────────────────────
    // Pass 1: All panels = preferred. Gaps flex ±1mm for exact total.
    // Pass 2: m×preferred + k×other (k=1 or 2), other in ±50mm steps.
    //         Maximise preferred panel count. Other must be ≥50% of preferred.
    // Pass 3: Uniform expansion ±50mm from preferred (all panels same size).
    // ─────────────────────────────────────────────────────────────────────

    let bestResult = null;
    let bestScore  = Infinity;
    const hingeCount = hasHingePanel ? 1 : 0;

    // ── Pass 1: ALL panels = preferred. Gaps flex ±1mm per gap to hit exact total ──
    // Priority order:
    //   1. Keep ALL panels at preferredPanelLength (never adjust panels in this pass).
    //   2. Compute the exact gap needed: rawGap = netLength - panels / variableGapCount.
    //   3. Floor/ceil to integer mm; spread remainder as ±1mm across variable gaps only.
    //      Fixed hardware gaps (HINGE 10mm, LATCH 7mm) are NEVER touched.
    //   4. Accept if baseGap ∈ [MIN_GAP, MAX_GAP] AND every spread gap stays in range.
    //   5. If rawGap is out of range entirely, try clamping to MIN_GAP or MAX_GAP and
    //      absorbing the shortfall/excess as ±1mm spread — still preferred panels only.
    //      Only skip this panel count if even ±1mm spread cannot fix it.
    for (let n = 1; n <= 60; n++) {
      const gaps       = vc(n);
      const panelCount = n + hingeCount;
      if (netLength / panelCount < MIN_PANEL) break;

      const usedByPanels = preferredPanelLength * panelCount;
      const gapPool      = netLength - usedByPanels;
      if (gapPool <= 0) break;

      const rawGap = gapPool / gaps;

      // ── Step A: normal in-range path (rawGap already within [MIN_GAP, MAX_GAP]) ──
      if (rawGap >= MIN_GAP && rawGap <= MAX_GAP) {
        const gapFloor = Math.floor(rawGap);
        const gapCeil  = gapFloor + 1;
        const remFloor = (gapPool - gapFloor * gaps) | 0;
        const remCeil  = (gapCeil  * gaps - gapPool) | 0;
        let baseGap, rem;
        if (remFloor === 0) {
          baseGap = gapFloor; rem = 0;
        } else if (gapCeil <= MAX_GAP && remCeil === 0) {
          baseGap = gapCeil;  rem = 0;
        } else {
          const floorDev = Math.abs(gapFloor - preferredGapSpacing);
          const ceilDev  = gapCeil <= MAX_GAP ? Math.abs(gapCeil - preferredGapSpacing) : Infinity;
          if (ceilDev <= floorDev && gapCeil <= MAX_GAP) {
            baseGap = gapCeil;  rem = -remCeil;
          } else {
            baseGap = gapFloor; rem = remFloor;
          }
        }
        if (baseGap >= MIN_GAP && baseGap <= MAX_GAP) {
          // Verify spread gap also stays in range
          const spreadGap = rem !== 0 ? baseGap + (rem > 0 ? 1 : -1) : baseGap;
          if (spreadGap >= MIN_GAP && spreadGap <= MAX_GAP) {
            const gapDev = Math.abs(baseGap - preferredGapSpacing);
            if (gapDev < bestScore) {
              bestScore  = gapDev;
              const widths      = Array(n).fill(preferredPanelLength);
              const hingeActual = hingeCount ? clampHinge(preferredPanelLength) : 0;
              bestResult = buildResult(n, widths, hingeActual, baseGap, baseGap, rem);
            }
          }
        }
        continue; // already handled, move to next n
      }

      // ── Step B: rawGap out of range — try ±1mm clamp rescue (preferred panels preserved) ──
      // Clamp rawGap to the nearest boundary (MIN_GAP or MAX_GAP), then check if the
      // shortfall/excess (= diff × gaps mm total) can be absorbed by spreading ±1mm
      // across variable gaps. Each variable gap may only move by ±1mm from clampedBase,
      // so max absorbable = gaps mm (one unit per gap). If shortfall ≤ gaps, it fits.
      const clampedBase = rawGap < MIN_GAP ? MIN_GAP : MAX_GAP;
      const shortfall   = Math.round(Math.abs(gapPool - clampedBase * gaps)); // mm to absorb
      if (shortfall <= gaps) {
        // Spread the shortfall as ±1mm across 'shortfall' variable gaps.
        // rem > 0: clampedBase is floor, need to add +1mm to rem gaps
        // rem < 0: clampedBase is ceil,  need to subtract 1mm from |rem| gaps
        const rem = (gapPool - clampedBase * gaps) | 0; // positive = need to add, negative = remove
        const spreadGap = clampedBase + (rem > 0 ? 1 : -1);
        if (spreadGap >= MIN_GAP && spreadGap <= MAX_GAP) {
          const gapDev = Math.abs(clampedBase - preferredGapSpacing);
          if (gapDev < bestScore) {
            bestScore  = gapDev;
            const widths      = Array(n).fill(preferredPanelLength);
            const hingeActual = hingeCount ? clampHinge(preferredPanelLength) : 0;
            bestResult = buildResult(n, widths, hingeActual, clampedBase, clampedBase, rem);
          }
        }
      }
      // If shortfall > gaps, this panel count genuinely can't fit with preferred panels.
      // Continue to next n — Pass 1.5 will try panel adjustments if no n works.
    }
    if (bestResult) return bestResult;

    // ── Pass 1.5: preferred panels + adjust 1-2 panels to fit ──────────
    // Peter's rule: keep preferred panels, adjust 1-2 by ±50mm steps.
    // Score by: (1) gap closest to preferred, (2) adjusted panels closest to preferred.
    // This avoids tiny filler panels — a 2000mm adjustment beats a 400mm filler.
    {
      let adjBest = null; let adjScore = Infinity;
      for (let n = 1; n <= 30; n++) {
        const panelCount = n + hingeCount;
        if (netLength / panelCount < MIN_PANEL) break;

        for (let k = 1; k <= Math.min(2, n); k++) {
          const m = n - k;
          const prefUsed = (m + hingeCount) * preferredPanelLength;

          // Boss rule: 1-3 panels can adjust by exactly ±50mm from preferred.
          // All panels must be within 50mm of each other.
          // Try both preferredPanelLength-50 and preferredPanelLength+50 only.
          const adjCandidates = [
            preferredPanelLength - 50,
            preferredPanelLength + 50
          ].filter(w => w >= MIN_PANEL && w <= MAX_PANEL);

          for (const adjPanel of adjCandidates) {
            const gapPool = netLength - prefUsed - k * adjPanel;
            if (gapPool <= 0) continue;

            const gaps   = vc(n);
            const rawGap = gapPool / gaps;
            if (rawGap < MIN_GAP || rawGap > MAX_GAP) continue;

            const gf = Math.floor(rawGap); const gc = gf + 1;
            const rf = (gapPool - gf * gaps) | 0;
            const rc = (gc * gaps - gapPool) | 0;
            let baseGap, rem;
            if (rf === 0) { baseGap = gf; rem = 0; }
            else if (gc <= MAX_GAP && rc === 0) { baseGap = gc; rem = 0; }
            else {
              const fd = Math.abs(gf - preferredGapSpacing);
              const cd = gc <= MAX_GAP ? Math.abs(gc - preferredGapSpacing) : Infinity;
              if (cd <= fd && gc <= MAX_GAP) { baseGap = gc; rem = -rc; }
              else { baseGap = gf; rem = rf; }
            }
            if (baseGap < MIN_GAP || baseGap > MAX_GAP) continue;
            if (rem !== 0) {
              const sp = baseGap + (rem > 0 ? 1 : -1);
              if (sp < MIN_GAP || sp > MAX_GAP) continue;
            }

            // Score: gap closest to preferred FIRST, then panel closest to preferred.
            // k (number of adjusted panels) is NOT in score — 2x2000 beats 1x400
            // because 2000mm is much closer to preferred 1800mm than 400mm is.
            const gapDev   = Math.abs(baseGap - preferredGapSpacing);
            const panelDev = Math.abs(adjPanel - preferredPanelLength);
            const score    = gapDev * 100000 + panelDev;
            if (score < adjScore) {
              adjScore = score;
              const mid    = Math.floor(m / 2);
              // Interleave adjusted panels evenly among preferred panels
              const widths = [...Array(mid).fill(preferredPanelLength),
                              ...Array(k).fill(adjPanel),
                              ...Array(m - mid).fill(preferredPanelLength)];
              const hingeActual = hingeCount ? clampHinge(preferredPanelLength) : 0;
              adjBest = buildResult(n, widths, hingeActual, baseGap, baseGap, rem);
            }
          }
        }
        if (adjBest && adjScore < 5 * 100000) break; // close-to-preferred solution found
      }
      if (adjBest) return adjBest;
    }

    // ── Pass 2: m×preferred + k×other ────────────────────────────────────
    // Other panel must be ≥50% of preferred (no tiny filler panels).
    // Try other sizes in ±50mm steps. Maximise m (preferred count).
    const minOtherSize = Math.max(MIN_PANEL, Math.round(preferredPanelLength * 0.5 / 50) * 50);
    let mixBest  = null;
    let mixScore = Infinity;

    for (let n = 2; n <= 30; n++) {
      const gaps       = vc(n);
      const panelCount = n + hingeCount;
      if (netLength / panelCount < MIN_PANEL) break;

      for (let k = 1; k <= Math.min(2, n - 1); k++) {
        const m        = n - k; // preferred panel count
        const prefUsed = (m + hingeCount) * preferredPanelLength;

        // Boss rule: other panel = preferredPanelLength ±50mm only
        const candidates = [preferredPanelLength - 50, preferredPanelLength + 50]
          .filter(w => w >= minOtherSize && w >= MIN_PANEL && w <= MAX_PANEL);
        for (const otherSize of candidates) {
          const remaining = netLength - prefUsed - k * otherSize;
          if (remaining <= 0) continue;

          const rawGap = remaining / gaps;
          if (rawGap < MIN_GAP || rawGap > MAX_GAP) continue;

          const gapFloor = Math.floor(rawGap);
          const gapCeil  = gapFloor + 1;
          const remFloor = (remaining - gapFloor * gaps) | 0;
          const remCeil  = (gapCeil   * gaps - remaining) | 0;
          let baseGap, rem;
          if (remFloor === 0) {
            baseGap = gapFloor; rem = 0;
          } else if (gapCeil <= MAX_GAP && remCeil === 0) {
            baseGap = gapCeil;  rem = 0;
          } else {
            const floorDev = Math.abs(gapFloor - preferredGapSpacing);
            const ceilDev  = gapCeil <= MAX_GAP ? Math.abs(gapCeil - preferredGapSpacing) : Infinity;
            if (ceilDev <= floorDev && gapCeil <= MAX_GAP) {
              baseGap = gapCeil;  rem = -remCeil;
            } else {
              baseGap = gapFloor; rem = remFloor;
            }
          }
          if (baseGap < MIN_GAP || baseGap > MAX_GAP) continue;
          if (rem !== 0) {
            const spreadGap = baseGap + (rem > 0 ? 1 : -1);
            if (spreadGap < MIN_GAP || spreadGap > MAX_GAP) continue;
          }

          // Score: fewer non-preferred panels, gap closer to preferred
          const panelOffset = Math.abs(otherSize - preferredPanelLength);
          const score = k * 10000 + panelOffset * 100 + Math.abs(baseGap - preferredGapSpacing);
          if (score < mixScore) {
            mixScore = score;
            const widths      = [...Array(m).fill(preferredPanelLength), ...Array(k).fill(otherSize)];
            const hingeActual = hingeCount ? clampHinge(preferredPanelLength) : 0;
            mixBest = buildResult(m + k, widths, hingeActual, baseGap, baseGap, rem);
          }
        }
      }
      // Stop once we have a single-filler solution
      if (mixBest && mixScore < 1 * 10000) break;
    }
    if (mixBest) return mixBest;

    // ── Pass 3: Uniform ±50mm expansion (all panels same size) ───────────
    // Hard rules (boss):
    //   1. Max deviation from preferred = 50mm — never produce e.g. 2050mm from 1800mm preferred
    //   2. Never exceed preferredPanelLength — if customer sets max 1800mm, no panel goes above 1800mm
    let expandScore = Infinity;

    for (let n = 1; n <= 60; n++) {
      const gaps       = vc(n);
      const panelCount = n + hingeCount;
      if (netLength / panelCount < MIN_PANEL) break;

      for (let offset = 50; offset <= 50; offset += 50) {
        const candidates = [preferredPanelLength - offset, preferredPanelLength + offset]
          .filter(w => w >= MIN_PANEL && w <= MAX_PANEL);

        for (const tryW of candidates) {
          const usedByPanels = tryW * panelCount;
          const gapPool      = netLength - usedByPanels;
          if (gapPool <= 0) continue;

          const rawGap = gapPool / gaps;
          if (rawGap < MIN_GAP || rawGap > MAX_GAP) continue;

          const gapFloor = Math.floor(rawGap);
          const gapCeil  = gapFloor + 1;
          const remFloor = (gapPool - gapFloor * gaps) | 0;
          const remCeil  = (gapCeil  * gaps - gapPool) | 0;
          let baseGap, rem;
          if (remFloor === 0) {
            baseGap = gapFloor; rem = 0;
          } else if (gapCeil <= MAX_GAP && remCeil === 0) {
            baseGap = gapCeil;  rem = 0;
          } else {
            const floorDev = Math.abs(gapFloor - preferredGapSpacing);
            const ceilDev  = gapCeil <= MAX_GAP ? Math.abs(gapCeil - preferredGapSpacing) : Infinity;
            if (ceilDev <= floorDev && gapCeil <= MAX_GAP) {
              baseGap = gapCeil;  rem = -remCeil; // spread DOWN from ceil
            } else {
              baseGap = gapFloor; rem = remFloor;  // spread UP from floor
            }
          }
          if (baseGap < MIN_GAP || baseGap > MAX_GAP) continue;
          if (rem !== 0) {
            const spreadGap = baseGap + (rem > 0 ? 1 : -1);
            if (spreadGap < MIN_GAP || spreadGap > MAX_GAP) continue;
          }

          const panelDev = Math.abs(tryW - preferredPanelLength);
          const gapDev   = Math.abs(baseGap - preferredGapSpacing);
          const score    = panelDev * 1000 + gapDev;

          if (score < expandScore) {
            expandScore = score;
            const widths      = Array(n).fill(tryW);
            const hingeActual = hingeCount ? clampHinge(preferredPanelLength) : 0;
            bestResult = buildResult(n, widths, hingeActual, baseGap, baseGap, rem);
          }
        }
      }
      if (bestResult && expandScore < 50 * 1000 + MAX_GAP) break;
    }

    return bestResult || null;
  }

  // ── GUARANTEED FALLBACK SOLVER ─────────────────────────────────────────
  // Called when peterCalculateLayout returns null. Ignores gap/panel preferences.
  // Finds ANY valid panel count where gaps stay within physical limits (5mm–120mm).
  // Always returns a result for any section length >= MIN_SECTION_LENGTH.
  guaranteedSolve(section) {
    const sectionLength = parseInt(section.length);
    if (!sectionLength) return null;

    const hasGate    = !!section.hasGate;
    const hasRaked   = !!section.hasRaked;
    const hasHingePanel = hasGate && section.gateHingeType !== 'wall-to-glass';
    const gateWidth  = hasGate  ? (parseInt(section.gateLength) || 920) : 0;  // 2026-08-30: was hardcoded 1000; blade gates are 800/850/900/920
    const rakedWidth = hasRaked ? (this.getRakedPanelWidth ? this.getRakedPanelWidth(section.fenceHeight) : CONFIG.DEFAULTS.RAKED_PANEL_WIDTH) : 0;
    const LATCH_GAP  = 70;  // 2026-08-30: 50mm gate post + 20mm latch clearance
    const HINGE_GAP  = 60;  // 2026-08-30: 50mm gate post + 10mm hinge clearance
    const SOFT_MIN   = 5;   // absolute minimum gap — anything below this is unusable
    const SOFT_MAX   = 120; // absolute maximum gap — beyond this looks wrong

    let netLength = sectionLength;
    if (hasGate)  netLength -= gateWidth + LATCH_GAP + HINGE_GAP;
    if (hasRaked) netLength -= rakedWidth;
    // 2026-08-30 REVERTED again. Zero-panel gate sections need FIVE layers to agree, not one:
    //   1. guaranteedSolve            - fixed (this guard)
    //   2. the preview renderer       - fixed (cutPlan.panels.length === 0 && !section.hasGate && !section.hasRaked)
    //   3. calculateCutPlan           - already returned { valid:true, panels:[] }
    //   4. updateCalculationResults   - REJECTS it: "!calculation.perfomesh" is true because the
    //                                   zero-panel object has no perfomesh block, so optimizedConfigs
    //                                   is nulled and the quote silently loses the whole section
    //   5. calculatePanelCosts        - unverified; must still emit gate + posts + hardware with
    //                                   no panels
    // Layer 4 produced a rendered gate-only fence with a $0.00 TOTAL, which is far more dangerous
    // than an error message. Reverted until 4 and 5 are done together.
    // GLASS already works end to end and keeps its relaxation.
    //
    // ── SUPERSEDED 2026-08-30 (Claude). Kept as history, but both its conclusion and its
    // five-layer map were wrong:
    //   * guaranteedSolve() is called from exactly ONE place — the snap-up probe — and is
    //     NOT in the render or quote path at all. That path runs through calculateSpread().
    //     Relaxing this guard never could have caused the $0.00 quote by itself; it only
    //     moved the snap target onto a length calculateSpread could not then lay out.
    //   * "Layer 5 (calculatePanelCosts)" needed no change. It returns 0 with no panels,
    //     which is CORRECT for a gate-only run. Gate, posts and hardware are priced by
    //     calculateGateCosts() and calculatePostAndCapCosts(), called from
    //     updateQuoteDisplay() inside `if (calculation)` — so they only ever depended on
    //     state.optimizedConfigs[n] being non-null, never on the panel count.
    //
    // The real defect was _calculateSpreadCore()'s zero-panel return carrying no
    // `perfomesh` block, now fixed at its one source. With that done this guard is safe:
    // netLength === 0 means the section IS the gate between its two posts. Only a NEGATIVE
    // remainder is impossible. peterCalculateLayout() keeps its own `<= 0` deliberately.
    if (netLength < 0) return null;
    // 2026-08-30 (Pete): "i need to be able to make small gate sections for the sides of houses".
    // netLength is already gate + its two 50mm posts + 30mm clearance, so landing on exactly 0 means
    // the section IS the gate between its two posts — a complete run with no panels. The loop below
    // starts at n=1 and can never express that, which is why the stated minimum validated but would
    // not draw. Only a NEGATIVE remainder is impossible.
    // NOTE: no !hasHingePanel test here, unlike glass. An aluminium gate always hangs off POSTS,
    // never off a panel — "hasHingePanel" is a frameless-glass concept that exists in this file only
    // because the solver was copied from it. At netLength === 0 the section is gate + its two posts,
    // and there is no panel of any kind, hinge or otherwise.
    if (hasGate && !hasRaked && netLength === 0) {
      return {
        panelWidth: 0, hingeWidth: 0,
        numVariablePanels: 0, numPanels: 0, regularPanels: 0, hingePanels: 0,
        gateAtRakeEnd: false, gatePanels: 1, rakedPanels: 0,
        gateSide: section.gateSide || 'right',
        gateHingeType: section.gateHingeType || 'glass-to-glass',
        wallGateEnd: null, rakedSide: null,
        gapSpacing: 0,
        panelWidths: [],
        gapSpacings: [HINGE_GAP, LATCH_GAP],
        fixedGapIndices: [0, 1],
        totalRequired: sectionLength,
        totalGaps: 0, totalGapWidth: 0,
        totalPanelCount: 1,
        variablePanelWidth: 0,
        remaining: 0,
        variance: 0, gapVariance: 0, withinVarianceLimit: true,
        optimized: false, score: 999,
        panelWeight: 1.0, gapWeight: 1.0, remainingScore: 1.0
      };
    }

    const isW2G = section.gateHingeType === 'W2G-REMOVED-2026-08-30' && section.wallGateEnd === 'right';
    const hingeCount = hasHingePanel ? 1 : 0;
    const gateSide = section.gateSide || 'right';
    const rakedSide = section.rakedSide || 'left';
    const vc = (n) => {
      if (!hasGate)  return n + 1 + (hasRaked ? 2 : 0);
      if (!hasHingePanel) return n + 1 + (hasRaked ? 2 : 0);
      // RHS + left rake: W2G right needs n+2 (startGap variable), G2G needs n+1
      if (hasRaked && rakedSide === 'left') return isW2G ? n + 2 : n + 1;
      // LHS + right rake: W2G left needs n+2 (startGap fixed=10mm), G2G needs n+3
      if (gateSide === 'left' && hasRaked && rakedSide === 'right') return isW2G ? n + 2 : n + 3;
      if (gateSide === 'left')  return n + 1 + (hasRaked ? 2 : 0); // LHS no rake
      return n + (hasRaked ? 1 : 0) + 1; // RHS + right rake or no rake
    };

    // Try increasing panel counts until gaps fit
    for (let n = 1; n <= 60; n++) {
      const panelCount = n + hingeCount;
      const minPanelSize = 100;
      if (netLength / panelCount < minPanelSize) break;

      // Try panel sizes from 100mm to 2100mm in 50mm steps
      for (let pw = 2100; pw >= 100; pw -= 50) {
        const used = pw * panelCount;
        const remaining = netLength - used;
        if (remaining <= 0) continue;

        const gaps   = vc(n);
        const rawGap = remaining / gaps;
        if (rawGap < SOFT_MIN || rawGap > SOFT_MAX) continue;
        // FIX 2026-08-10 (Claude): baseGap was USED here but only DECLARED ~60 lines below,
        // at function scope. const is block-scoped and hoisted into the temporal dead zone, so
        // every pass through this loop threw "Cannot access 'baseGap' before initialization"
        // and guaranteedSolve() - the safety net that is supposed to always return a layout -
        // crashed instead. Declaring it here (shadowing the later one) restores the intended
        // behaviour: floor the raw gap, then spread the leftover mm across the gaps below.
        const baseGap = Math.floor(rawGap);

        const rem     = Math.round(remaining - baseGap * gaps);

        // Build gapSpacings and distribute rem via cycling ±1mm through variable gaps
        const gapSpacingsG = Array(gaps).fill(baseGap);
        if (rem !== 0) {
          const corrDelta = rem > 0 ? +1 : -1;
          let toFix = Math.abs(rem);
          let vi = 0;
          while (toFix > 0) {
            gapSpacingsG[vi % gaps] += corrDelta;
            vi++; toFix--;
          }
        }
        // Add fixed gate gaps
        const gapSpacingsFull = [...gapSpacingsG];
        const fixedIndices  = [];
        if (hasGate) {
          gapSpacingsFull.push(HINGE_GAP); fixedIndices.push(gapSpacingsFull.length - 1);
          gapSpacingsFull.push(LATCH_GAP); fixedIndices.push(gapSpacingsFull.length - 1);
        }

        return {
          panelWidth:        pw,
          hingeWidth:        hasHingePanel ? pw : 0,
          numVariablePanels: n,
          numPanels:         n + hingeCount,
          regularPanels:     n,
          hingePanels:       hingeCount,
          gateAtRakeEnd:     false,
          gatePanels:        hasGate  ? 1 : 0,
          rakedPanels:       hasRaked ? 1 : 0,
          gateSide:          hasGate  ? (section.gateSide || 'right') : null,
          gateHingeType:     hasGate  ? (section.gateHingeType || 'glass-to-glass') : null,
          wallGateEnd:       null,
          rakedSide:         hasRaked ? (section.rakedSide || 'left') : null,
          gapSpacing:        baseGap,
          panelWidths:       Array(n).fill(pw),
          gapSpacings:       gapSpacingsFull,
          fixedGapIndices:   fixedIndices,
          totalRequired:     sectionLength,
          totalGaps:         gaps,
          totalGapWidth:     gapSpacingsG.reduce((a,b)=>a+b,0),
          totalPanelCount:   n + hingeCount + (hasGate ? 1 : 0) + (hasRaked ? 1 : 0),
          variablePanelWidth: pw * panelCount,
          remaining:         0,
          variance:          Math.abs(pw - (parseInt(section.panelLength) || 2400)),
          gapVariance:       Math.abs(baseGap - (parseInt(section.gapSpacing) || 25)),
          withinVarianceLimit: true,
          optimized:         false,
          score:             999,
          panelWeight:       1.0,
          gapWeight:         1.0,
          remainingScore:    1.0
        };
      }
    }

    // Absolute last resort: 1 panel, any size — guarantee exact section length
    const minGaps = vc(1);
    // 2026-08-30: this fallback FORCED a panel in via Math.max(100, ...) even with no room, so
    // guaranteedSolve reported success for lengths the real render path then rejected with
    // "Section too short for panels with current settings" — blade snapped a short run to 1051mm
    // and 1051mm would not draw. Refuse when the smallest panel plus minimum gaps will not fit, so
    // "solver says yes" and "it actually draws" mean the same thing.
    if (netLength < (CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH * (1 + hingeCount)) + (SOFT_MIN * minGaps)) return null;
    const onePanelWidth = Math.max(100, Math.min(2100,
      Math.floor((netLength - SOFT_MIN * minGaps) / (1 + hingeCount) / 50) * 50
    ));
    const leftover = netLength - onePanelWidth * (1 + hingeCount);
    const baseGap  = Math.max(SOFT_MIN, Math.floor(leftover / minGaps));
    // Distribute remainder across gaps
    const lrGapSpacings = Array(minGaps).fill(baseGap);
    const lrRem = leftover - baseGap * minGaps;
    if (lrRem !== 0) {
      const d = lrRem > 0 ? +1 : -1;
      let t = Math.abs(lrRem), vi = 0;
      while (t > 0) { lrGapSpacings[vi % minGaps] += d; vi++; t--; }
    }
    // FIX 2026-08-10 (Claude): this last-resort path never returned the two fixed gate
    // hardware gaps. netLength has them stripped up front, and the panel-size loop above
    // pushes them back onto its gap array (see the hasGate block there) - but this path
    // did not, so a gate section came back 30mm short in gapSpacings while still
    // reporting totalRequired === sectionLength. Anything drawing from gapSpacings, or
    // re-deriving a total from the returned parts, was short by exactly the hardware.
    const lrGapSpacingsFull = [...lrGapSpacings];
    const lrFixedIndices = [];
    if (hasGate) {
      lrGapSpacingsFull.push(HINGE_GAP); lrFixedIndices.push(lrGapSpacingsFull.length - 1);
      lrGapSpacingsFull.push(LATCH_GAP); lrFixedIndices.push(lrGapSpacingsFull.length - 1);
    }

    return {
      panelWidth:        onePanelWidth,
      hingeWidth:        hasHingePanel ? onePanelWidth : 0,
      numVariablePanels: 1,
      numPanels:         1 + hingeCount,
      regularPanels:     1,
      hingePanels:       hingeCount,
      gateAtRakeEnd:     true,
      gatePanels:        hasGate  ? 1 : 0,
      rakedPanels:       hasRaked ? 1 : 0,
      gateSide:          hasGate  ? (section.gateSide || 'right') : null,
      gateHingeType:     hasGate  ? (section.gateHingeType || 'glass-to-glass') : null,
      wallGateEnd:       null,
      rakedSide:         hasRaked ? (section.rakedSide || 'left') : null,
      gapSpacing:        baseGap,
      panelWidths:       [onePanelWidth],
      gapSpacings:       lrGapSpacingsFull,
      fixedGapIndices:   lrFixedIndices,
      totalRequired:     sectionLength,
      totalGaps:         minGaps,
      totalGapWidth:     lrGapSpacings.reduce((a,b)=>a+b,0),
      totalPanelCount:   1 + hingeCount + (hasGate ? 1 : 0) + (hasRaked ? 1 : 0),
      variablePanelWidth: onePanelWidth * (1 + hingeCount),
      remaining:         0,
      variance:          9999,
      gapVariance:       9999,
      withinVarianceLimit: false,
      optimized:         false,
      score:             9999,
      panelWeight:       1.0,
      gapWeight:         1.0,
      remainingScore:    1.0
    };
  }

  // ── PerfoMesh Mode B — Equal Spread (customer-facing) ────────────────────
  // This is what the customer sees, pays for, and what goes on the invoice.
  // Mode A (cut plan) runs in parallel but is never shown to the customer.
  // Even Panel Mode solver
  // Distributes available fence length into evenly-sized panels, all <= max_sheet.
  calculateEvenPanels(section_length, post_width, max_sheet, element, gateLeaf) {
    const MAX_SHEET  = max_sheet || 2400;
    const hasGate    = element === 'gate' || element === 'gate_rake';
    const hasRake    = element === 'rake' || element === 'gate_rake';
    const GATE_LEAF  = gateLeaf || 1010;
    const RAKE_SIZE  = CONFIG.DEFAULTS.RAKED_PANEL_WIDTH;
    const fixedMM    = (hasGate ? GATE_LEAF : 0) + (hasRake ? RAKE_SIZE : 0);
    let bestResult = null;

    for (let N = 1; N <= 50; N++) {
      const extraPosts = (hasGate ? 1 : 0) + (hasRake ? 1 : 0);
      const totalPosts = N + 1 + extraPosts;
      const available  = section_length - (totalPosts * post_width) - fixedMM;
      if (available <= 0) break;
      const evenSize = available / N;
      if (evenSize > MAX_SHEET) continue;
      if (evenSize < 300) break;

      // Build evenly-sized panels, fix rounding on last panel
      const panelWidths = Array(N).fill(Math.round(evenSize));
      const sumRounded = panelWidths.reduce((a, b) => a + b, 0);
      const diff = available - sumRounded;
      if (Math.abs(diff) > 0) panelWidths[N - 1] += Math.round(diff);

      const panels = panelWidths.map(w => ({
        footprint: w, sheet: MAX_SHEET,
        is_cut: false, kit: MAX_SHEET + 'mm', isEven: true
      }));

      bestResult = {
        perfomesh: {
          panels, panel_count: N, panel_space: available,
          deductions: totalPosts * post_width + fixedMM,
          actual_sheet: MAX_SHEET, filler: 0, element, post_width,
          checksOut: true, allSheetsValid: true
        },
        valid: true, panel_count: N, panel_space: available,
        deductions: totalPosts * post_width + fixedMM,
        total: section_length,
        panelWidth: Math.round(evenSize),
        evenPanelSize: Math.round(evenSize),
        panelWidths,
        gapSpacings: Array(totalPosts).fill(post_width),
        fixedGapIndices: [],
        regularPanels: N,
        hingePanels: hasGate ? 1 : 0,
        gatePanels: hasGate ? 1 : 0,
        rakedPanels: hasRake ? 1 : 0,
        numPanels: N,
        numVariablePanels: N,
        totalGaps: totalPosts,
        totalGapWidth: totalPosts * post_width,
        gapSpacing: post_width,
        remaining: 0,
        isEvenMode: true,
        billableSheets: N,
        originalSectionLength: section_length,
        withinVarianceLimit: true, optimized: true, variance: 0, gapVariance: 0
      };
      break;
    }
    return bestResult;
  }

  calculateSpread(section_length, post_width, max_sheet, element, isW2P = false, wallGateEnd = null, gateLeaf = 1010, rakeSize = CONFIG.DEFAULTS.RAKED_PANEL_WIDTH) {
    // Hang guard: an absurd length would iterate hundreds of thousands of panels.
    if (!(section_length > 0) || section_length > 200000) return null;
    const MIN_REMAINDER   = 500;
    const STANDARD_SHEETS = [max_sheet];  // use height-correct sheet size
    const hasRake = element === 'rake' || element === 'gate_rake';
    const hasGate = element === 'gate' || element === 'gate_rake';

    // Peter's rule: always honour the user's selected sheet (max_sheet).
    // If filler >= 500mm → accept as-is.
    // If filler < 500mm → mix in a smaller sheet to bring filler above 500mm.
    // Applies to ALL configurations: plain, gate, rake, gate+rake.
    const _cfFit = (r) => this._cfReconcileSpread(r, section_length, post_width, element, gateLeaf, rakeSize, isW2P, wallGateEnd);
    const result = this._calculateSpreadCore(section_length, post_width, max_sheet, element, isW2P, wallGateEnd, gateLeaf, rakeSize);
    const filler = result.remaining ?? 0;

    // Accept: clean fit or filler meets threshold
    if (result.valid && (filler === 0 || filler >= MIN_REMAINDER)) return _cfFit(result);

    // Filler < 500mm OR invalid — try mixing in a smaller sheet
    // NOTE (18 Aug 2026): STANDARD_SHEETS is [max_sheet], so this filter is always empty
    // and the mixed-sheet cascade never runs. Left in place deliberately — blade has a
    // single sheet size, so there is no smaller STANDARD sheet to mix in. The overshoot
    // it was meant to absorb is handled by _cfReconcileSpread() instead.
    const smallerSheets = STANDARD_SHEETS.filter(s => s < max_sheet).reverse();
    for (const mixSheet of smallerSheets) {
      const mixResult = this._calculateMixedSpread(section_length, post_width, max_sheet, mixSheet, element, isW2P, wallGateEnd, gateLeaf, rakeSize);
      if (mixResult && (mixResult.remaining === 0 || mixResult.remaining >= MIN_REMAINDER)) {
        return _cfFit(mixResult);
      }
    }

    // Best available — return whatever the solver gives
    return _cfFit(result);
  }

  // Pete, 18 Aug 2026: "the blade error is concerning check and fix."
  //
  // Blade drew a fence LONGER than the section it is built in — measured at 5050, 7500 and
  // 9950 plain, and 3500 and 6000 gated, always by exactly 50mm, which is exactly one post.
  // Cause: the branch in _calculateSpreadCore() that promotes an overflowing filler to
  // another full sheet recomputes its post count as (sheets + gate + rake + 1) — it drops
  // the filler slot it has just created, so the layout is assembled with one more post than
  // the arithmetic paid for. The mirror of it (the clean-fit branch KEEPING a filler-slot
  // post when there is no filler) is why 5000, 7450 and 9900 draw 50mm SHORT.
  //
  // Rather than re-cut that branch logic inside a 776KB file that cannot be fully
  // exercised, the assembled layout is reconciled here, at the single exit both solver
  // paths return through. One invariant, checked on every section:
  //
  //     Σ panels + Σ posts + gate + rake  ===  section length
  //
  // OVER: take the excess off the widest panel. Blade panels are cut to size on site and
  // billed by COUNT at the full sheet price, so shaving one panel changes the drawing and
  // not the money — verified at every affected length before this was written (5050 bills
  // 3 panels either way, 7500 bills 4, 6000 gated bills 3).
  //
  // UNDER: left alone, deliberately. Filling 5000mm exactly needs a THIRD panel where two
  // are billed today (+$271.14), and that is a pricing decision for Pete, not something to
  // change silently. The shortfall is recorded on _cfShortfall so it can be surfaced.
  _cfReconcileSpread(result, section_length, post_width, element, gateLeaf, rakeSize, isW2P, wallGateEnd) {
    try {
      if (!result || !Array.isArray(result.panelWidths) || !result.panelWidths.length) return result;
      const MIN_PANEL = 100;                       // blade's smallest cut panel
      const hasGate   = element === 'gate' || element === 'gate_rake';
      const hasRake   = element === 'rake' || element === 'gate_rake';
      const fixedMM   = (hasGate ? (gateLeaf || 0) : 0) + (hasRake ? (rakeSize || 0) : 0);
      // Posts come from what is actually DRAWN, never from gapSpacings. The solver's
      // totalPosts disagrees with its own panelWidths — that is the whole defect. Every
      // component (panel, gate, rake) gets a post each side and adjacent ones share, so
      // posts = components + 1, less one when the gate hangs off a wall (W2P).
      const comps     = result.panelWidths.length + (hasGate ? 1 : 0) + (hasRake ? 1 : 0);
      const w2pSaving = (isW2P && wallGateEnd) ? 1 : 0;
      const truePosts = Math.max(2, comps + 1 - w2pSaving);
      const postsMM   = truePosts * post_width;
      const panelsMM  = result.panelWidths.reduce((a, b) => a + b, 0);
      const excess    = (panelsMM + postsMM + fixedMM) - section_length;

      // Keep the plan's own post bookkeeping honest either way.
      result.gapSpacings   = Array(truePosts).fill(post_width);
      result.totalGaps     = truePosts;
      result.totalGapWidth = postsMM;
      result.gapSpacing    = post_width;

      if (excess === 0) { result._cfShortfall = 0; return result; }

      // ── SHORT: the run cannot be filled by the panels on hand ──────────────
      // Pete, 20 Aug 2026: "charge the extra panel." 5000mm cannot be filled with 2400mm
      // panels and 50mm posts — two panels and three posts is 4950. Add one more panel and
      // redistribute, because appending the 50mm shortfall as its own panel is impossible:
      // the extra panel brings its own post with it, so the new panel would have to be
      // (shortfall − post_width) = 0mm wide. Instead, re-cut the whole run across one more
      // panel: 5000 becomes 2400 + 2300 + 100 across 4 posts, and bills 3 panels where it
      // billed 2. Every panel is cut to size on site and billed at the full sheet price,
      // so the customer pays for one more sheet and gets a fence that fits the opening.
      if (excess < 0) {
        const oldN    = result.panelWidths.length;
        const n2      = oldN + 1;
        const posts2  = Math.max(2, n2 + (hasGate ? 1 : 0) + (hasRake ? 1 : 0) + 1 - w2pSaving);
        const avail   = section_length - (posts2 * post_width) - fixedMM;
        const maxSheet = Math.max.apply(null, result.panelWidths);
        if (avail < n2 * MIN_PANEL || avail > n2 * maxSheet) {
          result._cfShortfall = -excess;      // cannot be filled — leave the layout alone
          return result;
        }
        // Greedy full sheets first, always reserving MIN_PANEL for every panel still to come.
        const filled = [];
        let rem = avail;
        for (let k = 0; k < n2; k++) {
          const reserve = MIN_PANEL * (n2 - 1 - k);
          const take    = Math.min(maxSheet, rem - reserve);
          filled.push(take);
          rem -= take;
        }
        if (rem !== 0 || filled.some(w => w < MIN_PANEL)) {
          result._cfShortfall = -excess;
          return result;
        }
        result.panelWidths   = filled;
        result.gapSpacings   = Array(posts2).fill(post_width);
        result.totalGaps     = posts2;
        result.totalGapWidth = posts2 * post_width;
        result.total         = section_length;
        result.remaining     = filled[filled.length - 1];
        result._cfFilled     = -excess;
        result._cfShortfall  = 0;
        // Keep whichever panel-count convention this result was using, incremented.
        ['regularPanels', 'numPanels', 'numVariablePanels', 'panel_count'].forEach((k) => {
          if (typeof result[k] === 'number' && result[k] === oldN) result[k] = n2;
        });
        if (result.perfomesh && Array.isArray(result.perfomesh.panels)) {
          const proto = result.perfomesh.panels[0] || {};
          result.perfomesh.panels = filled.map((w) => Object.assign({}, proto, { footprint: w, sheet: w }));
          result.perfomesh.panel_count = n2;
          result.perfomesh.checksOut   = true;
        }
        return result;
      }

      const widths = result.panelWidths.slice();
      // Widest first: it has the most room to give and stays a sensible panel afterwards.
      const order = widths.map((w, i) => i).sort((a, b) => widths[b] - widths[a]);
      let left = excess;
      for (const i of order) {
        if (left <= 0) break;
        const room = widths[i] - MIN_PANEL;
        if (room <= 0) continue;
        const take = Math.min(room, left);
        widths[i] -= take;
        left -= take;
      }
      if (left > 0) return result;                 // cannot absorb it — leave the original

      result.panelWidths   = widths;
      result.total         = section_length;
      result._cfReconciled = excess;
      result._cfShortfall  = 0;
      if (result.perfomesh && Array.isArray(result.perfomesh.panels)) {
        result.perfomesh.panels.forEach((p, i) => {
          if (widths[i] == null) return;
          p.footprint = widths[i];
          p.sheet     = widths[i];
        });
        result.perfomesh.checksOut = true;
      }
    } catch (e) {
      // A drawing correction must never break the calculator.
      try { console.warn('[cf-blade-fit] reconcile failed:', e && e.message); } catch (_) {}
    }
    return result;
  }

  // Mixed panel solver — tries replacing one full sheet with a smaller sheet to fix sub-500mm filler
  _calculateMixedSpread(section_length, post_width, mainSheet, mixSheet, element, isW2P = false, wallGateEnd = null, gateLeaf = 1010, rakeSize = CONFIG.DEFAULTS.RAKED_PANEL_WIDTH) {
    const MIN_FILLER = 100;
    const GATE_LEAF  = gateLeaf || 1010;
    const RAKE_SIZE  = rakeSize;
    const hasGate    = element === 'gate' || element === 'gate_rake';
    const hasRake    = element === 'rake' || element === 'gate_rake';

    // Fixed deductions from gate/rake
    const fixedMM = (hasGate ? GATE_LEAF : 0) + (hasRake ? RAKE_SIZE : 0);
    const w2pSaving = (isW2P && hasGate && wallGateEnd) ? post_width : 0;

    // Get base result to know how many full mainSheet panels fit
    // Note: base may be invalid (sub-500mm filler) — still use its regularPanels count
    const base = this._calculateSpreadCore(section_length, post_width, mainSheet, element, isW2P, wallGateEnd, gateLeaf, rakeSize);
    // Compute baseQty directly if base is invalid
    let baseQty = base.regularPanels || 0;
    if (!base.valid || baseQty < 1) {
      // Estimate qty from available space
      const fixedMMEst = (hasGate ? GATE_LEAF : 0) + (hasRake ? RAKE_SIZE : 0);
      const availEst = section_length - 2 * post_width - fixedMMEst;
      baseQty = Math.max(1, Math.floor(availEst / (mainSheet + post_width)));
    }
    if (baseQty < 2) return null; // need at least 2 to swap one out

    // Try swapping one full sheet for mixSheet, try reducing baseQty if needed
    for (let tryQty = baseQty; tryQty >= 2; tryQty--) {
      const fixedComponents = (hasGate ? 1 : 0) + (hasRake ? 1 : 0);
      const panelComponents = (tryQty - 1) + 1 + 1;
      const totalPostsMm    = (panelComponents + fixedComponents + 1) * post_width - w2pSaving;
      const usable          = section_length - totalPostsMm - fixedMM;
      const filler          = usable - ((tryQty - 1) * mainSheet) - mixSheet;

      if (filler < 0 || filler >= mainSheet) continue;

      if (filler === 0 || filler >= MIN_FILLER) {
        const panelWidths = [...Array(tryQty - 1).fill(mainSheet), mixSheet];
        if (filler > 0) panelWidths.push(filler);
        return {
          valid: true,
          panelWidth: mainSheet,
          panelWidths,
          regularPanels: panelWidths.length,
          hingePanels: 0,
          gatePanels: hasGate ? 1 : 0,
          rakedPanels: hasRake ? 1 : 0,
          numPanels: panelWidths.length,
          remaining: filler,
          gapSpacing: post_width,
          totalGaps: panelWidths.length + 1,
          originalSectionLength: section_length,
          mixed: true,
          mixSheet
        };
      }
    }
    return null;
  }

  // Internal solver — does the actual spread calculation for a given sheet size.
  // Called by calculateSpread; do not call directly.
  _calculateSpreadCore(section_length, post_width, max_sheet, element, isW2P = false, wallGateEnd = null, gateLeaf = 1010, rakeSize = CONFIG.DEFAULTS.RAKED_PANEL_WIDTH) {
    // Option A fix: FRAME=0 (no frame deduction), actual sheet = selected size
    // Peter's rule: filler (remainder) must be >= 500mm or 0 (clean fit).
    // If no qty produces filler >= 500mm, reduce panel count until it does.
    const MIN_FILLER   = 100; // Blade: minimum cut panel is 100mm
    const actual_sheet = max_sheet;

    // ── Fixed component sizes ─────────────────────────────────────────────
    // FIX: Use actual gate leaf size (passed from section.gateLength) not hardcoded 1010
    const GATE_LEAF = gateLeaf;
    const RAKE_SIZE = rakeSize; // height-aware: 1200 (1200mm fence), 1825 (1500mm), 1632 (1800mm)
    const hasGate   = element === 'gate' || element === 'gate_rake';
    const hasRake   = element === 'rake' || element === 'gate_rake';

    // ── W2P: gate is mounted to wall — remove one post on the wall side ────
    // W2P gate-right: wall is on the right → no right post on gate → -1 post
    // W2P gate-left:  wall is on the left  → no left post on gate  → -1 post
    // That saved 50mm gets added to the filler panel.
    // 2026-08-30 (Pete): aluminium gates get a post on BOTH sides — there is no wall-mount
    // hinge for aluminium. Held at 0 rather than derived, so this survives a stale saved
    // section that still carries a wall-to-glass hinge type.
    const w2pPostSaving = 0;

    // ── Gate-only sections (2026-08-30, Claude) ───────────────────────────
    // A gate-only run — the gate hung between its own two posts, no panels at all — is a
    // real, buildable section and is Pete's stated minimum: 920 gate + 130 = 1050 on blade.
    //
    // This is computed HERE, at the top, and deliberately NOT from fixedMM/totalPosts.
    // Those two are only ever assigned INSIDE the solver loop below, and for a gate-only
    // section that loop never finds an exit — so by the time the zero-panel return is
    // reached they are both still 0. Deriving the band from them yields a 100mm base
    // instead of 1020mm and silently rejects every real gate-only section.
    //
    // The band matters in both directions, and both were measured on the preview:
    //   TOO SHORT — `filler` goes NEGATIVE once the section cannot hold the gate and its
    //   posts, so a bare `qty <= 0 && filler <= 0` test is TRUE for a 400mm section. That
    //   drew a 1020mm fence inside 400mm and quoted it at $526.53. A confident price for
    //   something unbuildable is worse than an error message.
    //   TOO LONG — past ~120mm of clearance the gate should hang off a panel rather than
    //   float in an oversized gap. 120 matches SOFT_MAX in guaranteedSolve().
    //
    // Low end is base + 30 (10mm hinge + 20mm latch clearance), which is exactly 1050.
    // 2026-08-30 (Pete): "check all dead bands and fix." Generalised from gate-only to
    // element-only, so a rake — and a gate + rake — get the same treatment as a gate.
    const _elementOnlyCount = (hasGate ? 1 : 0) + (hasRake ? 1 : 0);
    const _elementOnlyFixed = (hasGate ? GATE_LEAF : 0) + (hasRake ? RAKE_SIZE : 0);
    // Posts = components + 1. Two for one component, three for a gate AND a rake — the
    // old constant 2 was only ever right because this band excluded the rake.
    const _elementOnlyBase  = _elementOnlyFixed + ((_elementOnlyCount + 1) * post_width) - w2pPostSaving;
    const _isElementOnly    = _elementOnlyCount > 0
      // A gate needs 30mm of clearance (10mm hinge + 20mm latch); a rake is bolted between
      // its posts and needs none, so a rake-only run starts exactly at its own base.
      && section_length >= _elementOnlyBase + (hasGate ? 30 : 0)
      // 2026-08-30 (Pete): the upper edge runs right up to the first length a 100mm filler
      // panel can occupy — one more post plus 99mm — so there is no dead band left between
      // "gate only" and "gate + panel". A flat +120 left a hole: blade 1141-1199 and flat top
      // 1191-1999 drew nothing at all while still quoting.
      // The upper edge runs right up to the first length a minimum panel can occupy — one
      // more post plus (minimum - 1) — so no dead band is left between "element only" and
      // "element + panel". With a rake that minimum is one post width, so the most slack a
      // rake run can carry is 99mm rather than 149mm, and it is under the 100mm gap limit.
      && section_length <= _elementOnlyBase + post_width + ((hasRake ? post_width : 100) - 1);

    // ── Solver: posts = (components + 1) × post_width ─────────────────────
    // Each component (sheet, filler, gate, rake) gets a post on each side.
    // Adjacent components share a post → posts = components + 1.
    function solveLayout(sheetQty) {
      const hasFiller    = true; // assume filler, verify after
      const components   = sheetQty + (hasGate ? 1 : 0) + (hasRake ? 1 : 0) + 1; // +1 for filler slot
      const totalPosts   = components + 1;
      const fixedMM      = (hasGate ? GATE_LEAF : 0) + (hasRake ? RAKE_SIZE : 0);
      const usable       = section_length - (totalPosts * post_width) - fixedMM + w2pPostSaving;
      const filler       = usable - sheetQty * actual_sheet;
      return { filler, usable, totalPosts, fixedMM };
    }

    // ── Find sheet qty that gives a valid filler (500–actual_sheet-1) ─────
    // Three exit paths:
    //   1. filler in [MIN_FILLER, actual_sheet)  → valid remainder ≥ 100mm
    //   2. filler === 0                           → clean fit, no filler slot
    //   3. filler >= actual_sheet                 → overflow: promote to (q+1) panels
    // Fallback: reduce qty until filler >= 500mm.
    const maxQty = Math.floor((section_length - 2 * post_width) / (actual_sheet + post_width));
    let qty = maxQty;
    let filler = 0, usable = 0, totalPosts = 0, fixedMM = 0;
    let found = false;
    let bestFallbackQ = -1, bestFallbackFiller = Infinity;

    for (let q = maxQty; q >= 0; q--) {
      const res = solveLayout(q);
      if (res.filler > 0 && res.filler < actual_sheet && res.filler < bestFallbackFiller) {
        bestFallbackQ = q; bestFallbackFiller = res.filler;
      }
      if (res.filler >= MIN_FILLER && res.filler < actual_sheet) {
        qty = q; filler = res.filler; usable = res.usable;
        totalPosts = res.totalPosts; fixedMM = res.fixedMM;
        found = true; break;
      } else if (res.filler === 0) {
        // Clean fit — filler=0 detected with filler-slot post count.
        // Keep res.totalPosts as-is: it already accounts for the filler slot post.
        // Recalculating with comps-1 drops a post and breaks the total.
        qty = q; filler = 0;
        totalPosts = res.totalPosts;
        fixedMM    = res.fixedMM;
        usable     = res.usable;
        found = true; break;
      } else if (res.filler >= actual_sheet) {
        // Overflow: filler >= one full sheet — promote to (q+1) panels, no filler slot
        const q2     = q + 1;
        const comps2 = q2 + (hasGate ? 1 : 0) + (hasRake ? 1 : 0);
        const tp2    = comps2 + 1;
        const fx2    = (hasGate ? GATE_LEAF : 0) + (hasRake ? RAKE_SIZE : 0);
        const us2    = section_length - (tp2 * post_width) - fx2;
        const fi2    = us2 - q2 * actual_sheet;
        if (fi2 >= 0 && fi2 < actual_sheet) {
          qty = q2; filler = fi2; usable = us2; totalPosts = tp2; fixedMM = fx2;
          found = true; break;
        }
      }
    }
    if (!found && bestFallbackQ >= 0) {
      // No qty gave filler >= MIN_FILLER. Reduce panel count from bestFallbackQ
      // downward until filler >= MIN_FILLER or we reach 0 panels.
      // This guarantees the remainder is always ≥ 500mm or there is no filler at all.
      let resolvedQ = bestFallbackQ;
      while (resolvedQ > 0) {
        const res = solveLayout(resolvedQ);
        if (res.filler <= 0 || res.filler >= MIN_FILLER) break;
        resolvedQ--;
      }
      const res = solveLayout(resolvedQ);
      // If after reducing all the way down we still can't meet MIN_FILLER and
      // filler is not a clean zero, signal invalid so calculateSpread can cascade
      // up to the next standard sheet size (critical for rake configurations where
      // the 1185mm rake deduction pushes the remainder below 500mm).
      // 2026-08-30 (Claude): a gate-only section in the band above must NOT be failed here.
      // Measured before this guard: a 1100mm gated section left a 30mm remainder, tripped
      // this branch, rendered an EMPTY preview and still quoted $92.16. There is no panel
      // to find a remainder for — the correct layout is simply gate + two posts, which the
      // zero-panel return below produces.
      // 2026-08-30 (Pete): "add a panel and post at the end. bill panel brackets and post."
      // This branch used to fail any section whose only possible remainder was under
      // MIN_FILLER, which is what made the dead band dead. A remainder of 100mm or more is a
      // real, cuttable panel — it gets its own post and bracket pack, and the existing cost
      // functions bill all three because they count drawn panels and rendered posts. Only the
      // fallback path is relaxed, so every length that already solved normally is unchanged.
      // 2026-08-30 (Pete): a rake section gets a panel rather than clearance — see the
      // band below. 100mm still applies to a gate-only run, which Pete has signed off.
      const _CF_MIN_PANEL = hasRake ? post_width : 100;
      if (res.filler > 0 && res.filler < _CF_MIN_PANEL && !_isElementOnly) {
        return {
          valid: false,
          error: `No panel count yields a remainder ≥ ${MIN_FILLER}mm with a ${actual_sheet}mm sheet.`,
          panels: [], panel_count: 0, panel_space: 0, deductions: 0, total: 0,
          panelWidth: actual_sheet, panelWidths: [], gapSpacings: [],
          regularPanels: resolvedQ, hingePanels: 0,
          gatePanels: hasGate ? 1 : 0, rakedPanels: hasRake ? 1 : 0,
          numPanels: resolvedQ, totalGaps: 0, totalGapWidth: 0,
          gapSpacing: post_width, remaining: res.filler,
          originalSectionLength: section_length
        };
      }
      qty = resolvedQ; filler = Math.max(0, res.filler); usable = res.usable;
      totalPosts = res.totalPosts; fixedMM = res.fixedMM;
    }

    // ── MIXED-SHEET PATH (gate or rake present) ─────────────────────────
    // When gate or rake is present, uniform panels frequently produce sub-500mm filler
    // that no cascade can resolve. Fixed deductions (gate=gateLeaf, rake=1185mm) consume
    // too much space for uniform sheets to land cleanly.
    // Peter's rule: greedy largest-first — place 2400mm sheets
    //   gate only:   gate | 2400 | Xmm filler
    //   rake only:   rake | 2400 | Xmm filler
    //   gate + rake: rake | 2400 | gate | Xmm filler
    if (hasGate || hasRake) {
      // 2026-08-30: derive the fixed deduction HERE rather than trusting the loop
      // variable of the same name — it is still 0 when the uniform loop found no
      // layout and no fallback, which laid this block out with no gate and no rake.
      // Block-scoped, so every reading below is corrected without editing them.
      const fixedMM = (hasGate ? GATE_LEAF : 0) + (hasRake ? RAKE_SIZE : 0);
      const MIXED_SHEETS   = [max_sheet];  // use height-correct sheet size
      const mixedPanels    = [];
      const fixedComponents = (hasRake ? 1 : 0) + (hasGate ? 1 : 0);

      outerLoop: while (true) {
        for (const ms of MIXED_SHEETS) {
          const test       = [...mixedPanels, ms];
          const comps      = test.length + fixedComponents + 1; // +1 filler slot
          const postsTest  = comps + 1;
          const usedTest   = fixedMM + test.reduce((s, w) => s + w, 0) + postsTest * post_width - w2pPostSaving;
          const remTest    = section_length - usedTest;
          if (remTest >= MIN_FILLER) {
            mixedPanels.push(ms);
            continue outerLoop;         // placed — try adding another
          } else if (remTest === 0) {
            mixedPanels.push(ms);
            break outerLoop;            // clean fit
          }
          // else: this sheet makes filler go negative or below threshold — try next smaller
        }
        break; // no sheet fits cleanly — stop adding panels
      }

      // If the filler computed from the greedy loop exceeds actual_sheet, it means
      // the loop stopped one panel short (remainder was < MIN_FILLER so it bailed).
      // Force-add one more full panel without a filler slot. The true filler may be
      // sub-500mm but that's unavoidable for this section length — an over-panel filler
      // (>2400mm) is always wrong since max_sheet is 2400mm.
      {
        const testComps = mixedPanels.length + fixedComponents + 1; // +1 filler slot probe
        const testPosts = testComps + 1;
        const testUsed  = fixedMM + mixedPanels.reduce((s,w)=>s+w,0) + testPosts * post_width - w2pPostSaving;
        const testFill  = section_length - testUsed;
        if (testFill > actual_sheet) {
          // filler > sheet: absorb as another full panel (no filler slot this time)
          mixedPanels.push(actual_sheet);
        }
      }
      const mixedComps   = mixedPanels.length + fixedComponents + (mixedPanels.length > 0 ? 1 : 0);
      const mixedPosts   = mixedComps + 1;
      const mixedUsed    = fixedMM + mixedPanels.reduce((s, w) => s + w, 0) + mixedPosts * post_width - w2pPostSaving;
      const mixedFiller  = section_length - mixedUsed;

      if (mixedPanels.length > 0 && (mixedFiller === 0 || mixedFiller >= MIN_FILLER)) {
        const allWidths    = mixedFiller > 0 ? [...mixedPanels, mixedFiller] : mixedPanels;
        const mixedChecks  = mixedUsed + (mixedFiller > 0 ? mixedFiller : 0) === section_length;
        const primarySheet = mixedPanels[0] || actual_sheet; // largest used (for panelWidth field)
        const mixedPanelObjs = allWidths.map(w => ({
          footprint: w, sheet: w,
          is_cut: w !== primarySheet && !mixedPanels.includes(w),
          kit: mixedPanels.includes(w) ? `${w}mm` : 'filler'
        }));
        return {
          perfomesh: {
            panels: mixedPanelObjs, panel_count: mixedPanels.length,
            panel_space: section_length - fixedMM - mixedPosts * post_width + w2pPostSaving,
            deductions: mixedPosts * post_width + fixedMM,
            actual_sheet: primarySheet, filler: mixedFiller, element, post_width,
            checksOut: mixedChecks, allSheetsValid: true
          },
          valid:             mixedChecks,
          panel_count:       mixedPanels.length,
          panel_space:       section_length - fixedMM - mixedPosts * post_width + w2pPostSaving,
          deductions:        mixedPosts * post_width + fixedMM,
          total:             section_length,
          panelWidth:        primarySheet,
          hingeWidth:        primarySheet,
          panelWidths:       allWidths,
          gapSpacings:       Array(mixedPosts).fill(post_width),
          fixedGapIndices:   [],
          regularPanels:     allWidths.length,  // includes filler so gate clamping matches regCount in renderer
          hingePanels:       hasGate ? 1 : 0,
          gatePanels:        hasGate ? 1 : 0,
          rakedPanels:       hasRake ? 1 : 0,
          numPanels:         allWidths.length,
          numVariablePanels: mixedPanels.length,
          totalGaps:         mixedPosts,
          totalGapWidth:     mixedPosts * post_width,
          gapSpacing:        post_width,
          remaining:         mixedFiller,
          originalSectionLength: section_length,
          withinVarianceLimit: true, optimized: true, variance: 0, gapVariance: 0
        };
      }
      // If mixed solver produced nothing valid, fall through to uniform solver below
    }
    // ── END MIXED-SHEET PATH ──────────────────────────────────────────────

    const deductions   = totalPosts * post_width + fixedMM;
    const panel_space  = usable;
    const count        = qty + (filler > 0 ? 1 : 0); // total panel slots incl filler
    const panelWidths  = [
      ...Array(qty).fill(actual_sheet),
      ...(filler > 0 ? [filler] : [])
    ];
    const checksOut    = (totalPosts * post_width) + (qty * actual_sheet) + filler + fixedMM - w2pPostSaving === section_length;

    if (_isElementOnly || (qty <= 0 && filler <= 0)) {
      // Section too short for panels — return valid with 0 panels, solver always computes.
      //
      // The `perfomesh` block below is the whole fix for the $0.00 gate-only quote, and it
      // is attached ONLY for a genuine gate-only section (see _isElementOnly above). This
      // object was already `valid: true`; it simply had no perfomesh block, and
      // updateCalculationResults() guards with
      //     !calculation || calculation.valid === false || !calculation.perfomesh
      // so a legitimate zero-panel layout tripped the THIRD test, state.optimizedConfigs[n]
      // was set to null, and updateQuoteDisplay() then skipped the whole section — losing
      // the gate, both posts and every piece of hardware from the total while the preview
      // still drew the fence correctly. That $0.00 quote is why this was reverted twice.
      //
      // Outside the band there is no perfomesh block, so the object keeps its ORIGINAL
      // shape and is still rejected downstream — the customer gets the "can't be laid out"
      // note and the snap-up, exactly as before.
      //
      // checksOut is true because a gate between two posts IS a correct layout; it just
      // cannot be expressed by the panel arithmetic that computes checksOut above, which
      // assumes at least one panel. deductions comes from _elementOnlyBase rather than the
      // loop's totalPosts/fixedMM, which are still 0 here.
      return {
        ...(_isElementOnly ? { perfomesh: {
          panels: [], panel_count: 0, panel_space: 0,
          deductions: _elementOnlyBase,
          actual_sheet, filler: 0, element, post_width,
          checksOut: true, allSheetsValid: true
        } } : {}),
        valid: true,
        // 2026-09-07 (Pete: "the errors that stop people progressing through to cart have
        // to stop"). This return serves TWO cases: a real element-only run (rake only, gate
        // only, gate+rake only) and a section simply too short to build. Both reported
        // total: 0, and validateSection() blocks any config whose total is 0 - so a section
        // that drew and priced correctly still refused to reach Review. total is read in
        // exactly ONE place in this file (validateSection), so reporting the real length for
        // the in-band case is contained; the too-short case still returns 0 and is still
        // correctly rejected. Same edit already shipped in flat-top.js.
        panels: [], panel_count: 0, panel_space: 0, deductions: 0, total: _isElementOnly ? section_length : 0,
        panelWidth: actual_sheet, panelWidths: [], gapSpacings: [],
        regularPanels: 0, hingePanels: 0,
        gatePanels: hasGate ? 1 : 0, rakedPanels: hasRake ? 1 : 0,
        numPanels: 0, totalGaps: 0, totalGapWidth: 0,
        gapSpacing: post_width, remaining: 0,
        originalSectionLength: section_length
      };
    }

    const panels = panelWidths.map((w, i) => ({
      footprint: w, sheet: w,
      is_cut: w !== actual_sheet, // filler panel is the one that's not a full sheet
      kit: w === actual_sheet ? `${actual_sheet}mm` : 'filler'
    }));

    const totalGaps   = totalPosts;
    const gapSpacings = Array(totalGaps).fill(post_width);

    return {
      perfomesh: {
        panels, panel_count: qty, panel_space, deductions,
        actual_sheet, filler, element, post_width, checksOut, allSheetsValid: true
      },
      valid:        checksOut,
      panel_count:  qty,
      panel_space,
      deductions,
      total:        section_length,
      panelWidth:        actual_sheet,
      hingeWidth:        actual_sheet,
      panelWidths,
      gapSpacings,
      fixedGapIndices:   [],
      regularPanels:     qty,
      hingePanels:       hasGate ? 1 : 0,
      gatePanels:        hasGate ? 1 : 0,
      rakedPanels:       hasRake ? 1 : 0,
      numPanels:         count,
      numVariablePanels: qty,
      totalGaps,
      totalGapWidth:     totalGaps * post_width,
      gapSpacing:        post_width,
      remaining:         filler,
      originalSectionLength: section_length,
      withinVarianceLimit: true,
      optimized:         false,
      score:             1.0
    };
  }

  // ── PerfoMesh Mode A — Single Section Cut Plan ───────────────────────────
  // Calculates the cut plan for one section in isolation.
  // Cross-section off-cut banking is handled by calculateJobCutPlan().
  // calculateCutPlan — delegates to calculateSpread, returns cut-plan-shaped result
  calculateCutPlan(section_length, post_width, max_sheet, element, all_sections = [], isW2P = false, wallGateEnd = null, gateLeaf = 1010, rakeSize = CONFIG.DEFAULTS.RAKED_PANEL_WIDTH) {
    const spread = this.calculateSpread(section_length, post_width, max_sheet, element, isW2P, wallGateEnd, gateLeaf, rakeSize);
    if (!spread || !spread.valid) {
      // Always compute — return empty valid result rather than error
      return { valid: true, panels: [], cut_panel: null, off_cut: 0, order_notes: 'Section too short for panels.' };
    }
    const panelWidths = spread.panelWidths || [];
    if (panelWidths.length === 0) {
      return { valid: true, panels: [], cut_panel: null, off_cut: 0, order_notes: 'No panels fit in this section.' };
    }
    const STANDARD_SHEET_SIZES = new Set([1632, 1825, 2400]);
    const panels = panelWidths.map((w) => ({
      footprint: w, sheet: w,
      is_cut: false,
      kit: STANDARD_SHEET_SIZES.has(w) ? `${w}mm` : 'filler'
    }));
    return {
      valid: true,
      panels,
      cut_panel:           null,
      off_cut:             0,
      source_sheet:        null,
      cut_panel_extras:    null,
      from_bank:           false,
      bank_source_section: null,
      order_notes: spread.remaining > 0
        ? `${panels.length} panels including 1 filler panel (${spread.remaining}mm).`
        : `${panels.length} panels — clean fit.`
    };
  }

  // calculateJobCutPlan — delegates to calculateCutPlan per section, no off-cut banking
  calculateJobCutPlan() {
    const sectionNums = Object.keys(this.state.sections)
      .map(Number).sort((a, b) => a - b);
    const jobPlan = {};
    sectionNums.forEach(sNum => {
      const section = this.state.sections[sNum];
      if (!section || !section.length) return;
      const section_length = parseInt(section.length);
      const post_width     = parseInt(section.gapSpacing) || CONFIG.DEFAULTS.GAP_SPACING;
      const _rawSheet_jcp  = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
      const element        = (section.hasGate && section.hasRaked) ? 'gate_rake'
                           : section.hasGate ? 'gate'
                           : section.hasRaked ? 'rake' : 'none';
      // calculateSpread handles the cascade from 1000mm internally — pass resolved sheet directly.
      const max_sheet      = _rawSheet_jcp;
      const isW2P_jcp     = !!(section.hasGate && section.gateHingeType === 'W2G-REMOVED-2026-08-30');
      const wallGateEnd_jcp = isW2P_jcp ? (section.wallGateEnd || null) : null;
      const gateLeaf_jcp = parseInt(section.gateLength) || this.getDefaultGateLength();
      const rakeSize_jcp = this.getRakedPanelWidth(section.fenceHeight);
      const endPostSetup_jcp = section.endPostSetup || 'both';
      const removedEndPosts_jcp = endPostSetup_jcp === 'no-both' ? 2 : (endPostSetup_jcp === 'no-left' || endPostSetup_jcp === 'no-right') ? 1 : 0;
      const adjustedLength_jcp = section_length + (removedEndPosts_jcp * post_width);
      // Even Panel Mode: build cut plan from even panels directly
      if ((section.panelMode || 'cut') === 'even') {
        const evenResult = this.calculateEvenPanels(adjustedLength_jcp, post_width, max_sheet, element, gateLeaf_jcp);
        if (evenResult && evenResult.valid) {
          jobPlan[sNum] = {
            valid: true,
            panels: evenResult.panelWidths.map(w => ({ footprint: w, sheet: max_sheet, is_cut: false, kit: max_sheet + 'mm', isEven: true })),
            cut_panel: null, off_cut: 0, source_sheet: null,
            isEvenMode: true, billableSheets: evenResult.panelWidths.length,
            order_notes: evenResult.panelWidths.length + ' evenly-sized panels @ ' + evenResult.evenPanelSize + 'mm each.'
          };
        } else {
          jobPlan[sNum] = this.calculateCutPlan(adjustedLength_jcp, post_width, max_sheet, element, [], isW2P_jcp, wallGateEnd_jcp, gateLeaf_jcp, rakeSize_jcp);
        }
      } else {
        jobPlan[sNum] = this.calculateCutPlan(adjustedLength_jcp, post_width, max_sheet, element, [], isW2P_jcp, wallGateEnd_jcp, gateLeaf_jcp, rakeSize_jcp);
      }
    });
    return jobPlan;
  }

  // Fast single-pass recalc + render — used for mode toggles (panelMode, groundPost)
  // Runs the calculation once, stores it, renders preview and quote in one pass.
  // Avoids the double calculateJobCutPlan() call that updateCalculationResults triggers.
  _recalcAndRender(sectionNum) {
    const section = this.state.sections[sectionNum];
    if (!section || !section.length) return;

    const section_length  = parseInt(section.length);
    const post_width      = parseInt(section.gapSpacing) || CONFIG.DEFAULTS.GAP_SPACING;
    const max_sheet       = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
    const element         = (section.hasGate && section.hasRaked) ? 'gate_rake'
                          : section.hasGate ? 'gate'
                          : section.hasRaked ? 'rake' : 'none';
    const isW2P           = !!(section.hasGate && section.gateHingeType === 'W2G-REMOVED-2026-08-30');
    const wallGateEnd     = section.wallGateEnd || null;
    const gateLeaf        = parseInt(section.gateLength) || this.getDefaultGateLength();
    const rakeSize        = this.getRakedPanelWidth(section.fenceHeight);
    const endPostSetup    = section.endPostSetup || 'both';
    const removedEndPosts = endPostSetup === 'no-both' ? 2 : (endPostSetup === 'no-left' || endPostSetup === 'no-right') ? 1 : 0;
    const adjustedLength  = section_length + (removedEndPosts * post_width);
    const panelMode       = section.panelMode || 'cut';

    // Single calculation — no redundant re-runs
    const calculation = panelMode === 'even'
      ? (this.calculateEvenPanels(adjustedLength, post_width, max_sheet, element, gateLeaf)
         || this.calculateSpread(adjustedLength, post_width, max_sheet, element, isW2P, wallGateEnd, gateLeaf, rakeSize))
      : this.calculateSpread(adjustedLength, post_width, max_sheet, element, isW2P, wallGateEnd, gateLeaf, rakeSize);

    this.state.optimizedConfigs[sectionNum] = calculation;

    // Guard: too-long or unsolvable → friendly note instead of a blank preview/crash.
    if (section_length > 200000 || !calculation || calculation.valid === false || !calculation.perfomesh) {
      this.state.optimizedConfigs[sectionNum] = null;
      const _rd = document.getElementById('calculation-results');
      const _dd = document.getElementById('calc-details');
      if (_rd) _rd.classList.remove('hidden');
      if (_dd) _dd.innerHTML = section_length > 200000
        ? '<div style="padding:10px;color:#b45309;font-size:0.9rem;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;">That length looks too large — please enter the section length in millimetres (max 200,000mm / 200m).</div>'
        : '<div style="padding:10px;color:#b45309;font-size:0.9rem;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;">This section can\'t be laid out with the current options — try a longer section, or turn off the gate/rake.</div>';
      try { this.clearSectionPreview(sectionNum); } catch (e) {}
      this.updateQuoteDisplay();
      return;
    }

    // Render preview and update quote — no double-call
    try { this.renderSectionPreview(sectionNum); } catch(e) {}
    this.updateQuoteDisplay();
  }

    // Update calculation results — PerfoMesh (Mode B stored for quote, Mode A job plan shown)
  updateCalculationResults(sectionNum) {
    const section = this.state.sections[sectionNum];
    if (!section || !section.length) return;

    // Rake panels are not offered on 1500mm / 1800mm heights (custom-manufacture only,
    // Pete 19/07) — force the rake off before any layout runs, so no path can price one.
    if (section.hasRaked && (String(section.fenceHeight) === '1500' || String(section.fenceHeight) === '1800')) {
      section.hasRaked = false;
      const _rkToggle = document.getElementById(`section-${sectionNum}-raked`);
      const _rkFields = document.getElementById(`section-${sectionNum}-raked-fields`);
      if (_rkToggle) _rkToggle.checked = false;
      if (_rkFields) _rkFields.classList.add('hidden');
    }

    // Clear any manual cut-panel override on structural recalc —
    // section length, panel size, or gate config changed, so the cut size resets.
    delete section.overrideCutMm;

    const section_length = parseInt(section.length);
    // Too-long guard: reject absurd lengths up front (prevents the solver hanging on
    // a pasted huge number) with a clear message.
    if (section_length > 200000) {
      this.state.optimizedConfigs[sectionNum] = null;
      const _rd = document.getElementById('calculation-results');
      const _dd = document.getElementById('calc-details');
      if (_rd) _rd.classList.remove('hidden');
      if (_dd) _dd.innerHTML = '<div style="padding:10px;color:#b45309;font-size:0.9rem;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;">That length looks too large — please enter the section length in millimetres (max 200,000mm / 200m).</div>';
      try { this.clearSectionPreview(sectionNum); } catch (e) {}
      this.updateQuoteDisplay();
      return;
    }
    const post_width     = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
    const _rawSheet_ucr  = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
    const element        = (section.hasGate && section.hasRaked) ? 'gate_rake' : section.hasGate ? 'gate' : section.hasRaked ? 'rake' : 'none';
    // calculateSpread handles the cascade from 1000mm internally — pass resolved sheet directly.
    const max_sheet      = _rawSheet_ucr;

    // Mode B — stored for quote pricing
    const isW2P       = section.hasGate && section.gateHingeType === 'W2G-REMOVED-2026-08-30';
    const wallGateEnd = section.wallGateEnd || null;
    const gateLeaf_ucr = parseInt(section.gateLength) || this.getDefaultGateLength();
    const rakeSize_ucr = this.getRakedPanelWidth(section.fenceHeight);

    // ── End post setup: add removed post space back into available length ─
    const endPostSetup = section.endPostSetup || 'both';
    const removedEndPosts = endPostSetup === 'no-both' ? 2 : (endPostSetup === 'no-left' || endPostSetup === 'no-right') ? 1 : 0;
    const adjustedLength = section_length + (removedEndPosts * post_width);

    // Even Panel Mode: use calculateEvenPanels instead of calculateSpread
    const _panelMode = section.panelMode || 'cut';
    const calculation = _panelMode === 'even'
      ? (this.calculateEvenPanels(adjustedLength, post_width, max_sheet, element, gateLeaf_ucr) || this.calculateSpread(adjustedLength, post_width, max_sheet, element, isW2P, wallGateEnd, gateLeaf_ucr, rakeSize_ucr))
      : this.calculateSpread(adjustedLength, post_width, max_sheet, element, isW2P, wallGateEnd, gateLeaf_ucr, rakeSize_ucr);
    this.state.optimizedConfigs[sectionNum] = calculation;

    // ── Guard: unsolvable layout ─────────────────────────────────────────────
    // The solver returns null / an incomplete object when the section can't be laid
    // out (e.g. too short to fit the gate + rake, or an impossible size). Bail here
    // with a friendly note instead of crashing downstream on calculation.panelWidth,
    // calculation.perfomesh.deductions, or cutPlan.panels.
    if (!calculation || calculation.valid === false || !calculation.perfomesh) {
      this.state.optimizedConfigs[sectionNum] = null;
      const _rd = document.getElementById('calculation-results');
      const _dd = document.getElementById('calc-details');
      if (_rd) _rd.classList.remove('hidden');
      if (_dd) _dd.innerHTML = '<div style="padding:10px;color:#b45309;font-size:0.9rem;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;">This section can\'t be laid out with the current options — try a longer section, or turn off the gate/rake.</div>';
      try { this.clearSectionPreview(sectionNum); } catch (e) {}
      this.updateQuoteDisplay();
      return;
    }

    // ── Sync dropdown to actual resolved sheet ───────────────────────────────
    // For gate/rake: calculateSpread always starts from 1000mm internally so the
    // cascade runs fresh on every call. Do NOT write resolvedSheet back into
    // section.panelLength — that would overwrite the user's preference and cause
    // the next dropdown change event to re-read the wrong value, defeating the cascade.
    // For plain sections (no gate, no rake): sync as before so the dropdown reflects
    // any sheet upgrade needed to satisfy the ≥500mm remainder rule.
    const resolvedSheet = calculation.panelWidth;
    const hasGateOrRake = element === 'gate' || element === 'rake' || element === 'gate_rake';
    // Only sync panelLength in cut mode: in even mode, panelWidth = even panel size (not sheet size).
    // Writing even panel size back to panelLength corrupts max_sheet for all subsequent calculations.
    if (!hasGateOrRake && _panelMode !== 'even' && resolvedSheet && resolvedSheet !== max_sheet) {
      section.panelLength = String(resolvedSheet);
      const panelSel = document.getElementById(`section-${sectionNum}-panel`);
      if (panelSel && Array.from(panelSel.options).some(o => o.value === String(resolvedSheet))) {
        panelSel.value = String(resolvedSheet);
      }
    }
    // ─────────────────────────────────────────────────────────────────────────
    const jobPlan = this.calculateJobCutPlan();
    const cutPlan = jobPlan[sectionNum];

    const resultsDiv = document.getElementById('calculation-results');
    const detailsDiv = document.getElementById('calc-details');

    if (resultsDiv && detailsDiv) {
      resultsDiv.classList.remove('hidden');

      const pm = calculation.perfomesh;

      // Build Mode A panel rows
      let panelRows = '';
      cutPlan.panels.forEach((p, i) => {
        if (p.is_gate) {
          panelRows += `<span style="color:#7c3aed;font-weight:600;">Gate leaf: 1000mm (fixed)</span><br>`;
        } else if (p.is_hinge) {
          panelRows += `Hinge sheet: sheet <strong style="color:#0369a1;">${p.sheet}mm</strong><br>`;
        } else {
          const kitLabel = p.sheet === 1960 ? ' (2m kit)' : p.sheet === 2960 ? ' (3m kit)' : ' (custom)';
          panelRows += `Sheet ${i + 1}: footprint ${p.footprint}mm → sheet <strong>${p.sheet}mm</strong>${kitLabel}<br>`;
        }
      });

      // Extra join parts for cut panel
      let extrasNote = '';
      if (cutPlan.cut_panel_extras) {
        const ex = cutPlan.cut_panel_extras;
        extrasNote = `
          <div style="margin-top:8px;padding:8px;background:#fef9c3;border:1px solid #fde68a;border-radius:6px;font-size:0.85rem;">
            <strong>Join extras (ordering only — not customer pricing):</strong><br>
            1× ${ex.post.width}mm post &nbsp;|&nbsp;
            ${ex.channels.qty}× channel ${ex.channels.length}mm &nbsp;|&nbsp;
            ${ex.frames.qty}× frame ${ex.frames.length}mm &nbsp;|&nbsp;
            ${ex.clips.qty}× cover strip ${ex.clips.length}mm
          </div>`;
      }

      const bankNote = cutPlan.remaining_bank
        ? `<div style="margin-top:6px;font-size:0.8rem;color:#6b7280;">Remaining bank after this job: ${cutPlan.remaining_bank}</div>`
        : '';

      detailsDiv.innerHTML = `
        <strong>Section ${sectionNum} — PerfoMesh Blueprint</strong><br><br>
        <span style="color:#374151;font-weight:600;">Section Length:</span> ${section_length}mm<br>
        <span style="color:#374151;font-weight:600;">Post Size:</span> ${post_width}mm<br>
        <span style="color:#374151;font-weight:600;">Deductions:</span> ${pm.deductions}mm
          (${pm.element === 'none' ? '2 end posts' : pm.element === 'gate' ? '2 posts + gate opening' : pm.element === 'gate_rake' ? '2 posts + gate + rake' : '2 posts + rake'})<br>
        <span style="color:#374151;font-weight:600;">Panel Space:</span> ${pm.panel_space}mm<br><br>
        <span style="color:#374151;font-weight:600;">Layout (${cutPlan.panels.length} panels):</span><br>
        ${panelRows}
        <span style="color:#6b7280;font-size:0.85rem;">${cutPlan.order_notes}</span>
        ${extrasNote}
        ${bankNote}<br>
        <span style="color:#059669;font-weight:600;">✓ Total: ${pm.deductions}mm + ${pm.panel_space}mm = ${section_length}mm</span>
      `;
    }

    try { this.renderSectionPreview(sectionNum); } catch(e) {
      console.warn('renderSectionPreview failed:', e);
    }
    this.updateQuoteDisplay();
  }

  // Clean up optimized options from dropdowns
  cleanupOptimizedOptions(sectionNum) {
    const panelSelect = this.domCache.get(`section-${sectionNum}-panel`);
    if (panelSelect) {
      // Remove any previously added optimized panel options
      Array.from(panelSelect.options).forEach(option => {
        if (option.textContent.includes('(optimized)')) {
          option.remove();
        }
      });
    }
    
    const gapSelect = this.domCache.get(`section-${sectionNum}-gap`);
    if (gapSelect) {
      // Remove any previously added optimized gap options
      Array.from(gapSelect.options).forEach(option => {
        if (option.textContent.includes('(optimized)')) {
          option.remove();
        }
      });
    }
  }

  // Update form with optimized values
  updateFormWithOptimizedValues(sectionNum, calculation) {
    
    const section = this.state.sections[sectionNum];
    if (!section) return;

    // Clean up any previously added optimized options
    this.cleanupOptimizedOptions(sectionNum);

    const isVariablePanels = calculation.panelWidths && new Set(calculation.panelWidths).size > 1;
    const isVariableGaps = calculation.gapSpacings && new Set(calculation.gapSpacings).size > 1;

    const panelSelect = this.domCache.get(`section-${sectionNum}-panel`);
    if (panelSelect && calculation.panelWidth) {
      const optionExists = Array.from(panelSelect.options).some(option =>
        parseInt(option.value) === calculation.panelWidth
      );

      // If the optimized panel width doesn't exist in dropdown, add it
      if (!optionExists) {
        const newOption = document.createElement('option');
        newOption.value = calculation.panelWidth.toString();
        const label = isVariablePanels ? `${calculation.panelWidth}mm (variable)` : `${calculation.panelWidth}mm (optimized)`;
        newOption.textContent = label;
        newOption.style.fontStyle = 'italic';
        newOption.style.color = '#0369a1';

        // Insert in sorted order
        let inserted = false;
        for (let i = 0; i < panelSelect.options.length; i++) {
          if (parseInt(panelSelect.options[i].value) > calculation.panelWidth) {
            panelSelect.insertBefore(newOption, panelSelect.options[i]);
            inserted = true;
            break;
          }
        }
        if (!inserted) {
          panelSelect.appendChild(newOption);
        }
      }

      // Never override the user's selected panel length — the optimizer runs silently.
      // The dropdown shows whatever the user last picked; the calculation adapts around it.
    }

    const gapSelect = this.domCache.get(`section-${sectionNum}-gap`);
    if (gapSelect && calculation.gapSpacing) {
      const solvedGap    = calculation.gapSpacing.toString();
      // Build gap label — show range if rem causes some gaps to differ by 1mm
      const rem = calculation.gapSpacings
        ? (() => {
            const fixedSet = new Set(calculation.fixedGapIndices || []);
            const varGaps  = calculation.gapSpacings.filter((_,i)=>!fixedSet.has(i));
            const uniq     = [...new Set(varGaps)].sort((a,b)=>a-b);
            return uniq.length > 1 ? `${uniq[0]}–${uniq[uniq.length-1]}` : uniq[0]?.toString() || solvedGap;
          })()
        : solvedGap;
      const solvedLabel = rem !== solvedGap ? `${rem}mm (solved)` : `${solvedGap}mm (solved)`;

      // Remove any stale (optimized) or (solved) gap options injected by previous calculations
      Array.from(gapSelect.options)
        .filter(o => o.textContent.includes('(optimized)') || o.textContent.includes('(solved)'))
        .forEach(o => o.remove());

      // If the solved gap is not a standard dropdown option, inject it
      const gapOptionExists = Array.from(gapSelect.options).some(o => o.value === solvedGap);
      if (!gapOptionExists) {
        const newOpt = document.createElement('option');
        newOpt.value = solvedGap;
        newOpt.textContent = solvedLabel;
        newOpt.style.fontStyle = 'italic';
        newOpt.style.color = '#0369a1';

        // Insert in sorted numeric order
        let inserted = false;
        for (let i = 0; i < gapSelect.options.length; i++) {
          if (parseInt(gapSelect.options[i].value) > parseInt(solvedGap)) {
            gapSelect.insertBefore(newOpt, gapSelect.options[i]);
            inserted = true;
            break;
          }
        }
        if (!inserted) gapSelect.appendChild(newOpt);
      }

      // Show solved value in dropdown display — do NOT overwrite state preference
      gapSelect.value = solvedGap;
    }
  }

  // Show specific section
  showSection(sectionNum) {
    console.log(`Switching to Section ${sectionNum}, saving current panel state first`);
    
    // Save current panel configuration before switching sections
    this.updatePanelData();
    
    // Hide all tabs and forms
    document.querySelectorAll('.section-tab').forEach(tab => tab.classList.remove('active'));
    const targetTab = document.querySelector(`.section-tab:nth-child(${sectionNum})`);
    if (targetTab) {
      targetTab.classList.add('active');
    } else {
    }
    
    // Hide all section forms
    const sectionForms = document.querySelectorAll('[id^="section-"][id$="-form"]');
    sectionForms.forEach(form => {
      form.style.display = 'none';
    });
    
    // Hide all gate forms
    const gateForms = document.querySelectorAll('[id^="section-"][id$="-gate-form"]');
    gateForms.forEach(form => {
      form.style.display = 'none';
    });

    // Hide all rake forms
    const rakeForms = document.querySelectorAll('[id^="section-"][id$="-rake-form"]');
    rakeForms.forEach(form => {
      form.style.display = 'none';
    });
    
    // Show the target section form
    const targetForm = this.domCache.get(`section-${sectionNum}-form`);
    if (targetForm) {
      targetForm.style.display = 'block';
    } else {
      // Try fallback
      const fallbackForm = document.getElementById(`section-${sectionNum}-form`);
      if (fallbackForm) {
        fallbackForm.style.display = 'block';
      } else {
      }
    }
    
    // Show the target gate form
    const targetGateForm = document.getElementById(`section-${sectionNum}-gate-form`);
    if (targetGateForm) {
      targetGateForm.style.display = 'block';
    } else {
      // Try fallback
      const fallbackGateForm = document.getElementById(`section-${sectionNum}-gate-form`);
      if (fallbackGateForm) {
        fallbackGateForm.style.display = 'block';
      } else {
      }
    }

    // Show the target rake form
    const targetRakeForm = document.getElementById(`section-${sectionNum}-rake-form`);
    if (targetRakeForm) {
      targetRakeForm.style.display = 'block';
    }

    // Rake option is removed for 1500/1800mm heights (custom-manufacture only, Pete 19/07).
    // Re-apply on show so a height set via the SHARED control (which doesn't rebuild the
    // rake form) still hides the toggle + clears any rake state.
    {
      const _h = String(this.state.sections[sectionNum]?.fenceHeight || '1200');
      const _restricted = (_h === '1500' || _h === '1800');
      const _grp    = document.getElementById(`section-${sectionNum}-rake-toggle-group`);
      const _note   = document.getElementById(`section-${sectionNum}-rake-custom-note`);
      const _toggle = document.getElementById(`section-${sectionNum}-raked`);
      const _fields = document.getElementById(`section-${sectionNum}-raked-fields`);
      if (_grp)  _grp.style.display  = _restricted ? 'none' : '';
      if (_note) _note.style.display = _restricted ? 'block' : 'none';
      if (_restricted) {
        if (_toggle) _toggle.checked = false;
        if (this.state.sections[sectionNum]) this.state.sections[sectionNum].hasRaked = false;
        if (_fields) _fields.classList.add('hidden');
      }
    }

    this.state.currentSection = sectionNum;
    this.updateBladeHeightLabel(sectionNum);

    // Clear DOMCache for next access
    this.domCache.clear(`section-${sectionNum}-form`);
    this.domCache.clear(`section-${sectionNum}-gate-form`);

    // ── SMART CLEAR: only wipe the preview if we have nothing to show yet ──
    // If a valid calculation already exists for this section (e.g. returning
    // from Color Finishes or Review & Checkout), skip clearSectionPreview so
    // the existing SVG stays visible while updateCalculationResults re-renders.
    // This prevents the "Enter section length to see preview" blank flash.
    const hasExistingCalc = !!(this.state.optimizedConfigs?.[sectionNum]?.valid);
    if (!hasExistingCalc) {
      this.clearSectionPreview();
    }
    
    // Update hardware previews for the current section
    this.updateHardwarePreviews();
    
    const section = this.state.sections[sectionNum];

    // ── Reset dropdowns to state-preferred values before recalculating ──────
    // Strip any stale (solved)/(optimized) options injected by previous calculations
    // so the dropdown shows the user's actual preferred value, not a stale solved value.
    const panelSel = document.getElementById(`section-${sectionNum}-panel`);
    if (panelSel) {
      Array.from(panelSel.options)
        .filter(o => o.textContent.includes('(solved)') || o.textContent.includes('(optimized)'))
        .forEach(o => o.remove());
      const preferredPanel = section?.panelLength || this.getDefaultPanelLength(section?.fenceHeight);
      if (Array.from(panelSel.options).some(o => o.value === String(preferredPanel))) {
        panelSel.value = String(preferredPanel);
      }
    }

    const gapSel = document.getElementById(`section-${sectionNum}-gap`);
    if (gapSel) {
      Array.from(gapSel.options)
        .filter(o => o.textContent.includes('(solved)') || o.textContent.includes('(optimized)'))
        .forEach(o => o.remove());
      const preferredGap = section?.gapSpacing || CONFIG.DEFAULTS.GAP_SPACING.toString();
      if (Array.from(gapSel.options).some(o => o.value === String(preferredGap))) {
        gapSel.value = String(preferredGap);
      }
    }

    if (section && section.length) {
      this.updateCalculationResults(sectionNum);
    } else {
      this.showEmptyPreview(sectionNum);
    }
    this.updateWallToGlassAvailability(sectionNum);

    // Update section product preview when switching sections
    this.updateHardwarePreviews();

    // Keep Add-to-Cart hidden while stepping through sections (Pete 19/07).
    this._hideCheckoutUnlessReview();
  }

  // Clear section preview
  clearSectionPreview() {
    const previewContainer = document.getElementById('section-preview');
    const resultsDiv = document.getElementById('calculation-results');
    const detailsDiv = document.getElementById('calc-details');
    
    if (previewContainer) {
      previewContainer.innerHTML = '<div class="preview-placeholder"><p>Enter section length to see preview</p></div>';
    }
    
    if (resultsDiv) {
      resultsDiv.classList.add('hidden');
    }
    
    if (detailsDiv) {
      detailsDiv.innerHTML = '';
    }
  }

  // Show empty preview
  showEmptyPreview(sectionNum) {
    const previewContainer = document.getElementById('section-preview');
    const resultsDiv = document.getElementById('calculation-results');
    
    if (previewContainer) {
      previewContainer.innerHTML = `
        <div class="preview-placeholder">
          <p>Section ${sectionNum} Preview</p>
          <p style="font-size: 1.2rem; color: #94a3b8;">Enter section length to see sheet layout</p>
        </div>
      `;
    }
    
    if (resultsDiv) {
      resultsDiv.classList.add('hidden');
    }
  }

  // Calculate panels layout
  calculatePanelsLayout(config) {
    const {
      calculation,
      hasRaked,
      hasGate,
      gateWidth,
      rakedSide,
      retainedHeight,
      gateFlipped,
      fenceHeight
    } = config;

    const gateHingeType = config.gateHingeType || 'glass-to-glass';
    // wallGateEnd: read only from config (section state), never fall back to calculation —
    // calculation.wallGateEnd can be stale after gate moves away from wall end mid-run.
    const wallGateEnd = gateHingeType === 'W2G-REMOVED-2026-08-30'
      ? (config.wallGateEnd ?? null)
      : null;
    // isWallToGlass: type-based — hinge panel removed as soon as wall-to-glass is selected,
    // regardless of gate position. wallGateEnd only controls WHICH wall end the gate sits at.
    const isWallToGlass = gateHingeType === 'W2G-REMOVED-2026-08-30';
    // wallGateEnd controls gate-at-end positioning for gap/layout logic
    const isAtWallEnd   = isWallToGlass && !!wallGateEnd;
    // Match solver: W2G gateSide derived from wallGateEnd same as peterCalculateLayout.
    // wall-left  → gateSide='left'  → LHS branch (gate at left wall, opens right)
    // wall-right → gateSide='right' → RHS branch (gate at right wall, opens left)
    // G2G: use user-selected gateSide.
    const gateSide = isWallToGlass
      ? (wallGateEnd === 'left' ? 'left' : 'right')
      : (config.gateSide || 'right');

    // Define regularCount and gatePosition BEFORE gateAtRakeEndDisplay check
    if (!calculation) return [];
    const regularCount = calculation.regularPanels || 0;

    // gatePosition = how many regular panels are to the LEFT of the gate+hinge block
    const gatePosition = (config.gatePosition !== undefined && config.gatePosition !== null)
      ? config.gatePosition : 0;

    // Boss rule: hinge panel min 1000mm only — regular panels unaffected
    const rawHingeW  = calculation.hingeWidth || calculation.panelWidth || CONFIG.CONSTRAINTS.MIN_PANEL_WIDTH;
    const hingeWidth = Math.max(rawHingeW, 1000);

    // Gate at rake end: rake IS the hinge — no separate hinge panel needed.
    const gateAtRakeEndDisplay = hasGate && hasRaked && gateHingeType === 'glass-to-glass' && !isWallToGlass && (
      (rakedSide === 'left'  && gatePosition <= 0) ||
      (rakedSide === 'right' && gatePosition >= regularCount)
    );
    // Hinge panel is always present in solver. W2G only changes HOW it renders.
    const hasHingePanel = hasGate && !gateAtRakeEndDisplay;
    // W2G at right end: hinge panel renders as plain glass, gate faces left
    const w2gRightEnd = isWallToGlass && wallGateEnd === 'right';
    const w2gLeftEnd  = isWallToGlass && wallGateEnd === 'left';

    // FIX 5: Rake side is freely chosen by user — no forced override here.
    let enforcedRakedSide = rakedSide;

    // Build a flat list of regular panel widths — same layout for G2G and W2G
    const regularWidths = [];
    for (let i = 0; i < regularCount; i++) {
      regularWidths.push(
        (calculation.panelWidths && calculation.panelWidths[i] !== undefined)
          ? calculation.panelWidths[i]
          : calculation.panelWidth
      );
    }

    // Clamp gatePosition to valid range
    // isAtWallEnd: gate is confirmed at a wall end — force it flush to that end
    const clampedPos = isAtWallEnd
      ? (wallGateEnd === 'left' ? 0 : regularCount) // gate must sit at wall end
      : Math.max(0, Math.min(gatePosition, regularCount));

    // Split regular panels into left-of-gate and right-of-gate groups
    const leftPanels  = regularWidths.slice(0, clampedPos);
    const rightPanels = regularWidths.slice(clampedPos);

    // Build gate block depending on hinge side and position.
    // Gate at rake end: hinge always sits between rake and gate.
    //   [raked]-7mm-[hinge]-10mm-[gate]-varGap-[panels]  (rake LEFT)
    //   [panels]-varGap-[gate]-10mm-[hinge]-7mm-[raked]  (rake RIGHT)
    // Gate NOT at rake end: normal LHS/RHS layout.
    const gateBlock = [];
    // ── Build gate block and assemble panels ────────────────────────────────
    // Peter's rule: gate LATCHES off rake (7mm), hinge panel always on the opposite side (10mm).
    //
    // gateAtRakeEnd layouts (rake IS the latch side):
    //   Left rake:  [rake]-7mm-[gate]-10mm-[hinge]-var-[panels]
    //   Right rake: [panels]-var-[hinge]-10mm-[gate]-7mm-[rake]
    //
    // Normal layouts (gate not touching rake):
    //   LHS hinge: [startGap][hinge]-10mm-[gate]-7mm-[panels][rake?]
    //   RHS hinge no rake: [panels]-7mm-[gate]-10mm-[hinge]
    //   RHS hinge with rake: [panels]-var-[rake]-7mm-[gate]-10mm-[hinge]
    //                        rake is latch side even when gate is mid-run next to rake

    const rakedPanelDisplayWidth = this.getRakedPanelWidth ? this.getRakedPanelWidth(fenceHeight) : CONFIG.DEFAULTS.RAKED_PANEL_WIDTH;
    let panels = [];

    if (!hasGate) {
      // No gate — just render all panels and rake
      if (hasRaked && enforcedRakedSide === 'left') {
        panels.push({ type: 'left-raked', width: rakedPanelDisplayWidth, retainedHeight, flipped: false });
      }
      regularWidths.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
      if (hasRaked && enforcedRakedSide === 'right') {
        panels.push({ type: 'right-raked', width: rakedPanelDisplayWidth, retainedHeight, flipped: true });
      }
    } else if (gateAtRakeEndDisplay) {
      if (rakedSide === 'left') {
        panels.push({ type: 'left-raked',  width: rakedPanelDisplayWidth, retainedHeight, flipped: false });
        panels.push({ type: 'gate',        width: parseInt(gateWidth),     flipped: false });
        panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth,              flipped: false });
        rightPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
      } else {
        leftPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
        panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth,              flipped: false });
        panels.push({ type: 'gate',        width: parseInt(gateWidth),     flipped: true  });
        panels.push({ type: 'right-raked', width: rakedPanelDisplayWidth,  retainedHeight, flipped: true });
      }
    } else if (hasGate && gateSide === 'left') {
      // G2G LHS or W2G left (wallGateEnd='left' → gateSide='left' → this branch)
      if (hasRaked && enforcedRakedSide === 'left') {
        panels.push({ type: 'left-raked', width: rakedPanelDisplayWidth, retainedHeight, flipped: false });
        leftPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
        if (w2gLeftEnd) {
          // W2G left: [wall]-10mm-[gate→right]-7mm-[plain panel]-var-[rightPanels]
          panels.push({ type: 'gate',  width: parseInt(gateWidth), flipped: false });
          panels.push({ type: 'panel', width: hingeWidth,          flipped: false });
        } else {
          panels.push({ type: 'gate',        width: parseInt(gateWidth), flipped: true });
          if (hasHingePanel) panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth, flipped: true });
        }
        rightPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
      } else {
        leftPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
        if (hasRaked && enforcedRakedSide === 'right') {
          if (w2gLeftEnd) {
            panels.push({ type: 'gate',  width: parseInt(gateWidth), flipped: false });
            panels.push({ type: 'panel', width: hingeWidth,          flipped: false });
          } else {
            panels.push({ type: 'gate',        width: parseInt(gateWidth), flipped: true });
            if (hasHingePanel) panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth, flipped: true });
          }
          rightPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
          panels.push({ type: 'right-raked', width: rakedPanelDisplayWidth, retainedHeight, flipped: true });
        } else {
          if (w2gLeftEnd) {
            // W2G left: [wall]-10mm-[gate→right]-7mm-[plain panel]-var-[panels]
            panels.push({ type: 'gate',  width: parseInt(gateWidth), flipped: false });
            panels.push({ type: 'panel', width: hingeWidth,          flipped: false });
          } else {
            panels.push({ type: 'gate',        width: parseInt(gateWidth), flipped: true });
            if (hasHingePanel) panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth, flipped: true });
          }
          rightPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
        }
      }
    } else {
      // RHS hinge — G2G or W2G right (wallGateEnd='right' → gateSide='right' → this branch)
      if (hasRaked && enforcedRakedSide === 'left') {
        panels.push({ type: 'left-raked', width: rakedPanelDisplayWidth, retainedHeight, flipped: false });
        leftPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
        if (w2gRightEnd) {
          // W2G right: [rake][leftPanels]-var-[plain panel]-7mm-[gate←left]-10mm-[wall]
          panels.push({ type: 'panel', width: hingeWidth,          flipped: false });
          panels.push({ type: 'gate',  width: parseInt(gateWidth), flipped: true  });
        } else {
          if (hasHingePanel) panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth, flipped: false });
          panels.push({ type: 'gate', width: parseInt(gateWidth), flipped: true });
        }
        rightPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
      } else {
        leftPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
        if (hasRaked && enforcedRakedSide === 'right') {
          if (w2gRightEnd) {
            panels.push({ type: 'panel', width: hingeWidth,          flipped: false });
            panels.push({ type: 'gate',  width: parseInt(gateWidth), flipped: true  });
          } else {
            if (hasHingePanel) panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth, flipped: false });
            panels.push({ type: 'gate', width: parseInt(gateWidth), flipped: true });
          }
          rightPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
          panels.push({ type: 'right-raked', width: rakedPanelDisplayWidth, retainedHeight, flipped: true });
        } else {
          if (w2gRightEnd) {
            // W2G right no rake: [leftPanels]-var-[plain panel]-7mm-[gate←left]-10mm-[wall]
            panels.push({ type: 'panel', width: hingeWidth,          flipped: false });
            panels.push({ type: 'gate',  width: parseInt(gateWidth), flipped: true  });
          } else {
            if (hasHingePanel) panels.push({ type: 'panel', isHingeSlot: true, width: hingeWidth, flipped: false });
            panels.push({ type: 'gate', width: parseInt(gateWidth), flipped: true });
          }
          rightPanels.forEach(w => panels.push({ type: 'panel', width: w, flipped: false }));
        }
      }
    }

    return panels;
  }

  // Render section preview — PerfoMesh Mode A (cut panel layout)
  renderSectionPreview(sectionNum) {
    // ── SCROLL LOCK: capture position before any DOM mutation ─────────────
    // Prevents page jumping to top when sticky .live-preview-wrapper reflows
    // during gate move, rake toggle, or any other re-render trigger.
    // #configure-sections is the scroll container, not window.
    const _configEl = document.getElementById('configure-sections');
    const _scrollY = _configEl ? _configEl.scrollTop : (window.scrollY || window.pageYOffset);
    // ─────────────────────────────────────────────────────────────────────

    const section = this.state.sections[sectionNum];
    const previewContainer = document.getElementById('section-preview')
                          || document.querySelector('.visual-preview#section-preview')
                          || document.querySelector('#section-preview');

    if (!previewContainer) return;

    if (!section || !section.length) {
      this.showEmptyPreview(sectionNum);
      requestAnimationFrame(() => { if (_configEl) _configEl.scrollTop = _scrollY; });
      return;
    }

    // Self-heal: Blade panel sheet width is fully determined by fence height. If a
    // stale panelLength (e.g. the 2400 seed on a 1500/1800 section) doesn't match the
    // height, correct it here so the configure preview and the review recompute use
    // the same sheet and produce the same (correct) post count.
    const _correctSheet_prev = this.getPanelLengthForHeight(section.fenceHeight);
    if (String(section.panelLength) !== String(_correctSheet_prev)) {
      section.panelLength = _correctSheet_prev;
    }

    // ── Run Mode A (cut plan) directly for the preview ────────────────────
    const section_length = parseInt(section.length);
    const post_width     = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
    const _rawSheet_prev = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
    const element        = (section.hasGate && section.hasRaked) ? 'gate_rake' : section.hasGate ? 'gate' : section.hasRaked ? 'rake' : 'none';
    // calculateSpread handles the cascade from 1000mm internally — pass resolved sheet directly.
    const max_sheet      = _rawSheet_prev;

    // Mode A — use cached plan from _recalcAndRender if available, else compute
    // This avoids recalculating all sections just to render one preview
    const _cachedCalc = this.state.optimizedConfigs[sectionNum];
    // Bust stale cache when panelLength has changed (height/rake/gate toggle)
    const _expectedSheet = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
    // Even mode stores the even PANEL size in panelWidth (not the sheet size), so the
    // sheet-mismatch bust must skip even mode — otherwise it wrongly deletes the valid
    // even config on every render and the preview + quote break for nearly every size.
    if (_cachedCalc && !_cachedCalc.isEvenMode && _cachedCalc.panelWidth && _cachedCalc.panelWidth !== _expectedSheet) {
      delete this.state.optimizedConfigs[sectionNum];
    }
    let cutPlan;
    if (_cachedCalc && _cachedCalc.valid && (_cachedCalc.isEvenMode || !_cachedCalc.panelWidth || _cachedCalc.panelWidth === _expectedSheet)) {
      // Build cutPlan directly from cached calculation
      const _pw = _cachedCalc.panelWidths || [];
      const _maxSheet = parseInt(this.state.sections[sectionNum]?.panelLength) || parseInt(this.getDefaultPanelLength(this.state.sections[sectionNum]?.fenceHeight));
      cutPlan = {
        valid: true,
        isEvenMode: !!_cachedCalc.isEvenMode,
        panels: _pw.map(w => ({
          footprint: w,
          sheet: _cachedCalc.isEvenMode ? _maxSheet : w,
          is_cut: false,
          isEven: !!_cachedCalc.isEvenMode,
          kit: _cachedCalc.isEvenMode ? _maxSheet + 'mm' : (w === _maxSheet ? _maxSheet + 'mm' : 'filler')
        }))
      };
    } else {
      const jobPlan = this.calculateJobCutPlan();
      cutPlan = jobPlan[sectionNum];
    }

    // 2026-08-30 (Pete): a gate-only run — gate between its two posts, no panels — is a legitimate
    // section for the side of a house. cutPlan already returns { valid:true, panels:[] } for it, so
    // the only thing rejecting it was this length check. Zero panels is an error ONLY when there is
    // also no gate; with a gate there is still a fence to draw.
    if (!cutPlan || !cutPlan.valid || !cutPlan.panels || (cutPlan.panels.length === 0 && !section.hasGate && !section.hasRaked)) {
      previewContainer.innerHTML = `
        <div style="padding:20px;text-align:center;color:#64748b;font-size:0.9rem;">
          Section too short for panels with current settings. Try a longer section length.
        </div>
      `;
      requestAnimationFrame(() => { if (_configEl) _configEl.scrollTop = _scrollY; });
      return;
    }

    // ── Build flat panels array for the strip renderer ────────────────────
    // Filter out gate/hinge from cut plan panels (we place them manually below)
    const _isEvenMode = !!(cutPlan.isEvenMode);
    let regularStripPanels = cutPlan.panels
      .filter(p => !p.is_gate && !p.is_hinge)
      .map((p) => ({
        type:         'panel',
        // Even mode: use actual footprint for visual width; cut mode: use sheet size
        width:        _isEvenMode ? p.footprint : p.sheet,
        nominalWidth: _isEvenMode ? p.footprint : (p.source_sheet || p.sheet),
        isCut:        false,
        isEven:       !!p.isEven,
        footprint:    p.footprint,
        source_sheet: p.source_sheet || null,
        off_cut:      p.off_cut || 0,
        kit:          p.isEven ? `${p.sheet}mm` : (p.sheet === (p.source_sheet || p.sheet) ? `${p.sheet}mm` : 'filler')  // Even panels always full sheet kit
      }));

    // ── Gate offset: shift filler width by gateOffsetMm ─────────────────────
    // When gate has moved sub-slot (half-panel steps), the filler absorbs the
    // difference so total section length stays constant.
    const gateOffsetMm = (section.hasGate && section.gateOffsetMm) ? section.gateOffsetMm : 0;
    if (!_isEvenMode && gateOffsetMm > 0 && regularStripPanels.length > 0) {
      // Find the filler — smallest panel in the strip
      let fillerIdx = 0;
      let fillerMin = regularStripPanels[0].width;
      for (let i = 1; i < regularStripPanels.length; i++) {
        if (regularStripPanels[i].width < fillerMin) {
          fillerMin = regularStripPanels[i].width;
          fillerIdx = i;
        }
      }
      // Determine which side of the gate the filler is on
      // clampedPos is calculated below — use gatePos as proxy here
      const gatePos    = section.gatePosition !== undefined ? parseInt(section.gatePosition) : 0;
      const fillerIsRight = fillerIdx >= gatePos; // filler to the right of gate
      // Filler absorbs the offset; opposite panel donates it
      const newFillerW = regularStripPanels[fillerIdx].width - gateOffsetMm;
      if (newFillerW >= 0) {
        regularStripPanels[fillerIdx].width     = newFillerW;
        regularStripPanels[fillerIdx].footprint = newFillerW;
        // Find nearest full panel on the opposite side to donate
        const donorIdx = fillerIsRight
          ? regularStripPanels.slice(0, gatePos).reduceRight((acc, p, i) => acc === -1 && p.width > newFillerW ? i : acc, -1)
          : regularStripPanels.slice(gatePos).findIndex(p => p.width > newFillerW) + gatePos;
        if (donorIdx >= 0 && donorIdx < regularStripPanels.length) {
          regularStripPanels[donorIdx].width     += gateOffsetMm;
          regularStripPanels[donorIdx].footprint += gateOffsetMm;
        }
      }
    }

    // ── Hinge-side panel split + Remainder lock (Peter's rule v6) ─────────────
    // When gate is ON (G2G only) AND the filler/remainder is below 500mm:
    //   Find the nearest full sheet on the hinge side of the gate (skipping the
    //   filler slot, which sits at remainderIdx). Split it:
    //     SPLIT_OFFCUT = 500mm — taken from the full sheet, added to the filler.
    //     SPLIT_KEEP   = sheet_width - 500mm — stays in the slot, marked as cut.
    //
    //   The filler is ALWAYS the last panel (remainderIdx = regCount-1).
    //   For LHS hinge the gate sits at clampedPos (right end) so the full sheet
    //   to split is the slot just before the filler: remainderIdx - 1.
    //   For RHS hinge the gate sits at the left so the full sheet is at clampedPos.
    //
    //   Sheet sizes and resulting layouts (7530mm, gate=1010mm, post=50mm):
    //     3000mm: filler=320mm → split idx1(3000→2500+500) → rem=820mm
    //     2000mm: filler=270mm → split last full(2000→1500+500) → rem=770mm
    //     1000mm: filler=120mm → split last full(1000→500+500)  → rem=620mm
    //
    //   No split if filler already ≥ 500mm or no splittable full sheet exists.
    if (!_isEvenMode && section.hasGate) {
      const isW2G_split    = section.gateHingeType === 'W2G-REMOVED-2026-08-30';
      const gateSide_split = isW2G_split
        ? (section.wallGateEnd === 'left' ? 'right' : 'left')
        : (section.gateSide || 'left');

      const regCountR    = regularStripPanels.length;
      const remainderIdx = regCountR - 1;               // filler is always last slot
      const MIN_REMAINDER = 500;
      const SPLIT_OFFCUT  = 500;

      // Only split when the filler/remainder is below the minimum healthy threshold
      const existingRem = (remainderIdx >= 0) ? regularStripPanels[remainderIdx].width : 0;
      const needsSplit  = existingRem < MIN_REMAINDER;

      // Find the nearest full sheet to split (skipping the filler slot).
      // LHS hinge: gate is at right end → nearest full sheet is just before the filler (remainderIdx-1)
      // RHS hinge: gate is at left  end → nearest full sheet is after the gate (first slot, index 0)
      // Search for the nearest slot that is a full sheet (width > SPLIT_OFFCUT) and not the filler.
      let splitIdx = -1;
      if (needsSplit && !isW2G_split) {
        if (gateSide_split === 'left') {
          // LHS: scan rightward from remainderIdx-1 toward 0, take first full sheet
          for (let i = remainderIdx - 1; i >= 0; i--) {
            if (regularStripPanels[i].width > SPLIT_OFFCUT) { splitIdx = i; break; }
          }
        } else {
          // RHS: scan leftward from slot 0 toward remainderIdx-1, take first full sheet
          for (let i = 0; i < remainderIdx; i++) {
            if (regularStripPanels[i].width > SPLIT_OFFCUT) { splitIdx = i; break; }
          }
        }
      }

      const splitPanel  = splitIdx >= 0 ? regularStripPanels[splitIdx] : null;
      const splitKeep   = splitPanel ? (splitPanel.width - SPLIT_OFFCUT) : 0;
      const canSplit    = splitIdx >= 0 && splitKeep >= MIN_REMAINDER;

      if (canSplit) {
        // Trim the full sheet: keep (sheet - 500mm), mark as cut panel
        regularStripPanels[splitIdx] = {
          ...regularStripPanels[splitIdx],
          width: splitKeep, nominalWidth: splitKeep, footprint: splitKeep,
          isCut: true, isHingeCut: true
        };
        // Add 500mm offcut to the filler at the right end
        const newRemWidth = existingRem + SPLIT_OFFCUT;
        regularStripPanels[remainderIdx] = {
          ...regularStripPanels[remainderIdx],
          width: newRemWidth, nominalWidth: newRemWidth, footprint: newRemWidth,
          isRemainder: true
        };
      } else if (remainderIdx >= 0) {
        // Filler already ≥ 500mm or no splittable slot — just mark remainder
        regularStripPanels[remainderIdx] = {
          ...regularStripPanels[remainderIdx],
          isRemainder: true
        };
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    // FLEXIBLE MERGE removed: Blade max sheet is 2400mm, merging filler into
    // a 2400mm panel would always produce an over-2400mm panel in the visual.
    // Filler is always rendered as a separate cut panel.

    // ── Gate / hinge panel entries ────────────────────────────────────────
    const hasGate       = !!section.hasGate;
    const gateHingeType = section.gateHingeType || 'glass-to-glass';
    const isW2G         = gateHingeType === 'W2G-REMOVED-2026-08-30';
    // W2G: wallGateEnd drives which physical wall the gate sits at.
    // wall-left  → gateSide='left'  → gate at left wall, opens right
    // wall-right → gateSide='right' → gate at right wall, opens left
    // G2G: use section.gateSide (user-selected hinge side).
    const wallGateEnd   = isW2G ? (section.wallGateEnd || null) : null;
    // W2P: gate faces INWARD (away from wall) regardless of user hinge radio.
    // W2P LEFT  (wallGateEnd='left')  → gate faces right (inward) → gateSide='right'
    // W2P RIGHT (wallGateEnd='right') → gate faces left  (inward) → gateSide='left'
    // P2P: use user-selected gateSide.
    const gateSide      = isW2G
      ? (wallGateEnd === 'left' ? 'right' : 'left')
      : (section.gateSide || 'right');
    const isW2GLeft     = isW2G && wallGateEnd === 'left';
    const isW2GRight    = isW2G && wallGateEnd === 'right';

    const SHEET_MAP_LOCAL = { 1000: 1000, 1500: 1500, 2000: 1960, 2400: 2360, 2500: 2500, 3000: 2960 };
    const actual_sheet_local = SHEET_MAP_LOCAL[max_sheet] || max_sheet;

    const gateEntry = hasGate ? {
      type: 'gate', width: parseInt(section.gateLength) || this.getDefaultGateLength(), isGate: true,
      gateSide, gateHingeType,
      // W2G: gate faces away from wall — same flipped logic as ullc
      // wall-left  → gate faces right → flipped:false
      // wall-right → gate faces left  → flipped:true
      flipped: isW2G ? (wallGateEnd === 'right') : (gateSide === 'right')
    } : null;

    // G2G: hinge panel present. W2G: no glass hinge panel — gate bolts straight to wall.
    const hingeEntry = (hasGate && !isW2G) ? {
      type: 'hinge', width: actual_sheet_local, isHinge: true
    } : null;

    // ── Rake ──────────────────────────────────────────────────────────────
    const hasRaked       = !!section.hasRaked;
    const rakedSide      = section.rakedSide || 'left';
    const retainedHeight = parseInt(section.retainedHeight) || 300;

    const rakedEntry = hasRaked ? {
      type: rakedSide === 'right' ? 'right-raked' : 'left-raked',
      width: CONFIG.DEFAULTS.RAKED_PANEL_WIDTH, isCut: false, isRaked: true, retainedHeight
    } : null;

    // ── Gate position and gateAtRakeEnd detection ─────────────────────────
    // gateAtRakeEnd: gate is at the rake end — rake acts as latch side (no separate
    // hinge panel between rake and gate). Only applies to G2G.
    // Left rake + gatePos=0:           [rake]-7mm-[gate]-10mm-[hinge]-var-[panels]
    // Right rake + gatePos=regCount:   [panels]-var-[hinge]-10mm-[gate]-7mm-[rake]
    const regCount = regularStripPanels.length;
    // FIX: Resolve gate position using actual regCount from regularStripPanels.
    // Two cases that need resolution:
    //   1. Sentinel 9999 — gate just enabled, _placeAtMiddle=true: resolve to floor(regCount/2)
    //   2. Gate clamped to old regularPanels count that's less than regCount (off-by-one from
    //      mixed-sheet or hinge-split adding panels) — also resolve to middle on first render.
    // After resolving, clear _placeAtMiddle so subsequent moves are respected.
    const rawGatePos = section.gatePosition !== undefined ? parseInt(section.gatePosition) : 0;
    let gatePos;
    if (hasGate && (rawGatePos >= 9999 || (section._placeAtMiddle && rawGatePos >= regCount))) {
      gatePos = Math.floor(regCount / 2);
      section._placeAtMiddle = false; // consumed — subsequent moves use actual position
    } else {
      gatePos = rawGatePos;
    }

    const gateAtRakeEnd = hasGate && hasRaked && !isW2G && (
      (rakedSide === 'left'  && gatePos <= 0) ||
      (rakedSide === 'right' && gatePos >= regCount)
    );

    // gateAtRakeEnd: force gateSide to match rake side.
    // Rake LEFT:  gate latches off left rake → gate opens RIGHT → gateSide='left'
    // Rake RIGHT: gate latches off right rake → gate opens LEFT → gateSide='right'
    // Gate image and hinge image both derive from gateSide, so this keeps the pair consistent.
    if (gateAtRakeEnd && gateEntry) {
      gateEntry.gateSide = rakedSide;
    }
    // Effective gateSide for image selection below — overridden when gateAtRakeEnd
    const effectiveGateSide = (gateAtRakeEnd && hasGate) ? rakedSide : gateSide;

    // ── Gate position clamping ────────────────────────────────────────────
    // W2G: gate MUST sit flush at the wall end (position 0 or regCount).
    // G2G gateAtRakeEnd: same — force to rake end.
    // G2G normal + rake: hinge panel can never be adjacent to rake.
    let minAllowed = 0;
    let maxAllowed = regCount;

    if (isW2GLeft) {
      // W2G left wall: gate forced to position 0
      minAllowed = 0;
      maxAllowed = 0;
    } else if (isW2GRight) {
      // W2G right wall: gate forced to position regCount
      minAllowed = regCount;
      maxAllowed = regCount;
    } else if (hasGate && hasRaked && !isW2G) {
      // No pre-clamping based on hinge side — auto-flip at rake end handles the
      // correct hinge orientation. Gate is free to reach rake end from either side.
      // (minAllowed=0, maxAllowed=regCount remain as initialised above)
    }

    const clampedPos = Math.max(minAllowed, Math.min(gatePos, maxAllowed));

    // ── Assemble finalPanels ──────────────────────────────────────────────
    let finalPanels = [];

    if (!hasGate) {
      // No gate — panels + optional rake at correct end
      if (hasRaked && rakedSide === 'left')  finalPanels.push(rakedEntry);
      finalPanels.push(...regularStripPanels);
      if (hasRaked && rakedSide === 'right') finalPanels.push(rakedEntry);

    } else if (gateAtRakeEnd) {
      // Gate latches off rake — no separate hinge panel between rake and gate.
      // Peter's rule: gate LATCHES off rake (7mm), hinge on the far side (10mm).
      // Left rake:  [rake]-7mm-[gate]-10mm-[hinge]-var-[panels]
      // Right rake: [panels]-var-[hinge]-10mm-[gate]-7mm-[rake]
      if (rakedSide === 'left') {
        finalPanels.push(rakedEntry);
        finalPanels.push(gateEntry);
        if (hingeEntry) finalPanels.push(hingeEntry);
        finalPanels.push(...regularStripPanels);
      } else {
        finalPanels.push(...regularStripPanels);
        if (hingeEntry) finalPanels.push(hingeEntry);
        finalPanels.push(gateEntry);
        finalPanels.push(rakedEntry);
      }

    } else {
      // Normal gate placement (G2G or W2G, gate not at rake end)
      const leftPanels  = regularStripPanels.slice(0, clampedPos);
      const rightPanels = regularStripPanels.slice(clampedPos);

      // Build gate block per hinge side:
      // G2G LHS:   [gate]-10mm-[hinge]  (gate faces right, hinge on right)
      // G2G RHS:   [hinge]-10mm-[gate]  (hinge on left, gate faces left)
      // W2G left:  [gate]               (no hinge panel — wall is the hinge)
      // W2G right: [gate]               (no hinge panel — wall is the hinge)
      const gateBlock = [];
      if (gateSide === 'left') {
        gateBlock.push(gateEntry);
        if (!isW2G && hingeEntry) gateBlock.push(hingeEntry);
      } else {
        if (!isW2G && hingeEntry) gateBlock.push(hingeEntry);
        gateBlock.push(gateEntry);
      }

      // Insert rake at correct end, gate block at clampedPos.
      if (hasRaked && rakedSide === 'left')  finalPanels.push(rakedEntry);

      // Cut panel is now marked in-place in regularStripPanels — no special end-placement needed.
      // Normal assembly: leftPanels [gateBlock] rightPanels, rake at correct end.
      finalPanels.push(...leftPanels, ...gateBlock, ...rightPanels);
      if (hasRaked && rakedSide === 'right') finalPanels.push(rakedEntry);
    }

    // ── Store rendered panels in state for pricing to use ─────────────────
    const _epSetupStore   = this.state.sections[sectionNum]?.endPostSetup || 'both';
    const _epEffective    = _epSetupStore;
    const _skipLeft       = _epEffective === 'no-left'  || _epEffective === 'no-both';
    const _skipRight      = _epEffective === 'no-right' || _epEffective === 'no-both';
    // Post count = one post per inter-panel gap + end posts (skip flags respected)
    const _visiblePanels  = finalPanels.filter(p => !p.isHinge);
    const _gatePanels     = _visiblePanels.filter(p => p.type === 'gate').length;
    let   _postCount      = _visiblePanels.length + 1;   // panels + 1 base
    if (_skipLeft)  _postCount -= 1;
    if (_skipRight) _postCount -= 1;
    // 2026-08-14 REMOVED: `_postCount += _gatePanels;` ("each gate adds one extra post").
    // The renderer never draws that post. A gate's hinge and latch posts ARE the shared
    // run posts already counted by `_visiblePanels.length + 1`, and the drawn strip sums
    // to the section length exactly with that count. The extra one was pure overcharge:
    // $45.60 on every gated blade section. Verified drawn-vs-quoted across gate sides,
    // end-post setups and both hinge types before removal.

    this.state.sections[sectionNum].renderedPanels = finalPanels.map(p => ({
      type:   p.type,
      width:  p.width,
      isRaked: !!p.isRaked,
      isGate:  p.type === 'gate',
      retainedHeight: p.retainedHeight || null
    }));
    this.state.sections[sectionNum].renderedPostCount = _postCount;

    // ── Render the strip — SVG compositor ────────────────────────────────
    previewContainer.innerHTML = '';
    previewContainer.classList.add('has-panels');

    const panelCount    = finalPanels.length;
    const hasRakedPanel = finalPanels.some(p => p.isRaked);
    const widthResult   = this.calculatePreviewPanelWidth(panelCount, panelCount);
    const dynWidth      = typeof widthResult === 'object' ? widthResult.width : widthResult;
    // exactMode removed

    // ── SVG layout constants ──────────────────────────────────────────────
    // Peter's spec v2:
    //   PANEL = SQUARE (width == height), top aligns with post top, small gap at bottom
    //   POST  = thin black bar, T-foot at base, extends below panel to ground
    const SVG_POST_BAR_W       = 8;    // thin vertical bar width (px)
    const SVG_POST_FOOT_W      = 18;   // T-foot width at base (px)
    const SVG_POST_FOOT_H      = 6;    // T-foot height (px)
    const SVG_SLOT_W           = 130;  // total slot width per panel
    const SVG_GATE_W           = 110;  // gate slot width (px)
    const SVG_POST_W           = SVG_POST_BAR_W;
    const SVG_PANEL_W          = SVG_SLOT_W;
    const SVG_ASSET_W          = SVG_SLOT_W;
    const _fenceH              = parseInt(this.state.sections[sectionNum]?.fenceHeight || 1200);
    const _heightScale         = _fenceH / 1200;
    const SVG_PANEL_SQUARE     = Math.round((SVG_SLOT_W - SVG_POST_BAR_W) * _heightScale);
    const SVG_PANEL_OFFSET_TOP = 0;   // panel top flush with post top
    const SVG_PANEL_GAP_BOT    = 28;  // gap below panel before ground (foot sits here)
    const SVG_POST_H           = SVG_PANEL_SQUARE + SVG_PANEL_GAP_BOT; // post height = panel + gap
    const SVG_PANEL_H          = SVG_PANEL_SQUARE;
    const SVG_ASSET_H          = SVG_POST_H;
    const SVG_TOP_PAD          = hasRakedPanel ? 70 : 20;
    const SVG_LBL_H            = 36;
    const SVG_GROUND_Y         = SVG_TOP_PAD + SVG_POST_H;
    const groundPost           = this.state.sections[sectionNum]?.groundPost || false;
    const _epSetup             = this.state.sections[sectionNum]?.endPostSetup || 'both';
    const endPostSetupSVG      = _epSetup;
    const skipLeftPost         = endPostSetupSVG === 'no-left'  || endPostSetupSVG === 'no-both';
    const skipRightPost        = endPostSetupSVG === 'no-right' || endPostSetupSVG === 'no-both';
    const SVG_SPIKE_H          = 18;   // ground post spike below ground line (px)
    const SVG_TOTAL_H          = SVG_GROUND_Y + SVG_LBL_H + (groundPost ? SVG_SPIKE_H : 0);
    const labelOffsetY         = groundPost ? SVG_SPIKE_H + 4 : 4;  // push labels below spike when ground post ON

    // ── Compute total SVG width ───────────────────────────────────────────
    // visually: post | panel | post | panel | post ...
    // visiblePanels = finalPanels minus hidden hinge entries
    const visiblePanels = finalPanels.filter(p => !p.isHinge);
    const nPanels       = visiblePanels.length;
    const nPosts        = nPanels + 1; // one post at each outer end + between every panel

    // Gate composite: gate already includes its own posts — don't double-count
    // We'll handle gate width separately: gate takes dynWidth, its flanking posts
    // are embedded (no extra post slots needed between gate and neighbours).
    // Strategy: build a flat list of "columns" with widths.
    const columns = []; // {type:'post'|'panel'|'gate'|'rake', width, panel?}

    // ── Per-panel width, strictly to scale ───────────────────────────────
    // 2026-09-07 (Pete): "on the plans sent these rake panels look completely different.
    // lets try and make everything to scale. shrink or enlarge to fit."
    //
    // It was not to scale, in two separate ways:
    //   - a RAKE was pinned to 150px whatever its real width, so a 1200mm rake was drawn
    //     WIDER than an 1850mm panel and about the same as a 2950mm one
    //   - any panel under 1000mm was floored at 90px, so a 450mm filler and a 950mm panel
    //     drew identically, and both as wide as a full metre
    //
    // One constant scale for everything now: 90px per metre. A 1200 rake is 108px, a 2950
    // panel 266px, a 450 filler 40px - each in true proportion to the others.
    //
    // The 6px floor is a drawing minimum, not a fudge. The smallest cut this can emit is
    // 50mm (4.5px), which would otherwise vanish into the post beside it. It sits below
    // any real panel size, so nothing a customer actually sees is distorted by it.
    //
    // "Shrink or enlarge to fit" is already handled downstream: cf-preview-legible.js and
    // cf-preview-scroll.js scale the finished SVG to the panel and scroll it when a long
    // run cannot fit at a readable size. Keeping THIS purely proportional is what lets
    // those two work correctly - they were fitting a drawing that was already distorted.
    //
    // drawRake() is parametric on the column width (rakeW, flatFrac, mergeFrac, and a
    // picket count derived from rakeW), so a narrower rake draws correctly with
    // proportionally fewer pickets. Verified before this change.
    const mmToPx = (mm) => Math.max(6, Math.round((mm / 1000) * 90));

    // ── Column builder ────────────────────────────────────────────────────
    // Rule: each element is responsible for its OWN trailing post.
    // The very first element gets a leading post ONLY if it's a plain panel/gate.
    // Rake owns its tall post internally (drawn in drawColumns) — no leading post needed.
    // Gate owns its rightPost internally — no trailing post pushed here.
    visiblePanels.forEach((panel, i) => {
      const isFirst = i === 0;
      const panelPx = mmToPx(panel.width || 1000, panel.isRaked);

      if (panel.isGate) {
        const isW2PLeft  = isW2G && wallGateEnd === 'left';
        const isW2PRight = isW2G && wallGateEnd === 'right';
        const isLastCol = i === visiblePanels.length - 1;
        if (isFirst && !isW2PLeft && !skipLeftPost) columns.push({ type: 'post', label: `${post_width}mm`, subLabel: 'Post' });
        const leftPost  = false;
        const rightPost = !isW2PRight && !(isLastCol && skipRightPost);
        columns.push({ type: 'gate', width: panelPx, panel, leftPost, rightPost,
          label: `${panel.width}mm`, subLabel: 'Gate',
          postLabel: `${post_width}mm`, postSubLabel: 'Post' });

      } else if (panel.isRaked) {
        const isRightRake = (panel.type === 'right-raked');
        columns.push({ type: 'rake', width: panelPx, panel,
          label: `${panel.width}mm`, subLabel: `Rake (ret. ${panel.retainedHeight || 0}mm)` });
        if (!isRightRake) {
          columns.push({ type: 'post', label: `${post_width}mm`, subLabel: 'Post' });
        }

      } else {
        const isLast = i === visiblePanels.length - 1;
        if (isFirst && !skipLeftPost) columns.push({ type: 'post', label: `${post_width}mm`, subLabel: 'Post' });
        const isCutPanel = !!panel.isCut && !panel.isEven;
        const panelLabel = panel.isEven
          ? `${panel.nominalWidth || panel.width}mm`
          : `${panel.nominalWidth || panel.width}mm`;
        const panelSub   = panel.isEven ? 'Panel' : isCutPanel ? 'Cut' : 'Panel';
        columns.push({ type: 'panel', width: panelPx, panel, label: panelLabel, subLabel: panelSub, isCut: isCutPanel });
        if (!(isLast && skipRightPost)) columns.push({ type: 'post', label: `${post_width}mm`, subLabel: 'Post' });
      }
    });

    // Compute x positions — posts are SVG_POST_BAR_W, panels/gates/rakes use proportional width
    // Reserve a left margin for the height dimension line (building-plan style),
    // so the whole fence drawing shifts right and the dimension sits ~35px to its left.
    const SVG_LEFT_MARGIN = 52;
    let svgW = SVG_LEFT_MARGIN;
    const colX = [];
    columns.forEach(col => {
      colX.push(svgW);
      if (col.type === 'post' || col.type === 'post-skip') svgW += SVG_POST_BAR_W;
      else if (col.type === 'gate') {
        svgW += (col.leftPost ? SVG_POST_BAR_W : 0) + col.width + (col.rightPost ? SVG_POST_BAR_W : 0);
      }
      else svgW += col.width; // panel or rake — proportional to mm size
    });

    // ── Build SVG markup ──────────────────────────────────────────────────
    const ns  = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', `0 0 ${svgW} ${SVG_TOTAL_H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    // ── Fit the drawing inside the floating preview box (esp. tall 1500/1800 panels) ──
    // Cap the DISPLAY height; the viewBox keeps internal coordinates so the whole
    // drawing (posts, panels, labels) scales down proportionally to fit the box.
    const SVG_MAX_DISPLAY_H = 160;
    let _dispW = svgW, _dispH = SVG_TOTAL_H;
    if (SVG_TOTAL_H > SVG_MAX_DISPLAY_H) {
      const _k = SVG_MAX_DISPLAY_H / SVG_TOTAL_H;
      _dispW = Math.round(svgW * _k);
      _dispH = SVG_MAX_DISPLAY_H;
    }
    svg.setAttribute('width',  _dispW);
    svg.setAttribute('height', _dispH);
    svg.style.cssText = 'display:block;overflow:visible;flex-shrink:0;';

    const mk = (tag, attrs = {}) => {
      const el = document.createElementNS(ns, tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };

    // Helper: inline <image> element (no symbol/use — avoids xlink:href compat issues)
    const mkImg = (href, x, y, w, h) => {
      const img = mk('image', { href, x, y, width: w, height: h, preserveAspectRatio: 'none' });
      return img;
    };

    // Image URL map — resolved from shopifyData settings with CDN fallbacks
    const _sd        = this.shopifyData?.settings || {};
    const _poolPanel = this.getActiveMeshSheet();
    const imgUrls = {
      panel: _sd.panelImage || _poolPanel?.image
             || '',
      gateL: (_sd.gateImages?.left)
             || '',
      gateR: (_sd.gateImages?.right)
             || '',
      rakeL: (_sd.rakedImages?.left)
             || '',
      rakeR: (_sd.rakedImages?.right)
             || '',
      post:  _sd.postImage
             || '',
    };

    // ── Layout constants ─────────────────────────────────────────────────
    // Rake: tall end rises ABOVE panel top, short end matches panel top
    const RAKE_EXTRA_H = 60;                          // rake bars = 140% of panel height, so post = panel height + 40%
    const postTop      = SVG_TOP_PAD;
    const panelTop     = SVG_TOP_PAD + SVG_PANEL_OFFSET_TOP; // flush with post top
    const panelBot     = SVG_GROUND_Y - SVG_PANEL_GAP_BOT;
    const panelH       = SVG_PANEL_SQUARE;
    const rakeTallTop  = panelTop - RAKE_EXTRA_H;    // tall end top Y: matches tallest rake bar (140% of panel height)
    const rakeShortTop = panelTop;                    // short end top Y = same as panel top
    const groundY      = SVG_GROUND_Y;

    // ── Left-side height dimension line (building-plan style) ─────────────────
    // Point-to-point measure of the actual panel height (1200 / 1500 / 1800mm),
    // ~35px left of the fence, with the number rotated anti-clockwise.
    {
      const _fenceH  = parseInt(section.fenceHeight) || 1200;
      const _dimX    = SVG_LEFT_MARGIN - 34;   // vertical line x (≈18) — ~34px left of the fence
      const _tick    = 5;                       // end tick half-length
      const _dimCol  = '#334155';
      const _yTop    = panelTop;
      const _yBot    = panelBot;
      const _dg      = mk('g', {});
      _dg.appendChild(mk('line', { x1: _dimX, y1: _yTop, x2: _dimX, y2: _yBot, stroke: _dimCol, 'stroke-width': 1 }));
      _dg.appendChild(mk('line', { x1: _dimX - _tick, y1: _yTop, x2: _dimX + _tick, y2: _yTop, stroke: _dimCol, 'stroke-width': 1 }));
      _dg.appendChild(mk('line', { x1: _dimX - _tick, y1: _yBot, x2: _dimX + _tick, y2: _yBot, stroke: _dimCol, 'stroke-width': 1 }));
      const _lblX = _dimX - 8;
      const _lblY = (_yTop + _yBot) / 2;
      const _t = mk('text', {
        x: _lblX, y: _lblY,
        transform: `rotate(-90 ${_lblX} ${_lblY})`,
        'text-anchor': 'middle', 'font-size': 13, 'font-weight': 600,
        'font-family': 'sans-serif', fill: _dimCol
      });
      _t.textContent = `${_fenceH}mm`;
      _dg.appendChild(_t);
      svg.appendChild(_dg);
    }

    // ── Helpers ───────────────────────────────────────────────────────────
    const mkText = (x, y, text, size = 9, anchor = 'middle', fill = '#475569') => {
      const t = mk('text', { x, y, 'text-anchor': anchor, 'font-size': size,
        'font-family': 'sans-serif', fill, 'dominant-baseline': 'hanging' });
      t.textContent = text;
      return t;
    };

    // Rotated label for narrow slots — renders text vertically
    const MIN_LABEL_W = 50; // px — below this, rotate label -90°
    const mkLabel = (cx, y, line1, line2, slotWidth) => {
      const frag = document.createDocumentFragment();
      if (slotWidth < MIN_LABEL_W) {
        // Rotate -90° around the centre point, stack both lines as one
        const combined = line2 ? `${line1} ${line2}` : line1;
        const t = mk('text', {
          x: cx, y: y + 4,
          'text-anchor': 'end',
          'font-size': 8,
          'font-family': 'sans-serif',
          fill: '#475569',
          transform: `rotate(-90, ${cx}, ${y + 4})`
        });
        t.textContent = combined;
        frag.appendChild(t);
      } else {
        frag.appendChild(mkText(cx, y,      line1));
        if (line2) frag.appendChild(mkText(cx, y + 12, line2));
      }
      return frag;
    };

    // ── Draw helpers — Peter's screenshot spec ────────────────────────────
    // POST: thin vertical bar + T-foot at bottom, centred on x
    // Ground Post mode: spike below ground line instead of T-foot
    // x = left edge of the post slot (SVG_POST_BAR_W wide, centred in slot)
    const drawPost = (x, topY = postTop) => {
      const cx = x + SVG_POST_BAR_W / 2;
      // Vertical bar (above ground)
      svg.appendChild(mk('rect', {
        x:      cx - SVG_POST_BAR_W / 2,
        y:      topY,
        width:  SVG_POST_BAR_W,
        height: groundY - topY,
        fill:   '#1a1a1a'
      }));
      if (groundPost) {
        // Ground post: short spike below ground line, same width as post bar
        svg.appendChild(mk('rect', {
          x:      cx - SVG_POST_BAR_W / 2,
          y:      groundY,
          width:  SVG_POST_BAR_W,
          height: SVG_SPIKE_H,
          fill:   '#1a1a1a',
          rx:     1
        }));
      } else {
        // Standard post: T-foot (wider base plate)
        svg.appendChild(mk('rect', {
          x:      cx - SVG_POST_FOOT_W / 2,
          y:      groundY - SVG_POST_FOOT_H,
          width:  SVG_POST_FOOT_W,
          height: SVG_POST_FOOT_H,
          fill:   '#1a1a1a',
          rx:     1
        }));
      }
    };

    // ── BLADE PANEL renderer ──────────────────────────────────────────────────
    // Renders vertical pickets with horizontal top and bottom rails
    // Matches exact SVG specification: 14 vertical pickets, 2 horizontal rails
    
    const drawBladeFill = (svgTarget, px, tY, panelW, pH, slotX, slotW, mmWidth, isCutPanel = false, rightAlign = false) => {
      // ── BLADE FENCE STYLE ──────────────────────────────────────────────────
      // Renders sparse vertical pickets with 2 horizontal rails
      // Picket count based on panel width (mm):
      //   3000 mm = 13 bars (6 for cut/filler panels)
      //   2000 mm = 12 bars (6 for cut/filler panels)
      //   1000 mm = 10 bars (5 for cut/filler panels)
      //   500-999 mm = 10 bars (5 for cut/filler panels)
      
      // All panels: fit as many bars as FIXED_SLOT density allows,
      // then spread them evenly across the full panel width — equal half-gap margin on each side.
      const REF_PANEL_PX = 122;
      const REF_BARS     = 12;
      const FIXED_SLOT   = REF_PANEL_PX / (REF_BARS + 1); // ~9.38px reference density
      const picketW = 3;
      const barCount = Math.max(1, Math.floor(panelW / FIXED_SLOT));
      const spacing  = panelW / barCount; // equal slots, bar centered in each

      // Draw vertical pickets — each bar centered in its slot at (i-0.5)*spacing
      for (let i = 1; i <= barCount; i++) {
        const picketX = px + (i - 0.5) * spacing - (picketW / 2);
        svgTarget.appendChild(mk('rect', {
          x: picketX,
          y: tY,
          width: picketW,
          height: pH,
          fill: '#000'
        }));
      }
      
      // Draw 2 horizontal rails — inset slightly so picket tips are visible
      const railH = 5;  // prominent rail height
      const rail1Y = tY + 6;               // slightly below top
      const rail2Y = tY + pH - railH - 6;  // slightly above bottom
      
      svgTarget.appendChild(mk('rect', {
        x: slotX,
        y: rail1Y,
        width: slotW,
        height: railH,
        fill: '#000'
      }));
      
      svgTarget.appendChild(mk('rect', {
        x: slotX,
        y: rail2Y,
        width: slotW,
        height: railH,
        fill: '#000'
      }));
    };

    // Draw panel with spigots properly aligned to flanking post centerlines
    const drawPanel = (x, tY = panelTop, pw = SVG_SLOT_W, pH = panelH, mmWidth, leftPostX = null, rightPostX = null, isCutPanel = false, rightAlign = false) => {
      var inset = SVG_POST_BAR_W / 2;
      drawBladeFill(svg, x + inset, tY, pw - inset * 2, pH, x, pw, mmWidth, isCutPanel, rightAlign);
      
      // ── Draw spigots at bottom of panel ───────────────────────────────
      // Spigots mount at the base corners where posts connect
      // Only draw if flanking posts exist
      const spigotRadius = 2;  // small dot
      const spigotY = panelBot - 2;  // at the base, just above ground
      
      // Left spigot (only if there's a left post)
      if (leftPostX !== null) {
        const leftSpigotX = leftPostX + SVG_POST_BAR_W / 2;
        svg.appendChild(mk('circle', {
          cx: leftSpigotX,
          cy: spigotY,
          r: spigotRadius,
          fill: '#1a1a1a',
          stroke: '#000',
          'stroke-width': '0.5'
        }));
      }
      
      // Right spigot (only if there's a right post)
      if (rightPostX !== null) {
        const rightSpigotX = rightPostX + SVG_POST_BAR_W / 2;
        svg.appendChild(mk('circle', {
          cx: rightSpigotX,
          cy: spigotY,
          r: spigotRadius,
          fill: '#1a1a1a',
          stroke: '#000',
          'stroke-width': '0.5'
        }));
      }
    };

    // RAKE: Angled blade fence with pickets that slope from rake post height down to regular panel top.
    // High end picket top = rakeTallTop (matches the rake post top).
    // Low end picket top  = panelTop    (matches the regular panel top — seamless join).
    // Two horizontal rails:
    //   Top rail    — at panelTop + 6 (same as regular panels, runs full width)
    //   Bottom rail — at panelBot - railH - 6 (same as regular panels)
    const drawRake = (x, pw, isRight, mmWidth) => {
      const inset = SVG_POST_BAR_W / 2;
      const rakeX = x + inset;
      const rakeW = pw - inset * 2;
      const panelHeight = panelBot - panelTop;

      // Same density and centering as drawBladeFill: fit FIXED_SLOT bars, spread evenly.
      const REF_PANEL_PX = 122;
      const REF_BARS     = 12;
      const FIXED_SLOT   = REF_PANEL_PX / (REF_BARS + 1);
      const picketW = 3;
      const barCount = Math.max(1, Math.floor(rakeW / FIXED_SLOT));
      const spacing  = rakeW / barCount; // equal slots, bar centered in each

      // High end = rakeTallTop, low end = panelTop
      // isRight: high end is on the RIGHT side of the rake slot
      // First FLAT_COUNT bars from the high end stay flat at rakeTallTop.
      // Remaining bars slope — picket tops computed from the rail diagonal at each bar's X,
      // then shifted 6px above it for a uniform poke along the slope.
      const FLAT_COUNT = 5;
      const slopeCount = barCount - FLAT_COUNT;

      // Rail Y values (computed here so picket loop can reference them)
      const railH = 4;
      const highY = rakeTallTop + 6;  // rail top at the high/flat end
      const lowY  = panelTop    + 6;  // rail top at the low end

      // Bend point X — where flat zone meets slope zone
      // Mirrors the picket anchor direction: isRight anchors from right, !isRight from left.
      const bendBarIdx = FLAT_COUNT;
      const bendX = isRight
        ? rakeX + rakeW - ((bendBarIdx - 0.5) * spacing)
        : rakeX + ((bendBarIdx - 0.5) * spacing);

      // Slope zone X extents — from bendX to the low end of the panel
      const slopeLowX  = isRight ? x       : x + pw;
      const slopeHighX = bendX;
      const slopeSpanX = slopeLowX - slopeHighX;

      for (let i = 1; i <= barCount; i++) {
        // Each bar centered in its slot at (i-0.5)*spacing, same as drawBladeFill
        const picketX = isRight
          ? rakeX + rakeW - ((i - 0.5) * spacing) - (picketW / 2)
          : rakeX + ((i - 0.5) * spacing) - (picketW / 2);
        const picketCX = picketX + picketW / 2; // centre X of this picket

        // posFromHigh: 0 = tallest bar (high end).
        // Both orientations now use i-1 because i=1 is always the high end:
        //   !isRight: i=1 is leftmost = high end (left rake)
        //    isRight: i=1 is rightmost = high end (right rake, anchored from right)
        const posFromHigh = i - 1;

        let picketTop;
        if (posFromHigh < FLAT_COUNT) {
          // Flat zone — poke already built in (rail at rakeTallTop+6, picket at rakeTallTop)
          picketTop = rakeTallTop;
        } else {
          // Slope zone — interpolate rail Y at this bar's X, then poke 6px above it
          const xProgress = slopeSpanX !== 0 ? (picketCX - slopeHighX) / slopeSpanX : 1;
          const railYatX = highY + (lowY - highY) * Math.min(1, Math.max(0, xProgress));
          picketTop = railYatX - 6; // poke 6px above the rail at this exact X position
        }

        const picketHeight = panelBot - picketTop;

        svg.appendChild(mk('rect', {
          x: picketX,
          y: picketTop,
          width: picketW,
          height: picketHeight,
          fill: '#000'
        }));
      }

      // Top rail — bent polygon: flat at rakeTallTop for the first FLAT_COUNT bars,
      // then diagonal down to panelTop for the remaining bars.
      const leftX  = x;
      const rightX = x + pw;

      let topRailPoints;
      if (!isRight) {
        // High end LEFT: flat from leftX → bendX, then diagonal bendX → rightX
        // Top edge: leftX@highY → bendX@highY → rightX@lowY
        // Bottom edge (reversed): rightX@(lowY+railH) → bendX@(highY+railH) → leftX@(highY+railH)
        topRailPoints = [
          `${leftX},${highY}`,
          `${bendX},${highY}`,
          `${rightX},${lowY}`,
          `${rightX},${lowY + railH}`,
          `${bendX},${highY + railH}`,
          `${leftX},${highY + railH}`
        ].join(' ');
      } else {
        // High end RIGHT: diagonal from leftX → bendX, then flat bendX → rightX
        // Top edge: leftX@lowY → bendX@highY → rightX@highY
        // Bottom edge (reversed): rightX@(highY+railH) → bendX@(highY+railH) → leftX@(lowY+railH)
        topRailPoints = [
          `${leftX},${lowY}`,
          `${bendX},${highY}`,
          `${rightX},${highY}`,
          `${rightX},${highY + railH}`,
          `${bendX},${highY + railH}`,
          `${leftX},${lowY + railH}`
        ].join(' ');
      }

      svg.appendChild(mk('polygon', { points: topRailPoints, fill: '#000' }));

      // Bottom rail — flat horizontal, same as regular panels
      const rail2Y = panelBot - railH - 6;
      svg.appendChild(mk('rect', { x, y: rail2Y, width: pw, height: railH, fill: '#000' }));
    };

    // RAKE POST: taller post at the HIGH end of the rake
    const drawRakePost = (x, topY) => {
      const cx = x + SVG_POST_BAR_W / 2;
      svg.appendChild(mk('rect', {
        x: cx - SVG_POST_BAR_W / 2, y: topY,
        width: SVG_POST_BAR_W, height: groundY - topY,
        fill: '#1a1a1a'
      }));
      if (groundPost) {
        svg.appendChild(mk('rect', {
          x: cx - SVG_POST_BAR_W / 2, y: groundY,
          width: SVG_POST_BAR_W, height: SVG_SPIKE_H,
          fill: '#1a1a1a', rx: 1
        }));
      } else {
        svg.appendChild(mk('rect', {
          x: cx - SVG_POST_FOOT_W / 2, y: groundY - SVG_POST_FOOT_H,
          width: SVG_POST_FOOT_W, height: SVG_POST_FOOT_H,
          fill: '#1a1a1a', rx: 1
        }));
      }
    };

    // GATE: Blade-style gate — vertical pickets + top/bottom rails, hinges & latch.
    // Matches real product photo: black frame + pickets, dark-grey hinges, light-grey latch.
    const drawGate = (gx, pw, hingeOnRight, mmWidth, leftPostX = null, rightPostX = null) => {
      const gateH   = panelH;
      const railH   = 5;
      const picketW = 3;

      // ── Highlight the gate so it's easy to spot among the panels (all sizes) ──
      svg.appendChild(mk('rect', {
        x: gx - 3, y: panelTop - 5, width: pw + 6, height: gateH + 10,
        fill: 'rgba(14,158,213,0.16)', stroke: '#0e9ed5', 'stroke-width': 1.4, rx: 3
      }));

      // ── Top & bottom rails (same inset as regular panels) ─────────────
      const rail1Y = panelTop + 6;
      const rail2Y = panelBot - railH - 6;
      svg.appendChild(mk('rect', { x: gx, y: rail1Y,  width: pw, height: railH, fill: '#000' }));
      svg.appendChild(mk('rect', { x: gx, y: rail2Y,  width: pw, height: railH, fill: '#000' }));

      // ── Left & right frame stiles ──────────────────────────────────────
      const stileW = 4;
      svg.appendChild(mk('rect', { x: gx,            y: panelTop, width: stileW, height: gateH, fill: '#000' }));
      svg.appendChild(mk('rect', { x: gx + pw - stileW, y: panelTop, width: stileW, height: gateH, fill: '#000' }));

      // ── Vertical pickets inside the gate ──────────────────────────────
      // Count pickets proportional to gate width (920mm gate ~ 8 pickets)
      const innerW   = pw - stileW * 2;
      const picketCount = Math.max(4, Math.round(innerW / 13));
      const spacing  = innerW / (picketCount + 1);
      for (let i = 1; i <= picketCount; i++) {
        const px = gx + stileW + (i * spacing) - picketW / 2;
        svg.appendChild(mk('rect', {
          x: px, y: panelTop, width: picketW, height: gateH, fill: '#000'
        }));
      }

      // ── Hinges — dark grey, protrude from hinge-side stile ────────────
      // Two hinges: upper ~20%, lower ~70%
      const hingeW   = 8;
      const hingeH   = 10;
      const hingeCol = '#444444'; // dark grey
      const hingeX   = hingeOnRight ? gx + pw - hingeW + 2 : gx - 2;
      [0.22, 0.68].forEach(frac => {
        svg.appendChild(mk('rect', {
          x: hingeX,
          y: panelTop + (gateH * frac),
          width: hingeW, height: hingeH,
          fill: hingeCol, stroke: '#222', 'stroke-width': 0.5, rx: 1
        }));
        // Hinge bolt dot
        svg.appendChild(mk('circle', {
          cx: hingeOnRight ? gx + pw - 2 : gx + 2,
          cy: panelTop + (gateH * frac) + hingeH / 2,
          r: 1.5, fill: '#888'
        }));
      });

      // ── Latch — light grey, on the opposite side to hinges ────────────
      const latchW   = 7;
      const latchH   = 18;
      const latchCol = '#bbbbbb'; // light grey
      const latchX   = hingeOnRight ? gx - latchW + 2 : gx + pw - 2;
      svg.appendChild(mk('rect', {
        x: latchX,
        y: panelTop + (gateH * 0.42),
        width: latchW, height: latchH,
        fill: latchCol, stroke: '#999', 'stroke-width': 0.5, rx: 2
      }));
      // Latch handle nub
      svg.appendChild(mk('rect', {
        x: hingeOnRight ? latchX - 3 : latchX + latchW,
        y: panelTop + (gateH * 0.42) + latchH * 0.3,
        width: 3, height: latchH * 0.4,
        fill: '#999', rx: 1
      }));

      // ── Spigots at base ────────────────────────────────────────────────
      const spigotY = panelBot - 2;
      [[leftPostX, 1], [rightPostX, 1]].forEach(([postX]) => {
        if (postX !== null) {
          svg.appendChild(mk('circle', {
            cx: postX + SVG_POST_BAR_W / 2, cy: spigotY,
            r: 2, fill: '#1a1a1a', stroke: '#000', 'stroke-width': 0.5
          }));
        }
      });
    };

    // ── Draw columns ──────────────────────────────────────────────────────
    columns.forEach((col, ci) => {
      const x = colX[ci];

      if (col.type === 'post') {
        drawPost(x);
        const cx = x + SVG_POST_BAR_W / 2;
        svg.appendChild(mkText(cx, groundY + labelOffsetY, `${col.label} ${col.subLabel}`));

      } else if (col.type === 'post-skip') {
        // W2G wall side — no post

      } else if (col.type === 'panel') {
        // Find flanking posts for spigot alignment
        // Left post: previous column if it's a post
        // Right post: next column if it's a post
        const leftPostX = (ci > 0 && (columns[ci-1].type === 'post' || columns[ci-1].type === 'post-skip')) ? colX[ci-1] : null;
        const rightPostX = (ci < columns.length - 1 && (columns[ci+1].type === 'post' || columns[ci+1].type === 'post-skip')) ? colX[ci+1] : null;
        
        // Check if this is a cut/filler panel (never true in even mode)
        const isCutPanel = !_isEvenMode && col.isCut;
        
        // rightAlign: anchor pickets from right edge when the next column is a right-hand rake,
        // so the panel's last bar sits flush against the post and matches the rake's first bar.
        const nextCol = columns[ci + 1];
        const nextIsRightRake = nextCol && nextCol.type === 'rake' && nextCol.panel && nextCol.panel.type === 'right-raked';
        
        drawPanel(x, panelTop, col.width, panelH, col.panel && col.panel.width, leftPostX, rightPostX, isCutPanel, nextIsRightRake);
        svg.appendChild(mkLabel(x + col.width / 2, groundY + labelOffsetY, col.label, col.subLabel, col.width));

        // Cut panel renders as a regular panel — no blue overlay or dashed border

      } else if (col.type === 'rake') {
        const isRight = col.panel.type === 'right-raked';
        drawRake(x, col.width, isRight, col.panel && col.panel.width);
        // The rake's tall outer post is the section's end post on the rake side.
        // Respect the end-post on/off choice: a left-hand rake is removed by
        // "No Post Left", a right-hand rake by "No Post Right".
        const _rakePostRemoved = (isRight && skipRightPost) || (!isRight && skipLeftPost);
        if (!_rakePostRemoved) {
          const rakePostX = isRight ? x + col.width - SVG_POST_BAR_W : x;
          drawRakePost(rakePostX, rakeTallTop);
          const rakePostCx = rakePostX + SVG_POST_BAR_W / 2;
          const _rakeRetH = parseInt(col.panel?.retainedHeight) || 0;
          // In-Ground → 2400mm post (any rake). Base Mount → 1600mm (rake ≤1500)
          // or 1900mm (rake 1500–1800). Matches the order line sizing.
          const _rakePostLabel = groundPost
            ? '2400mm Post'
            : (_rakeRetH <= 1500 ? '1600mm Post' : '1900mm Post');
          svg.appendChild(mkText(rakePostCx, groundY + labelOffsetY, _rakePostLabel));
        }
        svg.appendChild(mkLabel(x + col.width / 2, groundY + labelOffsetY, col.label, col.subLabel, col.width));

      } else if (col.type === 'gate') {
        let gx = x;
        const isW2PGate = isW2G && !!wallGateEnd;
        // RHS Hinge = hinge on right, latch on left → hingeOnRight = true
        // LHS Hinge = hinge on left, latch on right → hingeOnRight = false
        // At rake end: latch faces rake → hinge faces away from rake
        //   left rake  → hinge on right → hingeOnRight = true  (gateSide='left' forced)
        //   right rake → hinge on left  → hingeOnRight = false (gateSide='right' forced)
        const hingeOnRight = isW2PGate
          ? (wallGateEnd === 'right')
          : gateAtRakeEnd
            ? (effectiveGateSide === 'left')   // left rake → gateSide='left' → hinge on right (away from rake)
            : (effectiveGateSide === 'right');  // normal: LHS radio (gateSide='left') → hinge on left; RHS radio (gateSide='right') → hinge on right

        let leftPostX = null;
        let rightPostX = null;
        
        if (col.leftPost) {
          leftPostX = gx;
          drawPost(gx);
          const cx = gx + SVG_POST_BAR_W / 2;
          svg.appendChild(mkText(cx, groundY + labelOffsetY, `${col.postLabel} ${col.postSubLabel}`));
          gx += SVG_POST_BAR_W;
        }

        if (col.rightPost) {
          rightPostX = gx + col.width;
        }
        
        drawGate(gx, col.width, hingeOnRight, col.panel && col.panel.width, leftPostX, rightPostX);
        svg.appendChild(mkLabel(gx + col.width / 2, groundY + labelOffsetY, col.label, col.subLabel, col.width));
        gx += col.width;

        if (col.rightPost) {
          drawPost(rightPostX);
          const cx = rightPostX + SVG_POST_BAR_W / 2;
          svg.appendChild(mkText(cx, groundY + labelOffsetY, `${col.postLabel} ${col.postSubLabel}`));
        }
      }
    });

    // Ground line — thin baseline under all assets
    svg.appendChild(mk('line', {
      x1: 0, y1: groundY, x2: svgW, y2: groundY,
      stroke: '#94a3b8', 'stroke-width': 1.5
    }));

    // ── Wrap in scrollable container ──────────────────────────────────────
    const panelContainer = document.createElement('div');
    panelContainer.className = 'panels-container';
    panelContainer.style.cssText = `
      display: flex;
      align-items: flex-end;
      justify-content: safe center;
      overflow-x: auto;
      overflow-y: visible;
      width: 100%;
      padding: 8px 0 0 0;
      box-sizing: border-box;
    `;

    panelContainer.appendChild(svg);
    previewContainer.appendChild(panelContainer);

    // ── SCROLL LOCK: restore after reflow, via rAF so browser has painted ─
    requestAnimationFrame(() => {
      if (_configEl) _configEl.scrollTop = _scrollY;
    });
    // ─────────────────────────────────────────────────────────────────────
  }


  // Render panel preview
  // gapWidth = trailing gaps array (one per panel, after the panel)
  // startGap = leading gap before the first panel (optional, defaults to gapWidth[0])
  renderPanelPreview(container, panels, gapWidth, startGap = null, gateSide = null, gateHingeType = 'glass-to-glass') {
    if (!container) return;

    // ── Gap enforcement ───────────────────────────────────────────────────
    const gapWidths = Array.isArray(gapWidth) ? gapWidth.slice() : Array(panels.length).fill(gapWidth);
    const hasHingePanelEnf = panels.some(p => p.type === 'hinge-panel');
    const isW2GEnf = (gateHingeType === 'W2G-REMOVED-2026-08-30') || (!hasHingePanelEnf && panels.some(p => p.type === 'gate'));
    for (let i = 0; i < panels.length; i++) {
      const cur = panels[i], next = panels[i + 1];
      if (cur.isHingeSlot && next?.type === 'gate')  { gapWidths[i] = 10; continue; }
      if (cur.type === 'gate' && next?.isHingeSlot)  { gapWidths[i] = 10; continue; }
      if (isW2GEnf) {
        const isW2GLeft = panels[0]?.type === 'gate';
        if (isW2GLeft) { if (cur.type === 'gate' && next) { gapWidths[i] = 7; continue; } }
        else {
          if (next?.type === 'gate')        { gapWidths[i] = 7;  continue; }
          if (cur.type === 'gate' && !next) { gapWidths[i] = 10; continue; }
        }
      } else {
        if (!next) continue;
        if (next.type === 'gate' || cur.type === 'gate') { gapWidths[i] = 7; continue; }
      }
    }

    container.innerHTML = '';

    // ── Constants ─────────────────────────────────────────────────────────
    const POST_W   = 10;   // post bar width px
    const FOOT_W   = 30;   // footing width px
    const FOOT_H   = 6;    // footing height px
    const POST_TOP = 5;    // post bar top y
    const _fenceH2  = parseInt(this.state.sections[this.state.currentSection]?.fenceHeight || 1200);
    const _hScale2  = _fenceH2 / 1200;
    const POST_H   = Math.round(150 * _hScale2);
    const PANEL_TOP = 25;
    const PANEL_H  = Math.round(130 * _hScale2);
    // Rake height follows the customer's "Retained Height" selection (1300-1800
    // in 100mm steps), not a fixed 1.25x the panel. This was 162 * scale
    // (162/130 = 1.25), so every rake drew identically whatever height was
    // chosen. Now 1300 is only slightly taller than the fence and 1800 is
    // visibly much taller. A 1500 rake on a 1200 fence reproduces the old
    // 1.25x exactly, so the common case is unchanged.
    const _rakeMmRaw = parseInt(this.state.sections[this.state.currentSection]?.retainedHeight) || 1500;
    const _rakeMm  = Math.max(1300, Math.min(1800, Math.round(_rakeMmRaw / 100) * 100));
    const RAKE_H   = Math.round(PANEL_H * (_rakeMm / _fenceH2));
    const BASELINE = POST_TOP + POST_H;
    const PAD_BOT  = 40;   // below baseline for labels
    const PANEL_W  = 155;  // each panel display width px

    const hasRaked = panels.some(p => p.type === 'left-raked' || p.type === 'right-raked' || p.type === 'raked');
    const svgH     = BASELINE + FOOT_H + PAD_BOT;

    // ── Build sequence: post, panel, post, panel, ..., post ──────────────
    const seq = [{ kind: 'post', mmLabel: '', typeLabel: '' }];
    panels.forEach((panel, i) => {
      const pt = panel.type;
      const mm = panel.width + 'mm';
      if      (pt === 'left-raked'  || (pt === 'raked' && !panel.flipped)) seq.push({ kind: 'rake-left',  mmLabel: mm, typeLabel: 'Rake'  });
      else if (pt === 'right-raked' || (pt === 'raked' &&  panel.flipped)) seq.push({ kind: 'rake-right', mmLabel: mm, typeLabel: 'Rake'  });
      else if (pt === 'gate') {
        const gs = this.state.sections[this.state.currentSection]?.gateSide || 'right';
        seq.push({ kind: 'gate-' + gs, mmLabel: mm, typeLabel: 'Gate' });
      } else {
        seq.push({ kind: 'panel', mmLabel: mm, typeLabel: 'Panel' });
      }
      if (i < panels.length - 1) seq.push({ kind: 'post', mmLabel: gapWidths[i] + 'mm', typeLabel: 'Post' });
    });
    seq.push({ kind: 'post', mmLabel: '', typeLabel: '' });

    // ── Total width ───────────────────────────────────────────────────────
    let totalW = 0;
    seq.forEach(el => { totalW += el.kind === 'post' ? POST_W : PANEL_W; });
    totalW = Math.max(totalW, 300);

    // ── SVG ───────────────────────────────────────────────────────────────
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', totalW);
    svg.setAttribute('height', svgH);
    svg.setAttribute('viewBox', '0 0 ' + totalW + ' ' + svgH);
    svg.style.cssText = 'display:block;overflow:visible;';

    const mk = (tag, attrs) => {
      const el = document.createElementNS(ns, tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const mkTxt = (txt, x, y) => {
      const t = mk('text', { x, y, 'text-anchor': 'middle', 'font-size': '10', 'font-family': 'sans-serif', 'fill': '#475569' });
      t.textContent = txt;
      return t;
    };

    // Ground line
    svg.appendChild(mk('line', { x1: 0, y1: BASELINE, x2: totalW, y2: BASELINE, stroke: '#1a1a1a', 'stroke-width': '1.5' }));

    let x = 0;
    seq.forEach(el => {
      const isPost  = el.kind === 'post';
      const elW     = isPost ? POST_W : PANEL_W;
      const cx      = x + elW / 2;

      if (isPost) {
        // Bar
        svg.appendChild(mk('rect', { x, y: POST_TOP, width: POST_W, height: POST_H, fill: '#1a1a1a' }));
        // Footing
        svg.appendChild(mk('rect', { x: cx - FOOT_W/2, y: BASELINE, width: FOOT_W, height: FOOT_H, fill: '#1a1a1a', rx: 2 }));
        // Labels
        if (el.mmLabel)   svg.appendChild(mkTxt(el.mmLabel,   cx, BASELINE + FOOT_H + 14));
        if (el.typeLabel) svg.appendChild(mkTxt(el.typeLabel, cx, BASELINE + FOOT_H + 26));

      } else {
        const isRakeL = el.kind === 'rake-left';
        const isRakeR = el.kind === 'rake-right';
        const isGateL = el.kind === 'gate-left';
        const isGateR = el.kind === 'gate-right';
        const isRake  = isRakeL || isRakeR;
        const ph      = isRake ? RAKE_H : PANEL_H;
        // Panel bottom always at BASELINE, top = BASELINE - ph
        const ptop    = BASELINE - ph;

        if (isRakeL) {
          // Tall on left (ptop), slopes down on right (ptop + drop)
          const drop = RAKE_H - PANEL_H;
          svg.appendChild(mk('polygon', {
            points: `${x},${ptop} ${x+PANEL_W},${ptop+drop} ${x+PANEL_W},${BASELINE} ${x},${BASELINE}`,
            fill: '#d4d4d4', stroke: '#1a1a1a', 'stroke-width': '2', 'stroke-linejoin': 'round'
          }));
        } else if (isRakeR) {
          const drop = RAKE_H - PANEL_H;
          svg.appendChild(mk('polygon', {
            points: `${x},${ptop+drop} ${x+PANEL_W},${ptop} ${x+PANEL_W},${BASELINE} ${x},${BASELINE}`,
            fill: '#d4d4d4', stroke: '#1a1a1a', 'stroke-width': '2', 'stroke-linejoin': 'round'
          }));
        } else {
          // Regular or gate panel
          svg.appendChild(mk('rect', { x, y: ptop, width: PANEL_W, height: ph, fill: '#d4d4d4', stroke: '#1a1a1a', 'stroke-width': '2' }));
          if (isGateL) {
            svg.appendChild(mk('rect', { x,             y: ptop+ph*0.4, width: 10, height: 16, fill: '#555', rx: 1 }));
            svg.appendChild(mk('rect', { x: x+PANEL_W-10, y: ptop+ph*0.2, width: 10, height: 14, fill: '#555', rx: 1 }));
            svg.appendChild(mk('rect', { x: x+PANEL_W-10, y: ptop+ph*0.7, width: 10, height: 14, fill: '#555', rx: 1 }));
          } else if (isGateR) {
            svg.appendChild(mk('rect', { x: x+PANEL_W-10, y: ptop+ph*0.4, width: 10, height: 16, fill: '#555', rx: 1 }));
            svg.appendChild(mk('rect', { x,             y: ptop+ph*0.2, width: 10, height: 14, fill: '#555', rx: 1 }));
            svg.appendChild(mk('rect', { x,             y: ptop+ph*0.7, width: 10, height: 14, fill: '#555', rx: 1 }));
          }
        }

        svg.appendChild(mkTxt(el.mmLabel,   cx, BASELINE + FOOT_H + 14));
        svg.appendChild(mkTxt(el.typeLabel, cx, BASELINE + FOOT_H + 26));
      }

      x += elW;
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'panels-container';
    wrapper.style.cssText = 'overflow-x:auto;overflow-y:visible;width:100%;padding:10px 0;box-sizing:border-box;';
    wrapper.appendChild(svg);
    container.appendChild(wrapper);
  }

  // Calculate preview panel width
  calculatePreviewPanelWidth(panelCount, gapCount, containerEl = null) {
    // Peter's rule: max 5 panels displayed at full size; beyond that, horizontal scroll
    // kicks in (panelContainer already has overflow-x: auto). We cap the sizing math at
    // 5 so panels 6+ stay at the preferred width instead of shrinking the whole row.
    const MAX_VISIBLE_PANELS = 5;
    const sizingPanelCount = Math.min(panelCount, MAX_VISIBLE_PANELS);
    const sizingGapCount   = Math.min(gapCount,   MAX_VISIBLE_PANELS);

    const visualPreview = containerEl || document.querySelector('#section-preview')?.closest('.visual-preview');
    if (!visualPreview) {
      return { width: CONFIG.PREVIEW.PREFERRED_PANEL_WIDTH, overflowing: false, reason: 'no-container' };
    }

    const clientWidth = visualPreview.clientWidth;
    const offsetWidth = visualPreview.offsetWidth;
    const computedStyles = window.getComputedStyle(visualPreview);
    
    let containerWidth = 0;
    
    if (clientWidth > 100) {
      containerWidth = clientWidth;
    } else if (offsetWidth > 100) {
      containerWidth = offsetWidth;
    } else {
      containerWidth = CONFIG.PREVIEW.FALLBACK_CONTAINER_WIDTH;
    }
    
    const paddingLeft = parseInt(computedStyles.paddingLeft) || 20;
    const paddingRight = parseInt(computedStyles.paddingRight) || 20;
    const totalPadding = paddingLeft + paddingRight;
    
    const availableWidth = containerWidth - totalPadding;
    const totalGapWidth = sizingGapCount * CONFIG.PREVIEW.GAP_WIDTH;
    const availableForPanels = availableWidth - totalGapWidth;

    const preferredTotalWidth = (sizingPanelCount * CONFIG.PREVIEW.PREFERRED_PANEL_WIDTH) + totalGapWidth;

    // When over the 5-panel cap, always return preferred width — scroll handles the overflow
    if (panelCount > MAX_VISIBLE_PANELS) {
      return { width: CONFIG.PREVIEW.PREFERRED_PANEL_WIDTH, overflowing: false, reason: 'scroll-mode' };
    }

    if (preferredTotalWidth <= availableWidth) {
      return { width: CONFIG.PREVIEW.PREFERRED_PANEL_WIDTH, overflowing: false, reason: 'preferred-fits' };
    }

    const calculatedPanelWidth = Math.floor(availableForPanels / sizingPanelCount);

    const maxPanelWidth = Math.min(200, calculatedPanelWidth);

    const minimumTotalWidth = (sizingPanelCount * CONFIG.PREVIEW.MIN_PANEL_WIDTH) + totalGapWidth;
    const willOverflow = minimumTotalWidth > availableWidth;
    
    if (willOverflow) {
      return { 
        width: CONFIG.PREVIEW.MIN_PANEL_WIDTH, 
        overflowing: true, 
        reason: 'minimum-overflow',
        neededWidth: minimumTotalWidth,
        availableWidth: availableWidth,
        shortfall: minimumTotalWidth - availableWidth
      };
    }
    
    const finalWidth = Math.max(CONFIG.PREVIEW.MIN_PANEL_WIDTH, Math.min(maxPanelWidth, calculatedPanelWidth));
    const isUsingMinimum = finalWidth === CONFIG.PREVIEW.MIN_PANEL_WIDTH && calculatedPanelWidth < CONFIG.PREVIEW.MIN_PANEL_WIDTH;
    const isUsingMaximum = finalWidth === maxPanelWidth && calculatedPanelWidth > maxPanelWidth;
    
    if (isUsingMinimum) {
      return { 
        width: finalWidth, 
        overflowing: false, 
        reason: 'using-minimum',
        calculatedWidth: calculatedPanelWidth
      };
    } else if (isUsingMaximum) {
      return { 
        width: finalWidth, 
        overflowing: false, 
        reason: 'using-maximum',
        calculatedWidth: calculatedPanelWidth
      };
    } else {
      return { 
        width: finalWidth, 
        overflowing: false, 
        reason: 'calculated-fits'
      };
    }
  }

  // Create panel element
  createPanelElement(panel, dynamicWidth = null) {
    const panelDiv = document.createElement('div');
    panelDiv.className = 'panel-preview';
    panelDiv.style.cssText = 'display: inline-flex; flex-direction: column; margin: 0; position: relative; align-items: center; overflow: visible;';
    
    // Set panel data attributes
    panelDiv.dataset.panelId = panel.id || `panel-${Date.now()}`;
    panelDiv.dataset.panelType = panel.type;
    if (panel.isHingeSlot) panelDiv.dataset.hingeSlot = 'true';

    let imageUrl, imageAlt, labelText, svgOverride = null;
    
    let width = dynamicWidth ? `${dynamicWidth}px` : '80px';
    
    if (!dynamicWidth) {
      width = '80px'; // uniform fallback for all panel types
    }

    switch (panel.type) {
      case 'panel': {
        const poolPanel = this.getActiveMeshSheet();
        imageUrl = this.shopifyData?.settings?.panelImage || poolPanel?.image || '';
        imageAlt = 'Sheet';
        labelText = `${panel.width}mm Sheet`;
        break;
      }

      case 'cut':
      case 'panel': {
        imageUrl = this.shopifyData?.settings?.panelImage || '';
        imageAlt = 'Panel';
        labelText = `${panel.width}mm<br>Panel`;
        break;
      }

      case 'hinge-panel': {
        const hpGateSide = this.state.sections[this.state.currentSection]?.gateSide || 'right';
        const hingeImages = this.shopifyData?.settings?.hingeImages || {};
        const customHingeUrl = hpGateSide === 'right' ? hingeImages.right : hingeImages.left;
        imageUrl = customHingeUrl || (hpGateSide === 'right'
          ? 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/Perf_hinge_right.svg'
          : 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/Perf_hinge_left.svg');
        imageAlt = 'Hinge Panel';
        labelText = `${panel.width}mm<br>Hinge Panel`;
        break;
      }

      case 'gate': {
        const gateSide = this.state.sections[this.state.currentSection]?.gateSide || 'right';
        const gateImages = this.shopifyData?.settings?.gateImages || {};
        imageUrl = gateSide === 'right'
          ? (gateImages.right || '')
          : (gateImages.left  || '');
        imageAlt = gateSide === 'right' ? 'Right Gate Panel' : 'Left Gate Panel';
        labelText = `${panel.width}mm<br>Gate Panel`;
        break;
      }

      case 'left-raked': {
        imageUrl = this.shopifyData?.settings?.rakedImages?.left || '';
        imageAlt = 'Left Raked Panel';
        const leftSectionNum = this.getCurrentSectionNumber();
        const leftSection = this.state.sections[leftSectionNum];
        const leftRakedWidth = panel.width || this.getRakedPanelWidth(leftSection?.fenceHeight);
        const leftRetainedHeight = (leftSection && leftSection.retainedHeight) ? leftSection.retainedHeight : '200';
        labelText = `${leftRakedWidth}mm<br>Raked`;
        break;
      }

      case 'right-raked': {
        imageUrl = this.shopifyData?.settings?.rakedImages?.right || '';
        imageAlt = 'Right Raked Panel';
        const rightSectionNum = this.getCurrentSectionNumber();
        const rightSection = this.state.sections[rightSectionNum];
        const rightRakedWidth = panel.width || this.getRakedPanelWidth(rightSection?.fenceHeight);
        const rightRetainedHeight = (rightSection && rightSection.retainedHeight) ? rightSection.retainedHeight : '200';
        labelText = `${rightRakedWidth}mm<br>Raked`;
        break;
      }

      case 'raked': {
        const isRightRaked = panel.flipped === true;
        const rakedImgs = this.shopifyData?.settings?.rakedImages || {};
        imageUrl = isRightRaked
          ? (rakedImgs.right || '')
          : (rakedImgs.left  || '');
        imageAlt = isRightRaked ? 'Right Raked Panel' : 'Left Raked Panel';
        const rakedSectionNum = this.getCurrentSectionNumber();
        const rakedSection = this.state.sections[rakedSectionNum];
        const rakedPanelWidth = panel.width || this.getRakedPanelWidth(rakedSection?.fenceHeight);
        const rakedRetainedHeight = (rakedSection && rakedSection.retainedHeight) ? rakedSection.retainedHeight : '200';
        labelText = `${rakedPanelWidth}mm<br>Raked`;
        break;
      }

      default:
        imageUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgNDAgMTAwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjM0I4MkY2Ii8+Cjwvc3ZnPgo=';
        imageAlt = 'Sheet';
        labelText = `${panel.width}mm Sheet`;
    }

    let fontSize = '11px';
    if (dynamicWidth) {
      if (dynamicWidth < 40) fontSize = '7px';
      else if (dynamicWidth < 50) fontSize = '8px';
      else if (dynamicWidth < 60) fontSize = '9px';
      else if (dynamicWidth < 80) fontSize = '10px';
      else if (dynamicWidth < 100) fontSize = '11px';
      else fontSize = '12px';
    }

    const isRakedType = panel.type === 'left-raked' || panel.type === 'right-raked' || panel.type === 'raked';

    // Set width for all panels
    panelDiv.style.width = width;

    // SPACER CONFIGURATION (Peter: "same spigot lining" — rake spigot locked inline
    // with regular panel spigots):
    // - Raked panels: 140px image + 5px bottom spacer → spigot line at +5px from base
    // - Regular/gate/hinge panels: 10px top + 110px image + 5px bottom → spigot line at +5px from base
    // Both panel types now share the same bottom spacer so their spigot baselines align.
    const panelHeight = isRakedType ? '150px' : '120px';
    const topSpacerHeight = '0px';
    const bottomSpacerHeight = '0px';
    // Clip 3px from each side of the panel image so the SVG's internal side-border
    // lines are hidden — the visible post bar between panels acts as the sole post indicator.
    const imgStyle = `width: 100%; height: ${panelHeight}; object-fit: fill; display: block; position: relative; z-index: 1;`;

    // Build panel content using DOM methods (no innerHTML)
    // If svgOverride is set, render it as an inline SVG element instead of an <img>
    if (svgOverride) {
      const svgWrapper = document.createElement('div');
      svgWrapper.style.cssText = 'width:100%;display:block;';
      svgWrapper.innerHTML = svgOverride;
      panelDiv.appendChild(svgWrapper);
    } else {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = imageAlt;
      img.style.cssText = imgStyle;
      img.onerror = function() {
        this.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgNDAgMTAwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjM0I4MkY2Ii8+Cjwvc3ZnPgo=';
      };
      
      // Add TOP spacer (10px for regular/gate/hinge, 0px for raked)
      if (topSpacerHeight !== '0px') {
        const topSpacer = document.createElement('div');
        topSpacer.style.cssText = `height: ${topSpacerHeight}; flex-shrink: 0;`;
        panelDiv.appendChild(topSpacer);
      }
      
      // Wrap image in fixed-height container so label never pushes image up
      const imgWrap = document.createElement('div');
      imgWrap.style.cssText = `flex-shrink: 0; line-height: 0;`;
      imgWrap.appendChild(img);
      panelDiv.appendChild(imgWrap);
      
      // Add BOTTOM spacer (5px for regular/gate/hinge, 0px for raked)
      if (bottomSpacerHeight !== '0px') {
        const bottomSpacer = document.createElement('div');
        bottomSpacer.style.cssText = `height: ${bottomSpacerHeight}; flex-shrink: 0;`;
        panelDiv.appendChild(bottomSpacer);
      }
    }

    const label = document.createElement('div');
    label.className = 'panel-width';
    
    // Container for text lines - no wrapping, text shrinks with font size instead
    label.style.cssText = `text-align: center; font-size: ${fontSize}; color: #475569; margin-top: 2px; line-height: 1.2; min-height: 32px; display: flex; flex-direction: column; justify-content: flex-start;`;
    
    // Split on <br> and create separate divs for each line
    // Each line stays on one line, no wrapping
    labelText.split('<br>').forEach((part, i) => {
      const lineDiv = document.createElement('div');
      lineDiv.style.cssText = 'white-space: nowrap; overflow: visible;';
      lineDiv.textContent = part;
      label.appendChild(lineDiv);
    });
    panelDiv.appendChild(label);

    // Add click handler for selection (raked panels cannot be moved)
    const isRakedPanel = panel.type === 'left-raked' || panel.type === 'right-raked';
    if (!isRakedPanel) {
      panelDiv.style.cursor = 'pointer';
      panelDiv.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectPanel(panelDiv);
      });
    }

    return panelDiv;
  }

  // Create panel control buttons
  createPanelControls(panelDiv, container) {
    const panelType = panelDiv.dataset.panelType;
    const isRaked   = panelType === 'left-raked' || panelType === 'right-raked' || panelType === 'raked';
    const isGate    = panelType === 'gate';
    const isHinge   = panelType === 'hinge-panel' || panelType === 'hinge';

    // Raked panels cannot be moved
    if (isRaked) {
      const controlsDiv = document.createElement('div');
      controlsDiv.className = 'panel-controls';
      controlsDiv.style.cssText = 'display: none;';
      return controlsDiv;
    }

    const controlsDiv = document.createElement('div');
    controlsDiv.className = 'panel-controls';

    // Gate and hinge panels: NO arrows — gate placing is handled by the
    // ← Move | Middle | Move → | Exact buttons in the Gate Configuration panel.
    // Regular panels: show selection arrows on click only (hidden by default).
    controlsDiv.style.cssText = `
      text-align: center;
      margin-top: 4px;
      display: none;
      justify-content: center;
      gap: 2px;
    `;

    // Only add move arrows for regular panels (not gate/hinge)
    if (!isGate && !isHinge) {
      const btnBase = `
        border: none; color: #fff; cursor: pointer;
        border-radius: 4px; font-weight: bold; font-size: 13px;
        padding: 3px 8px; min-width: 26px; background: #6B7280;
      `;
      const leftBtn = document.createElement('button');
      leftBtn.innerHTML = '&#8592;';
      leftBtn.title = 'Move left';
      leftBtn.style.cssText = btnBase;
      leftBtn.onclick = (ev) => { ev.stopPropagation(); this.movePanelLeft(panelDiv, container); };

      const rightBtn = document.createElement('button');
      rightBtn.innerHTML = '&#8594;';
      rightBtn.title = 'Move right';
      rightBtn.style.cssText = btnBase;
      rightBtn.onclick = (ev) => { ev.stopPropagation(); this.movePanelRight(panelDiv, container); };

      controlsDiv.appendChild(leftBtn);
      controlsDiv.appendChild(rightBtn);
    }

    return controlsDiv;
  }

  // Select panel and show controls
  selectPanel(panelDiv) {
    // Clear all previous selections
    this.clearAllSelections();
    
    // Mark this panel as selected
    panelDiv.dataset.selected = 'true';
    panelDiv.style.backgroundColor = '#EBF8FF'; // Light blue background
    panelDiv.style.border = '2px solid #3B82F6'; // Blue border
    panelDiv.style.borderRadius = '4px';
    
    // Show the controls for this panel
    const wrapper = panelDiv.parentNode;
    const controls = wrapper.querySelector('.panel-controls');
    if (controls) {
      controls.style.display = 'flex';
    }
    
    
  }

  // Clear all panel selections
  clearAllSelections() {
    const allPanels = document.querySelectorAll('.panel-preview');
    allPanels.forEach(panel => {
      panel.dataset.selected = 'false';
      panel.style.backgroundColor = '';
      panel.style.border = '';
      panel.style.borderRadius = '';
      
      // Hide controls
      const wrapper = panel.parentNode;
      const controls = wrapper.querySelector('.panel-controls');
      if (controls) {
        controls.style.display = 'none';
      }
    });
  }

  // Re-render all gaps properly — gate/hinge fixed gaps (10mm/7mm) are preserved
  reRenderGaps(container) {
    const gaps = container.querySelectorAll('.gap-preview');
    gaps.forEach(gap => gap.remove());

    const wrappers = Array.from(container.children).filter(child =>
      child.querySelector && child.querySelector('.panel-preview')
    );

    // Determine default gap from stored config
    const sectionNum = this.getCurrentSectionNumber
      ? this.getCurrentSectionNumber()
      : this.state.currentSection;
    const cfg = this.state.optimizedConfigs && this.state.optimizedConfigs[sectionNum];
    const defaultGap = cfg ? cfg.gapSpacing : CONFIG.DEFAULTS.GAP_SPACING;
    const gateSide = this.state.sections[sectionNum]?.gateSide || 'right';

    wrappers.forEach((wrapper, idx) => {
      // Determine the correct gap size based purely on adjacent panel types.
      // This is position-independent — works regardless of where gate block moved.
      // Rules:
      //   gate → hinge-panel : 10mm (LHS hinge: gate is left of hinge)
      //   hinge-panel → gate : 10mm (RHS hinge: hinge is left of gate)
      //   panel/raked → gate : 7mm  (latch gap before gate)
      //   gate → panel/raked : 7mm  (latch gap after gate)
      const thisPanel = wrapper.querySelector('.panel-preview');
      const nextWrapper = wrappers[idx + 1];
      const nextPanel = nextWrapper ? nextWrapper.querySelector('.panel-preview') : null;
      let gapSize = defaultGap;

      if (thisPanel && nextPanel) {
        const thisType = thisPanel.dataset.panelType;
        const nextType = nextPanel.dataset.panelType;

        // Fixed hinge gap (10mm) — between gate and hinge-panel in either order
        if ((thisType === 'gate' && nextPanel.dataset.hingeSlot) ||
            (thisPanel.dataset.hingeSlot && nextType === 'gate'))      gapSize = 10;
        // Fixed latch gap (7mm) — between gate and any regular panel or raked
        else if (thisType === 'gate' || nextType === 'gate')            gapSize = 7;
      }

      const gap = this.createGapElement(gapSize);

      // Insert gap after current wrapper
      if (wrapper.nextSibling) {
        container.insertBefore(gap, wrapper.nextSibling);
      } else {
        container.appendChild(gap);
      }
    });
    
    
  }

  // Get the panel type from a wrapper element
  getWrapperType(wrapper) {
    const panel = wrapper.querySelector('.panel-preview');
    return panel ? panel.dataset.panelType : null;
  }

  // Move panel left — gate and its adjacent hinge-panel move together as a pair
  // Helper: check if moving would place a regular panel between gate and hinge
  wouldSplitGateBlock(allWrappers, movingWrapper, targetIndex) {
    const types = allWrappers.map(w => this.getWrapperType(w));
    const movingType = this.getWrapperType(movingWrapper);

    // Only regular panels can potentially split the gate block
    if (movingType !== 'panel' && movingType !== 'left-raked' && movingType !== 'right-raked') return false;

    // Simulate the new order after move
    const currentIndex = allWrappers.indexOf(movingWrapper);
    const simulated = [...types];
    simulated.splice(currentIndex, 1);
    const insertAt = targetIndex > currentIndex ? targetIndex - 1 : targetIndex;
    simulated.splice(insertAt, 0, movingType);

    // Check if gate and hinge-panel are still adjacent in the simulated order
    for (let i = 0; i < simulated.length - 1; i++) {
      const curr = simulated[i];
      const next = simulated[i + 1];
      // LHS: gate then hinge-panel must be adjacent
      // RHS: hinge-panel then gate must be adjacent
      if (curr === 'gate' || curr === 'hinge-panel') {
        if (next !== 'gate' && next !== 'hinge-panel') {
          // One of gate/hinge has no adjacent partner — they'd be split
          if (simulated.includes('gate') && simulated.includes('hinge-panel')) return true;
        }
      }
    }
    return false;
  }

  movePanelLeft(panelDiv, container) {
    const panelWrapper = panelDiv.parentNode;
    const panelType = panelDiv.dataset.panelType;

    // RULE: Rake panels are locked to ends — they cannot be moved at all
    if (panelType === 'left-raked' || panelType === 'right-raked' || panelType === 'raked') return;

    const allWrappers = Array.from(container.children).filter(child =>
      child.querySelector && child.querySelector('.panel-preview')
    );
    const currentIndex = allWrappers.indexOf(panelWrapper);
    if (currentIndex <= 0) return;

    // Build the group to move: gate + hinge-panel always travel together.
    let groupWrappers = [panelWrapper];

    if (panelType === 'gate') {
      const leftNeighbour = allWrappers[currentIndex - 1];
      if (leftNeighbour && this.getWrapperType(leftNeighbour) === 'hinge-panel') {
        groupWrappers = [leftNeighbour, panelWrapper];
      }
    } else if (panelType === 'hinge-panel') {
      const rightNeighbour = allWrappers[currentIndex + 1];
      if (rightNeighbour && this.getWrapperType(rightNeighbour) === 'gate') {
        groupWrappers = [panelWrapper, rightNeighbour];
      }
    }

    const groupFirstIndex = allWrappers.indexOf(groupWrappers[0]);
    if (groupFirstIndex <= 0) return;

    // Skip over gate+hinge block AND rake panels — neither can be split or displaced
    let insertBefore = allWrappers[groupFirstIndex - 1];
    const leftType = this.getWrapperType(insertBefore);
    if (leftType === 'gate' || leftType === 'hinge-panel') {
      const skipIndex = groupFirstIndex - 2;
      if (skipIndex < 0) return;
      insertBefore = allWrappers[skipIndex];
    } else if (leftType === 'left-raked' || leftType === 'right-raked' || leftType === 'raked') {
      // Cannot move past a rake panel — it is locked to the end
      return;
    }

    groupWrappers.forEach(wrapper => {
      container.insertBefore(wrapper, insertBefore);
    });

    this.reRenderGaps(container);
    this.updatePanelData();
  }

  // Move panel right — gate and its adjacent hinge-panel move together as a pair
  movePanelRight(panelDiv, container) {
    const panelWrapper = panelDiv.parentNode;
    const panelType = panelDiv.dataset.panelType;

    // RULE: Rake panels are locked to ends — they cannot be moved at all
    if (panelType === 'left-raked' || panelType === 'right-raked' || panelType === 'raked') return;

    const allWrappers = Array.from(container.children).filter(child =>
      child.querySelector && child.querySelector('.panel-preview')
    );
    const currentIndex = allWrappers.indexOf(panelWrapper);
    if (currentIndex >= allWrappers.length - 1) return;

    // Build the group: same pairing logic as movePanelLeft
    let groupWrappers = [panelWrapper];

    if (panelType === 'gate') {
      const leftNeighbour = allWrappers[currentIndex - 1];
      if (leftNeighbour && this.getWrapperType(leftNeighbour) === 'hinge-panel') {
        groupWrappers = [leftNeighbour, panelWrapper];
      }
    } else if (panelType === 'hinge-panel') {
      const rightNeighbour = allWrappers[currentIndex + 1];
      if (rightNeighbour && this.getWrapperType(rightNeighbour) === 'gate') {
        groupWrappers = [panelWrapper, rightNeighbour];
      }
    }

    const groupLastIndex = allWrappers.indexOf(groupWrappers[groupWrappers.length - 1]);
    if (groupLastIndex >= allWrappers.length - 1) return;

    // Skip over gate+hinge block AND rake panels — neither can be split or displaced
    let insertAfter = allWrappers[groupLastIndex + 1];
    const rightType = this.getWrapperType(insertAfter);
    if (rightType === 'gate' || rightType === 'hinge-panel') {
      const skipIndex = groupLastIndex + 2;
      if (skipIndex >= allWrappers.length) return;
      insertAfter = allWrappers[skipIndex];
    } else if (rightType === 'left-raked' || rightType === 'right-raked' || rightType === 'raked') {
      // Cannot move past a rake panel — it is locked to the end
      return;
    }

    groupWrappers.forEach(wrapper => {
      container.insertBefore(wrapper, insertAfter.nextSibling);
    });

    this.reRenderGaps(container);
    this.updatePanelData();
  }

  // Update panel data based on current DOM order
  updatePanelData() {
    // Get the current active section number
    const currentSectionNumber = this.getCurrentSectionNumber();
    console.log(`Updating panel data for current section: ${currentSectionNumber}`);
    
    // Find the panel container for the current section only
    const container = document.querySelector('.panels-container');
    
    if (!container) {
      console.warn(`No panels container found for section ${currentSectionNumber}`);
      return;
    }
    
    const panels = container.querySelectorAll('.panel-preview');
    const panelData = [];
    
    panels.forEach((panelDiv, index) => {
      const panelId = panelDiv.dataset.panelId;
      const panelType = panelDiv.dataset.panelType;

      panelData.push({
        id: panelId,
        type: panelType,
        position: index
      });
    });
    
    // Update the section's custom panel order
    if (!this.state.sections[currentSectionNumber]) {
      this.state.sections[currentSectionNumber] = {};
    }
    
    // Save the custom panel layout to the current section
    this.state.sections[currentSectionNumber].customPanelOrder = panelData;
    console.log(`Saved custom panel order for Section ${currentSectionNumber}:`, panelData);
    
    // Trigger 3D preview update
    this.trigger3DUpdate();
    
    // Update any visible section details
    this.refreshSectionDetails();
  }

  // Refresh section details display after panel changes
  refreshSectionDetails() {
    // Update the Add Components tab section details if visible
    if (this.state.currentTab === 'add-components') {
      this.updateAddComponentsPreview();
    }
    
    // Update the Review & Checkout final sections preview if visible
    if (this.state.currentTab === 'review-checkout') {
      this.updateFinalSectionsPreview();
    }
  }

  // Create gap element (for Configure Sections and Add Components - original styling)
  // Get the correct gap width for a panel, applying gate-specific gaps (10mm/7mm)
  getGapForPanel(panels, index, gapWidths, gateSide) {
    let thisGap = gapWidths[index] !== undefined ? gapWidths[index] : (gapWidths[0] || CONFIG.DEFAULTS.GAP_SPACING);
    const panel     = panels[index];
    const nextPanel = panels[index + 1];
    const prevPanel = panels[index - 1];
    const hasHingePanel = panels.some(p => p.type === 'hinge-panel');
    const hasRakedPanel = panels.some(p => p.type === 'left-raked' || p.type === 'right-raked');

    // ── Perfmesh: all gaps are standard posts (50mm) — no hinge/latch hardware gaps ──
    // Gate sits between standard 50mm posts on each side, same as any other panel.

    // ── Gate + rake: all gaps are standard posts (50mm) — no hardware gaps ──

    // ── Wall-to-glass: no hinge panel ──────────────────────────────────────
    // Layout: [wall]-10mm-[gate]-7mm-[extraPanel]-var-[panels]  (wall-left)
    //         [panels]-var-[extraPanel]-7mm-[gate]-10mm-[wall]  (wall-right)
    // W2G: [extra]-7mm-[gate]-10mm-[wall] for both layouts.
    // extra→gate = 7mm (latch). gate trailing = 10mm if last (wall-right), variable if not.
    // W2G: also use standard post gaps (50mm) — no hardware gaps for perfmesh

    // G2G: standard post gaps — no hardware gaps for perfmesh
    return thisGap;
  }


  // Build per-panel TRAILING gap widths for preview rendering.
  //
  // gapSpacings[] from buildResult is always in solver order:
  //   [start_gap, inter_gaps..., raked_gap?, LATCH(7)?, HINGE(10)?, end_gap]
  //
  // Rules:
  //   - gapSpacings[0]        = leading/start gap (wall → first panel)
  //   - gapSpacings[1..n-1]   = inter-panel variable gaps
  //   - gapSpacings[last]     = end gap (last panel / hinge → wall)
  //   - LATCH(7) / HINGE(10)  = fixed gate gaps — getGapForPanel() handles these
  //
  // We extract variable gaps (strip fixed gate values), then assign:
  //   varGaps[0] → startGap (returned by buildStartGap, NOT used here)
  //   varGaps[1] → trailing gap after panel[0] in display
  //   varGaps[2] → trailing gap after panel[1] in display
  //   ...
  // gate and hinge-panel entries get baseGap as sentinel (overridden by getGapForPanel).
  //
  // This is display-order-agnostic: raked panels, gate panels, hinge panels all handled.
  // ── buildTrailingGaps + buildStartGap ───────────────────────────────────
  //
  // The renderer draws: startGap | panel[0] | trailing[0] | panel[1] | trailing[1] | ...
  //
  // gapSpacings[] is in PHYSICAL (solver) order. fixedGapIndices[] marks which
  // positions are LATCH(7mm)/HINGE(10mm) gate hardware gaps.
  //
  // Key insight: variable gaps must be filtered by POSITION (fixedGapIndices),
  // not by VALUE — a variable gap can have value 10mm after spread reduction
  // and must not be confused with HINGE hardware gap.
  //
  // Physical layout:
  //   No gate:  [start, inter..., raked?, end]
  //   RHS gate: [start, inter..., raked?, LATCH(7), HINGE(10), end]  -- physical L→R
  //   LHS gate: [start, HINGE(10), LATCH(7), inter..., raked?, end]  -- physical L→R
  //             display reverses gate block: [gate][hinge] not [hinge][gate]
  //             and left-raked is displayed first even though it's physically last
  // ─────────────────────────────────────────────────────────────────────────
  buildTrailingGaps(panels, calculation) {
    const baseGap = calculation.gapSpacing || CONFIG.DEFAULTS.GAP_SPACING;
    // Perfmesh: all gaps are standard posts — no hinge/latch hardware gaps
    // Return baseGap (50mm) for every trailing gap
    return panels.map(() => baseGap);
  }

  _buildTrailingGaps_UNUSED(panels, calculation) {
    const baseGap = calculation.gapSpacing || CONFIG.DEFAULTS.GAP_SPACING;
    const LATCH = 7;
    const HINGE = 10;
    const isW2G = calculation.gateHingeType === 'W2G-REMOVED-2026-08-30';

    // ── Fixed-gap rules — HINGE(10mm) and LATCH(7mm) never flex ──────────
    // These are returned directly without consulting the spread math.
    const isW2GLeft = isW2G && panels[0]?.type === 'gate'; // gate is first = W2G wall-left
    const fixedGapFor = (panel, next) => {
      if (panel.type === 'hinge-panel' && next?.type === 'gate') return HINGE; // G2G hinge→gate
      if (panel.type === 'gate' && next?.type === 'hinge-panel') return HINGE; // G2G gate→hinge (LHS)
      if (isW2G && panel.type === 'gate' && !next)               return HINGE; // W2G gate→wall (right)
      // W2G left: gate→panel is VARIABLE (same slot as G2G LHS startGap)
      // W2G right: extra→gate = LATCH(7mm) fixed; gate→nothing = HINGE(10mm) fixed (caught above)
      // W2G LEFT: gate is first. gap after gate → extraPanel = LATCH(7mm) fixed.
      // (The 10mm HINGE gap before the gate is the startGap, handled by buildStartGap.)
      if (isW2GLeft && panel.type === 'gate')                    return LATCH; // W2G left gate→extraPanel: 7mm
      if (panel.type === 'gate')                                  return LATCH; // gate→anything else
      if (next?.type === 'gate')                                  return LATCH; // anything→gate
      return null; // not a fixed gap
    };

    // ── Count variable trailing gaps in display order ─────────────────────
    // A trailing gap is variable if fixedGapFor returns null for that panel.
    const nVar = panels.reduce((count, panel, i) => {
      const next = panels[i + 1];
      return fixedGapFor(panel, next) === null ? count + 1 : count;
    }, 0);

    if (nVar === 0) {
      // All gaps are fixed (shouldn't happen but safe fallback)
      return panels.map((panel, i) => fixedGapFor(panel, panels[i + 1]) ?? baseGap);
    }

    // ── Honour user's selected gap; spread remainder across all variable slots ──
    // startGap + nVar trailing gaps = nTotal variable slots total.
    // All start at baseGap. Remainder spread as ±1mm across first nAdjust slots.
    // startGap is the LAST slot so trailing gaps absorb adjustments first.
    // buildStartGap uses the SAME formula (last slot) so both always agree.
    const sectionLength  = calculation.originalSectionLength || calculation.totalRequired || 0;
    const allPanelWidths = panels.reduce((s, p) => s + (p.width || 0), 0);
    const fixedSum       = panels.reduce((s, panel, i) => {
      const f = fixedGapFor(panel, panels[i + 1]);
      return f !== null ? s + f : s;
    }, 0);

    // startGap is fixed (not variable) when gate is the first display panel:
    //   W2G wall-left: wall→gate = 10mm (fixed)
    //   LHS G2G:       latch→gate = 7mm (fixed)
    // In those cases exclude startGap from the variable pool.
    const isFixedStart  = panels[0]?.type === 'gate';
    const fixedStartVal = isFixedStart ? (isW2G ? HINGE : LATCH) : 0;
    const nTotal        = isFixedStart ? nVar : nVar + 1; // slots in variable pool

    // Always recompute totalVar from sectionLength — never trust solver spread values.
    // Guard: if totalVar ≤ 0 (stale cache, or panels exceed section after rake/gate toggle),
    // fall back to baseGap so display shows something reasonable instead of breaking.
    const totalVar = sectionLength - allPanelWidths - fixedSum - fixedStartVal;
    let varBase, varRem, nAdjust, delta;
    if (totalVar > 0 && nTotal > 0) {
      varBase  = Math.floor(totalVar / nTotal);
      varRem   = totalVar - varBase * nTotal;
      nAdjust  = Math.abs(varRem);
      delta    = varRem > 0 ? 1 : -1;
    } else {
      // Fallback: section length not yet updated after config change — use baseGap
      varBase  = baseGap;
      varRem   = 0;
      nAdjust  = 0;
      delta    = 0;
    }

    // ── Build trailing gap array in display order ─────────────────────────
    // Fixed gaps returned directly.
    // Variable trailing gaps: first nAdjust slots get ±1mm, rest stay at varBase.
    // All variable gaps clamped to [MIN_GAP, MAX_GAP] — always safe.
    let varSlot = 0;
    return panels.map((panel, i) => {
      const next  = panels[i + 1];
      const fixed = fixedGapFor(panel, next);
      if (fixed !== null) return fixed;
      const g = varBase + (varSlot < nAdjust ? delta : 0);
      varSlot++;
      return Math.max(CONFIG.CONSTRAINTS.MIN_GAP, Math.min(CONFIG.CONSTRAINTS.MAX_GAP, g));
    });
  }

  // Returns the leading (start) gap shown before the first display panel.
  buildStartGap(calculation, panels = []) {
    // Perfmesh: start gap is always baseGap (50mm post)
    return calculation.gapSpacing || CONFIG.DEFAULTS.GAP_SPACING;
  }

  _buildStartGap_UNUSED(calculation, panels = []) {
    const baseGap       = calculation.gapSpacing || CONFIG.DEFAULTS.GAP_SPACING;
    const gateHingeType = calculation.gateHingeType || 'glass-to-glass';
    const isW2G         = gateHingeType === 'W2G-REMOVED-2026-08-30';
    const gateSide      = calculation.gateSide || 'right';
    const LATCH = 7;
    const HINGE = 10;

    // ── W2G wall-left: gate is first panel, wall is on the left ──────────
    // startGap = wall → gate = 10mm (fixed hardware gap, never variable)
    if (isW2G && panels[0]?.type === 'gate') return HINGE;

    // ── LHS G2G: gate is first display panel, open field is on the left ──
    // startGap = latch gap = 7mm (fixed hardware gap, never variable)
    if (!isW2G && gateSide === 'left' && panels[0]?.type === 'gate') return LATCH;

    // ── All other cases: compute startGap fresh from section total ────────
    // Use same fixedGapFor logic as buildTrailingGaps.
    const isW2GLeftSG   = isW2G && panels[0]?.type === 'gate';
    const fixedGapFor = (panel, next) => {
      if (panel.type === 'hinge-panel' && next?.type === 'gate') return HINGE;
      if (panel.type === 'gate' && next?.type === 'hinge-panel') return HINGE;
      if (isW2G && panel.type === 'gate' && !next)               return HINGE; // W2G RIGHT gate→wall
      // W2G LEFT: gate→extraPanel = LATCH(7mm) fixed
      if (isW2GLeftSG && panel.type === 'gate' && next)          return LATCH;
      if (panel.type === 'gate')                                  return LATCH;
      if (next?.type === 'gate')                                  return LATCH;
      return null;
    };

    const sectionLength  = calculation.originalSectionLength || calculation.totalRequired || 0;
    const allPanelWidths = panels.reduce((s, p) => s + (p.width || 0), 0);
    const fixedSum       = panels.reduce((s, panel, i) => {
      const f = fixedGapFor(panel, panels[i + 1]);
      return f !== null ? s + f : s;
    }, 0);
    // nVar = number of variable trailing gaps (excludes startGap itself)
    const nVar = panels.reduce((count, panel, i) => {
      const next = panels[i + 1];
      return fixedGapFor(panel, next) === null ? count + 1 : count;
    }, 0);

    // Always recompute from sectionLength — guard against stale cache after toggle.
    // nTotal = nVar trailing + 1 startGap slot. startGap gets the last slot.
    const nTotal   = nVar + 1;
    const totalVar = sectionLength - allPanelWidths - fixedSum;
    if (totalVar <= 0 || nTotal <= 0) {
      // Panels exceed section (stale cache after rake/gate toggle) — show baseGap
      return Math.max(CONFIG.CONSTRAINTS.MIN_GAP, Math.min(CONFIG.CONSTRAINTS.MAX_GAP, baseGap));
    }
    const varBase   = Math.floor(totalVar / nTotal);
    const varRem    = totalVar - varBase * nTotal;
    const startSlot = nTotal - 1;
    const delta     = varRem > 0 ? 1 : (varRem < 0 ? -1 : 0);
    const nAdjust   = Math.abs(varRem);
    const startGap  = varBase + (startSlot < nAdjust ? delta : 0);

    return Math.max(CONFIG.CONSTRAINTS.MIN_GAP, Math.min(CONFIG.CONSTRAINTS.MAX_GAP, startGap));
  }

  createGapElement(gapWidth) {
    // Post gap (>=30mm): pure DOM post — zero flex width, drawn absolutely so panels sit flush
    if (gapWidth >= 30) {
const BAR_W   = 8;
      const FOOT_W  = 20;
      const FOOT_H  = 5;
      const BAR_H   = 120;
      const TOP_OFF = 0;

      const wrapper = document.createElement('div');
      wrapper.className = 'gap-preview post-bar post-svg';
      wrapper.style.cssText = 'position:relative;width:0;flex-shrink:0;align-self:flex-end;display:inline-flex;flex-direction:column;align-items:center;overflow:visible;margin:0;z-index:2;';

      const postWrap = document.createElement('div');
      postWrap.style.cssText = 'position:relative;width:0;height:' + (TOP_OFF + BAR_H + FOOT_H + 37) + 'px;flex-shrink:0;overflow:visible;z-index:2;';

      const bar = document.createElement('div');
      bar.style.cssText = 'position:absolute;top:' + TOP_OFF + 'px;left:' + (-(BAR_W/2)) + 'px;width:' + BAR_W + 'px;height:' + BAR_H + 'px;background:#1a1a1a;';

      const foot = document.createElement('div');
      foot.style.cssText = 'position:absolute;top:' + (TOP_OFF + BAR_H) + 'px;left:' + (-(FOOT_W/2)) + 'px;width:' + FOOT_W + 'px;height:' + FOOT_H + 'px;background:#1a1a1a;border-radius:2px;';

      const lbl = document.createElement('div');
      lbl.className = 'panel-width';
      lbl.style.cssText = 'position:absolute;top:' + (TOP_OFF + BAR_H + FOOT_H + 4) + 'px;left:-17px;width:34px;text-align:center;font-size:8px;color:#475569;line-height:1.2;';
      [gapWidth + 'mm', 'Post'].forEach(function(t) {
        const d = document.createElement('div');
        d.style.cssText = 'white-space:nowrap;text-align:center;';
        d.textContent = t;
        lbl.appendChild(d);
      });

      postWrap.appendChild(bar);
      postWrap.appendChild(foot);
      postWrap.appendChild(lbl);
      wrapper.appendChild(postWrap);
      return wrapper;
    }

    // Gate hardware gap (<30mm) — zero-width line so panels stay flush
    const gapDiv = document.createElement('div');
    gapDiv.className = 'gap-preview post-bar';
    gapDiv.style.cssText = 'position:relative;width:0;flex-shrink:0;align-self:flex-end;overflow:visible;margin:0;z-index:2;';
    const _wrap = document.createElement('div');
    _wrap.style.cssText = 'position:relative;width:0;height:162px;overflow:visible;';
    const _line = document.createElement('div');
    _line.style.cssText = 'position:absolute;top:0px;left:-1px;width:2px;height:120px;background:#1a1a1a;';
    const _lbl = document.createElement('div');
    _lbl.className = 'panel-width';
    _lbl.style.cssText = 'position:absolute;top:125px;left:-17px;width:34px;text-align:center;font-size:8px;color:#475569;line-height:1.2;';
    const _d = document.createElement('div'); _d.style.cssText = 'white-space:nowrap;text-align:center;'; _d.textContent = gapWidth + 'mm';
    _lbl.appendChild(_d);
    _wrap.appendChild(_line); _wrap.appendChild(_lbl);
    gapDiv.appendChild(_wrap);
    return gapDiv;
  }

  // Create gap element for final sections preview (Review & Checkout - centered styling)
  createFinalGapElement(gapWidth) {
    if (gapWidth >= 30) {
const BAR_W   = 8;
      const FOOT_W  = 20;
      const FOOT_H  = 5;
      const BAR_H   = 120;
      const TOP_OFF = 0;

      const wrapper = document.createElement('div');
      wrapper.className = 'gap-preview final-gap post-bar post-svg';
      wrapper.style.cssText = 'position:relative;width:0;flex-shrink:0;align-self:flex-end;display:inline-flex;flex-direction:column;align-items:center;overflow:visible;margin:0;z-index:2;';

      const postWrap = document.createElement('div');
      postWrap.style.cssText = 'position:relative;width:0;height:' + (TOP_OFF + BAR_H + FOOT_H + 37) + 'px;flex-shrink:0;overflow:visible;z-index:2;';

      const bar = document.createElement('div');
      bar.style.cssText = 'position:absolute;top:' + TOP_OFF + 'px;left:' + (-(BAR_W/2)) + 'px;width:' + BAR_W + 'px;height:' + BAR_H + 'px;background:#1a1a1a;';

      const foot = document.createElement('div');
      foot.style.cssText = 'position:absolute;top:' + (TOP_OFF + BAR_H) + 'px;left:' + (-(FOOT_W/2)) + 'px;width:' + FOOT_W + 'px;height:' + FOOT_H + 'px;background:#1a1a1a;border-radius:2px;';

      const lbl = document.createElement('div');
      lbl.className = 'panel-width';
      lbl.style.cssText = 'position:absolute;top:' + (TOP_OFF + BAR_H + FOOT_H + 4) + 'px;left:-17px;width:34px;text-align:center;font-size:8px;color:#475569;line-height:1.2;';
      [gapWidth + 'mm', 'Post'].forEach(function(t) {
        const d = document.createElement('div');
        d.style.cssText = 'white-space:nowrap;text-align:center;';
        d.textContent = t;
        lbl.appendChild(d);
      });

      postWrap.appendChild(bar);
      postWrap.appendChild(foot);
      postWrap.appendChild(lbl);
      wrapper.appendChild(postWrap);
      return wrapper;
    }

    const gapDiv = document.createElement('div');
    gapDiv.className = 'gap-preview final-gap post-bar';
    gapDiv.style.cssText = [
      'display: inline-flex',
      'align-self: stretch',
      'flex-shrink: 0',
      'width: 6px',
      'min-width: 6px',
      'background: #1a1a1a',
      'border-radius: 1px',
      'margin: 0',
      'position: relative',
      'z-index: 2',
    ].join(';');
    return gapDiv;
  }

  // Recalculate preview on resize
  recalculatePreviewOnResize() {
    setTimeout(() => {
      const currentSection = this.state.currentSection;
      if (currentSection && this.state.sections[currentSection]) {
        this.renderSectionPreview(currentSection);
      }
    }, 50);
  }


  // Update add-components preview with all configured sections
  updateAddComponentsPreview() {
    // Re-filter add-ons based on current spigot type when tab opens
    this.populateAddOns();

    // Sync global color dropdowns to current section state
    const firstSection = this.state.sections[1] || this.state.sections[Object.keys(this.state.sections)[0]];
    if (firstSection) {
      const hingeEl = document.getElementById('global-hinge-color');
      const latchEl = document.getElementById('global-latch-color');
      if (hingeEl && firstSection.hingeColor) hingeEl.value = firstSection.hingeColor;
      if (latchEl && firstSection.latchColor) latchEl.value = firstSection.latchColor;
    }

    const previewContainer = document.querySelector('#add-components .preview-placeholder.side');
    if (!previewContainer) return;

    // Clear existing content
    previewContainer.innerHTML = '';
    
    const totalSections = this.state.sectionsCount;
    if (totalSections === 0 || Object.keys(this.state.sections).length === 0) {
      previewContainer.innerHTML = '<p>Configure sections to see preview</p>';
      return;
    }

    // Create container for all section previews
    const allSectionsContainer = document.createElement('div');
    allSectionsContainer.className = 'all-sections-preview';
    allSectionsContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 15px;
      width: 100%;
      overflow-y: auto;
    `;

    // Generate preview for each configured section
    Object.entries(this.state.sections).forEach(([sectionNum, section]) => {
      if (!section.length) return;

      // Use same engine as preview — optimizedConfigs first, fallback to peter/legacy
      const sNum = parseInt(sectionNum);
      let calculation = this.state.optimizedConfigs[sNum];
      if (!calculation) {
        const prefPanel = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
        const prefGap   = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
        calculation = this.peterCalculateLayout(section, prefPanel, prefGap)
                   || this.calculateOptimalPanels(section);
      }
      if (!calculation || calculation.numPanels === 0) return;

      // Build display strings for variable panels/gaps
      let panelDisplay = `${calculation.numPanels} \u00d7 ${calculation.panelWidth}mm`;
      if (calculation.panelWidths && new Set(calculation.panelWidths).size > 1) {
        const groups = {};
        calculation.panelWidths.forEach(w => { groups[w] = (groups[w] || 0) + 1; });
        panelDisplay = Object.entries(groups).map(([s, q]) => `${s}mm(\u00d7${q})`).join(', ');
      }
      let gapDisplay = `${calculation.gapSpacing}mm`;
      if (calculation.gapSpacings && new Set(calculation.gapSpacings).size > 1) {
        const groups = {};
        calculation.gapSpacings.forEach(g => { groups[g] = (groups[g] || 0) + 1; });
        gapDisplay = Object.entries(groups).map(([s, q]) => `${s}mm(\u00d7${q})`).join(', ');
      }

      // Create section wrapper
      const sectionWrapper = document.createElement('div');
      sectionWrapper.className = 'section-preview-wrapper';
      sectionWrapper.style.cssText = `
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 10px;
        width: 100%;
        box-sizing: border-box;
      `;

      // Section header with details
      const sectionHeader = document.createElement('div');
      sectionHeader.className = 'section-preview-header';
      sectionHeader.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
        font-weight: 600;
        color: #334155;
        margin-bottom: 8px;
        gap: 10px;
      `;

      const sectionLabel = document.createElement('span');
      sectionLabel.textContent = `Section ${sectionNum}`;
      sectionHeader.appendChild(sectionLabel);

      const sectionDetails = document.createElement('span');
      sectionDetails.className = 'section-preview-details';
      sectionDetails.style.cssText = `
        font-size: 11px;
        color: #64748b;
        font-weight: normal;
        line-height: 1.3;
        text-align: left;
        flex: 1;
      `;
      sectionDetails.innerHTML = `
        Length: ${section.length}mm |
        Panels: ${panelDisplay} |
        Gap: ${gapDisplay}
        ${section.hasGate ? ' | Gate: ' + (section.gateLength || 'Default') + 'mm' : ''}
        ${section.hasRaked ? ' | Raked' : ''}
      `;
      sectionHeader.appendChild(sectionDetails);

      sectionWrapper.appendChild(sectionHeader);

      // ── Section Details: use renderSectionPreview engine directly ────────
      // renderSectionPreview() already builds the correct post/T-foot/
      // panel-square/rake SVG diagram — same engine as the live Configure
      // Sections preview above. Route Section Details through it instead of
      // the flat glass-image-card row below (calculation.perfomesh is never
      // set in this file — was permanently false, i.e. this path was dead).
      const isPerfomesh = true;

      // Create panels container for this section
      const sectionPanelsContainer = document.createElement('div');
      sectionPanelsContainer.className = 'section-panels-container';
      sectionPanelsContainer.style.cssText = `
        display: flex;
        align-items: flex-end;
        justify-content: center;
        flex-wrap: wrap;
        overflow-x: auto;
        overflow-y: visible;
        width: 100%;
        padding: 8px;
        box-sizing: border-box;
        min-height: 180px;
      `;

      if (isPerfomesh) {
        // Mount a temporary div for renderSectionPreview, then steal its innerHTML
        const tempDiv = document.createElement('div');
        tempDiv.id = `__pm_preview_temp_${sNum}`;
        tempDiv.style.cssText = 'position:absolute;left:-9999px;visibility:hidden;width:900px;';
        document.body.appendChild(tempDiv);

        // Swap previewContainer reference temporarily so renderSectionPreview targets tempDiv
        const _origId = 'section-preview';
        const existingEl = document.getElementById(_origId);
        tempDiv.id = _origId; // hijack the ID renderSectionPreview looks for
        if (existingEl) existingEl.id = `${_origId}__bak`;

        try {
          this.renderSectionPreview(sNum);
          // Copy rendered content into our container
          sectionPanelsContainer.innerHTML = tempDiv.innerHTML;
          sectionPanelsContainer.style.paddingTop = section.hasRaked ? '80px' : '8px';
        } finally {
          // PATCH: restore ID *before* remove — prevents #section-preview permanently
          // becoming #section-preview__bak when navigating back to Configure Sections.
          if (existingEl) existingEl.id = _origId;
          tempDiv.remove();
        }
      } else {
        // Glass section: use calculatePanelsLayout as before
        let panels;
        if (section.customPanelOrder && section.customPanelOrder.length > 0) {
          panels = this.reconstructPanelsFromCustomOrder(section, calculation);
        } else {
          panels = this.calculatePanelsLayout({
            calculation: calculation,
            sectionLength: parseInt(section.length),
            fenceHeight: section.fenceHeight || '1200',
            hasRaked: section.hasRaked || false,
            hasGate: section.hasGate || false,
            gateWidth: 1000,
            rakedSide: section.rakedSide || 'left',
            retainedHeight: section.retainedHeight || CONFIG.DEFAULTS.RETAINED_HEIGHT,
            hingeType: section.hingeType || 'SHG-90L',
            gateFlipped: section.gateFlipped || false,
            gateSide: section.gateSide || 'right',
            wallGateEnd: section.wallGateEnd || null,
            gateHingeType: section.gateHingeType || 'glass-to-glass'
          });
        }

        const sectionHasRaked = panels.some(p => p.type === 'left-raked' || p.type === 'right-raked' || p.type === 'raked');
        sectionPanelsContainer.style.paddingTop = sectionHasRaked ? '40px' : '8px';

        const widthResult = this.calculatePreviewPanelWidth(panels.length, panels.length, sectionPanelsContainer);
        const componentsPanelWidth = typeof widthResult === 'object' ? widthResult.width : widthResult;

        const _tgComp      = this.buildTrailingGaps(panels, calculation);
        const _startComp   = this.buildStartGap(calculation, panels);
        const _gateSideComp = section.gateSide || 'right';

        sectionPanelsContainer.appendChild(this.createGapElement(_startComp));
        panels.forEach((panel, index) => {
          const panelWrapper = document.createElement('div');
          panelWrapper.style.cssText = 'display: inline-block; vertical-align: bottom; margin: 0 -16px; position: relative; z-index: 1;';
          panelWrapper.appendChild(this.createPanelElement(panel, componentsPanelWidth));
          sectionPanelsContainer.appendChild(panelWrapper);
          const thisGap = this.getGapForPanel(panels, index, _tgComp, _gateSideComp);
          sectionPanelsContainer.appendChild(this.createGapElement(thisGap));
        });
      }

      sectionWrapper.appendChild(sectionPanelsContainer);
      allSectionsContainer.appendChild(sectionWrapper);
    });

    previewContainer.appendChild(allSectionsContainer);
  }

  // Update final sections preview for Review & Checkout tab (same styling as Add Components)
  updateFinalSectionsPreview() {
    const previewContainer = document.querySelector('#final-sections-container');
    if (!previewContainer) return;

    // Clear existing content
    previewContainer.innerHTML = '';
    
    const totalSections = this.state.sectionsCount;
    if (totalSections === 0 || Object.keys(this.state.sections).length === 0) {
      previewContainer.innerHTML = '<p style="color: #64748b; font-size: 0.9rem;">Configure sections to see details</p>';
      return;
    }

    // Create container for all section previews (vertical stacking)
    const allSectionsContainer = document.createElement('div');
    allSectionsContainer.className = 'final-sections-container';
    allSectionsContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 15px;
      width: 100%;
    `;

    // Generate preview for each configured section
    Object.entries(this.state.sections).forEach(([sectionNum, section]) => {
      if (!section.length) return;

      // Use same engine as preview — optimizedConfigs first, fallback to peter/legacy
      const sNum = parseInt(sectionNum);
      let calculation = this.state.optimizedConfigs[sNum];
      if (!calculation) {
        const prefPanel = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
        const prefGap   = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
        calculation = this.peterCalculateLayout(section, prefPanel, prefGap)
                   || this.calculateOptimalPanels(section);
      }
      if (!calculation || calculation.numPanels === 0) return;

      // Build display strings for variable panels/gaps
      let panelDisplay = `${calculation.numPanels} × ${calculation.panelWidth}mm`;
      if (calculation.panelWidths && new Set(calculation.panelWidths).size > 1) {
        const groups = {};
        calculation.panelWidths.forEach(w => { groups[w] = (groups[w] || 0) + 1; });
        panelDisplay = Object.entries(groups).map(([s, q]) => `${s}mm(×${q})`).join(', ');
      }
      let gapDisplay = `${calculation.gapSpacing || CONFIG.DEFAULTS.GAP_SPACING}mm`;
      if (calculation.gapSpacings && new Set(calculation.gapSpacings).size > 1) {
        const groups = {};
        calculation.gapSpacings.forEach(g => { groups[g] = (groups[g] || 0) + 1; });
        gapDisplay = Object.entries(groups).map(([s, q]) => `${s}mm(×${q})`).join(', ');
      }

      // Create section wrapper (same styling as Add Components)
      const sectionWrapper = document.createElement('div');
      sectionWrapper.className = 'section-preview-wrapper';
      sectionWrapper.style.cssText = `
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 10px;
        width: 100%;
        box-sizing: border-box;
      `;

      // Section header with details (same styling as Add Components)
      const sectionHeader = document.createElement('div');
      sectionHeader.className = 'section-preview-header';
      sectionHeader.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
        font-weight: 600;
        color: #334155;
        margin-bottom: 8px;
        gap: 10px;
      `;

      const sectionLabel = document.createElement('span');
      sectionLabel.textContent = `Section ${sectionNum}`;
      sectionHeader.appendChild(sectionLabel);

      const sectionDetails = document.createElement('span');
      sectionDetails.className = 'section-preview-details';
      sectionDetails.style.cssText = `
        font-size: 11px;
        color: #64748b;
        font-weight: normal;
        line-height: 1.3;
        text-align: left;
        flex: 1;
      `;
      sectionDetails.innerHTML = `
        Length: ${section.length}mm |
        Panels: ${panelDisplay} |
        Gap: ${gapDisplay}
        ${section.hasGate ? ' | Gate: ' + (section.gateLength || 'Default') + 'mm' : ''}
        ${section.hasRaked ? ' | Raked' : ''}
      `;
      sectionHeader.appendChild(sectionDetails);
      
      sectionWrapper.appendChild(sectionHeader);

      // ── Section Details: use renderSectionPreview engine directly ────────
      // Same fix as updateAddComponentsPreview() above — calculation.perfomesh
      // is never set in this file, so this was permanently dead code.
      const isPerfomeshFinal = true;

      // Create panels container for this section (same style as Configure Sections)
      const sectionPanelsContainer = document.createElement('div');
      sectionPanelsContainer.className = 'section-panels-container';
      sectionPanelsContainer.style.cssText = `
        display: flex;
        align-items: flex-end;
        justify-content: center;
        flex-wrap: wrap;
        overflow-x: auto;
        overflow-y: visible;
        width: 100%;
        padding: 8px;
        box-sizing: border-box;
        min-height: 180px;
      `;

      if (isPerfomeshFinal) {
        // Route to renderSectionPreview — same fix as Color Finishes tab
        const _origIdF  = 'section-preview';
        const existingElF = document.getElementById(_origIdF);
        const tempDivF  = document.createElement('div');
        tempDivF.id     = _origIdF;
        tempDivF.style.cssText = 'position:absolute;left:-9999px;visibility:hidden;width:900px;';
        document.body.appendChild(tempDivF);
        if (existingElF) existingElF.id = `${_origIdF}__bak`;
        try {
          this.renderSectionPreview(sNum);
          sectionPanelsContainer.innerHTML = tempDivF.innerHTML;
          sectionPanelsContainer.style.paddingTop = section.hasRaked ? '80px' : '8px';
        } finally {
          // PATCH: restore ID *before* remove — prevents #section-preview permanently
          // becoming #section-preview__bak when navigating back to Configure Sections.
          if (existingElF) existingElF.id = _origIdF;
          tempDivF.remove();
        }
        sectionWrapper.appendChild(sectionPanelsContainer);
        allSectionsContainer.appendChild(sectionWrapper);
      } else {
      // Glass section: use calculatePanelsLayout as before
      let panels;
      if (section.customPanelOrder && section.customPanelOrder.length > 0) {
        panels = this.reconstructPanelsFromCustomOrder(section, calculation);
      } else {
        panels = this.calculatePanelsLayout({
          calculation: calculation,
          sectionLength: parseInt(section.length),
          fenceHeight: section.fenceHeight || '1200',
          hasRaked: section.hasRaked || false,
          hasGate: section.hasGate || false,
          gateWidth: 1000,
          rakedSide: section.rakedSide || 'left',
          retainedHeight: section.retainedHeight || CONFIG.DEFAULTS.RETAINED_HEIGHT,
          hingeType: section.hingeType || 'SHG-90L',
          gateFlipped: section.gateFlipped || false,
          gateSide: section.gateSide || 'right',
          wallGateEnd: section.wallGateEnd || null,
          gateHingeType: section.gateHingeType || 'glass-to-glass'
        });
      }

      const sectionHasRakedF = panels.some(p => p.type === 'left-raked' || p.type === 'right-raked' || p.type === 'raked');
      sectionPanelsContainer.style.paddingTop = sectionHasRakedF ? '40px' : '8px';

      // Use a smaller panel width for the final preview to fit within padding
      const widthResultFinal = this.calculatePreviewPanelWidth(panels.length, panels.length, sectionPanelsContainer);
      const rawFinalWidth = typeof widthResultFinal === 'object' ? widthResultFinal.width : widthResultFinal;
      const finalPanelWidth = Math.min(rawFinalWidth, 80);

      // Scale factor for final preview — panels render at 65% of normal size
      const FINAL_SCALE = 0.65;
      // Build gap arrays once outside loop
      const _tgFinal    = this.buildTrailingGaps(panels, calculation);
      const _startFinal = this.buildStartGap(calculation, panels);
      const _gateSideFinal = section.gateSide || 'right';

      // Leading start gap before first panel
      sectionPanelsContainer.appendChild(this.createFinalGapElement(_startFinal));

      panels.forEach((panel, index) => {
        const panelWrapper = document.createElement('div');
        panelWrapper.style.cssText = `
          display: inline-block;
          vertical-align: bottom;
          transform: scale(${FINAL_SCALE});
          transform-origin: bottom center;
          margin: 0 ${Math.round((finalPanelWidth * (1 - FINAL_SCALE)) / -2) - 16}px;
          position: relative;
          z-index: 1;
        `;
        panelWrapper.appendChild(this.createPanelElement(panel, finalPanelWidth));
        sectionPanelsContainer.appendChild(panelWrapper);
        const panelGap = this.getGapForPanel(panels, index, _tgFinal, _gateSideFinal);
        sectionPanelsContainer.appendChild(this.createFinalGapElement(panelGap));
      });

      sectionWrapper.appendChild(sectionPanelsContainer);
      allSectionsContainer.appendChild(sectionWrapper);
      } // end else (glass section)
    });

    previewContainer.appendChild(allSectionsContainer);
  }

  // Reconstruct panels from custom panel order stored in Configure Sections
  reconstructPanelsFromCustomOrder(section, calculation) {
    const customOrder = section.customPanelOrder;
    const reconstructedPanels = [];
    
    // Get base panel configuration for reference
    const basePanels = this.calculatePanelsLayout({
      calculation: calculation,
      sectionLength: parseInt(section.length),
      fenceHeight: section.fenceHeight || '1200',
      hasRaked: section.hasRaked || false,
      hasGate: section.hasGate || false,
      gateWidth: 1000,
      rakedSide: section.rakedSide || 'left',
      retainedHeight: section.retainedHeight || CONFIG.DEFAULTS.RETAINED_HEIGHT,
      hingeType: section.hingeType || 'SHG-90L',
      gateFlipped: section.gateFlipped || false,
      gateSide: section.gateSide || 'right',
      wallGateEnd: section.wallGateEnd || null,
      gateHingeType: section.gateHingeType || 'glass-to-glass'
    });
    
    // Create a lookup map of panels by type for easy reference
    const panelLookup = {};
    basePanels.forEach(panel => {
      const key = panel.type;
      if (!panelLookup[key]) {
        panelLookup[key] = [];
      }
      panelLookup[key].push(panel);
    });
    
    // Reconstruct panels in the custom order
    customOrder.forEach((customPanel, index) => {
      const panelType = customPanel.type;

      // Find a matching panel from the base calculation
      let basePanel = null;
      if (panelLookup[panelType] && panelLookup[panelType].length > 0) {
        basePanel = panelLookup[panelType].shift(); // Take the first available panel of this type
      } else {
        // Fallback: create a basic panel if we can't find a match
        basePanel = {
          type: panelType,
          width: calculation.panelWidth || CONFIG.DEFAULTS.PANEL_LENGTH,
          height: parseInt(section.fenceHeight || 1200)
        };
      }

      const reconstructedPanel = {
        ...basePanel,
        position: index
      };

      reconstructedPanels.push(reconstructedPanel);
    });
    
    return reconstructedPanels;
  }

  // Update quote display
  updateQuoteDisplay() {
    // Re-entrancy guard: updateCalculationResults() calls back into this method, and
    // this method calls updateCalculationResults() for uncached sections. Without this
    // guard, an invalid config (e.g. Even Panels + no end posts at 1800mm) makes the two
    // mutually recurse until the stack overflows and blanks the floating preview.
    if (this._inQuoteDisplay) return;
    this._inQuoteDisplay = true;
    try {
    let total = 0;
    this.state.cartItems = [];
    this.state.quoteBreakdown = [];

    Object.entries(this.state.sections).forEach(([sectionNum, section]) => {
      if (section.length) {
        // ── CRITICAL: always use the same engine as the preview ──────────────
        // peterCalculateLayout result is stored in optimizedConfigs after
        // updateCalculationResults runs. Use it here so quote panel sizes
        // match exactly what the preview shows. Only fall back to the legacy
        // engine if optimizedConfigs has no entry (e.g. section not yet viewed).
        const sNum = parseInt(sectionNum);
        let calculation = this.state.optimizedConfigs[sNum];
        if (!calculation) {
          // Section not yet visited — run the same calculation path as updateCalculationResults
          // so quote always uses the same engine and respects the hard panel cap.
          this.updateCalculationResults(sNum);
          calculation = this.state.optimizedConfigs[sNum];
        }
        // Final fallback — should never fire but guards against edge cases
        if (!calculation) {
          const preferredPanelLength = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
          const preferredGapSpacing  = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
          calculation = this.peterCalculateLayout(section, preferredPanelLength, preferredGapSpacing)
                     || this.calculateOptimalPanels(section);
        }
        if (calculation) {
          const _bdStart = this.state.quoteBreakdown.length;
          total += this.calculatePanelCosts(section, calculation);

          // State is always authoritative (set by toggle handlers).
          // DOM fallbacks removed: they corrupt state during toggle transitions.
          if (section.hasRaked) {
            total += this.calculateRakedPanelCosts(section);
          }

          if (section.hasClamp) {
            total += this.calculateClampCosts(section);
          }

          if (section.hasGate) {
            total += this.calculateGateCosts(section, calculation);
          }

          total += this.calculatePostAndCapCosts(section);

          // Per-section redesign: tag every line item this section produced with its
          // section number so the breakdown + order form can group by section.
          // Also tag the section's colour so it can be shown on each order line.
          const _secColourKey = this.resolveSectionColour ? this.resolveSectionColour(section) : (section.color || 'satin-black');
          const _COLOUR_LABEL = { 'satin-black': 'Satin Black', 'white': 'Satin White', 'monument': 'Monument' };
          const _secColourLabel = _COLOUR_LABEL[_secColourKey] || _secColourKey;
          for (let _i = _bdStart; _i < this.state.quoteBreakdown.length; _i++) {
            if (this.state.quoteBreakdown[_i] && this.state.quoteBreakdown[_i].section == null) {
              this.state.quoteBreakdown[_i].section = sNum;
            }
            if (this.state.quoteBreakdown[_i] && this.state.quoteBreakdown[_i].colour == null
                && !this.state.quoteBreakdown[_i].noColour) {
              this.state.quoteBreakdown[_i].colour = _secColourLabel;
            }
          }
        }
      }
    });

    // ── Supply & Install / DIY / Pick Up pricing (May 2026 Price Guide) ──
    // All modes use per-linear-metre flat rates from the price guide.
    // S&I:     $370/Lm, gate $510, rake $370
    // DIY:     $180/Lm, gate $350, rake $300
    // Pick Up: same as DIY — $180/Lm (no delivery charge)
    const s = this.shopifyData.settings || {};
    const pricingType = this.state.pricingType || 'diy';
    const isInstall   = pricingType === 'install';

    // Total fence length across all sections (mm → Lm)
    const totalLengthMm = Object.values(this.state.sections)
      .filter(sec => sec.length)
      .reduce((sum, sec) => sum + parseFloat(sec.length || 0), 0);
    const totalLm = totalLengthMm / 1000;

    // Gate and rake counts
    const totalGates = Object.values(this.state.sections)
      .filter(sec => sec.length && sec.hasGate).length;
    const totalRakes = Object.values(this.state.sections)
      .filter(sec => sec.length && sec.hasRaked).length;

    // Check if any gate section has Soft Close Premium (PHS) hinge selected
    const hasSoftCloseGate = Object.values(this.state.sections)
      .some(sec => sec.length && sec.hasGate && (sec.hingeType === 'PHS' || (sec.hingeType === 'TCA1L2S3WT' ? 'TCA1L2S3BT' : sec.hingeType) === 'PHS'));

    if (totalLm > 0) {
      const perLm        = isInstall ? (s.blade_install_per_lm  || 370) : (s.blade_diy_per_lm  || 180);
      const perGate      = isInstall ? (s.blade_install_gate     || 510) : (s.blade_diy_gate    || 350);
      const perRake      = isInstall ? (s.blade_install_rake     || 370) : (s.blade_diy_rake    || 300);
      const perSoftClose = isInstall ? (s.blade_install_soft_close || 265) : (s.blade_diy_soft_close || 180);
      const modeLabel    = isInstall ? 'Supply & Install' : 'DIY Supply Only';

      let rateTotal = (totalLm * perLm)
                    + (totalGates * perGate)
                    + (totalRakes * perRake);

      // Soft Close Premium hinge per-Lm adder (price guide: $265/Lm S&I, $180/Lm DIY)
      // This per-Lm cost is an INSTALLATION-service cost. It is added to rateTotal so
      // it is absorbed into the single Professional Installation line below (S&I only,
      // never itemised, never double-counted). On DIY there is no installation, so this
      // per-Lm cost is NOT charged and NOT shown — the physical PHS hinge hardware is
      // still charged separately as its own gate-hardware line.
      if (hasSoftCloseGate && totalLm > 0 && isInstall) {
        const softCloseTotal = totalLm * perSoftClose;
        rateTotal += softCloseTotal;
      }

      // Small job surcharge
      let surchargeRate = 0;
      let surchargeLabel = '';
      if (totalLm <= 4) {
        surchargeRate  = s.blade_surcharge_4lm || 0.40;
        surchargeLabel = 'Small Job Surcharge (≤4Lm +40%)';
      } else if (totalLm <= 8) {
        surchargeRate  = s.blade_surcharge_8lm || 0.30;
        surchargeLabel = 'Small Job Surcharge (≤8Lm +30%)';
      }
      if (surchargeRate > 0) {
        const surchargeAmt = rateTotal * surchargeRate;
        rateTotal += surchargeAmt;
        // Surcharge baked in silently — never itemised to customer
      }

      // Minimum quote floor
      const minQuote = s.blade_minimum_quote || 2425;
      if (rateTotal < minQuote) rateTotal = minQuote;

      if (isInstall) {
        // S&I: keep the itemised materials (Panel Kits, Raked Panels, Gate
        // Components, Posts & Hardware) already accumulated in `total` above,
        // and add ONE 'Supply & Install' line for just the remainder — the
        // installation-only portion of rateTotal not already covered by those
        // itemised materials. materials + this line = rateTotal exactly, so
        // nothing is billed twice.
        const materialsSubtotal = total;
        const installOnlyPortion = rateTotal - materialsSubtotal;
        total += installOnlyPortion; // total now equals rateTotal exactly

        if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
        this.state.quoteBreakdown.push({
          category: 'Professional Installation',
          item: 'Turn-key Design and Installation Service',
          sku: 'EASYSTREET',
          quantity: 1,
          unitPrice: installOnlyPortion,
          totalPrice: installOnlyPortion,
          variantId: null
        });
      } else {
        // DIY: use actual materials breakdown total
        // Add $92.16 processing fee when a gate is included (hidden from breakdown)
        if (totalGates > 0) total += 92.16;
      }
    }

    // Add $90 flat delivery charge if selected
    const deliveryCharge = this.shopifyData.settings?.deliveryCharge ?? 90;
    if (this.state.includeDelivery && this.state.pricingType !== 'install') {
      total += deliveryCharge;

      if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
      this.state.quoteBreakdown.push({
        category: 'Delivery',
        item: 'Perth Metro Delivery',
        quantity: 1,
        unitPrice: deliveryCharge,
        totalPrice: deliveryCharge,
        variantId: null
      });
    } else if (this.state.pricingType !== 'install') {
      // Pick Up selected — show a clear FREE pickup line on the order (Pete 19/07).
      if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
      this.state.quoteBreakdown.push({
        category: 'Delivery',
        item: 'Free Pickup from our Canning Vale Warehouse',
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
        variantId: null
      });
    }

    // Optional Fixing Materials — opt-in add-ons the customer ticked on Review.
    // In-Ground → Sika HES Class C Grout (1 bag / 3 posts — Pete, 29 Jul 2026). Base Mount → concrete
    // Base Fixing Anchors (4 / post, $4.50 each - Pete 5 Oct 2026). Only added when ticked.
    if (total > 0) {
      if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
      const fa = this.state.pricingType === 'install' ? {} : (this.state.fixingAddons || {});
      const _fixSecs = Object.values(this.state.sections || {}).filter(s => s && s.length);
      let _inGroundPosts = 0, _baseMountPosts = 0;
      _fixSecs.forEach(s => { const pc = s.renderedPostCount || 0; if (s.groundPost) _inGroundPosts += pc; else _baseMountPosts += pc; });
      if (fa.sika && _inGroundPosts > 0) {
        // Store price (CF_PRICE, cents), not a typed figure: the old 50.47 sat under the store's
        // 88.79, so the cart's real lines came to more than the quote and checkout fell back to one
        // 'Quote Total' line with no parts list (break test, 5 Oct 2026).
        const bags = Math.ceil(_inGroundPosts / 3);
        const _groutEach = ((typeof window.CF_PRICE === 'function' && window.CF_PRICE('49538')) || 8879) / 100;
        this.state.quoteBreakdown.push({ category: 'Optional Fixing Materials', item: 'Sika HES Class C Grout', sku: '49538', quantity: bags, unitPrice: _groutEach, totalPrice: _groutEach * bags, variantId: null });
        total += _groutEach * bags;
      }
      if (fa.anchors && _baseMountPosts > 0) {
        const qty = _baseMountPosts * 4;
        this.state.quoteBreakdown.push({ category: 'Optional Fixing Materials', item: 'Base Fixing Anchor (M8×80, concrete)', sku: '43032', quantity: qty, unitPrice: 4.50, totalPrice: 4.50 * qty, variantId: null });
        total += 4.50 * qty;
      }
    }

    this.state.quote = total;
    this.updateQuoteElements(total);
    
    if (this.shopifyData.settings?.showQuoteBreakdown !== false) {
      this.updateQuoteBreakdown();
    }

    // Derive cartItems from breakdown as single source of truth
    this._deriveCartItemsFromBreakdown();
    } finally {
      this._inQuoteDisplay = false;
    }
  }

  // Update quote breakdown
  updateQuoteBreakdown() {
    // Update all instances of the quote breakdown
    const breakdownContainers = [
      document.getElementById('breakdown-content'),
      document.getElementById('breakdown-content-configure'),
      document.getElementById('breakdown-content-configure-2'),
      document.getElementById('breakdown-content-final')
    ].filter(Boolean);
    
    if (breakdownContainers.length === 0) return;

    if (!this.state.quoteBreakdown || this.state.quoteBreakdown.length === 0) {
      breakdownContainers.forEach(container => {
        container.innerHTML = '<p style="color: #64748b; font-size: 0.9rem;">Configure sections to see breakdown</p>';
      });
      return;
    }

    // ── Per-section grouping ─────────────────────────────────────────────────
    // Group line items by their section (item.section, tagged in updateQuoteDisplay);
    // whole-job lines (Delivery, Supply & Install delta, soft-close adder, anything
    // untagged) go in a "Whole job" group at the end.
    const WHOLE_JOB_CATS = new Set(['Delivery', 'Supply & Install', 'DIY Supply Only', 'Optional Fixing Materials']);
    const sectionsMap = {};
    const wholeJob = [];
    const proInstall = [];
    const delivery = [];
    const postFixings = [];
    this.state.quoteBreakdown.forEach(item => {
      if (item.category === 'Spigots & Hardware') return;
      if (item.item && item.item.includes('Hinge Panel')) return;
      if (item.category === 'Professional Installation') {
        proInstall.push(item);
      } else if (item.category === 'Delivery') {
        delivery.push(item);
      } else if (item.category === 'Optional Fixing Materials') {
        postFixings.push(item);
      } else if (item.section != null && !WHOLE_JOB_CATS.has(item.category)) {
        (sectionsMap[item.section] = sectionsMap[item.section] || []).push(item);
      } else {
        wholeJob.push(item);
      }
    });

    const categoryDisplayOrder = ['Panel Kits', 'Raked Panels', 'Gate Components', 'Posts & Accessories'];
    const catRank = (c) => { const i = categoryDisplayOrder.indexOf(c); return i === -1 ? categoryDisplayOrder.length : i; };

    const renderItem = (item) => {
      const colourTag = item.colour ? `<span style="color:#64748b; font-size:0.78rem; margin-left:6px;">(${item.colour})</span>` : '';
      const skuTag = item.sku ? `<span style="color:#94a3b8; font-size:0.75rem; margin-left:6px;">SKU: ${item.sku}</span>` : '';
      return `
        <div class="breakdown-item" style="display:flex; justify-content:space-between; align-items:center; padding:4px 0; font-size:0.85rem;">
          <div class="item-details" style="flex:1;">
            <span class="item-name" style="color:#475569;">${item.item}</span>
            <span class="item-quantity" style="color:#64748b; margin-left:8px;">× ${item.quantity}</span>
            ${colourTag}${skuTag}
          </div>
          <div class="item-pricing" style="text-align:right; color:#374151;">
            <div style="font-size:0.8rem; color:#64748b;">${PriceHelper.format(item.unitPrice, this.shopifyData)} each</div>
            <div style="font-weight:500;">${PriceHelper.format(item.totalPrice, this.shopifyData)}</div>
          </div>
        </div>`;
    };

    let breakdownHTML = '';

    Object.keys(sectionsMap).map(Number).sort((a, b) => a - b).forEach(sNum => {
      const items = sectionsMap[sNum];
      if (!items.length) return;
      const secTotal = items.reduce((sum, it) => sum + it.totalPrice, 0);
      const byCat = {};
      items.forEach(it => { (byCat[it.category] = byCat[it.category] || []).push(it); });
      const cats = Object.keys(byCat).sort((a, b) => catRank(a) - catRank(b));
      breakdownHTML += `
        <div class="breakdown-section" style="margin-bottom:16px; border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px; background:#fafcff;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
            <h5 style="margin:0; color:#0e9ed5; font-weight:700; font-size:0.95rem;">Section ${sNum}</h5>
            <span style="color:#059669; font-weight:700; font-size:0.9rem;">${PriceHelper.format(secTotal, this.shopifyData)}</span>
          </div>`;
      cats.forEach(cat => {
        breakdownHTML += `<div style="color:#64748b; font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.03em; margin:6px 0 2px;">${cat}</div>`;
        byCat[cat].forEach(it => { breakdownHTML += renderItem(it); });
      });
      breakdownHTML += `</div>`;
    });

    if (proInstall.length) {
      const piTotal = proInstall.reduce((sum, it) => sum + it.totalPrice, 0);
      breakdownHTML += `
        <div class="breakdown-section" style="margin-bottom:16px; border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
            <h5 style="margin:0; color:#374151; font-weight:700; font-size:0.95rem;">Professional Installation</h5>
            <span style="color:#059669; font-weight:700; font-size:0.9rem;">${PriceHelper.format(piTotal, this.shopifyData)}</span>
          </div>`;
      proInstall.forEach(it => { breakdownHTML += renderItem(it); });
      breakdownHTML += `</div>`;
    }

    if (delivery.length) {
      const dTotal = delivery.reduce((sum, it) => sum + it.totalPrice, 0);
      breakdownHTML += `
        <div class="breakdown-section" style="margin-bottom:16px; border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
            <h5 style="margin:0; color:#374151; font-weight:700; font-size:0.95rem;">Perth Metro Delivery</h5>
            <span style="color:#059669; font-weight:700; font-size:0.9rem;">${PriceHelper.format(dTotal, this.shopifyData)}</span>
          </div>`;
      delivery.forEach(it => { breakdownHTML += renderItem(it); });
      breakdownHTML += `</div>`;
    }

    if (postFixings.length) {
      const pfTotal = postFixings.reduce((sum, it) => sum + it.totalPrice, 0);
      breakdownHTML += `
        <div class="breakdown-section" style="margin-bottom:16px; border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
            <h5 style="margin:0; color:#374151; font-weight:700; font-size:0.95rem;">Optional Fixing Materials</h5>
            <span style="color:#059669; font-weight:700; font-size:0.9rem;">${PriceHelper.format(pfTotal, this.shopifyData)}</span>
          </div>`;
      postFixings.forEach(it => { breakdownHTML += renderItem(it); });
      breakdownHTML += `</div>`;
    }

    if (wholeJob.length) {
      const wjTotal = wholeJob.reduce((sum, it) => sum + it.totalPrice, 0);
      breakdownHTML += `
        <div class="breakdown-section" style="margin-bottom:16px; border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
            <h5 style="margin:0; color:#374151; font-weight:700; font-size:0.95rem;">Whole job</h5>
            <span style="color:#059669; font-weight:700; font-size:0.9rem;">${PriceHelper.format(wjTotal, this.shopifyData)}</span>
          </div>`;
      wholeJob.forEach(it => { breakdownHTML += renderItem(it); });
      breakdownHTML += `</div>`;
    }

    const grandTotal = this.state.quoteBreakdown
      .filter(item => item.category !== 'Spigots & Hardware' && !item.item.includes('Hinge Panel'))
      .reduce((sum, item) => sum + item.totalPrice, 0);
    breakdownHTML += `
      <div class="breakdown-total" style="padding-top: 12px; border-top: 2px solid #e2e8f0; margin-top: 12px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="font-weight: 600; color: #1f2937; font-size: 1rem;">Total</span>
          <span style="font-weight: 700; color: #059669; font-size: 1.1rem;">${PriceHelper.format(grandTotal, this.shopifyData)}</span>
        </div>
      </div>
    `;

    // Write only to the active tab container to prevent duplication.
    // Multiple containers exist in the DOM simultaneously — only update the visible one.
    const tabContainerMap = {
      'add-sections':       'breakdown-content',
      'configure-sections': 'breakdown-content-configure',
      'add-components':     'breakdown-content-configure',
      'review-checkout':    'breakdown-content-final'
    };
    const activeContainerId = tabContainerMap[this.state.currentTab];
    const activeContainer = activeContainerId ? document.getElementById(activeContainerId) : null;

    if (activeContainer) {
      activeContainer.innerHTML = breakdownHTML;
      // ...and empty every other one. They all live inside the same visible #quote-breakdown
      // panel, so whatever was written during init - an empty $0.00 quote whose only line was
      // the free-pickup default - stayed on screen ABOVE the real quote, which is how someone
      // who had chosen delivery saw a Free Pickup line and a $99 delivery line at the same
      // time. Pete 2026-08-29: "if delivery isnt chosen dont say anything about delivery."
      breakdownContainers.forEach(function (c) { if (c !== activeContainer) c.innerHTML = ''; });
    } else {
      // Fallback: no active tab known yet — write to all (e.g. on init)
      const written = new Set();
      breakdownContainers.forEach(container => {
        if (!written.has(container)) {
          container.innerHTML = breakdownHTML;
          written.add(container);
        }
      });
    }
    
    // Save the HTML content for PDF generation
    this.savedQuoteBreakdownHTML = breakdownHTML;
  }

  // Calculate panel costs (supports variable per-panel widths)
  calculatePanelCosts(section, calculation) {
    const poolPanel = this.getActiveMeshSheet(section);
    if (!poolPanel || !poolPanel.variants) return 0;

    // ── Use cutPlan panels as source of truth for pricing ─────────────────
    // cutPlan comes from calculateJobCutPlan which uses the same engine as the preview.
    // Falls back to calculation.panelWidths if cutPlan not available yet.
    let panelWidths;
    const jobPlan = this.calculateJobCutPlan();
    const cutPlanForCost = jobPlan[Object.keys(this.state.sections).find(k => this.state.sections[k] === section)];
    if (cutPlanForCost && cutPlanForCost.valid && cutPlanForCost.panels?.length > 0) {
      const gateLengthMm = parseInt(section.gateLength) || 0;
      panelWidths = cutPlanForCost.panels
        .filter(p => !p.is_gate && !p.is_hinge && !(gateLengthMm && p.sheet === gateLengthMm))
        .map(p => p.sheet);
    } else if (section.renderedPanels && section.renderedPanels.length > 0) {
      const gateLengthMm = parseInt(section.gateLength) || 0;
      panelWidths = section.renderedPanels
        .filter(p => p.type !== 'gate' && p.type !== 'hinge' && p.type !== 'hinge-panel' && !p.isRaked && !(gateLengthMm && p.width === gateLengthMm))
        .map(p => p.width);
    } else {
      const regularPanelCount = calculation.regularPanels || calculation.numPanels;
      const gateLengthMm = parseInt(section.gateLength) || 0;
      const rawWidths = calculation.panelWidths || Array(regularPanelCount).fill(calculation.panelWidth);
      panelWidths = gateLengthMm ? rawWidths.filter(w => w !== gateLengthMm) : rawWidths;
    }

    // ── All panels (full + filler) = one consolidated line at 2400mm price ──
    // Every panel slot requires one 2400mm sheet purchase regardless of cut size.
    // Use numPanels (includes filler) not regularPanels (excludes filler) as fallback
    const totalPanelQty = panelWidths.length || (calculation.numPanels ?? calculation.regularPanels ?? 0);

    let totalCost = 0;
    if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];

    if (totalPanelQty > 0) {
      const bladePanel = this.getActiveMeshSheet(section);
      const panelVariant = bladePanel?.variants?.[0] || null;
      const panelPrice = panelVariant ? PriceHelper.fromCents(panelVariant.price) : 0;

      if (panelPrice > 0) {
        const totalPanelCost = panelPrice * totalPanelQty;
        totalCost += totalPanelCost;

        const _fhPanel = String(section?.fenceHeight || '1200');
        const _panelItem = _fhPanel === '1500' ? 'Blade Panel 1500mm h x 1824mm w'
                         : _fhPanel === '1800' ? 'Blade Panel 1800mm h x 1632mm w'
                         : `Blade Panel ${this.getPanelLengthForHeight(section?.fenceHeight) || section?.panelLength || '2400'}mm`;

        this.state.quoteBreakdown.push({
          category: 'Panel Kits',
          item: _panelItem,
          sku: panelVariant?.sku || String(panelVariant?.id || ''),
          quantity: totalPanelQty,
          unitPrice: panelPrice,
          totalPrice: totalPanelCost,
          variantId: panelVariant?.id || null
        });
      }
    }

    return totalCost;
  }

  // Calculate raked panel costs — Blade fencing
  calculateRakedPanelCosts(section) {
    const bladeRake = this.getActiveBladeRake(section);
    const rakeVariant = bladeRake?.variants?.[0] || null;

    // Rake height label for display
    const rakeH = section?.retainedHeight || '1300';
    const rakeSide = section?.rakedSide || 'right';
    const rakeSideLabel = rakeSide === 'left' ? 'Left' : 'Right';
    const itemLabel = `${rakeSideLabel} Rake Panel ${rakeH}mm`;

    if (!rakeVariant) {
      // Fallback — RRP for 1300H satin-black
      const RAKE_FALLBACK = 285.91;
      if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
      this.state.quoteBreakdown.push({
        category: 'Raked Panels',
        item: itemLabel,
        sku: '',
        quantity: 1,
        unitPrice: RAKE_FALLBACK,
        totalPrice: RAKE_FALLBACK,
        variantId: null
      });
      return RAKE_FALLBACK;
    }

    const rakePrice = PriceHelper.fromCents(rakeVariant.price);

    if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
    this.state.quoteBreakdown.push({
      category: 'Raked Panels',
      item: itemLabel,
      sku: rakeVariant.sku || bladeRake?.sku || '',
      quantity: 1,
      unitPrice: rakePrice,
      totalPrice: rakePrice,
      variantId: rakeVariant.id || null
    });
    return rakePrice;
  }

  // Calculate clamp costs
  calculateClampCosts(section) {
    let totalCost = 0;
    
    // Get clamp product
    const clampProduct = this.shopifyData.products?.clampStandard;
    if (!clampProduct || !clampProduct.variants) {
      return totalCost;
    }
    
    // Find matching variant based on color and position
    const clampColor = section.clampColor || 'black';
    const clampPosition = section.clampPosition || 'top';
    
    const clampVariant = clampProduct.variants.find(variant => {
      const colorMatch = !clampColor || variant.color?.toLowerCase() === clampColor.toLowerCase();
      const positionMatch = !clampPosition || variant.position?.toLowerCase() === clampPosition.toLowerCase();
      return colorMatch && positionMatch && variant.available;
    }) || clampProduct.variants.find(v => v.available);
    
    if (clampVariant) {
      const clampPrice = PriceHelper.fromCents(clampVariant.price);
      totalCost += clampPrice;
      
      // Add to quote breakdown
      if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
      this.state.quoteBreakdown.push({
        category: 'Hardware Components',
        item: `Clamp (${clampColor} - ${clampPosition})`,
        quantity: 1,
        unitPrice: clampPrice,
        totalPrice: clampPrice,
        variantId: clampVariant.id
      });
    }
    
    return totalCost;
  }

  // Calculate gate costs — Blade fencing
  // Uses getActiveBladeGate() to resolve SKU by fence height + colour.
  calculateGateCosts(section, calculation = null) {
    let totalCost = 0;

    // ── Blade gate: resolved by height + colour ───────────────────────────
    const bladeGateProduct = this.getActiveBladeGate(section);
    const gateVariant = bladeGateProduct?.variants?.[0] || null;

    if (gateVariant) {
      const gatePrice = PriceHelper.fromCents(gateVariant.price);
      if (gatePrice > 0) {
        totalCost += gatePrice;

        if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
        const _fhGate = String(section?.fenceHeight || '1200');
        const _gateItem = _fhGate === '1500' ? 'Ali Blade Gate 1500mm h x 920mm w'
                        : _fhGate === '1800' ? 'Ali Blade Gate 1800mm h x 920mm w'
                        : `Gate ${gateVariant?.gate || parseInt(section?.gateLength) || this.getDefaultGateLength()}mm`;

        this.state.quoteBreakdown.push({
          category: 'Gate Components',
          item: _gateItem,
          sku: gateVariant.sku || String(gateVariant.id || ''),
          quantity: 1,
          unitPrice: gatePrice,
          totalPrice: gatePrice,
          variantId: gateVariant.id || null
        });
      }
    }

    // Hinges & latches must NOT carry a colour tag on the order form (per Pete).
    // Everything pushed from here to the end of this method (hinge + latch) is
    // flagged noColour below so the per-section colour loop skips them.
    const _gcNoColourStart = this.state.quoteBreakdown.length;

    // ── Hinge ─────────────────────────────────────────────────────────────
    const sectionKey   = Object.keys(this.state.sections).find(k => this.state.sections[k] === section);
    // State is authoritative (set by the hinge-select change handler → section.hingeType).
    // Only fall back to the live DOM select when state is unset, then to the default.
    const _rawHingeVal = section.hingeType
                      || document.getElementById(`section-${sectionKey}-hinge-color`)?.value
                      || 'SHG-90L';
    // Normalise type strings → SKU strings so bladeHinges.find() always gets a SKU
    const HINGE_TYPE_TO_SKU = {
      'soft-close': 'SHG-90L',
      'spring':     'SHG-90L',
      'standard':   'SHG-90L',
      'fixed':      'SHG-90L',
      'adjustable': 'SHG-90L',
      'premium-soft-close': 'PHS'
    };
    const hingeTypeVal  = HINGE_TYPE_TO_SKU[_rawHingeVal] || _rawHingeVal;
    // Legacy normalisation: treat old WT SKU as BT
    const normHingeSku = hingeTypeVal === 'TCA1L2S3WT' ? 'TCA1L2S3BT' : hingeTypeVal;

    // Static fallback price map (cents) — used when Shopify variant ID is null
    const HINGE_PRICE_CENTS = (window.CF_LIVE_PRICES || (o => o))({ 'SHF-90L': 1820, 'SHG-90L': 3558, 'PHS': 7568, 'TCA1L2S3BT': 4441, 'TCHD1AL2S3BT': 8010 });
    const HINGE_LABELS      = { 'SHF-90L': 'Fixed Tension', 'SHG-90L': 'Adjustable Tension', 'PHS': 'Soft Close Premium', 'TCA1L2S3BT': 'Adjustable Tension With Legs', 'TCHD1AL2S3BT': 'TruClose Heavy Duty' };

    // Primary: resolve from bladeHinges array (loaded from blade-fence collection — real variant IDs)
    // Falls back to perfmeshProducts.hinges (shared products, same SKUs)
    const hingesArr  = this.shopifyData.products?.bladeHinges
                    || this.shopifyData.collections?.perfmeshProducts?.hinges
                    || [];
    // The heavy-duty hinge is not in the blade collection, so build its entry from the
    // live catalogue when it resolves. Falls through to the hard-coded price if not.
    const _hdVariantId = (window.CF_VARIANT && window.CF_VARIANT('TCHD1AL2S3BT')) || null;
    const hingeEntry = (normHingeSku === 'TCHD1AL2S3BT' && _hdVariantId)
      ? { sku: 'TCHD1AL2S3BT', id: _hdVariantId,
          price: (window.CF_PRICE && window.CF_PRICE('TCHD1AL2S3BT')) || 8010,
          title: 'TruClose Heavy Duty Hinges' }
      : hingesArr.find(h => h.sku === normHingeSku);

    if (hingeEntry && hingeEntry.price != null) {
      const hingePrice = PriceHelper.fromCents(hingeEntry.price);
      const hingeQty   = 1;
      const hingeTotal = hingePrice * hingeQty;
      totalCost += hingeTotal;

      // Hinges are sold as a set, and the Shopify title on this path is just the
      // tension type ("Adjustable Tension"), so the quote never named the part.
      const _bHingeTitle = hingeEntry.title || normHingeSku;
      const hingeLabel = /hinges?\b/i.test(_bHingeTitle) ? _bHingeTitle : (_bHingeTitle + ' Hinges');
      this.state.quoteBreakdown.push({
        category: 'Gate Components',
        item: hingeLabel,
        sku: hingeEntry.sku || '',
        quantity: hingeQty,
        unitPrice: hingePrice,
        totalPrice: hingeTotal,
        variantId: hingeEntry.id || null
      });
    } else {
      // Fallback: products keyed by type
      const hingeProductKey = normHingeSku === 'PHS'            ? 'hingePolaris'
                            : normHingeSku === 'SHG-90L'        ? 'hingeSoftClose'
                            : normHingeSku.startsWith('TCA')    ? 'hingeDDTruClose'
                            : 'hingeStandard';
      const hingeProduct = this.shopifyData.products?.[hingeProductKey];
      const hingeVariant = hingeProduct?.variants?.[0] || null;
      if (hingeVariant) {
        const hingePrice = PriceHelper.fromCents(hingeVariant.price);
        const hingeQty   = 1;
        const hingeTotal = hingePrice * hingeQty;
        totalCost += hingeTotal;
        const hingeLabel = hingeProductKey === 'hingePolaris'    ? 'Polaris Soft Close Hinges'
                         : hingeProductKey === 'hingeSoftClose'  ? 'Safetech Adjustable Tension Hinges'
                         : hingeProductKey === 'hingeDDTruClose' ? 'D&D TruClose Hinges'
                         : 'Safetech Fixed Tension Hinges';
        this.state.quoteBreakdown.push({
          category: 'Gate Components',
          item: hingeLabel,
          sku: hingeVariant.sku || normHingeSku || '',
          quantity: hingeQty,
          unitPrice: hingePrice,
          totalPrice: hingeTotal,
          variantId: hingeVariant.id || null
        });
      } else if (HINGE_PRICE_CENTS[normHingeSku]) {
        const hingePrice = PriceHelper.fromCents(HINGE_PRICE_CENTS[normHingeSku]);
        totalCost += hingePrice;
        this.state.quoteBreakdown.push({
          category: 'Gate Components',
          item: (HINGE_LABELS[normHingeSku] || normHingeSku) + ' Hinges',
          quantity: 1,
          unitPrice: hingePrice,
          totalPrice: hingePrice,
          variantId: null
        });
      }
    }

    // ── Latch ─────────────────────────────────────────────────────────────
    // latch-type select always holds SKU values (SL-50K etc.) from generateLatchTypeOptions.
    // Normalise any legacy type strings just in case.
    const _rawLatchVal = section.latchType
                      || document.getElementById(`section-${sectionKey}-latch-type`)?.value
                      || 'SL-50K';
    const LATCH_TYPE_TO_SKU = { 'standard': 'SL-50K', 'key-lockable': 'SL-50H', 'lockable': 'SL-50H' };
    const latchTypeVal = LATCH_TYPE_TO_SKU[_rawLatchVal] || _rawLatchVal;

    // Static fallback price map (cents)
    const LATCH_PRICE_CENTS = (window.CF_LIVE_PRICES || (o => o))({ 'SL-50K': 5564, 'SL-50H': 6799 });
    const LATCH_LABELS      = { 'SL-50K': 'Top Pull MagnaLatch Keyless', 'SL-50H': 'Top Pull MagnaLatch Lockable' };

    // Primary: resolve from bladeLatches array (loaded from blade-fence collection — real variant IDs)
    // Falls back to perfmeshProducts.latches (shared products, same SKUs)
    const latchesArr  = this.shopifyData.products?.bladeLatches
                     || this.shopifyData.collections?.perfmeshProducts?.latches
                     || [];
    const latchEntry  = latchesArr.find(l => l.sku === latchTypeVal);

    if (latchEntry && latchEntry.price != null) {
      const latchPrice = PriceHelper.fromCents(latchEntry.price);
      totalCost += latchPrice;
      const latchLabel = latchEntry.title || latchTypeVal;
      this.state.quoteBreakdown.push({
        category: 'Gate Components',
        item: latchLabel,
        sku: latchEntry.sku || '',
        quantity: 1,
        unitPrice: latchPrice,
        totalPrice: latchPrice,
        variantId: latchEntry.id || null
      });
    } else {
      // Fallback: gateLatch / gateLatchDDKit product keys
      const latchProductKey = latchTypeVal.startsWith('MTST') ? 'gateLatchDDKit' : 'gateLatch';
      const latchProduct    = this.shopifyData.products?.[latchProductKey];
      let latchVariant      = null;
      if (latchProduct?.variants?.length) {
        latchVariant = latchProduct.variants.find(v =>
          v.color === latchTypeVal || String(v.id) === latchTypeVal
        ) || latchProduct.variants.find(v => v.color === 'standard') || latchProduct.variants[0];
      }
      if (latchVariant) {
        const latchPrice = PriceHelper.fromCents(latchVariant.price);
        totalCost += latchPrice;
        const latchLabel = latchTypeVal === 'SL-50H'       ? 'Safetech Magnetic Latch (Key Lockable)'
                         : latchTypeVal.startsWith('MTST') ? 'D&D MagnaLatch Kit'
                         : 'Safetech Magnetic Latch (Standard)';
        this.state.quoteBreakdown.push({
          category: 'Gate Components',
          item: latchLabel,
          sku: latchVariant.sku || latchTypeVal || '',
          quantity: 1,
          unitPrice: latchPrice,
          totalPrice: latchPrice,
          variantId: latchVariant.id || null
        });
      } else if (LATCH_PRICE_CENTS[latchTypeVal]) {
        const latchPrice = PriceHelper.fromCents(LATCH_PRICE_CENTS[latchTypeVal]);
        totalCost += latchPrice;
        this.state.quoteBreakdown.push({
          category: 'Gate Components',
          item: LATCH_LABELS[latchTypeVal] || `Gate Latch (${latchTypeVal})`,
          quantity: 1,
          unitPrice: latchPrice,
          totalPrice: latchPrice,
          variantId: null
        });
      }
    }

    // Flag all hinge/latch line items so no colour label is applied to them.
    for (let _i = _gcNoColourStart; _i < this.state.quoteBreakdown.length; _i++) {
      if (this.state.quoteBreakdown[_i]) this.state.quoteBreakdown[_i].noColour = true;
    }

    return totalCost;
  }

  // Calculate post bracket + slat cap costs from rendered section preview
  calculatePostAndCapCosts(section) {
    let totalCost = 0;

    // ── Resolve colour for variant lookup (per-section) ──────────────────
    const colour = this.resolveSectionColour(section);

    // ── Post count from rendered preview (exact match to what user sees) ─
    const postCount = section.renderedPostCount ?? 0;
    if (postCount <= 0) return 0;

    // ── Raked-panel outer post ────────────────────────────────────────────
    // A raked panel's tall outer post is a LONGER post than a normal run post.
    // In-Ground → 50×50×2400mm (any rake size). Base Mount → 50×50×1600mm for a
    // rake retained-height ≤1500mm, or 50×50×1900mm for 1500–1800mm. It only
    // exists when the rake's end post is actually present (respecting the
    // end-post on/off choice: LHS rake removed by "No Post Left", RHS by "No
    // Post Right"). When present, it's billed as its own line and excluded from
    // the normal post count so it isn't double-counted.
    const _isInGround = !!section.groundPost;
    let rakePostQty = 0;
    let rakePostSize = 0;
    if (section.hasRaked) {
      const _rakedSide = section.rakedSide || 'left';
      const _ep = section.endPostSetup || 'both';
      const _skipL = _ep === 'no-left'  || _ep === 'no-both';
      const _skipR = _ep === 'no-right' || _ep === 'no-both';
      const _rakePostRemoved = (_rakedSide === 'left' && _skipL) || (_rakedSide === 'right' && _skipR);
      if (!_rakePostRemoved) {
        rakePostQty = 1;
        const _retH = parseInt(section.retainedHeight) || 0;
        rakePostSize = _isInGround ? 2400 : (_retH <= 1500 ? 1600 : 1900);
      }
    }
    const normalPostCount = Math.max(0, postCount - rakePostQty);

    // ── Posts (50×50) ─────────────────────────────────────────────────────
    // In-Ground (groundPost ON / core-drill) → 1800mm post, $39.50 (incl. cap +
    // dress ring). Base Mount (OFF / base plate) → 1300mm post w/ base + cap,
    // $45.60 (incl. cap + base cover). Priced INLINE — Blade's Liquid data has
    // no post product, so we can't look one up. Quantity = rendered post count.
    if (normalPostCount > 0) {
      // ── Post size depends on fence height (Pete 19/07) ────────────────────
      //   1500 fence → Base Mount 1600mm / In-Ground 2400mm
      //   1800 fence → Base Mount 1900mm / In-Ground 2400mm
      //   1000/1200  → Base Mount 1300mm / In-Ground 1800mm  (unchanged)
      // Same James Glen posts (SKU/price) as the raked-post family for the tall sizes.
      const _fhPost = String(section.fenceHeight || '1200');
      let _postCfg;
      if (_isInGround) {
        _postCfg = (_fhPost === '1500' || _fhPost === '1800')
          ? { price: 59.59, size: 2400, skus: { 'satin-black': '49323', 'white': '49932', 'monument': '49933' } }
          : { price: 39.50, size: 1800, skus: { 'satin-black': '49321', 'white': '49926', 'monument': '49927' } };
      } else if (_fhPost === '1500') {
        _postCfg = { price: 52.46, size: 1600, skus: { 'satin-black': '49839', 'white': '49944', 'monument': '49945' } };
      } else if (_fhPost === '1800') {
        _postCfg = { price: 63.51, size: 1900, skus: { 'satin-black': '49896', 'white': '49947', 'monument': '49948' } };
      } else {
        _postCfg = { price: 45.60, size: 1300, skus: { 'satin-black': '49838', 'white': '49941', 'monument': '49942' } };
      }
      const postSku   = _postCfg.skus[colour] || _postCfg.skus['satin-black'];
      /* Live Shopify price + variant, 2026-08-15. The post prices in this file are
         hard-coded constants. They match the catalogue today, but nothing keeps them in
         step: verified 2026-08-15 by overriding CF_PRICE for 49838 and watching the quote
         stay at $45.60, and there is no shopifyData entry carrying a post SKU on this page.
         PerfMesh had the identical setup and three of its five post prices had already
         drifted, by up to $7.69 a section. Same fix applied there the same day.
         CF_PRICE / CF_VARIANT come from assets/cf-sku-prices.js. Both fall back to the
         hard-coded value, so if the /products.json fetch fails nothing changes.
         Pete's rule since 2026-08-11: one price, from Shopify, everywhere. */
      const _cfPrice = function (sku, fallback) {
        try {
          var c = (typeof window !== 'undefined' && typeof window.CF_PRICE === 'function') ? window.CF_PRICE(sku) : null;
          return (c != null && c > 0) ? c / 100 : fallback;
        } catch (e) { return fallback; }
      };
      const _cfVariant = function (sku) {
        try {
          return (typeof window !== 'undefined' && typeof window.CF_VARIANT === 'function') ? (window.CF_VARIANT(sku) || null) : null;
        } catch (e) { return null; }
      };
      const postPrice = _cfPrice(postSku, _postCfg.price);
      const postLabel = `${_isInGround ? 'In-Ground Post' : 'Base Mount Post'} (50×50×${_postCfg.size}mm)`;
      const postTotal = postPrice * normalPostCount;
      totalCost += postTotal;

      this.state.quoteBreakdown.push({
        category:   'Posts & Accessories',
        item:       postLabel,
        sku:        postSku,
        quantity:   normalPostCount,
        unitPrice:  postPrice,
        totalPrice: postTotal,
        variantId:  _cfVariant(postSku)
      });
    }

    // ── Raked outer post (special longer post) ────────────────────────────
    // Real James Glen products (SKUs confirmed from the Sept-2025 distributor list).
    // Customer price = net cost × the same margin multiple as the standard post in
    // that family (in-ground ×1.7906 = $39.50/$22.06; base-mount ×1.7854 = $45.60/$25.54).
    //   In-Ground 2400 "raked panel post": cost $33.28 → $59.59
    //   Base-Mount 1900:                    cost $35.57 → $63.51
    //   Base-Mount 1600:                    cost $29.38 → $52.46
    if (rakePostQty > 0) {
      const RAKE_POST = {
        2400: { price: 59.59, label: 'In-Ground Raked Post (50×50×2400mm)',
                skus: { 'satin-black': '49323', 'white': '49932', 'monument': '49933' } },
        1900: { price: 63.51, label: 'Base Mount Raked Post (50×50×1900mm)',
                skus: { 'satin-black': '49896', 'white': '49947', 'monument': '49948' } },
        1600: { price: 52.46, label: 'Base Mount Raked Post (50×50×1600mm)',
                skus: { 'satin-black': '49839', 'white': '49944', 'monument': '49945' } },
      };
      const rp = RAKE_POST[rakePostSize] || RAKE_POST[_isInGround ? 2400 : 1600];
      const rakePostSku = rp.skus[colour] || rp.skus['satin-black'];
      /* Live Shopify price + variant, 2026-08-15. The post prices in this file are
         hard-coded constants. They match the catalogue today, but nothing keeps them in
         step: verified 2026-08-15 by overriding CF_PRICE for 49838 and watching the quote
         stay at $45.60, and there is no shopifyData entry carrying a post SKU on this page.
         PerfMesh had the identical setup and three of its five post prices had already
         drifted, by up to $7.69 a section. Same fix applied there the same day.
         CF_PRICE / CF_VARIANT come from assets/cf-sku-prices.js. Both fall back to the
         hard-coded value, so if the /products.json fetch fails nothing changes.
         Pete's rule since 2026-08-11: one price, from Shopify, everywhere. */
      const _cfPrice = function (sku, fallback) {
        try {
          var c = (typeof window !== 'undefined' && typeof window.CF_PRICE === 'function') ? window.CF_PRICE(sku) : null;
          return (c != null && c > 0) ? c / 100 : fallback;
        } catch (e) { return fallback; }
      };
      const _cfVariant = function (sku) {
        try {
          return (typeof window !== 'undefined' && typeof window.CF_VARIANT === 'function') ? (window.CF_VARIANT(sku) || null) : null;
        } catch (e) { return null; }
      };
      const rakePostPrice = _cfPrice(rakePostSku, rp.price);
      const rakePostTotal = rakePostPrice * rakePostQty;
      totalCost += rakePostTotal;

      this.state.quoteBreakdown.push({
        category:   'Posts & Accessories',
        item:       rp.label,
        sku:        rakePostSku,
        quantity:   rakePostQty,
        unitPrice:  rakePostPrice,
        totalPrice: rakePostTotal,
        variantId:  _cfVariant(rakePostSku)
      });
    }

    // ── Post Brackets ────────────────────────────────────────────────────
    // 4 brackets per fence panel, 4 per rake panel, 0 for gates.
    const bracketProduct = this.shopifyData.products?.bladePostBracket;
    if (bracketProduct?.variants?.length) {
      const bracketVariant = bracketProduct.variants.find(v => v.color === colour)
                          || bracketProduct.variants[0];
      if (bracketVariant) {
        const bracketPrice = PriceHelper.fromCents(bracketVariant.price);
        // One pack of 4 per non-gate/non-hinge panel (gates & hinge panels: no brackets).
        const renderedPanels = section.renderedPanels || [];
        const bracketQty = renderedPanels.reduce((sum, p) => {
          if (p.isGate || p.type === 'hinge') return sum;
          return sum + 1; // packs (4 brackets each)
        }, 0);
        if (bracketQty > 0) {
          const bracketTotal = bracketPrice * bracketQty;
          totalCost += bracketTotal;

          this.state.quoteBreakdown.push({
            category: 'Posts & Accessories',
            item: `Bracket Pack of 4`,
            sku: bracketVariant.sku || String(bracketVariant.id || ''),
            quantity: bracketQty,
            unitPrice: bracketPrice,
            totalPrice: bracketTotal,
            variantId: bracketVariant.id || null
          });
        }
      }
    }

    // ── Slat Caps ────────────────────────────────────────────────────────
    // Slat caps are included at no charge — omitted from quote/cost.

    return totalCost;
  }

  // Calculate spigot costs
  calculateSpigotCosts() {
    const spigotProduct = this.shopifyData.products?.spigot;
    if (!spigotProduct || !spigotProduct.variants) return 0;

    const spigotType      = this.domCache.get('spigot-type')?.value;
    const spigotShape     = this.domCache.get('spigot-shape')?.value;
    const spigotBaseShape = document.getElementById('spigot-shape-select')?.value;

    const spigotVariant = VariantHelper.findVariant(spigotProduct, {
      description: spigotType,
      color: spigotShape,
      shape: spigotBaseShape
    });

    if (!spigotVariant) return 0;

    const totalGlassPanels = Object.values(this.state.sections).reduce((sum, section) => {
      if (section.length) {
        const calc = this.calculateOptimalPanels(section);
        let sectionGlassPanelCount = 0;
        
        if (calc) {
          sectionGlassPanelCount += calc.regularPanels || 0;
          sectionGlassPanelCount += calc.hingePanels || 0;
          sectionGlassPanelCount += calc.rakedPanels || 0;
        }
        
        return sum + sectionGlassPanelCount;
      }
      return sum;
    }, 0);

    const spigotPrice = PriceHelper.fromCents(spigotVariant.price);
    const spigotQuantity = totalGlassPanels * 2;
    const totalCost = spigotPrice * spigotQuantity;

    if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
    this.state.quoteBreakdown.push({
      category: 'Spigots & Hardware',
      item: `Spigot (${spigotType || 'Standard'})`,
      quantity: spigotQuantity,
      unitPrice: spigotPrice,
      totalPrice: totalCost,
      variantId: spigotVariant.id
    });

    return totalCost;
  }

  // Update components quote
  updateComponentsQuote() {
    if (this.state.quoteBreakdown) {
      this.state.quoteBreakdown = this.state.quoteBreakdown.filter(item => 
        item.category !== 'Add-Ons'
      );
    }

    let additionalCost = 0;

    const addOnElements = document.querySelectorAll('#addon-list input[type="checkbox"]:checked');
    addOnElements.forEach(checkbox => {
      const productId = checkbox.dataset.productId;
      const variantId = checkbox.dataset.variantId;

      // Read quantity — grout items have a number input, others default to 1
      const qtyInput = document.querySelector(`#addon-list input.addon-qty[data-product-id="${productId}"]`);
      const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value) || 1) : 1;

      const addOns = this.shopifyData.collections?.addOns || [];
      const product = addOns.find(p => p.id == productId);
      
      if (product) {
        const unitPrice  = PriceHelper.fromCents(product.price);
        const totalPrice = unitPrice * qty;
        additionalCost  += totalPrice;
        
        if (!this.state.quoteBreakdown) this.state.quoteBreakdown = [];
        this.state.quoteBreakdown.push({
          category: 'Add-Ons',
          item: product.title,
          quantity: qty,
          unitPrice: unitPrice,
          totalPrice: totalPrice,
          variantId: parseInt(variantId)
        });
      }
    });

    const checkedVariantIds = Array.from(addOnElements).map(cb => parseInt(cb.dataset.variantId));
    this.state.cartItems = this.state.cartItems.filter(item => {
      const isAddOn = this.shopifyData.collections?.addOns?.some(addon => 
        addon.variants.some(variant => variant.id === item.variantId)
      );
      return !isAddOn || checkedVariantIds.includes(item.variantId);
    });

    const newTotal = this.state.quote + additionalCost;
    
    const componentsQuote = this.domCache.get('components-quote');
    const finalQuote = this.domCache.get('final-quote');
    
    if (componentsQuote) componentsQuote.textContent = PriceHelper.format(newTotal, this.shopifyData);
    if (finalQuote) finalQuote.textContent = PriceHelper.format(newTotal, this.shopifyData);

    if (this.shopifyData.settings?.showQuoteBreakdown !== false) {
      this.updateQuoteBreakdown();
    }

    // Re-derive cartItems to include add-on changes
    this._deriveCartItemsFromBreakdown();
  }

  // Update quote elements
  updateQuoteElements(total) {
    const formattedPrice = PriceHelper.format(total, this.shopifyData);
    const isInstall = this.state.pricingType === 'install';
    
    const quoteElements = [
      'instant-quote',
      'components-quote', 
      'final-quote'
    ];
    
    quoteElements.forEach(elementId => {
      const element = this.domCache.get(elementId);
      if (element) {
        element.textContent = formattedPrice;
      }
    });

    // Update quote button label to show pricing type
    const quoteBtn = document.querySelector('.instant-quote-btn, [id*="quote-btn"], .quote-display h4');
    const allH4s = document.querySelectorAll('.quote-display h4');
    allH4s.forEach(h4 => {
      const isUpdated = h4.textContent.includes('Updated');
      const prefix = isUpdated ? 'Updated Quote' : 'Instant Quote';
      h4.textContent = this.state.pricingType === 'install'
        ? `${prefix} (Supply & Install)`
        : `${prefix} (DIY)`;
    });

    if (ENABLE_SIZE_DEBUG) {
      this.updateSizeDebug();
    }
  }

  // Update size debug information
  updateSizeDebug() {
    let debugContainer = document.getElementById('size-debug-container');
    
    if (!debugContainer) {
      const instantQuoteElement = this.domCache.get('instant-quote');
      if (!instantQuoteElement) return;
      
      debugContainer = document.createElement('div');
      debugContainer.id = 'size-debug-container';
      debugContainer.style.cssText = `
        margin-top: 15px;
        padding: 12px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        font-family: monospace;
        font-size: 12px;
        color: #475569;
      `;
      
      const parentContainer = instantQuoteElement.closest('.quote-section') || instantQuoteElement.parentElement;
      if (parentContainer) {
        parentContainer.appendChild(debugContainer);
      }
    }

    // if (!this.state.sections || Object.keys(this.state.sections).length === 0) {
    //   debugContainer.innerHTML = '<div style="color: #64748b;">No sections configured</div>';
    //   return;
    // }

    // let debugHtml = '<div style="font-weight: bold; margin-bottom: 8px; color: #334155;">Size Debug:</div>';
    let debugHtml = '';
    let totalLength = 0;
    let totalPanelWidth = 0;
    let totalGapWidth = 0;

    Object.entries(this.state.sections).forEach(([sectionNum, section]) => {
      if (!section || !section.length) return;
      
      const sectionLength = parseInt(section.length) || 0;
      const calculation = this.calculateOptimalPanels(section);
      
      totalLength += sectionLength;
      
      debugHtml += `<div style="margin: 6px 0; padding: 6px; background: white; border-radius: 4px;">`;
      debugHtml += `<div style="font-weight: bold;">Section ${sectionNum}: ${sectionLength}mm</div>`;
      
      if (calculation) {
        const numRegularPanels = calculation.regularPanels || 0;
        const numHingePanels = calculation.hingePanels || 0;
        const numGatePanels = calculation.gatePanels || 0;
        const numRakedPanels = calculation.rakedPanels || 0;

        // Calculate regular panel widths (variable or uniform)
        const panelWidthsArr = calculation.panelWidths || Array(numRegularPanels).fill(calculation.panelWidth);
        const regularPanelWidth = panelWidthsArr.reduce((sum, w) => sum + w, 0);
        const hingePanelWidth = numHingePanels > 0 ? (panelWidthsArr[panelWidthsArr.length - 1] || calculation.panelWidth) * numHingePanels : 0;
        const gateWidth = section.hasGate && section.gateLength ? parseInt(section.gateLength) : 0;
        const rakedWidth = section.hasRaked ? this.getRakedPanelWidth(section.fenceHeight) : 0;
        const gapWidth = calculation.totalGapWidth;

        const totalSectionPanelWidth = regularPanelWidth + hingePanelWidth + gateWidth + rakedWidth;

        totalPanelWidth += totalSectionPanelWidth;
        totalGapWidth += gapWidth;

        debugHtml += `<div style="margin-left: 8px; font-size: 11px;">`;

        // Show per-panel breakdown if variable, otherwise uniform display
        if (calculation.panelWidths && new Set(calculation.panelWidths).size > 1) {
          const groups = {};
          calculation.panelWidths.forEach(w => { groups[w] = (groups[w] || 0) + 1; });
          const panelDesc = Object.entries(groups).map(([s, q]) => `${q}×${s}mm`).join(' + ');
          debugHtml += `Regular Panels: ${panelDesc} = ${regularPanelWidth}mm<br>`;
        } else {
          debugHtml += `Regular Panels: ${numRegularPanels} × ${calculation.panelWidth}mm = ${regularPanelWidth}mm<br>`;
        }

        if (numHingePanels > 0) {
          const hingeW = panelWidthsArr[panelWidthsArr.length - 1] || calculation.panelWidth;
          debugHtml += `Hinge Panel: ${numHingePanels} × ${hingeW}mm = ${hingePanelWidth}mm<br>`;
        }

        if (gateWidth > 0) {
          debugHtml += `Gate Panel: 1 × ${gateWidth}mm = ${gateWidth}mm<br>`;
        }

        if (section.hasRaked) {
          debugHtml += `Raked Panel: 1 × ${rakedWidth}mm = ${rakedWidth}mm<br>`;
        }

        debugHtml += `Total Panels: ${totalSectionPanelWidth}mm<br>`;

        // Show per-gap breakdown if variable
        if (calculation.gapSpacings && new Set(calculation.gapSpacings).size > 1) {
          const groups = {};
          calculation.gapSpacings.forEach(g => { groups[g] = (groups[g] || 0) + 1; });
          const gapDesc = Object.entries(groups).map(([s, q]) => `${q}×${s}mm`).join(' + ');
          debugHtml += `Gaps: ${gapDesc} = ${gapWidth}mm<br>`;
        } else {
          debugHtml += `Gaps: ${calculation.totalGaps} × ${calculation.gapSpacing}mm = ${gapWidth}mm<br>`;
        }
        debugHtml += `Remainder: ${calculation.remaining}mm`;
        debugHtml += `</div>`;
      }
      
      debugHtml += `</div>`;
    });

    debugHtml += `<div style="margin-top: 10px; padding: 8px; background: #e2e8f0; border-radius: 4px; font-weight: bold;">`;
    debugHtml += `<div>Total Project Length: ${totalLength}mm</div>`;
    debugHtml += `<div style="margin-top: 4px; font-weight: normal; font-size: 11px;">`;
    debugHtml += `All Panels: ${totalPanelWidth}mm | Gaps: ${totalGapWidth}mm<br>`;
    debugHtml += `Total Used: ${totalPanelWidth + totalGapWidth}mm | Difference: ${totalLength - (totalPanelWidth + totalGapWidth)}mm`;
    debugHtml += `</div></div>`;

    debugContainer.innerHTML = debugHtml;
  }

  // Navigation functions
  navigateBack() {
    const tabs = ['add-sections', 'configure-sections', 'review-checkout'];
    const currentIndex = tabs.indexOf(this.state.currentTab);
    
    if (currentIndex > 0) {
      this.switchTab(tabs[currentIndex - 1]);
    }
  }

  navigateNext() {
    // On configure-sections, Next steps through sections one at a time (scrolled to
    // top) instead of validating every section at once — no "fill out section N"
    // warning for sections the user hasn't reached yet. handleSectionNextClick
    // advances to the next section, or proceeds to review from the last section.
    if (this.state.currentTab === 'configure-sections') {
      this.handleSectionNextClick();
      return;
    }
    const tabs = ['add-sections', 'configure-sections', 'review-checkout'];
    const currentIndex = tabs.indexOf(this.state.currentTab);
    
    if (!this.validateCurrentTab()) {
      return;
    }
    
    if (currentIndex < tabs.length - 1) {
      this.switchTab(tabs[currentIndex + 1]);
    }
  }

  // Validate current tab
  validateCurrentTab() {
    switch (this.state.currentTab) {
      case 'add-sections':
        return this.validateAddSections();
      case 'configure-sections':
        return this.validateConfigureSections();
      case 'add-components':
        return true;
      case 'review-checkout':
        return true;
      default:
        return true;
    }
  }

  // Validate add sections
  validateAddSections() {
    const sectionsSelect = this.domCache.get('sections-count')
      || document.querySelector('select[name="sections-count"]')
      || document.querySelector('.sections-count-select');
    const hasValue = (sectionsSelect && sectionsSelect.value) || this.state.sectionsCount;
    if (!hasValue) {
      this.showError('sections-error', 'Please select the number of sections to continue.');
      return false;
    }
    return true;
  }

  // Validate configure sections
  validateConfigureSections() {
    const incompleteSection = this.findFirstSectionWithoutLength();
    
    if (incompleteSection) {
      alert(`Please enter length for Section ${incompleteSection}`);
      this.showSection(incompleteSection);
      return false;
    }
    
    return true;
  }

  // At least one fence section is "completed": has a valid length and its layout
  // isn't explicitly invalid. Used to gate the Add to Cart / checkout controls so
  // they can't fire on an empty config (which would push an empty quote and crash
  // the quote-review page).
  hasCompletedSection() {
    const MIN = (typeof CONFIG !== 'undefined' && CONFIG.CONSTRAINTS && CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH) || 500;
    return Object.entries(this.state.sections || {}).some(([num, s]) => {
      const len = parseInt(s && s.length);
      if (!Number.isFinite(len) || len < MIN) return false;
      const cfg = this.state.optimizedConfigs[parseInt(num)];
      if (cfg && cfg.valid === false) return false; // explicitly unsolvable layout
      return true;
    });
  }

  // ── Review page: Last chance colour change + Post fixings ─────────────
  renderReviewOptions() {
    this.renderReviewFixings();
  }

  // Optional Fixing Materials — install-specific add-ons the customer can opt into.
  // In-Ground posts → Sika HES Class C Grout (1 bag per 3 posts - Pete 5 Oct 2026, store price). Base Mount posts
  // → Base Fixing Anchors (4 per post - Pete 5 Oct 2026). Suggested quantities are computed from the
  // post counts; ticking adds them (priced) to the order.
  reviewFixingItems() {
    const secs = Object.values(this.state.sections || {}).filter(s => s && s.length);
    let inGroundPosts = 0, baseMountPosts = 0;
    secs.forEach(s => {
      const pc = s.renderedPostCount || 0;
      if (s.groundPost) inGroundPosts += pc; else baseMountPosts += pc;
    });
    const items = [];
    if (inGroundPosts > 0) {
      const bags = Math.ceil(inGroundPosts / 3);
      const groutEach = ((typeof window.CF_PRICE === 'function' && window.CF_PRICE('49538')) || 8879) / 100;
      items.push({ key: 'sika', label: 'Sika HES Class C Grout', sku: '49538', qty: bags, price: groutEach,
        sub: `1 bag per 3 in-ground posts · suggested ${bags} bag${bags !== 1 ? 's' : ''}` });
    }
    if (baseMountPosts > 0) {
      const qty = baseMountPosts * 4;
      items.push({ key: 'anchors', label: 'Base Fixing Anchors (M8×80)', sku: '43032', qty, price: 4.50,
        sub: `4 per base-mount post · suggested ${qty}` });
    }
    return items;
  }

  renderReviewFixings() {
    const el = document.getElementById('review-fixings-info');
    if (!el) return;
    const _fg = el.closest('.form-group');
    if (this.state.pricingType === 'install') { el.innerHTML = ''; if (_fg) _fg.style.display = 'none'; return; }
    if (_fg) _fg.style.display = '';
    if (!this.state.fixingAddons) {
      // A refresh keeps the fence (cf-calc-memory) - keep the fixing ticks with it (break test 6 Oct 2026).
      let _fx = null;
      try {
        if (/^(restoring|done)$/.test(String(window.__cfCalcMemoryState || ''))) {
          _fx = JSON.parse(sessionStorage.getItem('cf-fix:' + location.pathname.replace(/\/+$/, '')) || 'null');
        }
      } catch (e) {}
      this.state.fixingAddons = { sika: !!(_fx && _fx.sika), anchors: !!(_fx && _fx.anchors) };
      if (this.state.fixingAddons.sika || this.state.fixingAddons.anchors) {
        setTimeout(() => { try { this.updateQuoteDisplay(); this.updateQuoteBreakdown(); this.renderReviewFixings(); } catch (e) {} }, 0);
      }
    }
    const items = this.reviewFixingItems();
    if (!items.length) { el.innerHTML = '<p style="font-size:12px; color:#64748b; margin:0;">No posts configured yet.</p>'; return; }
    el.innerHTML = items.map(it => {
      const checked = !!this.state.fixingAddons[it.key];
      return `<label style="display:flex; align-items:center; gap:10px; padding:11px 12px; border:2px solid ${checked ? '#0e9ed5' : '#e2e8f0'}; border-radius:8px; margin-bottom:6px; background:${checked ? '#f0f9ff' : '#fff'}; cursor:pointer;">
        <input type="checkbox" class="cf-fixing-addon" data-key="${it.key}" ${checked ? 'checked' : ''} style="width:18px; height:18px; accent-color:#0e9ed5; flex-shrink:0;">
        <span style="flex:1;">
          <span style="display:block; font-weight:700; color:#374151; font-size:14px;">${it.label} <span style="color:#64748b; font-weight:600;">× ${it.qty}</span></span>
          <span style="display:block; font-size:12px; color:#64748b;">${it.sub}</span>
        </span>
        <span style="font-weight:700; color:#0e9ed5; white-space:nowrap;">${PriceHelper.format(it.price * it.qty, this.shopifyData)}</span>
      </label>`;
    }).join('');
    el.querySelectorAll('.cf-fixing-addon').forEach(cb => {
      cb.addEventListener('change', () => {
        this.state.fixingAddons[cb.getAttribute('data-key')] = cb.checked;
        try { sessionStorage.setItem('cf-fix:' + location.pathname.replace(/\/+$/, ''), JSON.stringify(this.state.fixingAddons)); } catch (e) {}
        this.updateQuoteDisplay();
        this.updateQuoteBreakdown();
        this.renderReviewFixings();
      });
    });
  }

  // Colour swatches on Review. Mirrors how the fence was built: one shared set
  // in "all sections the same" mode, or one set per section otherwise. Changing
  // a colour re-runs the quote so the order's colour-specific SKUs + colour
  // labels update to match.
  renderReviewColourChange() {
    const container = document.getElementById('review-colour-change');
    if (!container) return;
    const IMG = (window.CF_SWATCH_IMAGES) || {
      'satin-black': 'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeBlk.png?v=1783627985',
      'white':       'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeWht.png?v=1783627986',
      'monument':    'https://cdn.shopify.com/s/files/1/0570/6530/8254/files/BladeGry.png?v=1783627986'
    };
    const NAME = { 'satin-black': 'Satin Black', 'white': 'Satin White', 'monument': 'Monument' };
    const COLOURS = ['satin-black', 'white', 'monument'];

    const swatchRow = (current, scopeAttrs) =>
      `<div style="display:flex; gap:10px; flex-wrap:wrap;">` +
      COLOURS.map(ck => {
        const sel = ck === current;
        return `<label ${scopeAttrs} data-colour="${ck}" style="cursor:pointer; text-align:center;">
          <span style="display:block; width:64px; height:64px; border-radius:9px; overflow:hidden; border:3px solid ${sel ? '#0e9ed5' : 'transparent'}; box-shadow:0 0 0 1px ${sel ? '#0e9ed5' : '#cbd5e1'};">
            <img src="${IMG[ck]}" alt="${NAME[ck]}" style="width:100%; height:100%; object-fit:cover; display:block;">
          </span>
          <span style="display:block; font-size:11.5px; color:#374151; margin-top:5px; font-weight:600;">${NAME[ck]}</span>
        </label>`;
      }).join('') + `</div>`;

    let html = '';
    if (this.state.sameForAll) {
      const cur = (this.state.shared && this.state.shared.color) || 'satin-black';
      html = swatchRow(cur, 'data-scope="shared"');
    } else {
      const nums = Object.keys(this.state.sections).map(Number)
        .filter(n => this.state.sections[n] && this.state.sections[n].length).sort((a, b) => a - b);
      html = nums.map(n => {
        const cur = this.state.sections[n].color || 'satin-black';
        return `<div style="margin-bottom:12px;">
          <div style="font-size:12px; font-weight:700; color:#0e9ed5; margin-bottom:6px;">Section ${n}</div>
          ${swatchRow(cur, `data-scope="section" data-section="${n}"`)}
        </div>`;
      }).join('');
    }
    container.innerHTML = html;

    container.querySelectorAll('label[data-colour]').forEach(lbl => {
      lbl.addEventListener('click', () => {
        const colour = lbl.getAttribute('data-colour');
        const scope  = lbl.getAttribute('data-scope');
        if (scope === 'shared') {
          if (!this.state.shared) this.state.shared = {};
          this.state.shared.color = colour;
          Object.keys(this.state.sections).forEach(k => {
            if (this.state.sections[k]) this.state.sections[k].color = colour;
            delete this.state.optimizedConfigs[k];
          });
        } else {
          const n = parseInt(lbl.getAttribute('data-section'));
          if (this.state.sections[n]) { this.state.sections[n].color = colour; delete this.state.optimizedConfigs[n]; }
        }
        // Recompute so the ORDER (colour-specific SKUs + colour labels) changes,
        // then refresh the review views and re-highlight the swatches.
        this.updateQuoteDisplay();
        this.updateFinalSectionsPreview();
        this.updateQuoteBreakdown();
        this.renderReviewColourChange();
      });
    });
  }

  // Update navigation buttons
  // Add-to-Cart / checkout buttons must NOT appear until the final Review & Checkout
  // page (Pete 19/07). This force-hides them on every other tab; updateNavigationButtons
  // still handles SHOWING them correctly on review-checkout.
  _hideCheckoutUnlessReview() {
    const onReview = this.state.currentTab === 'review-checkout';
    // Use an inline display override (with !important) rather than the .hidden class —
    // other quote/preview updates strip the class off these buttons, but they never touch
    // inline style, so this stays put. On review we clear it and let the normal
    // updateNavigationButtons class logic show the correct button.
    // Nav-bar checkout buttons live OUTSIDE the tab panels, so they'd otherwise show on
    // every tab. (final-quote-box lives inside the review panel and is already gated.)
    ['btn-diy-checkout', 'btn-si-enquiry', 'add-to-cart'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (onReview) el.style.removeProperty('display');
      else el.style.setProperty('display', 'none', 'important');
    });
  }

  updateNavigationButtons() {
    const backBtn = this.domCache.get('nav-back')
      || document.querySelector('[data-action="nav-back"]')
      || Array.from(document.querySelectorAll('button')).find(b => b.textContent.trim() === '← Back');
    const nextBtn = this.domCache.get('nav-next')
      || document.querySelector('[data-action="nav-next"]')
      || Array.from(document.querySelectorAll('button')).find(b => /^Next\s*→/.test(b.textContent.trim()));
    const addToCartBtn = this.domCache.get('add-to-cart');
    
    const tabs = ['add-sections', 'configure-sections', 'review-checkout'];
    const currentIndex = tabs.indexOf(this.state.currentTab);
    
    if (backBtn) {
      backBtn.disabled = currentIndex === 0;
    }
    
    const diyBtn2 = document.getElementById('btn-diy-checkout');
    const siBtn2  = document.getElementById('btn-si-enquiry');
    const isInstallMode = this.state.pricingType === 'install';
    // Keep the green quote box's CTA label in sync with the pricing mode.
    const ctaLabel = document.getElementById('final-quote-cta');
    if (ctaLabel) ctaLabel.textContent = isInstallMode ? '🏗️ Book Site Inspection' : '🛒 Add to Cart';

    // Only show the Add to Cart box when there's actually something to add
    // (at least one completed section). Hidden entirely otherwise.
    const complete = this.hasCompletedSection();
    const finalBox = document.getElementById('final-quote-box');
    if (finalBox) {
      finalBox.style.display = complete ? '' : 'none';
      finalBox.classList.toggle('quote-display--cta', complete);
      finalBox.dataset.ctaEnabled = complete ? '1' : '0';
      finalBox.style.cursor = complete ? 'pointer' : 'default';
      if (ctaLabel) ctaLabel.style.display = complete ? '' : 'none';
    }

    if (currentIndex === tabs.length - 1) {
      if (nextBtn) nextBtn.classList.add('hidden');
      // Show the relevant action button only when a section is completed.
      if (diyBtn2) diyBtn2.classList.toggle('hidden', isInstallMode || !complete);
      if (siBtn2)  siBtn2.classList.toggle('hidden', !isInstallMode || !complete);
    } else {
      if (nextBtn) nextBtn.classList.remove('hidden');
      if (diyBtn2) diyBtn2.classList.add('hidden');
      if (siBtn2)  siBtn2.classList.add('hidden');
    }
  }

  // Update quote summary
  updateQuoteSummary() {
    const summaryDiv = this.domCache.get('quote-summary');
    if (!summaryDiv) return;

    let summaryHTML = '<div class="quote-sections">';
    
    Object.entries(this.state.sections).forEach(([num, section]) => {
      if (section.length) {
        // Always use the cached optimized config — same source as preview and quote
        const sNum = parseInt(num);
        let calculation = this.state.optimizedConfigs[sNum];
        if (!calculation) {
          const prefPanel = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
          const prefGap   = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
          calculation = this.peterCalculateLayout(section, prefPanel, prefGap)
                     || this.calculateOptimalPanels(section);
        }
        // Panels = regular mesh sheets only (no hinge, no gate, no raked)
        const totalPanelCount = calculation ? (calculation.regularPanels || 0) : 0;
        // Posts = regular panels + 1 base + 1 if gate + 1 if raked
        const hasGateCalc = (calculation?.gatePanels || 0) > 0;
        const hasRakedCalc = (calculation?.rakedPanels || 0) > 0;
        const postCount = calculation
          ? (totalPanelCount + 1 + (hasGateCalc ? 1 : 0) + (hasRakedCalc ? 1 : 0))
          : 0;
        summaryHTML += `
          <div class="section-summary" style="
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 12px 14px;
            margin-bottom: 10px;
          ">
            <h4 style="
              font-size: 13px;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 10px 0;
              padding-bottom: 6px;
              border-bottom: 1px solid #e2e8f0;
            ">Section ${num}</h4>
            <div style="display: flex; flex-direction: column; gap: 5px;">
              <div style="display: flex; justify-content: space-between; font-size: 12px; color: #475569;">
                <span style="font-weight: 600; color: #64748b;">Section Length</span>
                <span style="font-weight: 600; color: #1e293b;">${section.length}mm</span>
              </div>
              <div style="display: flex; justify-content: space-between; font-size: 12px; color: #475569;">
                <span style="font-weight: 600; color: #64748b;">Panel Height</span>
                <span style="font-weight: 600; color: #1e293b;">${parseInt(section.fenceHeight) || 1200}mm</span>
              </div>
              ${calculation ? `
              <div style="display: flex; justify-content: space-between; font-size: 12px; color: #475569;">
                <span style="font-weight: 600; color: #64748b;">Panel Count</span>
                <span style="font-weight: 600; color: #1e293b;">${totalPanelCount}</span>
              </div>` : ''}
              <div style="display: flex; justify-content: space-between; font-size: 12px; color: #475569;">
                <span style="font-weight: 600; color: #64748b;">${section.groundPost ? 'In Ground Posts' : 'Base Mount Posts'}</span>
                <span style="font-weight: 600; color: #1e293b;">${postCount}</span>
              </div>
              ${section.hasGate ? `
              <div style="display: flex; justify-content: space-between; font-size: 12px;">
                <span style="font-weight: 600; color: #64748b;">Gate</span>
                <span style="font-weight: 600; color: #059669;">✓ ${section.gateLength}mm</span>
              </div>` : ''}
              ${section.hasRaked ? `
              <div style="display: flex; justify-content: space-between; font-size: 12px;">
                <span style="font-weight: 600; color: #64748b;">Raked</span>
                <span style="font-weight: 600; color: #059669;">✓ Included</span>
              </div>` : ''}
            </div>
          </div>
        `;
      }
    });
    
    summaryHTML += '</div>';

    summaryHTML += '<div class="components-summary"><h4>Selected Components</h4>';
    
    const spigotType = this.domCache.get('spigot-type')?.value;
    const spigotShape = this.domCache.get('spigot-shape')?.value;
    const spigotFinish = this.domCache.get('spigot-finish')?.value;
    
    if (spigotType) summaryHTML += `<p>✓ Fence Type: ${spigotType}</p>`;
    if (spigotShape) summaryHTML += `<p>✓ Finish: ${spigotShape}</p>`;
    if (spigotFinish) summaryHTML += `<p>✓ Base Shape: ${spigotFinish}</p>`;
    
    // Hinge & Latch cards removed — no latch/hinge display on Review (per Pete).

    const checkedAddOns = document.querySelectorAll('#addon-list input[type="checkbox"]:checked');
    checkedAddOns.forEach(checkbox => {
      const label = checkbox.parentElement.querySelector('span:not(.addon-price)');
      if (label) {
        summaryHTML += `<p>✓ ${label.textContent}</p>`;
      }
    });
    
    summaryHTML += '</div>';
    summaryDiv.innerHTML = summaryHTML;
  }

  // Quote actions
  downloadQuote() {
    try {
      // Check if jsPDF is available (multiple possible ways it might be loaded)
      let jsPDF;
      
      // Debug: Check what's available
      const debugInfo = {
        'window.jspdf': !!window.jspdf,
        'window.jsPDF': !!window.jsPDF,
        'window.jspdf.jsPDF': !!(window.jspdf && window.jspdf.jsPDF),
        'typeof window.jspdf': typeof window.jspdf
      };
      
      if (window.jspdf && window.jspdf.jsPDF) {
        jsPDF = window.jspdf.jsPDF;
      } else if (window.jsPDF) {
        jsPDF = window.jsPDF;
      } else if (window.jspdf) {
        // Sometimes it's directly on jspdf without nested jsPDF
        jsPDF = window.jspdf;
      } else {
        alert(`PDF library not loaded. Debug info: ${JSON.stringify(debugInfo)}`);
        return;
      }

      // Add a small delay to ensure DOM is fully updated with quote breakdown
      setTimeout(() => {
        this.generatePDFWithData(jsPDF);
      }, 100);
    } catch (error) {
      alert('Error generating PDF. Please try again.');
    }
  }

  // Separate method to generate PDF with proper data extraction
  generatePDFWithData(jsPDF) {
    try {

      // Create new PDF document
      const doc = new jsPDF();
      
      // PDF styling
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let currentY = margin;
      
      // Color constants matching website design
      const colors = {
        primary: [14, 158, 213],     // #0e9ed5 - brand blue
        green: [26, 107, 35],        // #1a6b23 - quote green  
        lightGray: [248, 250, 252],  // #f8fafc - background
        darkGray: [55, 65, 81],      // #374151 - text
        mediumGray: [100, 116, 139], // #64748b - secondary text
        border: [226, 232, 240]      // #e2e8f0 - borders
      };
      
      // Helper function to add text with word wrap
      const addText = (text, x, y, options = {}) => {
        const fontSize = options.fontSize || 11;
        const maxWidth = options.maxWidth || (pageWidth - 2 * margin);
        const fontStyle = options.fontStyle || 'normal';
        
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return y + (lines.length * fontSize * 0.4);
      };
      
      // Add header with brand styling
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, pageWidth, 45, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('Pool Fencing Quote', margin, 28);
      
      // Add date in header
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      });
      doc.text(`Generated: ${currentDate}`, pageWidth - margin - 80, 28);
      
      // Reset text color and position
      doc.setTextColor(0, 0, 0);
      currentY = 75;
      
      // Add Quote Breakdown section styled like the website
      doc.setFillColor(...colors.lightGray);
      doc.rect(margin, currentY - 15, pageWidth - 2 * margin, 30, 'F');
      doc.setDrawColor(...colors.border);
      doc.rect(margin, currentY - 15, pageWidth - 2 * margin, 30, 'S');
      
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.darkGray);
      doc.text('Quote Breakdown', margin + 10, currentY);
      currentY += 35;
      
      // Get structured quote breakdown from saved HTML
      const quoteBreakdownData = this.getQuoteBreakdownDataFromSavedHTML();
      if (quoteBreakdownData && quoteBreakdownData.length > 0) {
        quoteBreakdownData.forEach((category, catIndex) => {
          // Category header with green accent like website
          doc.setFillColor(255, 255, 255);
          doc.rect(margin + 10, currentY - 5, pageWidth - 2 * margin - 20, 20, 'F');
          doc.setDrawColor(...colors.border);
          doc.rect(margin + 10, currentY - 5, pageWidth - 2 * margin - 20, 20, 'S');
          
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.darkGray);
          doc.text(category.name, margin + 20, currentY + 8);
          
          // Category total (right aligned with green color)
          doc.setTextColor(...colors.green);
          doc.setFont('helvetica', 'bold');
          const totalText = category.total;
          const totalWidth = doc.getTextWidth(totalText);
          doc.text(totalText, pageWidth - margin - 20 - totalWidth, currentY + 8);
          currentY += 30;
          
          // Category items with proper formatting and spacing
          if (category.items && category.items.length > 0) {
            category.items.forEach((item) => {
              // Calculate available width for description to prevent overlap
              const descriptionMaxWidth = pageWidth - margin - 120; // Leave space for prices
              
              doc.setFontSize(11);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(...colors.mediumGray);
              
              // Item description with proper text wrapping
              const itemText = item.description || `${item.quantity || ''} ${item.name || ''}`.trim();
              const wrappedText = doc.splitTextToSize(itemText, descriptionMaxWidth);
              doc.text(wrappedText, margin + 30, currentY);
              
              // Calculate height needed for wrapped text
              const textHeight = wrappedText.length * 12;
              
              // Unit price (smaller, positioned to avoid overlap)
              doc.setFontSize(10);
              doc.setTextColor(...colors.mediumGray);
              const unitPriceText = item.unitPrice || '';
              if (unitPriceText && unitPriceText !== '') {
                doc.text(unitPriceText, pageWidth - margin - 100, currentY + 8);
              }
              
              // Total price (right aligned, positioned to avoid overlap)
              doc.setFontSize(11);
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(...colors.darkGray);
              const totalPriceText = item.totalPrice || item.price || '';
              if (totalPriceText && totalPriceText !== '') {
                const priceWidth = doc.getTextWidth(totalPriceText);
                doc.text(totalPriceText, pageWidth - margin - 20 - priceWidth, currentY + 8);
              }
              
              // Increase Y position based on text height with minimum spacing
              currentY += Math.max(textHeight, 18);
            });
          }
          currentY += 10; // Space between categories
        });
        
        // Add separator line before total
        currentY += 10;
        doc.setDrawColor(...colors.border);
        doc.setLineWidth(2);
        doc.line(margin + 10, currentY, pageWidth - margin - 10, currentY);
        currentY += 20;
        
        // Total section with green background like website
        doc.setFillColor(...colors.green);
        doc.rect(margin + 10, currentY - 8, pageWidth - 2 * margin - 20, 25, 'F');
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('Total', margin + 20, currentY + 5);
        
        // Get total from final quote
        const finalQuoteElement = document.getElementById('final-quote') || 
                                 document.getElementById('components-quote') || 
                                 document.getElementById('instant-quote');
        const finalQuote = finalQuoteElement?.textContent || '$0.00';
        
        const totalWidth = doc.getTextWidth(finalQuote);
        doc.text(finalQuote, pageWidth - margin - 20 - totalWidth, currentY + 5);
        currentY += 40;
      } else {
        // No breakdown available
        doc.setFillColor(254, 242, 242);
        doc.rect(margin + 10, currentY, pageWidth - 2 * margin - 20, 30, 'F');
        doc.setDrawColor(252, 165, 165);
        doc.rect(margin + 10, currentY, pageWidth - 2 * margin - 20, 30, 'S');
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(185, 28, 28);
        doc.text('Quote breakdown will be calculated based on your configuration', margin + 20, currentY + 18);
        currentY += 50;
      }
      
      // Add Fence Sections Details section styled like the website
      doc.setFillColor(...colors.lightGray);
      doc.rect(margin, currentY - 15, pageWidth - 2 * margin, 30, 'F');
      doc.setDrawColor(...colors.border);
      doc.rect(margin, currentY - 15, pageWidth - 2 * margin, 30, 'S');
      
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.darkGray);
      doc.text('Fence Sections Details', margin + 10, currentY);
      currentY += 35;
      
      // Get fence sections data and render cards like the website
      const fenceSectionsData = this.getFenceSectionsDataFromDOM();
      console.log('PDF: Fence sections data:', fenceSectionsData); // Debug log
      
      if (fenceSectionsData && fenceSectionsData.length > 0) {
        
        // Calculate card layout - 1 per row for better readability
        const cardWidth = pageWidth - 2 * margin - 20; // Full width cards
        const cardHeight = 50;
        const cardSpacing = 10;
        const cardsPerRow = 1;
        
        let cardX = margin + 10;
        let cardY = currentY;
        
        fenceSectionsData.forEach((section, index) => {
          // Each section gets its own row
          if (index > 0) {
            cardY += cardHeight + cardSpacing;
          }
          
          // Draw card background with white fill and border like website
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(...colors.border);
          doc.setLineWidth(1);
          doc.rect(cardX, cardY, cardWidth, cardHeight, 'FD');
          
          // Card header
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.darkGray);
          doc.text(`Section ${section.number}`, cardX + 10, cardY + 15);
          
          // Section details line
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.mediumGray);
          const detailsText = `Length: ${section.length} | Panels: ${section.panelInfo} | Gap: ${section.gap}`;
          doc.text(detailsText, cardX + 10, cardY + 28);
          
          // Panel visualization area (simplified)
          const panelAreaY = cardY + 35;
          const panelAreaHeight = 15;
          
          // Draw simplified panel representation
          if (section.panels && section.panels.length > 0) {
            const totalPanelWidth = cardWidth - 40; // Leave margins
            const panelWidth = totalPanelWidth / section.panels.length;
            
            section.panels.forEach((panel, panelIndex) => {
              const panelX = cardX + 20 + (panelIndex * panelWidth);
              
              // Different styling for different panel types
              if (panel.type === 'gate') {
                doc.setFillColor(255, 240, 240); // Light red for gates
              } else if (panel.type === 'raked') {
                doc.setFillColor(240, 255, 240); // Light green for raked
              } else {
                doc.setFillColor(240, 248, 255); // Light blue for regular
              }
              
              doc.rect(panelX, panelAreaY, panelWidth - 2, panelAreaHeight, 'F');
              doc.setDrawColor(...colors.border);
              doc.rect(panelX, panelAreaY, panelWidth - 2, panelAreaHeight, 'S');
              
              // Panel size label
              doc.setFontSize(7);
              doc.setTextColor(...colors.mediumGray);
              const labelText = `${panel.size}mm`;
              const labelWidth = doc.getTextWidth(labelText);
              if (labelWidth < panelWidth - 4) {
                doc.text(labelText, panelX + (panelWidth - labelWidth) / 2 - 1, panelAreaY + 10);
              }
            });
          }
          
          // No need to move X position since we're using one section per row
        });
        
        // Calculate how much space the cards took
        currentY = cardY + cardHeight + 30;
        
      } else {
        // No sections configured
        doc.setFillColor(254, 242, 242);
        doc.rect(margin + 10, currentY, pageWidth - 2 * margin - 20, 40, 'F');
        doc.setDrawColor(252, 165, 165);
        doc.rect(margin + 10, currentY, pageWidth - 2 * margin - 20, 40, 'S');
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(185, 28, 28);
        doc.text('No fence sections configured', margin + 20, currentY + 25);
        currentY += 60;
      }
      
      // Reset text color
      doc.setTextColor(0, 0, 0);
      
      // Add footer
      currentY = doc.internal.pageSize.getHeight() - 30;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('This quote is valid for 30 days. All prices include GST.', margin, currentY);
      doc.text('Quality Assurance: Highest-quality materials, backed by a 10-year manufacturer warranty. A local company here to help every step of the way.', margin, currentY + 10);
      
      // Save the PDF
      const fileName = `pool-fencing-quote-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      alert('Error generating PDF. Please try again.');
    }
  }

  // Helper function to extract fence sections data for PDF
  getFenceSectionsData() {
    try {
      const sections = [];
      
      // Get fence sections summary from current state
      if (this.state.sections && Object.keys(this.state.sections).length > 0) {
        Object.entries(this.state.sections).forEach(([sectionNum, section]) => {
          if (section && section.length) {
            const sectionData = {
              number: sectionNum,
              length: section.length,
              details: []
            };
            
            if (section.hasGate) {
              sectionData.details.push(`Gate: ${1000}mm`);
            }
            
            if (section.hasRaked) {
              sectionData.details.push('Raked panel included');
            }
            
            if (section.hasClamp) {
              sectionData.details.push('Wall clamp included');
            }
            
            sections.push(sectionData);
          }
        });
      }
      
      return sections;
      
    } catch (error) {
      return [];
    }
  }

  // Helper function to extract structured quote breakdown data for PDF
  getQuoteBreakdownData() {
    try {
      const categories = [];
      
      // Try to get from existing quote breakdown first
      if (this.state.quoteBreakdown && this.state.quoteBreakdown.length > 0) {
        // Group items by category
        const groupedItems = {};
        
        this.state.quoteBreakdown.forEach(item => {
          let category = 'Other';
          
          // Categorize items based on description
          if (item.description.toLowerCase().includes('mesh sheet') || 
              item.description.toLowerCase().includes('panel')) {
            category = 'Panel Kits';
          } else if (item.description.toLowerCase().includes('spigot') || 
                     item.description.toLowerCase().includes('hardware')) {
            category = 'Spigots & Hardware';
          } else if (item.description.toLowerCase().includes('gate')) {
            category = 'Gates & Hardware';
          } else if (item.description.toLowerCase().includes('clamp')) {
            category = 'Clamps & Accessories';
          }
          
          if (!groupedItems[category]) {
            groupedItems[category] = {
              name: category,
              items: [],
              total: 0
            };
          }
          
          groupedItems[category].items.push({
            description: item.description,
            price: item.formattedPrice || `$${(item.price || 0).toFixed(2)}`
          });
          
          groupedItems[category].total += item.price || 0;
        });
        
        // Convert to array and format totals
        Object.values(groupedItems).forEach(category => {
          category.total = `$${category.total.toFixed(2)}`;
          categories.push(category);
        });
        
      } else {
        // Generate basic breakdown from sections if no existing breakdown
        let totalPanels = 0;
        let totalSpigots = 0;
        let gates = 0;
        let rakedPanels = 0;
        let clamps = 0;
        
        if (this.state.sections) {
          Object.values(this.state.sections).forEach(section => {
            if (section && section.length) {
              const calculation = this.calculateOptimalPanels(section);
              if (calculation) {
                totalPanels += calculation.numPanels || 0;
                totalSpigots += (calculation.numPanels || 0) * 2; // Assume 2 spigots per panel
              }
              if (section.hasGate) gates++;
              if (section.hasRaked) rakedPanels++;
              if (section.hasClamp) clamps++;
            }
          });
        }
        
        // Create Mesh Sheets category
        if (totalPanels > 0) {
          categories.push({
            name: 'Panel Kits',
            items: [{
              description: `1450mm Mesh Sheet × ${totalPanels}`,
              price: '$99.18 each'
            }],
            total: `$${(totalPanels * 99.18).toFixed(2)}`
          });
        }
        
        // Create Spigots & Hardware category
        if (totalSpigots > 0) {
          const spigotShape = document.getElementById('spigot-shape')?.value || 'Square';
          categories.push({
            name: 'Spigots & Hardware',
            items: [{
              description: `Spigot (${spigotShape}) × ${totalSpigots}`,
              price: '$46.08 each'
            }],
            total: `$${(totalSpigots * 46.08).toFixed(2)}`
          });
        }
        
        // Add other categories if needed
        if (gates > 0 || rakedPanels > 0 || clamps > 0) {
          const otherItems = [];
          if (gates > 0) otherItems.push({ description: `Gates × ${gates}`, price: 'Included' });
          if (rakedPanels > 0) otherItems.push({ description: `Raked Panels × ${rakedPanels}`, price: 'Quote on request' });
          if (clamps > 0) otherItems.push({ description: `Wall Clamps × ${clamps}`, price: 'Quote on request' });
          
          if (otherItems.length > 0) {
            categories.push({
              name: 'Additional Items',
              items: otherItems,
              total: 'Included'
            });
          }
        }
      }
      
      return categories;
      
    } catch (error) {
      return [];
    }
  }

  // Helper function to extract quote breakdown data from saved HTML
  getQuoteBreakdownDataFromSavedHTML() {
    try {
      const categories = [];
      
      if (!this.savedQuoteBreakdownHTML) {
        console.log('PDF: No saved HTML, falling back to DOM extraction');
        return this.getQuoteBreakdownDataFromDOM();
      }
      
      // Create a temporary DOM parser to parse the saved HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${this.savedQuoteBreakdownHTML}</div>`, 'text/html');
      const container = doc.body.firstChild;
      
      console.log('PDF: Parsing saved HTML breakdown...'); // Debug
      const categoryElements = container.querySelectorAll('.breakdown-category');
      console.log('PDF: Found', categoryElements.length, 'categories in saved HTML'); // Debug
      
      categoryElements.forEach((categoryEl, index) => {
        const categoryHeader = categoryEl.querySelector('.category-header h5');
        const categoryTotal = categoryEl.querySelector('.category-header span');
        const categoryItems = categoryEl.querySelectorAll('.breakdown-item');
        
        console.log(`PDF: Category ${index}:`, categoryHeader?.textContent, 'Items:', categoryItems.length); // Debug
        
        if (categoryHeader) {
          const category = {
            name: categoryHeader.textContent.trim(),
            total: categoryTotal ? categoryTotal.textContent.trim() : '$0.00',
            items: []
          };
          
          categoryItems.forEach((itemEl, itemIndex) => {
            const itemName = itemEl.querySelector('.item-name');
            const itemQuantity = itemEl.querySelector('.item-quantity');
            const itemPricing = itemEl.querySelector('.item-pricing');
            
            if (itemName) {
              const name = itemName.textContent.trim();
              const quantity = itemQuantity ? itemQuantity.textContent.trim() : '';
              const description = name + (quantity ? ' ' + quantity : '');
              
              // Parse the pricing structure from the HTML
              let unitPrice = '';
              let totalPrice = '';
              
              if (itemPricing) {
                // Look for pricing divs within item-pricing
                const unitPriceDiv = itemPricing.querySelector('div:first-child');
                const totalPriceDiv = itemPricing.querySelector('div:last-child');
                
                if (unitPriceDiv && totalPriceDiv) {
                  unitPrice = unitPriceDiv.textContent.trim();
                  totalPrice = totalPriceDiv.textContent.trim();
                } else {
                  // Fallback to text content
                  const pricingText = itemPricing.textContent.trim();
                  if (pricingText.includes('each')) {
                    const lines = pricingText.split('\n').filter(line => line.trim());
                    unitPrice = lines.find(line => line.includes('each')) || '';
                    totalPrice = lines.find(line => line.includes('$') && !line.includes('each')) || pricingText;
                  } else {
                    totalPrice = pricingText;
                  }
                }
              }
              
              console.log(`PDF: Item ${itemIndex}:`, description, 'Unit:', unitPrice, 'Total:', totalPrice); // Debug
              
              category.items.push({
                description: description,
                unitPrice: unitPrice,
                totalPrice: totalPrice,
                price: totalPrice
              });
            }
          });
          
          categories.push(category);
        }
      });
      
      // Fallback to DOM if parsing saved HTML fails
      if (categories.length === 0) {
        console.log('PDF: Saved HTML parsing failed, falling back to DOM');
        return this.getQuoteBreakdownDataFromDOM();
      }
      
      console.log('PDF: Successfully parsed', categories.length, 'categories from saved HTML');
      return categories;
      
    } catch (error) {
      console.log('PDF: Error parsing saved HTML:', error);
      // Ultimate fallback
      return this.getQuoteBreakdownDataFromDOM();
    }
  }

  // Helper function to extract quote breakdown data from DOM (for PDF styling)
  getQuoteBreakdownDataFromDOM() {
    try {
      const categories = [];
      
      // Primary: Try to get from current state breakdown if available
      if (this.state.quoteBreakdown && this.state.quoteBreakdown.length > 0) {
        const groupedItems = {};
        
        this.state.quoteBreakdown.forEach(item => {
          let categoryName = 'Other';
          
          // Categorize items based on description
          if (item.description.toLowerCase().includes('mesh sheet') || 
              item.description.toLowerCase().includes('panel')) {
            categoryName = 'Panel Kits';
          } else if (item.description.toLowerCase().includes('spigot') || 
                     item.description.toLowerCase().includes('hardware')) {
            categoryName = 'Spigots & Hardware';
          } else if (item.description.toLowerCase().includes('gate')) {
            categoryName = 'Gate Components';
          } else if (item.description.toLowerCase().includes('clamp')) {
            categoryName = 'Clamps & Accessories';
          }
          
          if (!groupedItems[categoryName]) {
            groupedItems[categoryName] = {
              name: categoryName,
              items: [],
              total: 0
            };
          }
          
          // Format item description and pricing
          const quantity = item.quantity || 1;
          const unitPrice = item.price ? (item.price / quantity) : 0;
          const formattedUnitPrice = `$${unitPrice.toFixed(2)} each`;
          const formattedTotalPrice = item.formattedPrice || `$${(item.price || 0).toFixed(2)}`;
          
          groupedItems[categoryName].items.push({
            description: `${item.description} × ${quantity}`,
            unitPrice: formattedUnitPrice,
            totalPrice: formattedTotalPrice,
            price: formattedTotalPrice
          });
          
          groupedItems[categoryName].total += item.price || 0;
        });
        
        // Convert to array and format totals
        Object.values(groupedItems).forEach(category => {
          category.total = `$${category.total.toFixed(2)}`;
          categories.push(category);
        });
        
        return categories;
      }
      
      // Secondary: Try DOM extraction with improved parsing
      const breakdownContainer = document.getElementById('breakdown-content');
      if (breakdownContainer) {
        console.log('PDF: Found breakdown container, parsing...'); // Debug
        const categoryElements = breakdownContainer.querySelectorAll('.breakdown-category');
        console.log('PDF: Found', categoryElements.length, 'categories'); // Debug
        
        categoryElements.forEach((categoryEl, index) => {
          const categoryHeader = categoryEl.querySelector('.category-header h5');
          const categoryTotal = categoryEl.querySelector('.category-header span');
          const categoryItems = categoryEl.querySelectorAll('.breakdown-item');
          
          console.log(`PDF: Category ${index}:`, categoryHeader?.textContent, 'Items:', categoryItems.length); // Debug
          
          if (categoryHeader) {
            const category = {
              name: categoryHeader.textContent.trim(),
              total: categoryTotal ? categoryTotal.textContent.trim() : '$0.00',
              items: []
            };
            
            categoryItems.forEach((itemEl, itemIndex) => {
              const itemName = itemEl.querySelector('.item-name');
              const itemQuantity = itemEl.querySelector('.item-quantity');
              const itemPricing = itemEl.querySelector('.item-pricing');
              
              if (itemName) {
                const name = itemName.textContent.trim();
                const quantity = itemQuantity ? itemQuantity.textContent.trim() : '';
                const description = name + (quantity ? ' ' + quantity : '');
                
                // Parse the pricing structure from the HTML
                let unitPrice = '';
                let totalPrice = '';
                
                if (itemPricing) {
                  // Look for pricing divs within item-pricing
                  const unitPriceDiv = itemPricing.querySelector('div:first-child');
                  const totalPriceDiv = itemPricing.querySelector('div:last-child');
                  
                  if (unitPriceDiv && totalPriceDiv) {
                    unitPrice = unitPriceDiv.textContent.trim();
                    totalPrice = totalPriceDiv.textContent.trim();
                  } else {
                    // Fallback to text content
                    const pricingText = itemPricing.textContent.trim();
                    if (pricingText.includes('each')) {
                      const lines = pricingText.split('\n').filter(line => line.trim());
                      unitPrice = lines.find(line => line.includes('each')) || '';
                      totalPrice = lines.find(line => line.includes('$') && !line.includes('each')) || pricingText;
                    } else {
                      totalPrice = pricingText;
                    }
                  }
                }
                
                console.log(`PDF: Item ${itemIndex}:`, description, 'Unit:', unitPrice, 'Total:', totalPrice); // Debug
                
                category.items.push({
                  description: description,
                  unitPrice: unitPrice,
                  totalPrice: totalPrice,
                  price: totalPrice
                });
              }
            });
            
            categories.push(category);
          }
        });
      }
      
      // Fallback to generating from state if no DOM data
      if (categories.length === 0) {
        return this.getQuoteBreakdownData();
      }
      
      return categories;
      
    } catch (error) {
      // Ultimate fallback
      return this.getQuoteBreakdownData();
    }
  }

  // Helper function to extract fence sections data from DOM (for PDF styling) 
  getFenceSectionsDataFromDOM() {
    try {
      const sections = [];
      
      // Try multiple selectors to find fence section data
      let sectionCards = document.querySelectorAll('[id^="section-"][id$="-card"]');
      
      // Alternative: Look for accordion content with section data
      if (sectionCards.length === 0) {
        sectionCards = document.querySelectorAll('#fence-sections-cards .visual-preview, .all-sections-preview');
      }
      
      // Another alternative: Check the fence sections details accordion
      if (sectionCards.length === 0) {
        const fenceSectionsContainer = document.querySelector('#fence-sections-cards');
        if (fenceSectionsContainer) {
          // Look for any element that might contain section info
          const possibleSections = fenceSectionsContainer.querySelectorAll('div[data-section], .section-preview, .preview-placeholder');
          sectionCards = possibleSections;
        }
      }
      
      // Try to extract from current calculator state as primary source
      if (this.state && this.state.sections && Object.keys(this.state.sections).length > 0) {
        Object.entries(this.state.sections).forEach(([sectionNum, section]) => {
          if (section && section.length) {
            const panels = [];
            
            // Get optimized config for this section
            const optimizedConfig = this.state.optimizedConfigs?.[sectionNum];
            const calculation = optimizedConfig || this.calculateOptimalPanels(section);
            
            if (calculation) {
              // Add regular panels with per-panel sizes
              const panelWidthsArr = calculation.panelWidths || Array(calculation.regularPanels || 0).fill(calculation.panelWidth || 1450);
              for (let i = 0; i < (calculation.regularPanels || 0); i++) {
                panels.push({
                  size: (panelWidthsArr[i] || calculation.panelWidth || 1450).toString(),
                  type: 'regular'
                });
              }

              // Add gate panels
              if (section.hasGate && section.gateLength) {
                panels.push({
                  size: section.gateLength.toString(),
                  type: 'gate'
                });
              }

              // Add raked panels
              if (section.hasRaked) {
                panels.push({
                  size: String(CONFIG.DEFAULTS.RAKED_PANEL_WIDTH),
                  type: 'raked'
                });
              }
            }

            // Build panel info string (variable or uniform)
            let panelInfo;
            if (calculation?.panelWidths && new Set(calculation.panelWidths).size > 1) {
              const groups = {};
              calculation.panelWidths.forEach(w => { groups[w] = (groups[w] || 0) + 1; });
              panelInfo = Object.entries(groups).map(([s, q]) => `${q}x${s}mm`).join(', ');
            } else {
              const panelCount = panels.length;
              panelInfo = `${panelCount} x ${calculation?.panelWidth || 1450}mm`;
            }

            // Build gap info string (variable or uniform)
            let gapInfo;
            if (calculation?.gapSpacings && new Set(calculation.gapSpacings).size > 1) {
              const groups = {};
              calculation.gapSpacings.forEach(g => { groups[g] = (groups[g] || 0) + 1; });
              gapInfo = Object.entries(groups).map(([s, q]) => `${q}x${s}mm`).join(', ');
            } else {
              gapInfo = (calculation?.gapSpacing || section.gapSpacing || '25') + 'mm';
            }

            sections.push({
              number: parseInt(sectionNum),
              length: section.length + 'mm',
              panelInfo: panelInfo,
              gap: gapInfo,
              panels: panels
            });
          }
        });
      }
      
      // If we still don't have sections, try DOM extraction as fallback
      if (sections.length === 0 && sectionCards.length > 0) {
        sectionCards.forEach((card, index) => {
          const sectionNum = index + 1;
          
          // Try to extract basic info from various possible structures
          const headerText = card.querySelector('h4, h3, .section-title')?.textContent || '';
          const detailsText = card.querySelector('p, .section-details')?.textContent || '';
          
          // Extract length from header or details
          const lengthMatch = (headerText + ' ' + detailsText).match(/(\d+)mm/);
          const length = lengthMatch ? lengthMatch[1] + 'mm' : `${4000 + (index * 1000)}mm`; // Fallback
          
          sections.push({
            number: sectionNum,
            length: length,
            panelInfo: '3 x 1450mm', // Default
            gap: '25mm',
            panels: [
              { size: '1450', type: 'regular' },
              { size: '1450', type: 'regular' },
              { size: '1450', type: 'regular' }
            ]
          });
        });
      }
      
      // Ultimate fallback if no sections found
      if (sections.length === 0) {
        // Create default sections based on common configurations
        for (let i = 1; i <= 4; i++) {
          sections.push({
            number: i,
            length: `${4000 + (i * 1000)}mm`,
            panelInfo: '3 x 1450mm',
            gap: '25mm',
            panels: [
              { size: '1450', type: 'regular' },
              { size: '1450', type: 'regular' },
              { size: '1450', type: 'regular' }
            ]
          });
        }
      }
      
      return sections;
      
    } catch (error) {
      // Emergency fallback
      return [
        {
          number: 1,
          length: '4444mm',
          panelInfo: '3 x 1450mm',
          gap: '31mm',
          panels: [
            { size: '1450', type: 'regular' },
            { size: '1450', type: 'regular' },
            { size: '1450', type: 'regular' }
          ]
        }
      ];
    }
  }

  emailQuote() {
    const email = prompt('Enter email address to send quote:');
    if (email) {
      alert(`Quote would be emailed to: ${email}`);
    }
  }

  // Add to cart
  // ── Derive cart items from quote breakdown (single source of truth) ─────────
  // Physical lines (variantId != null) → direct map, quantities merged by variantId.
  // Rate/surcharge/delivery lines (variantId == null) → bundled into one line
  // using bladeQuoteProduct if configured, otherwise skipped (physical items still added).
  _deriveCartItemsFromBreakdown() {
    const breakdown = this.state.quoteBreakdown || [];
    const skuMap = this.shopifyData.skuVariantMap || {};
    console.log('[Blade ATC] skuVariantMap:', JSON.stringify(skuMap));
    console.log('[Blade ATC] quoteBreakdown:', JSON.stringify(breakdown.map(i => ({cat: i.category, item: i.item, vid: i.variantId, sku: i.sku, qty: i.quantity}))));
    const merged = new Map(); // variantId → { variantId, quantity, price, title }
    const unresolved = []; // breakdown items that failed every resolution tier

    // Physical items — merge duplicates by variantId, skip nulls and glass products
    breakdown.forEach(item => {
      // Spigots are a frameless glass product — never cart items for blade
      if (item.category === 'Spigots & Hardware') return;

      // Resolve variantId: use direct id if real Shopify ID, else look up by SKU.
      // Also force skuMap lookup when variantId is a product-level ID (not a variant ID):
      // product IDs and variant IDs are both large numbers, so we always prefer the SKU
      // map when a SKU is present — it guarantees a variant ID, not a product ID.
      let variantId = item.variantId;
      const isSkuString = item.sku && isNaN(Number(item.sku)); // e.g. "SHF-90L", "SL-50K"
      const isFakeId = variantId == null || Number(variantId) < 1_000_000 || isSkuString;
      if (isFakeId && item.sku) {
        // 1. Try skuVariantMap (covers products whose Shopify variant.sku matches item.sku)
        variantId = skuMap[String(item.sku)] || (window.CF_VARIANT && window.CF_VARIANT(item.sku)) || null;

        // 2. If still null: check bladeHinges / bladeLatches / bladeGateKits directly.
        //    These arrays store our canonical SKU strings (SHF-90L, SL-50K etc.) mapped
        //    to variants[0].id from the blade collection — the actual Shopify variant ID.
        //    This covers hinge/latch products whose Shopify variant.sku may be blank or differ.
        if (variantId == null) {
          const hwArrays = [
            ...(this.shopifyData.products?.bladeHinges    || []),
            ...(this.shopifyData.products?.bladeLatches   || []),
            ...(this.shopifyData.products?.bladeGateKits  || [])
          ];
          const hwEntry = hwArrays.find(e => e.sku === String(item.sku));
          if (hwEntry?.id && Number(hwEntry.id) > 1_000_000) variantId = hwEntry.id;
        }
      }
      if (variantId == null || Number(variantId) < 1_000_000) {
        // Genuinely unresolved — could be a true rate/service line (no SKU by
        // design) or a physical item whose lookup failed. Either way, this is
        // what the fallback bundle below should be priced from.
        unresolved.push(item);
        return;
      }

      const key = String(variantId);
      if (merged.has(key)) {
        merged.get(key).quantity += item.quantity;
      } else {
        merged.set(key, {
          variantId: variantId,
          quantity: item.quantity,
          price: item.unitPrice,
          title: item.item
        });
      }
    });

    // Rate/surcharge/delivery bundle → one line item covering ONLY the items
    // that failed to resolve above. Uses bladeQuoteProduct variantId.
    if (unresolved.length > 0) {
      const quoteProduct = this.shopifyData.products?.bladeQuoteProduct;
      // bladeQuoteProduct has no SKU so look up by title match or use direct variantId
      const bundleVariantId = (quoteProduct?.variantId && Number(quoteProduct.variantId) > 1_000_000)
        ? quoteProduct.variantId : null;
      if (bundleVariantId) {
        const bundleTotal = unresolved.reduce((sum, i) => sum + (Number(i.totalPrice) || 0), 0);
        const bundleLabel = unresolved
          .map(i => `${i.item} ×${i.quantity} = $${i.totalPrice.toFixed(2)}`)
          .join(' | ');
        merged.set('__bundle__', {
          variantId: bundleVariantId,
          quantity: 1,
          price: bundleTotal,
          title: quoteProduct.title || 'Blade Fence Quote',
          _bundleSummary: bundleLabel
        });
      }
      // If no bladeQuoteProduct configured: physical items still go to cart,
      // unresolved lines are omitted. Cart total won't match quote but won't block ATC.
    }

    // Split line items exceeding Shopify's per-line max of 50 into multiple lines
    const cartItems = [];
    const MAX_QTY = 50;
    Array.from(merged.values()).forEach(item => {
      if (item.quantity <= MAX_QTY) {
        cartItems.push(item);
      } else {
        let remaining = item.quantity;
        while (remaining > 0) {
          const chunk = Math.min(remaining, MAX_QTY);
          cartItems.push({ ...item, quantity: chunk });
          remaining -= chunk;
        }
      }
    });

    this.state.cartItems = cartItems;
  }

  // Resolves a section's actual panel layout (type/width per panel + gaps) —
  // shared by proceedToQuotePage() and addSiteInspectionToCart() so both
  // checkout paths send quote-review.js real panels[] instead of just
  // section metadata (length/hasGate/hasRaked with no panel breakdown).
  // Same resolution updateAddComponentsPreview()/updateFinalSectionsPreview()
  // use for the on-page Section Details previews.
  buildSectionPanelData(s, sNum) {
    let calculation = this.state.optimizedConfigs[sNum];
    if (!calculation) {
      const prefPanel = parseInt(s.panelLength) || parseInt(this.getDefaultPanelLength(s.fenceHeight));
      const prefGap   = parseInt(s.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
      calculation = this.peterCalculateLayout(s, prefPanel, prefGap) || this.calculateOptimalPanels(s);
    }

    let panels = [];
    let startGap = 0;
    let trailingGaps = [];
    if (calculation && calculation.numPanels > 0) {
      // FIX: quote-review must show EXACTLY what the calculator's own Section
      // Details / Final Preview displayed — not a parallel recomputation that
      // can drift from it. renderSectionPreview() stores its finalPanels into
      // section.renderedPanels every time it runs, and the Review & Checkout
      // tab renders every section through it (Section Details) before the
      // DIY/S&I buttons are even clickable — so by checkout time this is
      // guaranteed to be the literal data behind what's on screen. Prefer it;
      // only fall back to recomputing (computeModeAFinalPanels) if a section
      // was somehow never rendered (e.g. Section Details tab never viewed).
      if (s.customPanelOrder && s.customPanelOrder.length > 0) {
        panels = this.reconstructPanelsFromCustomOrder(s, calculation);
      } else if (s.renderedPanels && s.renderedPanels.length > 0) {
        panels = s.renderedPanels;
      } else {
        panels = this.computeModeAFinalPanels(s, sNum);
      }
      // Hinge is a hidden sizing-only entry — renderSectionPreview's own on-screen
      // diagram excludes it (visiblePanels = finalPanels.filter(p => !p.isHinge)),
      // but finalPanels/renderedPanels keep it for width-accounting purposes.
      // Strip it here so quote-review never shows a panel the customer didn't
      // actually see — same convention PerfMesh's addToCart already applies.
      panels = panels.filter(p => p.type !== 'hinge');
      startGap     = this.buildStartGap(calculation, panels);
      trailingGaps = this.buildTrailingGaps(panels, calculation);
    }

    return {
      panels, startGap, trailingGaps,
      gateSide:  s.gateSide  || 'right',
      rakedSide: s.rakedSide || 'left',
    };
  }

  // ── Mode A panel data for checkout (no DOM) ─────────────────────────────
  // Pure-data duplicate of renderSectionPreview's Mode A panel-assembly block
  // (calculateJobCutPlan → gate-offset absorption → hinge-side split rule →
  // finalPanels). Deliberately duplicated rather than refactoring
  // renderSectionPreview itself, so the live preview rendering path is
  // untouched. If Peter changes the panel-assembly rules, update BOTH this
  // function and the corresponding block in renderSectionPreview.
  computeModeAFinalPanels(section, sectionNum) {
    // Self-heal stale panelLength — mirrors renderSectionPreview so both paths agree.
    const _correctSheet_cmp = this.getPanelLengthForHeight(section.fenceHeight);
    if (String(section.panelLength) !== String(_correctSheet_cmp)) {
      section.panelLength = _correctSheet_cmp;
    }
    const post_width     = parseInt(section.gapSpacing)  || CONFIG.DEFAULTS.GAP_SPACING;
    const _rawSheet_prev = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
    const max_sheet      = _rawSheet_prev;

    const _cachedCalc    = this.state.optimizedConfigs[sectionNum];
    const _expectedSheet = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
    // Bust stale cache when panelLength has changed — mirrors renderSectionPreview.
    // Even mode stores the even PANEL size in panelWidth (not the sheet size), so the
    // sheet-mismatch bust must skip even mode — otherwise it wrongly deletes the valid
    // even config on every render and the preview + quote break for nearly every size.
    if (_cachedCalc && !_cachedCalc.isEvenMode && _cachedCalc.panelWidth && _cachedCalc.panelWidth !== _expectedSheet) {
      delete this.state.optimizedConfigs[sectionNum];
    }
    let cutPlan;
    if (_cachedCalc && _cachedCalc.valid && (_cachedCalc.isEvenMode || !_cachedCalc.panelWidth || _cachedCalc.panelWidth === _expectedSheet)) {
      const _pw = _cachedCalc.panelWidths || [];
      const _maxSheet = parseInt(section.panelLength) || parseInt(this.getDefaultPanelLength(section.fenceHeight));
      cutPlan = {
        valid: true,
        isEvenMode: !!_cachedCalc.isEvenMode,
        panels: _pw.map(w => ({
          footprint: w,
          sheet: _cachedCalc.isEvenMode ? _maxSheet : w,
          is_cut: false,
          isEven: !!_cachedCalc.isEvenMode,
          kit: _cachedCalc.isEvenMode ? _maxSheet + 'mm' : (w === _maxSheet ? _maxSheet + 'mm' : 'filler')
        }))
      };
    } else {
      const jobPlan = this.calculateJobCutPlan();
      cutPlan = jobPlan[sectionNum];
    }

    // 2026-08-30 (Pete): a gate-only run — gate between its two posts, no panels — is a legitimate
    // section for the side of a house. cutPlan already returns { valid:true, panels:[] } for it, so
    // the only thing rejecting it was this length check. Zero panels is an error ONLY when there is
    // also no gate; with a gate there is still a fence to draw.
    if (!cutPlan || !cutPlan.valid || !cutPlan.panels || (cutPlan.panels.length === 0 && !section.hasGate && !section.hasRaked)) {
      return [];
    }

    const _isEvenMode = !!(cutPlan.isEvenMode);
    let regularStripPanels = cutPlan.panels
      .filter(p => !p.is_gate && !p.is_hinge)
      .map((p) => ({
        type:         'panel',
        width:        _isEvenMode ? p.footprint : p.sheet,
        nominalWidth: _isEvenMode ? p.footprint : (p.source_sheet || p.sheet),
        isCut:        false,
        isEven:       !!p.isEven,
        footprint:    p.footprint,
        source_sheet: p.source_sheet || null,
        off_cut:      p.off_cut || 0,
        kit:          p.isEven ? `${p.sheet}mm` : (p.sheet === (p.source_sheet || p.sheet) ? `${p.sheet}mm` : 'filler')
      }));

    // Gate offset: shift filler width by gateOffsetMm — mirrors renderSectionPreview.
    const gateOffsetMm = (section.hasGate && section.gateOffsetMm) ? section.gateOffsetMm : 0;
    if (!_isEvenMode && gateOffsetMm > 0 && regularStripPanels.length > 0) {
      let fillerIdx = 0;
      let fillerMin = regularStripPanels[0].width;
      for (let i = 1; i < regularStripPanels.length; i++) {
        if (regularStripPanels[i].width < fillerMin) {
          fillerMin = regularStripPanels[i].width;
          fillerIdx = i;
        }
      }
      const gatePos = section.gatePosition !== undefined ? parseInt(section.gatePosition) : 0;
      const fillerIsRight = fillerIdx >= gatePos;
      const newFillerW = regularStripPanels[fillerIdx].width - gateOffsetMm;
      if (newFillerW >= 0) {
        regularStripPanels[fillerIdx].width     = newFillerW;
        regularStripPanels[fillerIdx].footprint = newFillerW;
        const donorIdx = fillerIsRight
          ? regularStripPanels.slice(0, gatePos).reduceRight((acc, p, i) => acc === -1 && p.width > newFillerW ? i : acc, -1)
          : regularStripPanels.slice(gatePos).findIndex(p => p.width > newFillerW) + gatePos;
        if (donorIdx >= 0 && donorIdx < regularStripPanels.length) {
          regularStripPanels[donorIdx].width     += gateOffsetMm;
          regularStripPanels[donorIdx].footprint += gateOffsetMm;
        }
      }
    }

    // Hinge-side split + remainder lock (Peter's rule v6) — mirrors renderSectionPreview.
    if (!_isEvenMode && section.hasGate) {
      const isW2G_split    = section.gateHingeType === 'W2G-REMOVED-2026-08-30';
      const gateSide_split = isW2G_split
        ? (section.wallGateEnd === 'left' ? 'right' : 'left')
        : (section.gateSide || 'left');

      const regCountR     = regularStripPanels.length;
      const remainderIdx  = regCountR - 1;
      const MIN_REMAINDER = 500;
      const SPLIT_OFFCUT  = 500;

      const existingRem = (remainderIdx >= 0) ? regularStripPanels[remainderIdx].width : 0;
      const needsSplit  = existingRem < MIN_REMAINDER;

      let splitIdx = -1;
      if (needsSplit && !isW2G_split) {
        if (gateSide_split === 'left') {
          for (let i = remainderIdx - 1; i >= 0; i--) {
            if (regularStripPanels[i].width > SPLIT_OFFCUT) { splitIdx = i; break; }
          }
        } else {
          for (let i = 0; i < remainderIdx; i++) {
            if (regularStripPanels[i].width > SPLIT_OFFCUT) { splitIdx = i; break; }
          }
        }
      }

      const splitPanel = splitIdx >= 0 ? regularStripPanels[splitIdx] : null;
      const splitKeep  = splitPanel ? (splitPanel.width - SPLIT_OFFCUT) : 0;
      const canSplit   = splitIdx >= 0 && splitKeep >= MIN_REMAINDER;

      if (canSplit) {
        regularStripPanels[splitIdx] = {
          ...regularStripPanels[splitIdx],
          width: splitKeep, nominalWidth: splitKeep, footprint: splitKeep,
          isCut: true, isHingeCut: true
        };
        const newRemWidth = existingRem + SPLIT_OFFCUT;
        regularStripPanels[remainderIdx] = {
          ...regularStripPanels[remainderIdx],
          width: newRemWidth, nominalWidth: newRemWidth, footprint: newRemWidth,
          isRemainder: true
        };
      } else if (remainderIdx >= 0) {
        regularStripPanels[remainderIdx] = {
          ...regularStripPanels[remainderIdx],
          isRemainder: true
        };
      }
    }

    // Gate / hinge panel entries
    const hasGate       = !!section.hasGate;
    const gateHingeType = section.gateHingeType || 'glass-to-glass';
    const isW2G          = gateHingeType === 'W2G-REMOVED-2026-08-30';
    const wallGateEnd    = isW2G ? (section.wallGateEnd || null) : null;
    const gateSide       = isW2G
      ? (wallGateEnd === 'left' ? 'right' : 'left')
      : (section.gateSide || 'right');
    const isW2GLeft  = isW2G && wallGateEnd === 'left';
    const isW2GRight = isW2G && wallGateEnd === 'right';

    const SHEET_MAP_LOCAL = { 1000: 1000, 1500: 1500, 2000: 1960, 2400: 2360, 2500: 2500, 3000: 2960 };
    const actual_sheet_local = SHEET_MAP_LOCAL[max_sheet] || max_sheet;

    const gateEntry = hasGate ? {
      type: 'gate', width: parseInt(section.gateLength) || this.getDefaultGateLength(), isGate: true,
      gateSide, gateHingeType,
      flipped: isW2G ? (wallGateEnd === 'right') : (gateSide === 'right')
    } : null;

    const hingeEntry = (hasGate && !isW2G) ? {
      type: 'hinge', width: actual_sheet_local, isHinge: true
    } : null;

    // Rake
    const hasRaked       = !!section.hasRaked;
    const rakedSide       = section.rakedSide || 'left';
    const retainedHeight  = parseInt(section.retainedHeight) || 300;

    const rakedEntry = hasRaked ? {
      type: rakedSide === 'right' ? 'right-raked' : 'left-raked',
      width: CONFIG.DEFAULTS.RAKED_PANEL_WIDTH, isCut: false, isRaked: true, retainedHeight
    } : null;

    // Gate position and gateAtRakeEnd detection
    const regCount    = regularStripPanels.length;
    const rawGatePos  = section.gatePosition !== undefined ? parseInt(section.gatePosition) : 0;
    let gatePos;
    if (hasGate && (rawGatePos >= 9999 || (section._placeAtMiddle && rawGatePos >= regCount))) {
      gatePos = Math.floor(regCount / 2);
      section._placeAtMiddle = false; // consumed — mirrors renderSectionPreview
    } else {
      gatePos = rawGatePos;
    }

    const gateAtRakeEnd = hasGate && hasRaked && !isW2G && (
      (rakedSide === 'left'  && gatePos <= 0) ||
      (rakedSide === 'right' && gatePos >= regCount)
    );

    if (gateAtRakeEnd && gateEntry) {
      gateEntry.gateSide = rakedSide;
    }

    let minAllowed = 0;
    let maxAllowed = regCount;
    if (isW2GLeft) {
      minAllowed = 0;
      maxAllowed = 0;
    } else if (isW2GRight) {
      minAllowed = regCount;
      maxAllowed = regCount;
    }
    const clampedPos = Math.max(minAllowed, Math.min(gatePos, maxAllowed));

    // Assemble finalPanels
    let finalPanels = [];

    if (!hasGate) {
      if (hasRaked && rakedSide === 'left')  finalPanels.push(rakedEntry);
      finalPanels.push(...regularStripPanels);
      if (hasRaked && rakedSide === 'right') finalPanels.push(rakedEntry);

    } else if (gateAtRakeEnd) {
      if (rakedSide === 'left') {
        finalPanels.push(rakedEntry);
        finalPanels.push(gateEntry);
        if (hingeEntry) finalPanels.push(hingeEntry);
        finalPanels.push(...regularStripPanels);
      } else {
        finalPanels.push(...regularStripPanels);
        if (hingeEntry) finalPanels.push(hingeEntry);
        finalPanels.push(gateEntry);
        finalPanels.push(rakedEntry);
      }

    } else {
      const leftPanels  = regularStripPanels.slice(0, clampedPos);
      const rightPanels = regularStripPanels.slice(clampedPos);

      const gateBlock = [];
      if (gateSide === 'left') {
        gateBlock.push(gateEntry);
        if (!isW2G && hingeEntry) gateBlock.push(hingeEntry);
      } else {
        if (!isW2G && hingeEntry) gateBlock.push(hingeEntry);
        gateBlock.push(gateEntry);
      }

      if (hasRaked && rakedSide === 'left')  finalPanels.push(rakedEntry);
      finalPanels.push(...leftPanels, ...gateBlock, ...rightPanels);
      if (hasRaked && rakedSide === 'right') finalPanels.push(rakedEntry);
    }

    return finalPanels;
  }

  // Serialize quote state to sessionStorage and redirect to quote page
  proceedToQuotePage() {
    // Guard: never push an empty quote to the review page (it would hang/crash there).
    if (!this.hasCompletedSection()) {
      alert('Please complete at least one fence section before adding to cart.');
      return;
    }
    this.updateQuoteDisplay();

    const grandTotal = this.state.quoteBreakdown
      ? this.state.quoteBreakdown.reduce((sum, i) => sum + (i.totalPrice || 0), 0)
      : (this.state.quote || 0);

    const payload = {
      breakdown:   this.state.quoteBreakdown || [],
      grandTotal:  grandTotal,
      pricingType: this.state.pricingType,
      currency:    this.shopifyData?.shop?.currency || 'AUD',
      sections:    Object.entries(this.state.sections || {}).map(([num, s]) => {
        const sNum = parseInt(num);
        return {
          num:        sNum,
          length:     s.length,
          hasGate:    s.hasGate    || false,
          hasRaked:   s.hasRaked   || false,
          gateLength: s.gateLength || null,
          hingeType:  s.hingeType  || null,
          latchType:  s.latchType  || null,
          latchColor: s.latchColor || null,
          ...this.buildSectionPanelData(s, sNum),
        };
      }),
      generatedAt: new Date().toISOString(),
      calcType:    'Blade',
    };

    try {
      let quoteNum = 1;
      try {
        quoteNum = (parseInt(localStorage.getItem('blade_quote_counter') || '0') + 1);
        localStorage.setItem('blade_quote_counter', String(quoteNum));
      } catch {}
      payload.quoteName = `Blade Quote ${quoteNum}`;
      payload.quoteNum  = quoteNum;

      try {
        let quotes = [];
        try { quotes = JSON.parse(localStorage.getItem('blade_quotes') || '[]'); } catch {}
        quotes.push(payload);
        localStorage.setItem('blade_quotes', JSON.stringify(quotes));
        localStorage.setItem('blade_active_quote', String(quoteNum));
      } catch {}

      sessionStorage.setItem('ullc_quote', JSON.stringify(payload));
    } catch (e) {
      console.error('sessionStorage write failed:', e);
      alert('Unable to proceed — please try again.');
      return;
    }

    window.location.href = '/pages/quote-review';
  }

  // Supply & Install flow — saves full quote payload to sessionStorage and
  // routes through /pages/quote-review (same as the DIY flow above) so the
  // customer sees Section Details diagrams and can download the PDF via the
  // Enquire flow there, instead of skipping straight to a contact form.
  addSiteInspectionToCart() {
    // Guard: never push an empty quote to the review page (it would hang/crash there).
    if (!this.hasCompletedSection()) {
      alert('Please complete at least one fence section before booking a site inspection.');
      return;
    }
    const siBtn = document.getElementById('btn-si-enquiry');
    try {
      if (siBtn) { siBtn.disabled = true; siBtn.textContent = 'Redirecting…'; }

      // Snapshot current quote into sessionStorage so quote-review can read it
      this.updateQuoteDisplay();
      const grandTotal = this.state.quote || 0;
      const payload = {
        breakdown:    this.state.quoteBreakdown || [],
        grandTotal:   grandTotal,
        pricingType:  'install',
        currency:     this.shopifyData?.shop?.currency || 'AUD',
        sections:     Object.entries(this.state.sections || {}).map(([num, s]) => {
          const sNum = parseInt(num);
          return {
            num:            sNum,
            length:         s.length,
            fenceHeight:    s.fenceHeight,
            colour:         s.postColor || s.meshColor,
            hasGate:        s.hasGate    || false,
            hasRaked:       s.hasRaked   || false,
            gateLength:     s.gateLength || null,
            hingeType:      s.hingeType  || null,
            latchType:      s.latchType  || null,
            groundPost:     s.groundPost || false,
            retainedHeight: s.retainedHeight || null,
            ...this.buildSectionPanelData(s, sNum),
          };
        }),
        generatedAt:  new Date().toISOString(),
        calcType:     'Blade',
      };

      try {
        let quoteNum = 1;
        try {
          quoteNum = (parseInt(localStorage.getItem('blade_quote_counter') || '0') + 1);
          localStorage.setItem('blade_quote_counter', String(quoteNum));
        } catch {}
        payload.quoteName = `Blade Quote ${quoteNum}`;
        payload.quoteNum  = quoteNum;

        try {
          let quotes = [];
          try { quotes = JSON.parse(localStorage.getItem('blade_quotes') || '[]'); } catch {}
          quotes.push(payload);
          localStorage.setItem('blade_quotes', JSON.stringify(quotes));
          localStorage.setItem('blade_active_quote', String(quoteNum));
        } catch {}

        sessionStorage.setItem('blade_si_quote', JSON.stringify(payload));
        sessionStorage.setItem('ullc_quote',     JSON.stringify(payload));
      } catch (e) { console.warn('sessionStorage write failed:', e); }

      window.location.href = '/pages/quote-review';
    } catch (error) {
      if (siBtn) { siBtn.disabled = false; siBtn.textContent = '🏗️ Book Site Inspection'; }
      alert(`Error: ${error.message}`);
    }
  }

  async addToCart() {
    const addToCartBtn = this.domCache.get('add-to-cart');
    try {
      // Disable button immediately to prevent double-clicks
      if (addToCartBtn) { addToCartBtn.disabled = true; addToCartBtn.textContent = 'Adding…'; }

      // State is already current — no recalc needed
      const validCartItems = (this.state.cartItems || []).filter(item => item.variantId != null);
      if (validCartItems.length === 0) {
        throw new Error('No purchasable items to add to cart. Please check your configuration.');
      }

      // Clear cart then add each line individually — batch fails entirely if
      // any single line hits availability limits (e.g. split qty-50 lines)
      await fetch('/cart/clear.js', { method: 'POST' });

      for (const item of validCartItems) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: item.variantId,
              quantity: item.quantity,
              properties: {
                '_Calculator_Item': 'Blade Fencing Calculator',
                '_Item_Type': item.title || item.item || '',
                '_SKU': item.sku || '',
                '_Unit_Price': `$${(item.unitPrice || 0).toFixed(2)}`,
                '_Total_Price': `$${(item.totalPrice || 0).toFixed(2)}`,
                '_Pricing_Mode': this.state.pricingType === 'install' ? 'Supply & Install' : 'DIY Supply Only',
                '_Created': new Date().toLocaleString(),
                ...(item._bundleSummary ? { '_Quote_Breakdown': item._bundleSummary } : {})
              }
            }]
          })
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.message || err.description || 'Failed to add to cart');
        }
      }

      window.location.href = '/cart';
    } catch (error) {
      if (addToCartBtn) { addToCartBtn.disabled = false; addToCartBtn.textContent = 'Add to Cart'; }
      alert(`Error adding configuration to cart: ${error.message}`);
    }
  }

  // Prepare item properties
  prepareItemProperties(item) {
    const quoteData = this.gatherQuoteData();
    
    return {
      '_Calculator_Item': 'Glass Fencing Calculator',
      '_Configuration_ID': `GFC-${Date.now()}`,
      '_Item_Type': item.title,
      '_Total_Quote': quoteData.quote.toFixed(2),
      '_Created': new Date().toLocaleString(),
      '_Configuration_Summary': this.generateConfigurationSummary()
    };
  }

  // Generate configuration summary
  generateConfigurationSummary() {
    const sections = Object.keys(this.state.sections).length;
    const totalPanels = Object.values(this.state.sections).reduce((sum, section) => {
      if (section.length) {
        const calc = this.calculateOptimalPanels(section);
        return sum + (calc ? calc.numPanels : 0);
      }
      return sum;
    }, 0);
    
    const hasGates = Object.values(this.state.sections).some(s => s.hasGate);
    const hasRaked = Object.values(this.state.sections).some(s => s.hasRaked);
    
    return `${sections} sections, ${totalPanels} panels${hasGates ? ', gates' : ''}${hasRaked ? ', raked' : ''}`;
  }

  // Gather quote data
  gatherQuoteData() {
    const totalCost = this.calculateTotalWithAddons();
    
    return {
      sections: this.state.sections,
      sectionsCount: this.state.sectionsCount,
      components: this.gatherComponentSelections(),
      quote: totalCost,
      timestamp: new Date().toISOString(),
      calculations: this.gatherCalculations(),
      cartItems: this.state.cartItems
    };
  }

  // Calculate total with addons
  calculateTotalWithAddons() {
    let total = this.state.quote;
    
    const addOnElements = document.querySelectorAll('#addon-list input[type="checkbox"]:checked');
    addOnElements.forEach(checkbox => {
      const productId = checkbox.dataset.productId;
      const addOns = this.shopifyData.collections?.addOns || [];
      const product = addOns.find(p => p.id == productId);
      
      if (product) {
        total += PriceHelper.fromCents(product.price);
      }
    });
    
    return total;
  }

  // Gather component selections
  gatherComponentSelections() {
    const components = {
      spigotType: this.domCache.get('spigot-type')?.value,
      spigotShape: this.domCache.get('spigot-shape')?.value,
      spigotFinish: this.domCache.get('spigot-finish')?.value,
      addons: []
    };
    
    const addOnElements = document.querySelectorAll('#addon-list input[type="checkbox"]:checked');
    addOnElements.forEach(checkbox => {
      const productId = checkbox.dataset.productId;
      const addOns = this.shopifyData.collections?.addOns || [];
      const product = addOns.find(p => p.id == productId);
      
      if (product) {
        components.addons.push({
          id: product.id,
          title: product.title,
          handle: product.handle
        });
      }
    });
    
    return components;
  }

  // Gather calculations
  gatherCalculations() {
    const calculations = {};
    
    Object.entries(this.state.sections).forEach(([num, section]) => {
      if (section.length) {
        calculations[num] = this.calculateOptimalPanels(section);
      }
    });
    
    return calculations;
  }

  // Show validation popup
  showValidationPopup(message, onOkCallback = null) {
    const existingPopup = this.domCache.get('validation-popup');
    if (existingPopup) {
      existingPopup.remove();
    }

    const popup = document.createElement('div');
    popup.id = 'validation-popup';
    popup.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.2s ease-out;
    `;

    const popupContent = document.createElement('div');
    popupContent.style.cssText = `
      background: white;
      padding: 24px;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
      max-width: 400px;
      width: 90%;
      text-align: center;
      animation: slideIn 0.3s ease-out;
    `;

    popupContent.innerHTML = `
      <div style="margin-bottom: 20px;">
        <div style="width: 48px; height: 48px; background: #fef2f2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
          <svg width="24" height="24" fill="#dc2626" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <h3 style="color: #dc2626; margin-bottom: 12px; font-size: 1.25rem; font-weight: 600;">Validation Error</h3>
        <p style="color: #374151; font-size: 1rem; line-height: 1.5; margin: 0;">${message}</p>
      </div>
      <button id="validation-ok-btn" style="
        background: #2563eb;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s ease;
      ">OK</button>
    `;

    popup.appendChild(popupContent);
    document.body.appendChild(popup);

    const okButton = popup.querySelector('#validation-ok-btn');
    const closePopup = () => {
      popup.remove();
      if (onOkCallback) {
        onOkCallback();
      }
    };

    okButton.addEventListener('click', closePopup);
    
    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        closePopup();
      }
    });

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closePopup();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    setTimeout(() => okButton.focus(), 100);
  }

  // Update section tab state
  updateSectionTabState(sectionNum) {
    const tab = document.querySelector(`.section-tab:nth-child(${sectionNum})`);
    if (!tab) return;

    const section = this.state.sections[sectionNum];
    
    tab.classList.remove('complete', 'incomplete');
    
    if (section && section.length && parseInt(section.length) >= CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH) {
      tab.classList.add('complete');
      tab.style.background = '#059669';
      tab.style.color = 'white';
      tab.style.borderColor = '#059669';
    } else {
      tab.classList.add('incomplete');
      tab.style.background = '#fef3c7';
      tab.style.color = '#92400e';
      tab.style.borderColor = '#f59e0b';
    }
  }

  // Find first incomplete section
  findFirstIncompleteSection() {
    for (let i = 1; i <= this.state.sectionsCount; i++) {
      if (!this.validateSection(i)) {
        return i;
      }
    }
    
    return null;
  }

  // Find first section without length
  findFirstSectionWithoutLength() {
    for (let i = 1; i <= this.state.sectionsCount; i++) {
      const section = this.state.sections[i];
      
      // Check state first
      let hasValidLength = section && section.length && parseInt(section.length) >= CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH;
      
      // If not in state, check DOM as fallback
      if (!hasValidLength) {
        const lengthInput = document.getElementById(`section-${i}-length`);
        if (lengthInput && lengthInput.value) {
          const inputValue = parseInt(lengthInput.value);
          hasValidLength = inputValue >= CONFIG.CONSTRAINTS.MIN_SECTION_LENGTH;
        }
      }
      
      if (!hasValidLength) {
        return i;
      }
    }
    return null;
  }

  // Utility functions
  // ── Sticky preview helpers ────────────────────────────────────────────
  _setupStickyPreview() {
    // Measure where #configure-sections sits in the viewport so we can set its
    // height to exactly fill the remaining space — making it the scroll container
    // while the preview bar sticks at the top of it.
    const configEl = document.getElementById('configure-sections');
    if (!configEl) return;

    // Only measure when the tab is active (visible); getBoundingClientRect
    // returns zeros for display:none elements.
    if (configEl.offsetParent === null) return;

    // Temporarily scroll to top so measurement is accurate.
    const savedScroll = configEl.scrollTop;
    configEl.scrollTop = 0;

    // Top of #configure-sections relative to viewport.
    const configTop = Math.max(0, Math.round(configEl.getBoundingClientRect().top));

    // Height of the .calculator-navigation bar at the bottom (Back/Next buttons).
    const navEl = document.querySelector('.calculator-navigation');
    const navH = navEl ? Math.round(navEl.getBoundingClientRect().height) : 0;

    // Remaining viewport height for the scroll container.
    const availableH = window.innerHeight - configTop - navH;

    configEl.scrollTop = savedScroll;

    // Set height directly on the element — more reliable than a CSS var approach
    // when the theme CSS may have competing rules on .tab-content.
    // Natural page-flow layout: the PAGE scrolls and the preview floats via CSS
    // sticky. Clear any inline height/overflow so #configure-sections is NOT a
    // fixed-height inner scroll container (which would trap the form + break the
    // floating preview offset).
    configEl.style.height = '';
    configEl.style.overflowY = '';
    configEl.style.overflowX = '';
  }

  _updateStickyPreview() {
    requestAnimationFrame(() => this._setupStickyPreview());
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  showError(elementId, message) {
    const errorElement = this.domCache.get(elementId);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove('hidden');
    }
  }

  hideError(elementId) {
    const errorElement = this.domCache.get(elementId);
    if (errorElement) {
      errorElement.classList.add('hidden');
    }
  }
}

// Tooltip Functionality
window.setupTooltips = function() {
    // Add CSS for tooltips if not already added
    if (!document.querySelector('#tooltip-styles')) {
      const style = document.createElement('style');
      style.id = 'tooltip-styles';
      style.textContent = `
        .tooltip-container:hover .tooltip-text {
          visibility: visible !important;
          opacity: 1 !important;
        }
        
        .tooltip-icon {
          transition: background-color 0.2s ease;
        }
        
        .tooltip-icon:hover {
          background-color: #000 !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Setup hover listeners for tooltip containers
    const tooltipContainers = document.querySelectorAll('.tooltip-container');
    tooltipContainers.forEach(container => {
      const icon = container.querySelector('.tooltip-icon');
      const text = container.querySelector('.tooltip-text');
      
      if (icon && text) {
        // Remove existing listeners to avoid duplicates
        icon.removeEventListener('mouseenter', handleTooltipShow);
        icon.removeEventListener('mouseleave', handleTooltipHide);
        
        // Add hover listeners
        icon.addEventListener('mouseenter', handleTooltipShow);
        icon.addEventListener('mouseleave', handleTooltipHide);
        
        function handleTooltipShow() {
          text.style.visibility = 'visible';
          text.style.opacity = '1';
        }
        
        function handleTooltipHide() {
          text.style.visibility = 'hidden';
          text.style.opacity = '0';
        }
      }
    });
};

// Initialize tooltips when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.setupTooltips);
} else {
  window.setupTooltips();
}

// Hardware Image Magnify Functionality
(function() {
  // Setup click-to-magnify for hardware images
  function setupImageMagnify() {
    const modal = document.getElementById('image-modal');
    const modalImage = document.getElementById('modal-image');
    
    if (!modal || !modalImage) return;
    
    // Add click listeners to all hardware and magnifiable images
    const magnifiableImages = document.querySelectorAll('.hardware-image, .magnifiable');
    
    magnifiableImages.forEach(img => {
      // Remove any existing click listeners to avoid duplicates
      img.removeEventListener('click', handleImageClick);
      
      // Only add click handler if image has a source
      if (img.src && img.src !== '' && !img.src.includes('data:image')) {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', handleImageClick);
      }
    });
    
    function handleImageClick(e) {
      e.stopPropagation();
      
      // Show modal with the clicked image
      modalImage.src = this.src;
      modalImage.alt = this.alt;
      modal.classList.add('active');
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    // Close modal when clicking on it
    modal.addEventListener('click', function() {
      this.classList.remove('active');
      document.body.style.overflow = '';
    });
    
    // Close modal with Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        modal.classList.remove('active');
        document.body.style.overflow = '';
      }
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupImageMagnify);
  } else {
    setupImageMagnify();
  }
  
  // Re-initialize when hardware previews are updated
  const originalUpdateHardwarePreviews = window.glassCalculator?.updateHardwarePreviews;
  if (originalUpdateHardwarePreviews) {
    window.glassCalculator.updateHardwarePreviews = function() {
      originalUpdateHardwarePreviews.call(this);
      setTimeout(setupImageMagnify, 100); // Re-setup after images are updated
    };
  }
})();

// Initialize calculator when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  if (document.querySelector('.calculator-page')) {
    window.glassCalculator = new GlassFencingCalculator();
    window.glassFencingCalculator = window.glassCalculator; // Alias for compatibility
  }

});

// ---------------------------------------------------------------------------
// Lightweight in-browser tests (run from DevTools console)
// Usage: window.runUllcGateTests()
// ---------------------------------------------------------------------------
window.runUllcGateTests = function runUllcGateTests() {
  const calc = window.glassCalculator;
  if (!calc) {
    console.error('[ULLC TESTS] glassCalculator not initialized (open calculator page first).');
    return false;
  }

  const assert = (cond, msg) => {
    if (!cond) throw new Error(msg);
  };

  const summarize = (panels) => panels.map(p => p.type).join(',');

  const baseCalculation = {
    regularPanels: 4,
    hingePanels: 1,
    gatePanels: 1,
    rakedPanels: 0,
    panelWidth: 1750,
    hingeWidth: 1750,
    panelWidths: [1750, 1750, 1750, 1750],
    gapSpacing: 37
  };

  const runCase = (name, configOverrides, calcOverrides, expectations) => {
    const section = {
      hasGate: true,
      hasRaked: false,
      gateLength: 1010, // fixed — always 1010mm
      gateSide: 'right',
      gateHingeType: 'glass-to-glass',
      wallGateEnd: null,
      gatePosition: 9999,
      retainedHeight: '200',
      rakedSide: 'left',
      ...configOverrides
    };

    const panels = calc.calculatePanelsLayout(
      {
        ...section,
        gateWidth: section.gateLength,
        gateFlipped: false
      },
      {
        ...baseCalculation,
        ...calcOverrides
      }
    );

    assert(Array.isArray(panels) && panels.length > 0, `${name}: expected non-empty panels array`);

    if (expectations.noHingePanel) {
      assert(!panels.some(p => p.type === 'hinge-panel'), `${name}: should not include hinge-panel. Got: ${summarize(panels)}`);
    }
    if (expectations.hasHingePanel) {
      assert(panels.some(p => p.type === 'hinge-panel'), `${name}: expected hinge-panel. Got: ${summarize(panels)}`);
    }
    // W2G LEFT: gate is FIRST (directly against left wall), replacement panel is SECOND (latch side, 7mm from gate)
    if (expectations.gateAtStart) {
      assert(panels[0].type === 'gate',
        `${name}: expected gate at index 0 (against left wall). Got: ${summarize(panels)}`);
    }
    // W2G LEFT: replacement panel sits immediately after the gate (index 1)
    if (expectations.replacementAtIndex1) {
      assert(panels[1] && panels[1].type === 'panel' && panels[1].width === (calcOverrides?.hingeWidth || baseCalculation.hingeWidth),
        `${name}: expected replacement panel at index 1 (latch side). Got index 1: ${panels[1]?.type} ${panels[1]?.width}`);
    }
    // W2G RIGHT: gate is LAST (directly against right wall), replacement panel is second-to-last (latch side, 7mm from gate)
    if (expectations.gateAtEnd) {
      const last = panels[panels.length - 1];
      assert(last.type === 'gate',
        `${name}: expected gate at last index (against right wall). Got last: ${last.type}`);
    }
    if (expectations.replacementSecondToLast) {
      const secondLast = panels[panels.length - 2];
      assert(secondLast && secondLast.type === 'panel' && secondLast.width === (calcOverrides?.hingeWidth || baseCalculation.hingeWidth),
        `${name}: expected replacement panel at second-to-last (latch side). Got: ${secondLast?.type} ${secondLast?.width}`);
    }
    if (expectations.gateFlipped !== undefined) {
      const gate = panels.find(p => p.type === 'gate');
      assert(gate, `${name}: expected gate panel`);
      assert(gate.flipped === expectations.gateFlipped, `${name}: expected gate.flipped=${expectations.gateFlipped}, got ${gate.flipped}`);
    }

    return panels;
  };

  const results = [];
  const start = performance.now();

  try {
    // 1) Glass-to-glass left hinge: must include hinge-panel, gate not flipped
    results.push(runCase(
      'glass-to-glass / LHS hinge',
      { gateHingeType: 'glass-to-glass', gateSide: 'left', wallGateEnd: null },
      { hingePanels: 1 },
      { hasHingePanel: true, gateFlipped: false }
    ));

    // 2) Glass-to-glass right hinge: must include hinge-panel, gate flipped
    results.push(runCase(
      'glass-to-glass / RHS hinge',
      { gateHingeType: 'glass-to-glass', gateSide: 'right', wallGateEnd: null },
      { hingePanels: 1 },
      { hasHingePanel: true, gateFlipped: true }
    ));

    // 3) Wall-to-glass LEFT:
    //    Physical layout: [wall]-10mm-[gate]-7mm-[extraPanel]-var-[panels...]
    //    Gate is FIRST (against left wall, hinge bolt in wall).
    //    Extra panel is SECOND (gate latch catches this at 7mm).
    //    Gate faces RIGHT → flipped: false
    results.push(runCase(
      'wall-to-glass / wall-left',
      { gateHingeType: 'wall-to-glass', gateSide: 'left', wallGateEnd: 'left' },
      { hingePanels: 0, regularPanels: 4 },
      { noHingePanel: true, gateAtStart: true, replacementAtIndex1: true, gateFlipped: false }
    ));

    // 4) Wall-to-glass RIGHT:
    //    Physical layout: [panels...]-var-[extraPanel]-7mm-[gate]-10mm-[wall]
    //    Gate is LAST (against right wall, hinge bolt in wall).
    //    Extra panel is SECOND-TO-LAST (gate latch catches this at 7mm).
    //    Gate faces LEFT → flipped: true
    results.push(runCase(
      'wall-to-glass / wall-right',
      { gateHingeType: 'wall-to-glass', gateSide: 'right', wallGateEnd: 'right' },
      { hingePanels: 0, regularPanels: 4 },
      { noHingePanel: true, gateAtEnd: true, replacementSecondToLast: true, gateFlipped: true }
    ));

    const elapsed = Math.round(performance.now() - start);
    console.log(`[ULLC TESTS] PASS (${results.length} cases, ${elapsed}ms)`);
    return true;
  } catch (err) {
    console.error('[ULLC TESTS] FAIL:', err?.message || err);
    return false;
  }
};

// ---------------------------------------------------------------------------
// PerfoMesh solver tests — run from DevTools: window.runPerfoMeshTests()
// Covers Mode B (calculateSpread) and Mode A (calculateCutPlan).
// ---------------------------------------------------------------------------
window.runPerfoMeshTests = function runPerfoMeshTests() {
  const calc = window.glassCalculator;
  if (!calc) {
    console.error('[PERFOMESH TESTS] glassCalculator not initialized.');
    return false;
  }

  const assert = (cond, msg) => { if (!cond) throw new Error(msg); };
  let passed = 0;
  const start = performance.now();

  // ── Mode B verified test cases ──────────────────────────────────────────
  const modeBCases = [
    {
      label:  'B: section=7020 post=50 max=2960 element=none',
      args:   [7020, 50, 3000, 'none'],
      expect: { deductions: 100,  panel_space: 6920, count: 3,
                sheets: [2230, 2231, 2231], total: 7020 }
    },
    {
      label:  'B: section=7020 post=50 max=2960 element=rake',
      args:   [7020, 50, 3000, 'rake'],
      expect: { deductions: 1350, panel_space: 5670, count: 2,
                sheets: [2759, 2759], total: 7020 }
    },
    {
      label:  'B: section=9500 post=50 max=2960 element=none',
      args:   [9500, 50, 3000, 'none'],
      expect: { deductions: 100,  panel_space: 9400, count: 4,
                sheets: [2274, 2274, 2274, 2274], total: 9500 }
    },
    {
      label:  'B: section=9500 post=50 max=2960 element=gate',
      args:   [9500, 50, 3000, 'gate'],
      expect: { deductions: 1110, panel_space: 8390, count: 3,
                sheets: [2720, 2721, 2721], total: 9500 }
    },
    {
      label:  'B: section=9500 post=50 max=2960 element=rake',
      args:   [9500, 50, 3000, 'rake'],
      expect: { deductions: 1350, panel_space: 8150, count: 3,
                sheets: [2640, 2641, 2641], total: 9500 }
    }
  ];

  // ── Mode B with flexible merge (filler absorbed into under-max panel) ──────
  const modeBFlexCases = [
    {
      label:  'B-FLEX: section=7020 post=50 max=2960 → filler merged',
      config: { total: 7020, panelSize: 3000, postSize: 50, gateSize: 0, hasGate: false, hasRake: false, rakeSize: 0, maxSheetLength: 2960 },
      expectFlexed: true,
      expectLayout: (layout) => {
        // Should have 3 panels, last one flexed (absorbed filler)
        const panelCount = layout.filter(p => p.type === 'panel').length;
        const flexedPanel = layout.find(p => p.isFlexed);
        return panelCount === 3 && flexedPanel && flexedPanel.size < 2960 + 300; // rough bounds
      }
    },
    {
      label:  'B-FLEX: section=9500 post=50 max=2960 element=gate → filler merged',
      config: { total: 9500, panelSize: 3000, postSize: 50, gateSize: 900, hasGate: true, hasRake: false, rakeSize: 0, maxSheetLength: 2960 },
      expectFlexed: true,
      expectLayout: (layout) => {
        const flexedPanel = layout.find(p => p.isFlexed);
        const gatePanel = layout.find(p => p.type === 'gate');
        return flexedPanel && flexedPanel.size < 2960 && gatePanel; // gate present, one panel flexed
      }
    }
  ];

  console.log('[PERFOMESH TESTS] Mode B —');
  modeBCases.forEach(({ label, args, expect }) => {
    try {
      const r = calc.calculateSpread(...args);
      assert(r.valid,                              `${label}: result not valid — ${r.error}`);
      assert(r.deductions  === expect.deductions,  `${label}: deductions ${r.deductions} ≠ ${expect.deductions}`);
      assert(r.panel_space === expect.panel_space, `${label}: panel_space ${r.panel_space} ≠ ${expect.panel_space}`);
      assert(r.panel_count === expect.count,       `${label}: count ${r.panel_count} ≠ ${expect.count}`);
      assert(r.total       === expect.total,       `${label}: total ${r.total} ≠ ${expect.total}`);
      const sheets = r.perfomesh.panels.map(p => p.sheet);
      expect.sheets.forEach((s, i) => {
        assert(sheets[i] === s, `${label}: panel[${i}] sheet ${sheets[i]} ≠ ${s}`);
      });
      console.log(`  ✓ ${label}`);
      passed++;
    } catch(err) {
      console.error(`  ✗ ${err.message}`);
    }
  });

  console.log('[PERFOMESH TESTS] Mode B with Flexible Merge —');
  modeBFlexCases.forEach(({ label, config, expectFlexed, expectLayout }) => {
    try {
      const r = PanelCalculator.solveFenceLayout(config);
      assert(r.isFlexed === expectFlexed, `${label}: isFlexed ${r.isFlexed} ≠ ${expectFlexed}`);
      assert(expectLayout(r.layout), `${label}: layout does not meet expectations`);
      console.log(`  ✓ ${label}`);
      passed++;
    } catch(err) {
      console.error(`  ✗ ${err.message}`);
    }
  });

  // ── Mode A verified test cases ──────────────────────────────────────────
  const modeACases = [
    {
      label:  'A: section=7020 post=50 max=2960 element=none',
      args:   [7020, 50, 3000, 'none', []],
      expect: {
        full_panels: 2, cut_sheet: 772,
        source_sheet: 1960, off_cut: 1188,
        total_check: 7020
      }
    },
    {
      label:  'A: section=9500 post=50 max=2960 element=gate',
      args:   [9500, 50, 3000, 'gate', []],
      expect: {
        full_panels: 2, cut_sheet: 2242,
        source_sheet: 2960, off_cut: 718,
        total_check: 9500
      }
    }
  ];

  console.log('[PERFOMESH TESTS] Mode A —');
  modeACases.forEach(({ label, args, expect }) => {
    try {
      const r = calc.calculateCutPlan(...args);
      assert(r.valid,                                           `${label}: result not valid`);
      assert(r.cut_panel,                                      `${label}: expected a cut panel`);
      const fullCount = r.panels.filter(p => !p.is_cut).length;
      assert(fullCount      === expect.full_panels,            `${label}: full_panels ${fullCount} ≠ ${expect.full_panels}`);
      assert(r.cut_panel.sheet        === expect.cut_sheet,    `${label}: cut_sheet ${r.cut_panel.sheet} ≠ ${expect.cut_sheet}`);
      assert(r.cut_panel.source_sheet === expect.source_sheet, `${label}: source_sheet ${r.cut_panel.source_sheet} ≠ ${expect.source_sheet}`);
      assert(r.off_cut                === expect.off_cut,      `${label}: off_cut ${r.off_cut} ≠ ${expect.off_cut}`);
      const totalFP = r.panels.reduce((s, p) => s + p.footprint, 0);
      const DEDS = { none: 100, gate: 1110, rake: 1350 };
      assert(totalFP + DEDS[args[3]] === expect.total_check,   `${label}: total ${totalFP + DEDS[args[3]]} ≠ ${expect.total_check}`);
      console.log(`  ✓ ${label}`);
      passed++;
    } catch(err) {
      console.error(`  ✗ ${err.message}`);
    }
  });

  const total   = modeBCases.length + modeACases.length;
  const elapsed = Math.round(performance.now() - start);
  console.log(`[PERFOMESH TESTS] ${passed}/${total} passed (${elapsed}ms)`);
  return passed === total;
};
// cf-deploy 2026-08-05 — rake width 1252 + rake drawing + flex-merge guard + Canning Vale spelling
